import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ConfigProvider,
  theme,
  Card,
  Spin,
  Modal,
  Input,
  InputNumber,
  notification,
} from "antd";
import { useLocation, useParams, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import io from "socket.io-client";
import {
  IoBrushOutline,
  IoChatbubbleEllipsesOutline,
  IoCameraOutline,
  IoTrashOutline,
  IoCubeOutline,
  IoEyeOutline,
  IoFlagOutline,
  IoHomeOutline,
  IoMicOutline,
  IoOpenOutline,
  IoPeopleOutline,
  IoPersonCircleOutline,
  IoRadioOutline,
  IoRefreshOutline,
  IoSpeedometerOutline,
  IoTimeOutline,
  IoTrophyOutline,
} from "react-icons/io5";
import { useAuth } from "../../services/AuthContext";
import { clearStoredAuthToken, getStoredAuthToken } from "../../services/authStorage";
import { emitSocketVerificationOnce } from "../../services/socketVerification";
import { translations } from "../../translations";
import {
  getLanguageCopy,
  getLocaleForLanguage,
  localizeTournamentName,
  normalizeLanguage,
} from "../../services/language";
import { resolveAvatarSrc } from "../../utils/avatarUrl";
import { buildTutorialLevel2Target } from "../../utils/firstLobbyTutorial";
import {
  normalizeRoomCosmeticRevision,
  resolveRoomCosmeticValue,
  shouldAcceptRoomCosmeticRevision,
} from "../../utils/authoritativeRoomCosmetics";
import {
  DEFAULT_PREFERENCES,
  getPreferencesStorageKey,
  MAX_BLOCK_ANIMATION_SPEED,
  MIN_BLOCK_ANIMATION_SPEED,
  normalizeBlockAnimationSpeed,
  parsePreferences,
  writeGlobalBlockAnimationSpeed,
  writeUserPreference,
} from "../../services/userSettings";
import {
  VOICE_CONTROL_STATUS,
  createVoiceControlController,
  dispatchVoiceCommandToUnity,
  getVoiceControlStatusText,
  getVoiceControlStatusTone,
  getVoiceRecognitionLanguage,
  isTextEntryElement,
} from "../../services/voiceControl";
import { shouldFailPuzzleEndAsOpponentWon } from "./puzzleEndUtils";
import {
  PUZZLE_HUMAN_PLAYER_INDEX,
  resolvePuzzleHumanPlayerIndex,
} from "./puzzleOwnershipUtils";
import {
  PUZZLE_METADATA_STATUS,
  isPuzzleMetadataReadyForAutoplay,
} from "./puzzleAutoplayUtils";
import TournamentVoiceChat from "../../components/TournamentVoiceChat";
import ChampionshipWaitBanner from "../../components/championship/ChampionshipWaitBanner";
import moment from "moment";

import "./card.css";
import "./game7.css";
import { t as i18nT } from "../../i18n";


const MODE_LABELS = {
  dice: "Dice",
  free: "Free",
  cienzar: "Cienzar",
  sciencevsazar: "Ciencia V/S Azar",
  freebox: "FreeBox",
  botlocal: "Bot Local",
  botlocaldice: "Bot Azar",
  botlocalfree: "Bot Ciencia",
  botlocalcienzar: "Bot Cienzar",
  botlocalsciencevsazar: "Bot Ciencia V/S Azar",
  localdice: "Local Dice",
  localcienzar: "Local Cienzar",
  localsciencevsazar: "Local Ciencia V/S Azar",
  localfree: "Local Free",
};
const TOURNAMENT_MODE_ALIASES = Object.freeze({
  azar: "dice",
  dice: "dice",
  ciencia: "free",
  free: "free",
  libre: "free",
  cienzar: "cienzar",
  sciluck: "cienzar",
  sciencevsazar: "sciencevsazar",
  "science-vs-azar": "sciencevsazar",
  cienciavsazar: "sciencevsazar",
  "ciencia-vs-azar": "sciencevsazar",
  contrazar: "sciencevsazar",
  localazar: "localdice",
  localdice: "localdice",
  localciencia: "localfree",
  localfree: "localfree",
  localcienzar: "localcienzar",
  localsciencevsazar: "localsciencevsazar",
  localcienciavsazar: "localsciencevsazar",
  bot: "botlocal",
  freebot: "botlocal",
  botlocal: "botlocal",
  botlocalfree: "botlocalfree",
  botlocaldice: "botlocaldice",
  botlocalcienzar: "botlocalcienzar",
  botlocalsciencevsazar: "botlocalsciencevsazar",
  botlocalcienciavsazar: "botlocalsciencevsazar",
  botazar: "botlocaldice",
  botciencia: "botlocalfree",
  botcienzar: "botlocalcienzar",
  botsciencevsazar: "botlocalsciencevsazar",
  local: "localfree",
  freebox: "freebox",
});
const normalizeTournamentModeAlias = (value, fallback = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return fallback;
  return TOURNAMENT_MODE_ALIASES[normalized] || normalized;
};
const BOT_LOCAL_MODE_VALUES = new Set([
  "botlocal",
  "botlocaldice",
  "botlocalfree",
  "botlocalcienzar",
  "botlocalsciencevsazar",
  "botlocalcontrazar",
]);
const LOCAL_FRIENDS_MODE_VALUES = new Set([
  "localdice",
  "localcienzar",
  "localsciencevsazar",
  "localfree",
]);
const BOT_DIFFICULTY_VALUES = new Set(["easy", "medium", "hard", "injusto"]);
const normalizeRuntimeBotDifficulty = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === "injusta" || normalized === "unfair") return "injusto";
  return BOT_DIFFICULTY_VALUES.has(normalized) ? normalized : "";
};
const MODE_ICON_SRC_MAP = Object.freeze({
  azar: "/mode-icons/logo-modo-azar.png",
  ciencia: "/mode-icons/free.png",
  cienzar: "/mode-icons/cienzar.png",
  sciencevsazar: "/mode-icons/logo-contrazar.png",
  dice: "/mode-icons/logo-modo-azar.png",
  free: "/mode-icons/free.png",
  freebox: "/mode-icons/cienzar.png",
  botlocal: "/mode-icons/cienzar.png",
  botlocaldice: "/mode-icons/logo-modo-azar.png",
  botlocalfree: "/mode-icons/free.png",
  botlocalcienzar: "/mode-icons/cienzar.png",
  botlocalsciencevsazar: "/mode-icons/logo-contrazar.png",
  botlocalcontrazar: "/mode-icons/logo-contrazar.png",
  localdice: "/mode-icons/logo-modo-azar.png",
  localfree: "/mode-icons/free.png",
  localcienzar: "/mode-icons/cienzar.png",
  localsciencevsazar: "/mode-icons/logo-contrazar.png",
});
const PLAYER_COUNT_ICON_SRC_MAP = Object.freeze({
  2: "/mode-icons/players-1v1.png",
  3: "/mode-icons/players-1v2.png",
  4: "/mode-icons/players-1v3.png",
});
const TIME_ICON_SRC_MAP = Object.freeze({
  1: "/assets/images/1minutomorado.svg",
  2: "/assets/images/2minutosmorados.svg",
  5: "/assets/images/5minutosmorados.png",
  10: "/assets/images/10minutosmorados.png",
  30: "/assets/images/30minutosmorados.png",
});
const resolveTournamentModeIconSrc = (mode, freeBoxBaseMode = "") => {
  const normalizedModeValue = normalizeTournamentModeAlias(mode);
  if (normalizedModeValue === "freebox" || normalizedModeValue === "botlocal") {
    const normalizedBaseMode = normalizeTournamentModeAlias(freeBoxBaseMode);
    return (
      MODE_ICON_SRC_MAP[normalizedBaseMode] ||
      MODE_ICON_SRC_MAP[normalizedModeValue] ||
      MODE_ICON_SRC_MAP.cienzar
    );
  }
  return MODE_ICON_SRC_MAP[normalizedModeValue] || MODE_ICON_SRC_MAP.cienzar;
};
const isBotLocalModeValue = (mode) =>
  BOT_LOCAL_MODE_VALUES.has(normalizeTournamentModeAlias(mode));
const isLocalFriendsModeValue = (mode) =>
  LOCAL_FRIENDS_MODE_VALUES.has(normalizeTournamentModeAlias(mode));
const resolveTournamentTimeIconSrc = (minutes) => {
  const normalizedMinutes = Math.floor(Number(minutes));
  return TIME_ICON_SRC_MAP[normalizedMinutes] || "";
};
const GUIDED_TUTORIAL_UI_COPY = Object.freeze({
  level1Complete: {
    es: "NIVEL 1 COMPLETADO",
    en: "LEVEL 1 COMPLETE",
    de: "STUFE 1 ABGESCHLOSSEN",
    ru: "УРОВЕНЬ 1 ПРОЙДЕН",
  },
  level2Complete: {
    es: "NIVEL 2 COMPLETADO",
    en: "LEVEL 2 COMPLETE",
    de: "STUFE 2 ABGESCHLOSSEN",
    ru: "УРОВЕНЬ 2 ПРОЙДЕН",
  },
  nextLevel: {
    es: "Siguiente nivel",
    en: "Next level",
    de: "Nächste Stufe",
    ru: "Следующий уровень",
  },
  backToLobby: {
    es: "Volver al lobby",
    en: "Back to lobby",
    de: "Zurück zur Lobby",
    ru: "Вернуться в лобби",
  },
});
const formatTournamentSequenceLabel = (language, value) => {
  const formattedValue = new Intl.NumberFormat(getLocaleForLanguage(language)).format(
    Number(value) || 0
  );
  const normalizedLanguage = normalizeLanguage(language);
  if (normalizedLanguage === "en") return `Tournament #${formattedValue}`;
  if (normalizedLanguage === "de") return `Turnier #${formattedValue}`;
  return `Torneo #${formattedValue}`;
};
const normalizePublicSequenceNumberValue = (value) => {
  const parsed = Math.floor(Number(value) || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};
const resolvePublicSequenceNumber = (entry) => {
  return (
    normalizePublicSequenceNumberValue(entry?.tournamentUiSequenceNumber) ||
    normalizePublicSequenceNumberValue(entry?.tournamentSequenceNumber) ||
    normalizePublicSequenceNumberValue(entry?.tournamentNumber) ||
    normalizePublicSequenceNumberValue(entry?.displayNumber) ||
    0
  );
};
const getTournamentEntryId = (entry) =>
  String(entry?._id || entry?.id || entry?.tournamentId || "").trim();
const isTournamentLobbySequenceCandidate = (entry) => {
  const normalizedMode = normalizeTournamentModeAlias(entry?.mode);
  const sequenceMode =
    normalizedMode === "ciencia"
      ? "free"
      : normalizedMode === "azar"
      ? "dice"
      : normalizedMode;
  const isLobbyMode = ["dice", "cienzar", "sciencevsazar", "free", "freebox"].includes(sequenceMode);
  const isIndependent =
    entry?.isIndependentMatch === true ||
    String(entry?.isIndependentMatch || "").trim().toLowerCase() === "true";
  const isGeneratedChallenge = /^desaf[ií]o creado por\b/i.test(
    String(entry?.tournamentName || "").trim()
  );

  return (
    isLobbyMode &&
    !isIndependent &&
    entry?.isSuperuserCreated === true &&
    !isGeneratedChallenge
  );
};
const resolveTournamentFallbackSequenceNumberFromList = (entries, targetId) => {
  const normalizedTargetId = String(targetId || "").trim();
  if (!normalizedTargetId) return 0;
  const orderedEntries = (Array.isArray(entries) ? entries : [])
    .filter((entry) => entry?.status !== "finished")
    .filter(isTournamentLobbySequenceCandidate)
    .sort(
      (a, b) =>
        new Date(b?.createdAt || b?.startingTime || 0).valueOf() -
        new Date(a?.createdAt || a?.startingTime || 0).valueOf()
    );
  const index = orderedEntries.findIndex(
    (entry) => getTournamentEntryId(entry) === normalizedTargetId
  );
  return index >= 0 ? index + 1 : 0;
};
const GUEST_USER_PREFIX = "invitadoo";
const GUEST_EMAIL_DOMAIN = "guest.xima.local";
const DEFAULT_CIMAX_SCORES = Object.freeze({
  ciencia: 3000,
  cienzar: 2000,
  sciencevsazar: 2000,
  azar: 1000,
});
const MATCH_PASSWORD_MIN_LENGTH = 1;
const MATCH_PASSWORD_MAX_LENGTH = 4;
const MATCH_PASSWORD_REGEX = /^\d+$/;
const normalizeMatchPassword = (value) =>
  String(value || "")
    .replace(/[^\d]/g, "")
    .slice(0, MATCH_PASSWORD_MAX_LENGTH);
const isValidMatchPassword = (value) => {
  const normalizedPassword = normalizeMatchPassword(value);
  return (
    normalizedPassword.length >= MATCH_PASSWORD_MIN_LENGTH &&
    normalizedPassword.length <= MATCH_PASSWORD_MAX_LENGTH &&
    MATCH_PASSWORD_REGEX.test(normalizedPassword)
  );
};
const SHOW_FRONTEND_PLAYER_PANELS = false;
// Diseño «Juego 7/8» de la pantalla de partida: solo en ordenador. El móvil
// sigue con el panel anterior.
const GAME7_LAYOUT_QUERY = "(min-width: 1100px) and (min-height: 560px)";
const readGame7LayoutMatch = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia(GAME7_LAYOUT_QUERY).matches;
  } catch (error) {
    return false;
  }
};
const USE_SERVER_AUTHORITATIVE_TURN_CLOCK = true;
const PLAYER_PANEL_COLORS = [
  {
    border: "border-sky-400/80",
    background: "from-sky-500/28 via-sky-500/16 to-slate-900/94",
    accent: "bg-sky-400",
    text: "text-sky-100",
    mutedText: "text-sky-200/85",
    glow: "shadow-[0_0_18px_rgba(56,189,248,0.28)]",
  },
  {
    border: "border-rose-400/80",
    background: "from-rose-500/28 via-rose-500/16 to-slate-900/94",
    accent: "bg-rose-400",
    text: "text-rose-100",
    mutedText: "text-rose-100/80",
    glow: "shadow-[0_0_18px_rgba(251,113,133,0.28)]",
  },
  {
    border: "border-amber-300/80",
    background: "from-amber-400/30 via-amber-300/16 to-slate-900/94",
    accent: "bg-amber-300",
    text: "text-amber-50",
    mutedText: "text-amber-100/80",
    glow: "shadow-[0_0_18px_rgba(252,211,77,0.24)]",
  },
  {
    border: "border-emerald-400/80",
    background: "from-emerald-500/28 via-emerald-500/16 to-slate-900/94",
    accent: "bg-emerald-400",
    text: "text-emerald-100",
    mutedText: "text-emerald-100/80",
    glow: "shadow-[0_0_18px_rgba(52,211,153,0.24)]",
  },
];
const TURN_INDICATOR_COLORS = [
  "border-blue-200/90 bg-blue-600/90 text-blue-50 shadow-[0_10px_28px_rgba(37,99,235,0.45)]",
  "border-rose-200/90 bg-rose-600/90 text-rose-50 shadow-[0_10px_28px_rgba(225,29,72,0.45)]",
  "border-amber-200/90 bg-amber-400/95 text-slate-900 shadow-[0_10px_28px_rgba(245,158,11,0.45)]",
  "border-emerald-200/90 bg-emerald-600/90 text-emerald-50 shadow-[0_10px_28px_rgba(5,150,105,0.45)]",
];
const BOT_INTRO_AVATAR_DATA_URIS = [
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 128 128'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%230ea5e9'/><stop offset='1' stop-color='%233b82f6'/></linearGradient></defs><rect width='128' height='128' rx='24' fill='url(%23g)'/><circle cx='64' cy='48' r='20' fill='white' fill-opacity='.9'/><rect x='30' y='74' width='68' height='26' rx='13' fill='white' fill-opacity='.9'/></svg>",
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 128 128'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%2310b981'/><stop offset='1' stop-color='%2322c55e'/></linearGradient></defs><rect width='128' height='128' rx='24' fill='url(%23g)'/><circle cx='64' cy='48' r='20' fill='white' fill-opacity='.9'/><rect x='30' y='74' width='68' height='26' rx='13' fill='white' fill-opacity='.9'/></svg>",
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 128 128'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%23f59e0b'/><stop offset='1' stop-color='%23ef4444'/></linearGradient></defs><rect width='128' height='128' rx='24' fill='url(%23g)'/><circle cx='64' cy='48' r='20' fill='white' fill-opacity='.9'/><rect x='30' y='74' width='68' height='26' rx='13' fill='white' fill-opacity='.9'/></svg>",
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 128 128'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%23a855f7'/><stop offset='1' stop-color='%23ec4899'/></linearGradient></defs><rect width='128' height='128' rx='24' fill='url(%23g)'/><circle cx='64' cy='48' r='20' fill='white' fill-opacity='.9'/><rect x='30' y='74' width='68' height='26' rx='13' fill='white' fill-opacity='.9'/></svg>",
];
const isBotLikeName = (name) => /\bbot\b/i.test(String(name || "").trim());
const normalizeBotSlotIndexes = (value) => {
  const source = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  return Array.from(
    new Set(
      source
        .map((entry) => Number(entry))
        .filter((entry) => Number.isFinite(entry))
        .map((entry) => Math.floor(entry))
        .filter((entry) => entry >= 0 && entry <= 3)
    )
  );
};
const resolveBotSlotIndexesFromPayload = (payload, users = []) => {
  const explicitSlots = normalizeBotSlotIndexes(payload?.botSlots);
  if (explicitSlots.length > 0) return explicitSlots;

  return (Array.isArray(users) ? users : [])
    .map((entry, index) => (isBotLikeName(entry) ? index : -1))
    .filter((index) => index >= 0 && index <= 3);
};

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (typeof window !== "undefined" ? window.location.origin : "http://localhost:5001");
const UNITY_BUILD_DIR = String(process.env.REACT_APP_UNITY_BUILD_DIR || "Xima_build")
  .replace(/^\/+/, "")
  .replace(/\/+$/, "");
const UNITY_INDEX_FILE = String(process.env.REACT_APP_UNITY_INDEX_FILE || "index.html").replace(
  /^\/+/,
  ""
);
const UNITY_DEPLOY_VERSION = String(process.env.REACT_APP_DEPLOY_VERSION || "legacy");
const UNITY_IFRAME_BASE_PATH = `/${UNITY_BUILD_DIR}/${UNITY_INDEX_FILE}`;
const CAMERA_FOLLOW_GLOBAL_STORAGE_KEY = "xima_camera_follow_enabled_v1";

const readCameraFollowPreference = (email) => {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES.cameraFollowEnabled;
  const storageKey = getPreferencesStorageKey(email);
  const storedPreferences = parsePreferences(window.localStorage.getItem(storageKey));
  const enabled = Boolean(
    storedPreferences?.cameraFollowEnabled ?? DEFAULT_PREFERENCES.cameraFollowEnabled
  );
  window.localStorage.setItem(CAMERA_FOLLOW_GLOBAL_STORAGE_KEY, enabled ? "1" : "0");
  return enabled;
};

const readVoiceControlPreference = (email) => {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES.voiceControlEnabled;
  const storageKey = getPreferencesStorageKey(email);
  const storedPreferences = parsePreferences(window.localStorage.getItem(storageKey));
  return Boolean(
    storedPreferences?.voiceControlEnabled ?? DEFAULT_PREFERENCES.voiceControlEnabled
  );
};

const writeCameraFollowPreference = (email, enabled) => {
  if (typeof window === "undefined") return;
  const storageKey = getPreferencesStorageKey(email);
  const storedPreferences =
    parsePreferences(window.localStorage.getItem(storageKey)) || DEFAULT_PREFERENCES;
  window.localStorage.setItem(
    storageKey,
    JSON.stringify({
      ...storedPreferences,
      cameraFollowEnabled: enabled === true,
    })
  );
  window.localStorage.setItem(CAMERA_FOLLOW_GLOBAL_STORAGE_KEY, enabled === true ? "1" : "0");
};

const handleSessionInvalidated = () => {
  delete axios.defaults.headers["Authorization"];
  clearStoredAuthToken();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

const getSharedSocket = () => {
  if (typeof window === "undefined") return null;

  if (window.__ximaSocket) {
    window.__ximaSocket.off("SESSION_INVALIDATED", handleSessionInvalidated);
    window.__ximaSocket.on("SESSION_INVALIDATED", handleSessionInvalidated);
    if (
      window.__ximaSocket.disconnected &&
      typeof window.__ximaSocket.connect === "function"
    ) {
      window.__ximaSocket.connect();
    }
    window.socket = window.__ximaSocket;
    return window.__ximaSocket;
  }

  if (window.socket) {
    window.socket.off("SESSION_INVALIDATED", handleSessionInvalidated);
    window.socket.on("SESSION_INVALIDATED", handleSessionInvalidated);
    if (window.socket.disconnected && typeof window.socket.connect === "function") {
      window.socket.connect();
    }
    window.__ximaSocket = window.socket;
    return window.socket;
  }

  const sharedSocket = io(SOCKET_URL, {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 800,
    reconnectionDelayMax: 10000,
    randomizationFactor: 0.5,
    timeout: 20000,
  });
  sharedSocket.on("connect", () => {
    const token = String(getStoredAuthToken() || "0");
    emitSocketVerificationOnce(sharedSocket, token);
  });
  sharedSocket.on("SESSION_INVALIDATED", handleSessionInvalidated);

  window.__ximaSocket = sharedSocket;
  window.socket = sharedSocket;
  return sharedSocket;
};

const waitForSocketConnection = (socket, timeoutMs = 4500) =>
  new Promise((resolve) => {
    if (!socket) {
      resolve(false);
      return;
    }

    if (socket.connected) {
      resolve(true);
      return;
    }

    let settled = false;
    let timerId = null;

    const cleanup = () => {
      if (timerId) clearTimeout(timerId);
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
    };

    const finish = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(Boolean(value));
    };

    const handleConnect = () => finish(true);
    const handleConnectError = () => finish(false);

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    timerId = setTimeout(() => finish(Boolean(socket.connected)), timeoutMs);

    try {
      if (socket.disconnected && typeof socket.connect === "function") {
        socket.connect();
      }
    } catch (error) {
      finish(false);
    }
  });

const ensureSocketSessionReady = async () => {
  const socket = getSharedSocket();
  if (!socket) return false;

  const token = String(getStoredAuthToken() || "").trim();
  if (!token || token === "0") return false;

  const connected = await waitForSocketConnection(socket);
  if (!connected) return false;

  emitSocketVerificationOnce(socket, token);
  await new Promise((resolve) => setTimeout(resolve, 140));
  return true;
};

const IGNORED_CLIENT_FAILURE_DETAIL_PATTERNS = [
  /decodeaudiodata/i,
  /unknown content type/i,
  /loading fsb failed/i,
  /sound which is not loaded/i,
];

const shouldIgnoreClientFailureReport = (reason, detail) => {
  const normalizedReason = String(reason || "")
    .trim()
    .toLowerCase();
  const normalizedDetail = String(detail || "").trim();
  if (!normalizedDetail) return false;

  // Never ignore missing-script errors.
  if (normalizedReason === "script_missing") return false;

  return IGNORED_CLIENT_FAILURE_DETAIL_PATTERNS.some((pattern) =>
    pattern.test(normalizedDetail)
  );
};

const formatModeLabel = (mode) => {
  const normalizedMode = normalizeTournamentModeAlias(mode);
  return MODE_LABELS[normalizedMode] || String(mode || "").toUpperCase();
};
const BOARD_FILES = "ABCDEFGHIJKLM";
const BOARD_SQUARE_REGEX = /\b([A-M](?:1[0-3]|[1-9]))\b/i;

const buildNotationRows = (entries, playerCount) => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const safePlayerCount = Math.max(2, Math.min(4, Number(playerCount) || 2));
  const normalizedEntries = safeEntries
    .map((entry) => {
      const notation = String(entry?.notation || "").trim();
      if (!notation) return null;

      const rawIndex = Number(entry?.playerIndex);
      const playerIndex =
        Number.isFinite(rawIndex) && rawIndex >= 0 ? Math.floor(rawIndex) : 0;

      return { notation, playerIndex };
    })
    .filter(Boolean);

  if (normalizedEntries.length === 0) return [];

  // Replica el formato estable del MoveHistoryText de Unity:
  // 1. j1 j2 j3 ...
  // 2. j1 j2 j3 ...
  // Si un jugador mueve varias veces antes de rotar turno, se concatena en su celda.
  const columns = [];
  const columnByPlayer = new Map();
  normalizedEntries.forEach((entry) => {
    if (columnByPlayer.has(entry.playerIndex)) return;
    if (columns.length >= safePlayerCount) return;
    columnByPlayer.set(entry.playerIndex, columns.length);
    columns.push(entry.playerIndex);
  });

  for (let fallback = 0; columns.length < safePlayerCount; fallback += 1) {
    if (columnByPlayer.has(fallback)) continue;
    columnByPlayer.set(fallback, columns.length);
    columns.push(fallback);
  }

  const rows = [];
  let currentRowIndex = -1;
  let lastColumn = -1;
  let lastPlayer = NaN;
  let hasLast = false;

  normalizedEntries.forEach((entry) => {
    const resolvedColumnRaw = columnByPlayer.get(entry.playerIndex);
    const resolvedColumn = Number.isFinite(resolvedColumnRaw) ? resolvedColumnRaw : 0;
    const shouldStartNewRow =
      currentRowIndex < 0 ||
      !hasLast ||
      (entry.playerIndex !== lastPlayer && resolvedColumn <= lastColumn);

    if (shouldStartNewRow) {
      rows.push(new Array(columns.length).fill(""));
      currentRowIndex = rows.length - 1;
    }

    const row = rows[currentRowIndex];
    if (!row[resolvedColumn]) row[resolvedColumn] = entry.notation;
    else row[resolvedColumn] = `${row[resolvedColumn]} ${entry.notation}`.trim();

    hasLast = true;
    lastColumn = resolvedColumn;
    lastPlayer = entry.playerIndex;
  });

  return rows;
};

const appendNotationEntry = (entries, entry) => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const notation = String(entry?.notation || "").trim();
  if (!notation) return safeEntries;

  const playerIndexRaw = Number(entry?.playerIndex);
  const playerIndex =
    Number.isFinite(playerIndexRaw) && playerIndexRaw >= 0
      ? Math.floor(playerIndexRaw)
      : 0;
  const payload = String(entry?.payload || "").trim();
  const lastEntry = safeEntries[safeEntries.length - 1];

  if (
    lastEntry &&
    String(lastEntry.notation || "") === notation &&
    Number(lastEntry.playerIndex) === playerIndex &&
    String(lastEntry.payload || "") === payload
  ) {
    return safeEntries;
  }

  return [...safeEntries, { playerIndex, notation, payload }].slice(-240);
};

const clampValue = (value, min, max) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return Number(min) || 0;
  const safeMin = Number(min);
  const safeMax = Number(max);
  if (!Number.isFinite(safeMin) || !Number.isFinite(safeMax) || safeMax < safeMin) return parsed;
  return Math.min(Math.max(parsed, safeMin), safeMax);
};

const clampValueInRange = (value, min, max) => {
  const safeValue = Number(value);
  if (!Number.isFinite(safeValue)) return 0;
  const safeMin = Number.isFinite(Number(min)) ? Number(min) : 0;
  const safeMax = Number.isFinite(Number(max)) ? Number(max) : 0;
  const lower = Math.min(safeMin, safeMax);
  const upper = Math.max(safeMin, safeMax);
  return Math.min(Math.max(safeValue, lower), upper);
};

const parsePuzzleEndActionButtonPosition = (rawValue) => {
  try {
    const parsed = JSON.parse(String(rawValue || ""));
    if (!parsed || typeof parsed !== "object") return null;
    const x = Number(parsed.x);
    const y = Number(parsed.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x: Math.round(x), y: Math.round(y) };
  } catch {
    return null;
  }
};

const getDefaultPuzzleEndActionButtonPosition = (
  width = PUZZLE_END_ACTION_BUTTON_DEFAULT_WIDTH,
  height = PUZZLE_END_ACTION_BUTTON_DEFAULT_HEIGHT,
  options = {}
) => {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  const safeWidth = Math.max(1, Number(width) || PUZZLE_END_ACTION_BUTTON_DEFAULT_WIDTH);
  const safeHeight = Math.max(1, Number(height) || PUZZLE_END_ACTION_BUTTON_DEFAULT_HEIGHT);
  const safeOptions = options && typeof options === "object" ? options : {};
  const viewportWidth = Math.max(
    1,
    Number.isFinite(Number(safeOptions.viewportWidth))
      ? Number(safeOptions.viewportWidth)
      : Number(window.innerWidth) || 1
  );
  const viewportHeight = Math.max(
    1,
    Number.isFinite(Number(safeOptions.viewportHeight))
      ? Number(safeOptions.viewportHeight)
      : Number(window.innerHeight) || 1
  );
  const maxX = Math.max(0, viewportWidth - safeWidth);
  const maxY = Math.max(0, viewportHeight - safeHeight);

  if (Boolean(safeOptions.forceMobilePuzzleLayout)) {
    const mobileBottomSpacing = Math.max(
      24,
      Math.min(96, Math.round(viewportHeight * 0.16))
    );

    return {
      x: clampValue(Math.round((viewportWidth - safeWidth) / 2), 0, maxX),
      y: clampValue(viewportHeight - safeHeight - mobileBottomSpacing, 0, maxY),
    };
  }

  return {
    x: clampValue(Math.round((viewportWidth - safeWidth) / 2), 0, maxX),
    y: clampValue(Math.max(16, viewportHeight - safeHeight - 16), 0, maxY),
  };
};

const isCompactPuzzleLayoutDevice = () => {
  if (typeof window === "undefined") return false;
  if (window.innerWidth <= 768) return true;
  if (typeof window.matchMedia !== "function") return false;

  return (
    window.matchMedia("(pointer: coarse)").matches ||
    window.matchMedia("(hover: none)").matches
  );
};

const clampPuzzleEndActionButtonPosition = (position, width, height) => {
  const safeWidth = Math.max(1, Number(width) || PUZZLE_END_ACTION_BUTTON_DEFAULT_WIDTH);
  const safeHeight = Math.max(1, Number(height) || PUZZLE_END_ACTION_BUTTON_DEFAULT_HEIGHT);
  if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) {
    return getDefaultPuzzleEndActionButtonPosition(safeWidth, safeHeight);
  }
  if (typeof window === "undefined") return { x: position.x, y: position.y };
  return {
    x: clampValue(position.x, 0, Math.max(0, window.innerWidth - safeWidth)),
    y: clampValue(position.y, 0, Math.max(0, window.innerHeight - safeHeight)),
  };
};

const readPuzzleEndActionButtonPosition = () => {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  const stored = parsePuzzleEndActionButtonPosition(
    window.localStorage.getItem(PUZZLE_END_ACTION_BUTTON_POSITION_STORAGE_KEY)
  );
  if (!stored) return getDefaultPuzzleEndActionButtonPosition();
  return clampPuzzleEndActionButtonPosition(
    stored,
    PUZZLE_END_ACTION_BUTTON_DEFAULT_WIDTH,
    PUZZLE_END_ACTION_BUTTON_DEFAULT_HEIGHT
  );
};

const QUICK_CHAT_MESSAGES = [
  "Bien jugado \uD83D\uDC4F",
  "Tremenda jugada \uD83D\uDD25",
  "Casi \uD83D\uDE05",
  "Buena jugada \uD83D\uDC4D",
  "GG \uD83C\uDFC6",
  "Buena suerte \uD83C\uDF40",
  "Vamos que se puede \uD83D\uDCAA",
  "Buena estrategia \uD83E\uDDE0",
  "Rapido \u23F1\uFE0F",
  "Tomate tu tiempo \uD83D\uDE42",
  "Que tirada \uD83C\uDFB2",
  "Gracias \uD83D\uDE4C",
  "Dale \uD83D\uDE80",
  "Nice \uD83D\uDE0E",
  "Respeto \uD83E\uDD1D",
  "Jajaja \uD83D\uDE02",
  "Wow \uD83D\uDE2E",
  "Buena partida \uD83C\uDF89",
  "Vamos arriba \uD83D\uDD1D",
  "Buena defensa \uD83D\uDEE1\uFE0F",
  "Buen movimiento \u265F\uFE0F",
  "Te veo \uD83D\uDC40",
  "ESTAS AHI? \uD83D\uDC4B",
  "Excelente \u2728",
  "Paz \uD83D\uDD4A\uFE0F",
];
const CHAT_FLOATING_MIN_WIDTH = 360;
const CHAT_FLOATING_MIN_HEIGHT = 280;
const CHAT_FLOATING_DEFAULT_WIDTH = 520;
const CHAT_FLOATING_DEFAULT_HEIGHT = 470;
const CHAT_FLOATING_MAX_WIDTH = 980;
const CHAT_FLOATING_MAX_HEIGHT = 760;
const PUZZLE_END_ACTION_BUTTON_POSITION_STORAGE_KEY =
  "xima_puzzle_end_action_button_position_v1";
const PUZZLE_END_ACTION_BUTTON_DEFAULT_WIDTH = 320;
const PUZZLE_END_ACTION_BUTTON_DEFAULT_HEIGHT = 160;
const SPECIAL_BLOCK_STORAGE_PREFIX = "xima_special_block_v1_";
const COMMON_BLOCK_STORAGE_PREFIX = "xima_common_block_v1_";
const GLOBAL_SPECIAL_BLOCK_KEY = "xima_special_block_selected";
const GLOBAL_SPECIAL_BLOCK_IMAGE_KEY = "xima_special_block_image";
const GLOBAL_COMMON_BLOCK_THEME_KEY = "xima_common_block_theme_selected";
const GLOBAL_BLOCK_MATERIAL_TEXTURE_KEY = "xima_block_material_texture_selected";
const GLOBAL_BLOCK_ANIMATION_KEY = "xima_block_animation_selected";
const PROFILE_STORAGE_PREFIX = "xima_profile_v1_";
const PUZZLE_SOLVED_STORAGE_PREFIX = "xima_puzzle_solved_v1_";
const ACTIVE_MATCH_SESSION_STORAGE_KEY = "xima_active_match_session_v1";
const ACTIVE_MATCH_SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const DEFAULT_SPECIAL_BLOCK_ID = "default_star";
const DEFAULT_SPECIAL_SKIN_NAME = "default_star";
const DEFAULT_COMMON_BLOCK_THEME_ID = "default_star";
const DEFAULT_BLOCK_MATERIAL_TEXTURE_ID = "default";
const DEFAULT_BLOCK_ANIMATION_ID = "default";
const MAX_SKIN_ID_LENGTH = 120;
const MAX_SOCKET_AVATAR_URL_LENGTH = 350000;
const AVATAR_SYNC_MAX_DIMENSION = 320;
const AVATAR_SYNC_QUALITY_STEPS = [0.9, 0.82, 0.74, 0.66, 0.58];
const MAX_PUZZLE_MOVE_LOG_SIZE = 500;
const MAX_PUZZLE_MOVE_NOTATION_COUNT = 240;
const MAX_PUZZLE_SNAPSHOT_WAIT_MS = 7000;
const MAX_PUZZLE_TURN_LIMIT = 999;
const DEFAULT_PUZZLE_TURN_LIMIT = 30;
const DEFAULT_PUZZLE_REWARD_VALUE = 10;
const MAX_PUZZLE_REWARD_VALUE = 1_000_000_000;
const MAX_PUZZLE_SOLVE_TIME_LIMIT_SECONDS = 24 * 60 * 60;
const DEFAULT_PUZZLE_SOLVE_TIME_MINUTES = 5;
const MAX_PUZZLE_SOLVE_TIME_MINUTES = Math.floor(MAX_PUZZLE_SOLVE_TIME_LIMIT_SECONDS / 60);
const MAX_PUZZLE_SNAPSHOT_APPLY_RETRIES = 3;
const PUZZLE_SNAPSHOT_RETRY_DELAY_MS = 180;
const MAX_PUZZLE_SNAPSHOT_VERIFICATION_TIMEOUT_MS = 6800;
const MAX_PUZZLE_BOARD_READY_WAIT_MS = 4500;
const PUZZLE_BOARD_READY_PROBE_INTERVAL_MS = 220;
const POST_PUZZLE_ACTION_DELAY_MS = 0;
const PUZZLE_PRELOAD_INITIAL_AHEAD_COUNT = 3;
const PUZZLE_PRELOAD_ROLLING_AHEAD_COUNT = 2;
const PUZZLE_PRELOAD_LIST_TTL_MS = 15000;
const PUZZLE_PRELOAD_PAYLOAD_TTL_MS = 10 * 60 * 1000;
const PUZZLE_PRELOAD_MAX_CACHED_PAYLOADS = 30;
const MAX_PUZZLE_BOARD_VERSION_LENGTH = 120;
const MAX_PUZZLE_CUBE_ID_LENGTH = 96;
const PUZZLE_SNAPSHOT_EVENT = "XIMA_PUZZLE_POSITION_SNAPSHOT";
const PUZZLE_SNAPSHOT_APPLIED_EVENT = "XIMA_PUZZLE_POSITION_APPLIED";
const MATCH_STATE_APPLIED_EVENT = "XIMA_MATCH_STATE_APPLIED";
const LOCAL_GAME_END_EVENT = "XIMA_LOCAL_GAME_END";
const LOCAL_MATCH_MOVE_EVENT = "XIMA_LOCAL_MATCH_MOVE";
const LOCAL_MATCH_ROLL_DICE_EVENT = "XIMA_LOCAL_MATCH_ROLL_DICE";
const PUZZLE_PRELOAD_STATE = {
  orderedByMode: {},
  payloadByPuzzleId: {},
  inFlightByPuzzleId: {},
};
const normalizeSkinId = (value, fallback = "") => {
  let normalized = String(value || "").trim();

  if (!normalized) return fallback;
  normalized = normalized.replace(/^skins\//i, "");
  normalized = normalized.replace(/\.[a-z0-9]+$/i, "");

  if (!normalized) return fallback;
  if (normalized.length > MAX_SKIN_ID_LENGTH) return fallback;
  if (normalized.includes("..")) return fallback;
  if (normalized.includes("/") || normalized.includes("\\")) return fallback;

  return normalized;
};
const normalizeSpecialBlockId = (value) => normalizeSkinId(value, "");
const resolveStoredSpecialBlockImage = (skinId, explicitImage) => {
  const normalizedExplicitImage = String(explicitImage || "").trim();
  if (normalizedExplicitImage) return normalizedExplicitImage;

  const normalizedSkinId = normalizeSpecialBlockId(skinId);
  if (!normalizedSkinId) return "";
  return `/skins/${encodeURIComponent(normalizedSkinId)}.png`;
};

const getProfileStorageKey = (email) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  return normalizedEmail ? `${PROFILE_STORAGE_PREFIX}${normalizedEmail}` : "";
};

const getSolvedPuzzlesStorageKey = (email) => {
  const normalizedEmail = String(email || "guest").trim().toLowerCase();
  return `${PUZZLE_SOLVED_STORAGE_PREFIX}${normalizedEmail || "guest"}`;
};

const parseSolvedPuzzleIds = (rawValue) => {
  try {
    const parsed = JSON.parse(String(rawValue || "[]"));
    if (!Array.isArray(parsed)) return [];
    return Array.from(
      new Set(
        parsed
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      )
    );
  } catch {
    return [];
  }
};

const markPuzzleAsSolvedForUser = (email, puzzleId) => {
  if (typeof window === "undefined") return;
  const normalizedPuzzleId = String(puzzleId || "").trim();
  if (!normalizedPuzzleId) return;

  const storageKey = getSolvedPuzzlesStorageKey(email);
  if (!storageKey) return;

  const existingIds = parseSolvedPuzzleIds(window.localStorage.getItem(storageKey));
  if (existingIds.includes(normalizedPuzzleId)) return;

  window.localStorage.setItem(
    storageKey,
    JSON.stringify([...existingIds, normalizedPuzzleId])
  );
};

const appendPuzzleMovePayload = (entries, rawPayload) => {
  const payload = String(rawPayload || "").trim();
  if (!payload) return Array.isArray(entries) ? entries : [];

  const safeEntries = Array.isArray(entries) ? entries : [];
  if (safeEntries[safeEntries.length - 1] === payload) {
    return safeEntries;
  }

  return [...safeEntries, payload].slice(-MAX_PUZZLE_MOVE_LOG_SIZE);
};
const getPuzzleSequenceEntryId = (entry) =>
  String(entry?.id || entry?._id || entry?.tournamentId || "").trim();
const toOrderedPuzzleSequenceEntries = (rawEntries) =>
  (Array.isArray(rawEntries) ? rawEntries : [])
    .filter((entry) => getPuzzleSequenceEntryId(entry))
    .sort(
      (a, b) => new Date(a?.createdAt || 0).valueOf() - new Date(b?.createdAt || 0).valueOf()
    );

const parseStoredProfile = (rawValue) => {
  try {
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const getStoredAvatarByEmail = (email) => {
  if (typeof window === "undefined") return "";
  const key = getProfileStorageKey(email);
  if (!key) return "";
  const parsed = parseStoredProfile(localStorage.getItem(key));
  return String(parsed?.avatarUrl || "").trim();
};

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load image"));
    image.src = src;
  });

const compressAvatarDataUrl = async (dataUrl) => {
  const image = await loadImageElement(dataUrl);
  const maxEdge = Math.max(image.width || 1, image.height || 1);
  const scale = Math.min(1, AVATAR_SYNC_MAX_DIMENSION / maxEdge);
  const width = Math.max(1, Math.round((image.width || 1) * scale));
  const height = Math.max(1, Math.round((image.height || 1) * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return "";
  context.drawImage(image, 0, 0, width, height);

  let bestCandidate = "";
  for (let i = 0; i < AVATAR_SYNC_QUALITY_STEPS.length; i += 1) {
    const quality = AVATAR_SYNC_QUALITY_STEPS[i];
    const candidate = canvas.toDataURL("image/jpeg", quality);
    if (!candidate) continue;
    bestCandidate = candidate;
    if (candidate.length <= MAX_SOCKET_AVATAR_URL_LENGTH) {
      return candidate;
    }
  }

  return bestCandidate;
};

const normalizeAvatarForSocket = async (avatarUrl) => {
  const rawAvatar = String(avatarUrl || "").trim();
  if (!rawAvatar) return "";
  if (!rawAvatar.startsWith("data:image/")) {
    return rawAvatar.slice(0, MAX_SOCKET_AVATAR_URL_LENGTH);
  }

  if (rawAvatar.length <= MAX_SOCKET_AVATAR_URL_LENGTH) return rawAvatar;
  try {
    const compressed = await compressAvatarDataUrl(rawAvatar);
    if (compressed && compressed.length <= MAX_SOCKET_AVATAR_URL_LENGTH) {
      return compressed;
    }
    return String(compressed || "").slice(0, MAX_SOCKET_AVATAR_URL_LENGTH);
  } catch (error) {
    console.log(error);
    return "";
  }
};

const extractPrimaryName = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.includes("@")) {
    const alias = String(raw.split("@")[0] || "").trim();
    if (alias) return alias;
  }
  const parts = raw.split(/\s+/).filter(Boolean);
  return String(parts[0] || raw).trim();
};

const normalizeParticipantIdentity = (value) => {
  if (typeof value === "string") {
    return String(value || "").trim();
  }

  if (value && typeof value === "object") {
    const candidates = [
      value.userId,
      value.email,
      extractPrimaryName(value.displayName || value.name || value.nickname),
      extractPrimaryName(value.firstName),
      value.playerCode,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const raw = String(candidates[i] || "").trim();
      if (raw) return raw;
    }
  }

  return "";
};

const uniqueList = (values) => {
  const list = Array.isArray(values) ? values : [];
  const seen = new Set();
  const result = [];

  for (let i = 0; i < list.length; i += 1) {
    const raw = String(list[i] || "").trim();
    if (!raw) continue;
    const key = raw.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(raw);
  }

  return result;
};

const normalizeProfileId = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const readStoredActiveMatchSession = () => {
  if (typeof window === "undefined") return null;

  try {
    const parsed = JSON.parse(
      String(window.localStorage.getItem(ACTIVE_MATCH_SESSION_STORAGE_KEY) || "")
    );
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

    const tournamentId = String(parsed.tournamentId || "").trim();
    const roomId = String(parsed.roomId || "").trim();
    const userId = normalizeProfileId(parsed.userId);
    const updatedAtMs = Number(parsed.updatedAtMs);
    if (!tournamentId || !roomId || !userId || !Number.isFinite(updatedAtMs)) return null;
    if (Date.now() - updatedAtMs > ACTIVE_MATCH_SESSION_MAX_AGE_MS) return null;

    return {
      tournamentId,
      roomId,
      userId,
      updatedAtMs,
    };
  } catch {
    return null;
  }
};

const writeStoredActiveMatchSession = ({ tournamentId, roomId, userId }) => {
  if (typeof window === "undefined") return false;

  const safeTournamentId = String(tournamentId || "").trim();
  const safeRoomId = String(roomId || "").trim();
  const safeUserId = normalizeProfileId(userId);
  if (!safeTournamentId || !safeRoomId || !safeUserId) return false;

  try {
    window.localStorage.setItem(
      ACTIVE_MATCH_SESSION_STORAGE_KEY,
      JSON.stringify({
        tournamentId: safeTournamentId,
        roomId: safeRoomId,
        userId: safeUserId,
        updatedAtMs: Date.now(),
      })
    );
    return true;
  } catch {
    return false;
  }
};

const clearStoredActiveMatchSession = ({ tournamentId = "", userId = "" } = {}) => {
  if (typeof window === "undefined") return false;

  const stored = readStoredActiveMatchSession();
  const safeTournamentId = String(tournamentId || "").trim();
  const safeUserId = normalizeProfileId(userId);
  if (
    stored &&
    ((safeTournamentId && stored.tournamentId !== safeTournamentId) ||
      (safeUserId && stored.userId !== safeUserId))
  ) {
    return false;
  }

  try {
    window.localStorage.removeItem(ACTIVE_MATCH_SESSION_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
};

const isGuestProfileId = (value) => {
  const normalized = normalizeProfileId(value);
  if (!normalized) return false;
  if (normalized.startsWith(GUEST_USER_PREFIX)) return true;
  return normalized.endsWith(`@${GUEST_EMAIL_DOMAIN}`);
};

const normalizeOutUsersMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const result = {};
  Object.keys(value).forEach((rawUserId) => {
    const normalizedUserId = normalizeProfileId(rawUserId);
    if (!normalizedUserId) return;
    result[normalizedUserId] = Boolean(value[rawUserId]);
  });
  return result;
};

const normalizeCommonBlockThemeId = (value) =>
  normalizeSkinId(value, DEFAULT_COMMON_BLOCK_THEME_ID);
const normalizeBlockMaterialTextureId = (value) =>
  normalizeSkinId(value, DEFAULT_BLOCK_MATERIAL_TEXTURE_ID).toLowerCase();
const normalizeBlockAnimationId = (value) =>
  normalizeSkinId(value, DEFAULT_BLOCK_ANIMATION_ID).toLowerCase();
const normalizeOptionalBlockMaterialTextureId = (value) =>
  String(value || "").trim() ? normalizeBlockMaterialTextureId(value) : "";
const normalizeOptionalBlockAnimationId = (value) =>
  String(value || "").trim() ? normalizeBlockAnimationId(value) : "";
const parseClockSeconds = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 0) return numeric;

  const text = String(value || "").trim();
  if (!text) return NaN;

  const mmssMatch = text.match(/(\d{1,3})\s*[:;,.]\s*(\d{1,2})/);
  if (mmssMatch) {
    const minutes = Number(mmssMatch[1]);
    const seconds = Number(mmssMatch[2]);
    if (Number.isFinite(minutes) && Number.isFinite(seconds) && seconds >= 0 && seconds < 60) {
      return minutes * 60 + seconds;
    }
  }

  if (!text.includes(":")) return NaN;
  const [minutesPart, secondsPart] = text.split(":");
  const minutes = Number(minutesPart);
  const seconds = Number(secondsPart);
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return NaN;
  const total = minutes * 60 + seconds;
  return Number.isFinite(total) && total >= 0 ? total : NaN;
};

const formatClockLabel = (value) => {
  const totalSeconds = parseClockSeconds(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "00:00";

  const wholeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = wholeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const resolveTimestampMs = (value) => {
  if (value === undefined || value === null || value === "") return 0;

  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    // Soporta timestamps en segundos o milisegundos.
    return numeric < 1e12 ? Math.floor(numeric * 1000) : Math.floor(numeric);
  }

  const parsed = new Date(value).valueOf();
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const formatTournamentCountdownLabel = (value) => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "--:--:--";

  const seconds = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const hhmmss = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
    secs
  ).padStart(2, "0")}`;
  return days > 0 ? `${days}d ${hhmmss}` : hhmmss;
};

const playTournamentOneMinuteAlertTone = async () => {
  if (typeof window === "undefined") return;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (typeof AudioContextClass !== "function") return;

  let context = null;
  try {
    context = new AudioContextClass();
    if (context.state === "suspended") {
      await context.resume().catch(() => {});
    }

    const now = context.currentTime;
    const gain = context.createGain();
    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(920, now);
    oscillator.connect(gain);
    gain.connect(context.destination);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.23);

    oscillator.start(now);
    oscillator.stop(now + 0.25);
    oscillator.onended = () => {
      context?.close?.().catch(() => {});
    };
  } catch (error) {
    try {
      context?.close?.();
    } catch (closeError) {
      // noop
    }
  }
};

const parseForceValue = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;

  const text = String(value || "").trim();
  if (!text) return NaN;

  const normalized = text.replace(",", ".");
  const match = normalized.match(/-?\d+(?:\.\d+)?/);
  if (!match) return NaN;

  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const toObjectPayload = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;

  const text = value.trim();
  if (!text) return null;

  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
};

const resolveMetricsEnvelope = (rawValue) => {
  const root = toObjectPayload(rawValue);
  if (!root) return { root: null, payload: null };

  const payload =
    toObjectPayload(root.payload) ||
    toObjectPayload(root.data) ||
    toObjectPayload(root.detail) ||
    root;

  return {
    root,
    payload: payload && typeof payload === "object" ? payload : null,
  };
};

const parseMoveVector3 = (value) => {
  const text = String(value || "").trim();
  if (!text) return null;

  const normalized = text
    .replace(/[()]/g, " ")
    .replace(/;/g, ",")
    .replace(/\s+/g, " ")
    .trim();
  const tokens = normalized.match(/-?\d+(?:[.,]\d+)?/g);
  if (!Array.isArray(tokens) || tokens.length < 3) return null;

  const x = Number(tokens[0].replace(",", "."));
  const y = Number(tokens[1].replace(",", "."));
  const z = Number(tokens[2].replace(",", "."));
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  return { x, y, z };
};

const extractSquareToken = (value) => {
  const text = String(value || "")
    .trim()
    .toUpperCase();
  if (!text) return "";
  const match = text.match(BOARD_SQUARE_REGEX);
  return match ? match[1] : "";
};

const vectorToSquareToken = (vector, origin = { x: 0, z: 0 }) => {
  if (!vector) return "";

  const col = Math.round(Number(vector.x) - Number(origin?.x || 0)) + 7;
  const row = Math.round(Number(vector.z) - Number(origin?.z || 0)) + 7;
  if (!Number.isFinite(col) || !Number.isFinite(row)) return "";
  if (col < 1 || col > 13 || row < 1 || row > 13) return "";

  const file = BOARD_FILES[col - 1];
  return file ? `${file}${row}` : "";
};

const normalizeXimaNotation = (rawValue) => {
  const text = String(rawValue || "")
    .trim()
    .toUpperCase();
  if (!text) return "";

  const compact = text.replace(/\s+/g, "");
  const strictMatch = compact.match(
    /^([=-])?([A-M](?:1[0-3]|[1-9]))([A-M](?:1[0-3]|[1-9]))?$/
  );
  if (strictMatch) {
    const prefix = strictMatch[1] || "";
    const first = strictMatch[2];
    const second = strictMatch[3] || "";
    if (prefix) return `${prefix}${first}`;
    if (!second) return first;
    if (first === second) return `-${first}`;
    return `${first}${second}`;
  }

  const squares = [];
  const matcher = new RegExp(BOARD_SQUARE_REGEX.source, "gi");
  let tokenMatch = matcher.exec(text);
  while (tokenMatch) {
    squares.push(String(tokenMatch[1] || "").toUpperCase());
    tokenMatch = matcher.exec(text);
  }
  if (!squares.length) return "";

  const prefixMatch = text.match(/^[\s]*([=-])/);
  const prefix = prefixMatch ? prefixMatch[1] : "";
  if (prefix) return `${prefix}${squares[0]}`;
  if (squares.length === 1) return squares[0];

  const first = squares[0];
  const last = squares[squares.length - 1];
  if (first === last) return `-${first}`;
  return `${first}${last}`;
};

const normalizePuzzleNotationPayloadEntry = (rawEntry) => {
  if (!rawEntry) return null;

  let notation = "";
  let playerIndex = 0;
  let payload = "";

  if (typeof rawEntry === "string") {
    const trimmed = rawEntry.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsedEntry = JSON.parse(trimmed);
        if (parsedEntry && typeof parsedEntry === "object" && !Array.isArray(parsedEntry)) {
          notation = normalizeXimaNotation(
            parsedEntry.notation ||
              parsedEntry.moveNotation ||
              parsedEntry.move_notation ||
              parsedEntry.notationText
          );
          payload = String(
            parsedEntry.payload ||
              parsedEntry.movePayload ||
              parsedEntry.moveData ||
              parsedEntry.moveDataPayload ||
              ""
          ).trim();
          playerIndex =
            Number.isFinite(Number(parsedEntry.playerIndex))
              ? Math.max(0, Math.floor(Number(parsedEntry.playerIndex)))
              : 0;
        } else {
          notation = normalizeXimaNotation(trimmed);
        }
      } catch {
        notation = normalizeXimaNotation(trimmed);
      }
    } else {
      notation = normalizeXimaNotation(trimmed);
    }
  } else if (typeof rawEntry === "object") {
    notation = normalizeXimaNotation(
      rawEntry.notation ||
        rawEntry.moveNotation ||
        rawEntry.move_notation ||
        rawEntry.notationText
    );
    payload = String(
      rawEntry.payload || rawEntry.movePayload || rawEntry.moveData || rawEntry.moveDataPayload || ""
    ).trim();
    const resolvedPlayerIndex = Number(rawEntry?.playerIndex);
    playerIndex = Number.isFinite(resolvedPlayerIndex)
      ? Math.max(0, Math.floor(resolvedPlayerIndex))
      : 0;
    if (rawEntry?.playerSlot === 0 || rawEntry?.playerSlot) {
      const resolvedSlot = Number(rawEntry.playerSlot);
      playerIndex = Number.isFinite(resolvedSlot)
        ? Math.max(0, Math.floor(resolvedSlot))
        : playerIndex;
    }
  } else {
    return null;
  }

  notation = String(notation || "").trim().toUpperCase();
  if (!notation) return null;
  const normalizedPayload = String(payload || "").trim();
  const safePayload = parseMoveDataPipePayload(normalizedPayload) ? normalizedPayload : "";

  return {
    notation,
    playerIndex: Number.isFinite(playerIndex) ? Math.max(0, Math.floor(playerIndex)) : 0,
    payload: safePayload,
  };
};

const parseMoveDataPipePayload = (rawValue) => {
  const text = String(rawValue || "").trim();
  if (!text || !text.includes("|")) return null;

  const parts = text.split("|");
  if (parts.length < 2) return null;

  const firstToken = String(parts[0] || "").trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(firstToken)) return null;

  const playerIndex = Number(firstToken);
  const cubeNumber = String(parts[1] || "").trim();
  if (!cubeNumber) return null;
  const targetPoint = String(parts[2] || "").trim();
  const cubePoint = String(parts[3] || "").trim();

  return {
    playerIndex: Number.isFinite(playerIndex) ? Math.max(0, Math.floor(playerIndex)) : 0,
    cubeNumber,
    targetPoint,
    cubePoint,
  };
};

const buildPuzzleBlockCoverageSummary = (entries) => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const latestLocationByBlock = new Map();
  const sanitizedMoveLog = [];
  let invalidMoves = 0;

  safeEntries.forEach((rawEntry) => {
    const payload = String(rawEntry || "").trim();
    if (!payload) return;

    const parsed = parseMoveDataPipePayload(payload);
    if (!parsed) {
      invalidMoves += 1;
      return;
    }

    const parts = payload.split("|");
    const targetPointId = String(parts[4] || "").trim();
    const targetPoint = String(parsed.targetPoint || "").trim();
    const hasLocation = Boolean(targetPointId || targetPoint);
    sanitizedMoveLog.push(payload);
    if (!hasLocation) {
      return;
    }

    latestLocationByBlock.set(
      `${parsed.playerIndex}|${String(parsed.cubeNumber || "").trim()}`,
      targetPointId || targetPoint
    );
  });

  return {
    sanitizedMoveLog,
    blockCount: latestLocationByBlock.size,
    moveCount: sanitizedMoveLog.length,
    invalidMoves,
  };
};

const countFrozenBlocksFromMoveLog = (moveEntries, users, outUsersMap) => {
  const safeMoves = Array.isArray(moveEntries) ? moveEntries : [];
  const safeUsers = Array.isArray(users) ? users : [];
  const normalizedOutMap = normalizeOutUsersMap(outUsersMap);
  const frozenOwners = new Set(
    Object.keys(normalizedOutMap).filter((key) => normalizedOutMap[key] === true)
  );
  if (frozenOwners.size === 0) return 0;

  const latestByOwnerAndCube = new Map();
  safeMoves.forEach((rawEntry) => {
    const payload = String(rawEntry || "").trim();
    if (!payload) return;
    const parsed = parseMoveDataPipePayload(payload);
    if (!parsed) return;
    const ownerUserId = String(safeUsers[parsed.playerIndex] || "").trim();
    const normalizedOwnerId = normalizeProfileId(ownerUserId);
    if (!normalizedOwnerId) return;
    const cubeKey = String(parsed.cubeNumber || "").trim();
    if (!cubeKey) return;
    latestByOwnerAndCube.set(`${normalizedOwnerId}|${cubeKey}`, payload);
  });

  let frozenBlocks = 0;
  latestByOwnerAndCube.forEach((_payload, key) => {
    const ownerKey = String(key || "").split("|")[0];
    if (frozenOwners.has(ownerKey)) frozenBlocks += 1;
  });
  return frozenBlocks;
};

const normalizePuzzleSnapshotPayload = (rawValue) => {
  const candidates = [rawValue];
  const directObject = toObjectPayload(rawValue);
  if (directObject) {
    candidates.push(
      directObject.positionSnapshot,
      directObject.snapshot,
      directObject.snapshotPayload,
      directObject.payload,
      directObject.data
    );
  }

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const parsed = toObjectPayload(candidate) || (candidate && typeof candidate === "object" ? candidate : null);
    if (!parsed || Array.isArray(parsed)) continue;
    if (!Array.isArray(parsed.players)) continue;
    return parsed;
  }

  return null;
};

const normalizePuzzleSnapshotHash = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return /^[a-f0-9]{64}$/.test(normalized) ? normalized : "";
};

const normalizePuzzleBoardVersion = (value) =>
  String(value || "")
    .trim()
    .slice(0, MAX_PUZZLE_BOARD_VERSION_LENGTH);

const buildPuzzleSnapshotLoadKey = (puzzleId, roomTournamentId, roomId) => {
  const safePuzzleId = String(puzzleId || "").trim();
  const safeRoomTournamentId = String(roomTournamentId || "").trim();
  const safeRoomId = String(roomId || "").trim();
  if (!safePuzzleId || !safeRoomTournamentId || !safeRoomId) return "";
  return `${safePuzzleId}::${safeRoomTournamentId}::${safeRoomId}`;
};

const normalizePuzzleCubeId = (value) =>
  String(value || "")
    .trim()
    .slice(0, MAX_PUZZLE_CUBE_ID_LENGTH);

const normalizePuzzleCertificationStatus = (value, fallback = "fallback") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (
    normalized === "pending" ||
    normalized === "certified" ||
    normalized === "fallback" ||
    normalized === "quarantined"
  ) {
    return normalized;
  }

  const fallbackNormalized = String(fallback || "")
    .trim()
    .toLowerCase();
  if (
    fallbackNormalized === "pending" ||
    fallbackNormalized === "certified" ||
    fallbackNormalized === "fallback" ||
    fallbackNormalized === "quarantined"
  ) {
    return fallbackNormalized;
  }

  return "fallback";
};

const isReliablePuzzlePlanAEntry = (entry) => {
  const fallbackStatus = entry?.positionSnapshot ? "pending" : "fallback";
  const status = normalizePuzzleCertificationStatus(
    entry?.puzzleCertificationStatus,
    fallbackStatus
  );
  if (status !== "certified") return false;
  return Boolean(normalizePuzzleSnapshotHash(entry?.puzzleSnapshotHash));
};

const buildCanonicalPuzzleSnapshotForHash = (rawSnapshot) => {
  const snapshot = normalizePuzzleSnapshotPayload(rawSnapshot);
  if (!snapshot || !Array.isArray(snapshot.players)) return null;

  const players = snapshot.players
    .map((rawPlayer) => {
      const playerIndexRaw = Number(rawPlayer?.playerIndex ?? rawPlayer?.index);
      const playerIndex = Number.isFinite(playerIndexRaw)
        ? Math.max(0, Math.min(3, Math.floor(playerIndexRaw)))
        : 0;
      const cubes = (Array.isArray(rawPlayer?.cubes) ? rawPlayer.cubes : [])
        .map((rawCube) => {
          const active = Boolean(rawCube?.active ?? rawCube?.isActive ?? true);
          const rawState = String(rawCube?.state || "")
            .trim()
            .toLowerCase();
          const pointIdRaw = String(rawCube?.pointId || rawCube?.point || "")
            .trim()
            .slice(0, 64);
          const stateInside =
            active &&
            (rawState === "inside" || (rawState !== "outside" && pointIdRaw.length > 0));
          const siblingRaw = Number(
            rawCube?.siblingIndex ?? rawCube?.sibling ?? rawCube?.childIndex
          );
          const stackRaw = Number(rawCube?.stackIndex ?? rawCube?.stack ?? rawCube?.levelIndex);
          return {
            cubeId: String(rawCube?.cubeId || rawCube?.id || "")
              .trim()
              .slice(0, MAX_PUZZLE_CUBE_ID_LENGTH),
            cubeName: String(rawCube?.cubeName || rawCube?.name || "")
              .trim()
              .slice(0, 96)
              .toLowerCase(),
            siblingIndex: Number.isFinite(siblingRaw) ? Math.floor(siblingRaw) : -1,
            active,
            isSpecial: Boolean(rawCube?.isSpecial),
            state: stateInside ? "inside" : "outside",
            pointId: stateInside ? pointIdRaw : "",
            stackIndex:
              stateInside && Number.isFinite(stackRaw) ? Math.max(0, Math.floor(stackRaw)) : -1,
          };
        })
        .sort((left, right) => {
          const byCubeId = String(left?.cubeId || "").localeCompare(
            String(right?.cubeId || ""),
            "en",
            { sensitivity: "base" }
          );
          if (byCubeId !== 0) return byCubeId;

          const bySibling =
            Math.floor(Number(left?.siblingIndex) || -1) -
            Math.floor(Number(right?.siblingIndex) || -1);
          if (bySibling !== 0) return bySibling;

          const byName = String(left?.cubeName || "").localeCompare(
            String(right?.cubeName || ""),
            "en",
            { sensitivity: "base" }
          );
          if (byName !== 0) return byName;

          const byPoint = String(left?.pointId || "").localeCompare(String(right?.pointId || ""), "en", {
            sensitivity: "base",
          });
          if (byPoint !== 0) return byPoint;

          return Math.floor(Number(left?.stackIndex) || -1) - Math.floor(Number(right?.stackIndex) || -1);
        });

      return {
        playerIndex,
        specialCubeCaptured: Boolean(rawPlayer?.specialCubeCaptured),
        cubes,
      };
    })
    .sort((left, right) => left.playerIndex - right.playerIndex);

  if (players.length === 0) return null;

  const versionRaw = Number(snapshot.version);
  const turnIndexRaw = Number(
    snapshot.turnIndex ?? snapshot.turn ?? snapshot.currentTurnIndex ?? snapshot.turnPlayerSlot
  );

  return {
    version: Number.isFinite(versionRaw) ? Math.max(1, Math.floor(versionRaw)) : 1,
    mode: "freebox",
    freeBoxBaseMode: normalizeFreeBoxBaseMode(snapshot.freeBoxBaseMode, "freebox"),
    boardVersion: normalizePuzzleBoardVersion(
      snapshot.boardVersion ?? snapshot.boardId ?? snapshot.board ?? snapshot.boardTag
    ),
    turnIndex: Number.isFinite(turnIndexRaw)
      ? Math.max(0, Math.min(3, Math.floor(turnIndexRaw)))
      : 0,
    players,
  };
};

const computePuzzleSnapshotHash = async (rawSnapshot) => {
  const canonicalSnapshot = buildCanonicalPuzzleSnapshotForHash(rawSnapshot);
  if (!canonicalSnapshot) return "";

  let encoded = "";
  try {
    encoded = JSON.stringify(canonicalSnapshot);
  } catch (error) {
    return "";
  }
  if (!encoded) return "";

  if (
    typeof window === "undefined" ||
    !window.crypto ||
    !window.crypto.subtle ||
    typeof TextEncoder === "undefined"
  ) {
    return "";
  }

  const hashBuffer = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(encoded)
  );
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const countPuzzleSnapshotActiveBlocks = (snapshot) => {
  if (!snapshot || !Array.isArray(snapshot.players)) return 0;
  let activeCount = 0;

  snapshot.players.forEach((playerEntry) => {
    if (!playerEntry || !Array.isArray(playerEntry.cubes)) return;
    playerEntry.cubes.forEach((cubeEntry) => {
      if (!cubeEntry || typeof cubeEntry !== "object") return;
      if (cubeEntry.active === false) return;
      activeCount += 1;
    });
  });

  return activeCount;
};

const normalizePuzzleTurnLimit = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    const fallbackParsed = Number(fallback);
    if (!Number.isFinite(fallbackParsed)) return 0;
    return Math.max(0, Math.min(MAX_PUZZLE_TURN_LIMIT, Math.floor(fallbackParsed)));
  }

  return Math.max(0, Math.min(MAX_PUZZLE_TURN_LIMIT, Math.floor(parsed)));
};

const normalizePuzzleRewardValue = (value, fallback = DEFAULT_PUZZLE_REWARD_VALUE) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    const fallbackParsed = Number(fallback);
    if (!Number.isFinite(fallbackParsed)) return DEFAULT_PUZZLE_REWARD_VALUE;
    return Math.max(0, Math.min(MAX_PUZZLE_REWARD_VALUE, Math.floor(fallbackParsed)));
  }

  return Math.max(0, Math.min(MAX_PUZZLE_REWARD_VALUE, Math.floor(parsed)));
};

const normalizePuzzleSolveTimeLimitSeconds = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    const fallbackParsed = Number(fallback);
    if (!Number.isFinite(fallbackParsed)) return 0;
    return Math.max(
      0,
      Math.min(MAX_PUZZLE_SOLVE_TIME_LIMIT_SECONDS, Math.floor(fallbackParsed))
    );
  }

  return Math.max(0, Math.min(MAX_PUZZLE_SOLVE_TIME_LIMIT_SECONDS, Math.floor(parsed)));
};

const buildDefaultPuzzleNameFromRules = (rawTurnLimit, rawSolveTimeLimitSeconds = 0) => {
  const turnLimit = Math.max(1, normalizePuzzleTurnLimit(rawTurnLimit, DEFAULT_PUZZLE_TURN_LIMIT));
  const solveTimeLimitSeconds = normalizePuzzleSolveTimeLimitSeconds(rawSolveTimeLimitSeconds, 0);
  const solveTimeLimitMinutes =
    solveTimeLimitSeconds > 0 ? Math.max(1, Math.ceil(solveTimeLimitSeconds / 60)) : 0;
  const turnsLabel = `${turnLimit} ${turnLimit === 1 ? "turno" : "turnos"}`;

  if (solveTimeLimitMinutes <= 0) {
    return `Llega a la cima en ${turnsLabel}.`;
  }

  const minutesLabel = `${solveTimeLimitMinutes} ${
    solveTimeLimitMinutes === 1 ? "minuto" : "minutos"
  }`;
  return `Llega a la cima en ${turnsLabel} y ${minutesLabel}.`;
};

const resolvePuzzleRewardValueFromRecord = (rawRecord) => {
  const record =
    rawRecord && typeof rawRecord === "object" && !Array.isArray(rawRecord)
      ? rawRecord
      : {};
  const rewardCandidate = [
    record?.puzzleRewardValue,
    record?.rewardValue,
    record?.rewardAmount,
    record?.reward,
    record?.rewardX,
  ].find((value) => Number.isFinite(Number(value)));
  return normalizePuzzleRewardValue(rewardCandidate, DEFAULT_PUZZLE_REWARD_VALUE);
};

const resolvePuzzleSolveTimeLimitSecondsFromRecord = (rawRecord) => {
  const record =
    rawRecord && typeof rawRecord === "object" && !Array.isArray(rawRecord)
      ? rawRecord
      : {};
  const solveTimeCandidate = [
    record?.puzzleSolveTimeLimitSeconds,
    record?.puzzleSolveTimeSeconds,
    record?.solveTimeSeconds,
    record?.timeLimitSeconds,
  ].find((value) => Number.isFinite(Number(value)));
  return normalizePuzzleSolveTimeLimitSeconds(solveTimeCandidate, 0);
};

const formatPuzzleSolveCountdownLabel = (rawSeconds) => {
  const totalSeconds = Number(rawSeconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "00:00";
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
      seconds
    ).padStart(2, "0")}`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const normalizeFreeBoxBaseMode = (rawMode, fallback = "free") => {
  const normalized = String(rawMode || "")
    .trim()
    .toLowerCase();

  if (normalized === "dice" || normalized === "azar") return "dice";
  if (
    normalized === "sciencevsazar" ||
    normalized === "cienciavsazar" ||
    normalized === "science-vs-azar" ||
    normalized === "ciencia-vs-azar"
  ) {
    return "sciencevsazar";
  }
  if (normalized === "cienzar" || normalized === "sciluck") return "cienzar";
  if (normalized === "free" || normalized === "ciencia" || normalized === "libre") return "free";

  const fallbackNormalized = String(fallback || "")
    .trim()
    .toLowerCase();
  if (fallbackNormalized === "dice" || fallbackNormalized === "azar") return "dice";
  if (
    fallbackNormalized === "sciencevsazar" ||
    fallbackNormalized === "cienciavsazar" ||
    fallbackNormalized === "science-vs-azar" ||
    fallbackNormalized === "ciencia-vs-azar"
  ) {
    return "sciencevsazar";
  }
  if (fallbackNormalized === "cienzar" || fallbackNormalized === "sciluck") return "cienzar";
  return "free";
};

const normalizeCimaxScores = (rawValue) => {
  const raw =
    rawValue && typeof rawValue === "object" && !Array.isArray(rawValue) ? rawValue : {};
  const toScore = (value, fallback = 0) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(0, Math.round(parsed));
  };
  return {
    ciencia: toScore(
      raw.ciencia ?? raw.free ?? raw.cimaxCiencia,
      DEFAULT_CIMAX_SCORES.ciencia
    ),
    cienzar: toScore(raw.cienzar ?? raw.cimaxCienzar, DEFAULT_CIMAX_SCORES.cienzar),
    sciencevsazar: toScore(
      raw.sciencevsazar ?? raw.contrazar ?? raw.cimaxContrazar,
      DEFAULT_CIMAX_SCORES.sciencevsazar
    ),
    azar: toScore(raw.azar ?? raw.dice ?? raw.cimaxAzar, DEFAULT_CIMAX_SCORES.azar),
  };
};

const MODE_TO_CIMAX_FIELD = Object.freeze({
  free: "ciencia",
  ciencia: "ciencia",
  libre: "ciencia",
  localfree: "ciencia",
  localciencia: "ciencia",
  botlocalfree: "ciencia",
  botciencia: "ciencia",
  cienzar: "cienzar",
  sciluck: "cienzar",
  localcienzar: "cienzar",
  botlocalcienzar: "cienzar",
  sciencevsazar: "sciencevsazar",
  contrazar: "sciencevsazar",
  cienciavsazar: "sciencevsazar",
  localsciencevsazar: "sciencevsazar",
  localcontrazar: "sciencevsazar",
  botlocalsciencevsazar: "sciencevsazar",
  botlocalcontrazar: "sciencevsazar",
  dice: "azar",
  azar: "azar",
  localdice: "azar",
  localazar: "azar",
  botlocaldice: "azar",
  botazar: "azar",
});
const PUZZLE_MODE_TRANSLATION_KEYS = Object.freeze({
  ciencia: "PUZZLE_MODE_INFO_SCIENCE",
  azar: "PUZZLE_MODE_INFO_CHANCE",
  cienzar: "PUZZLE_MODE_INFO_CIENZAR",
  sciencevsazar: "PUZZLE_MODE_INFO_CIENZAR",
});

const CIMAX_MODE_LABELS = Object.freeze({
  ciencia: "CIENCIA",
  cienzar: "CIENZAR",
  sciencevsazar: "CONTRAZAR",
  azar: "AZAR",
});

const resolveCimaxModeFieldFromMode = (mode, freeBoxBaseMode = "") => {
  const normalizedMode = String(mode || "")
    .trim()
    .toLowerCase();
  if (normalizedMode === "freebox") {
    const normalizedBaseMode = normalizeFreeBoxBaseMode(freeBoxBaseMode, "free");
    return MODE_TO_CIMAX_FIELD[normalizedBaseMode] || "ciencia";
  }
  return MODE_TO_CIMAX_FIELD[normalizedMode] || "";
};

const formatCimaxModeLabel = (modeField) =>
  CIMAX_MODE_LABELS[String(modeField || "").trim().toLowerCase()] ||
  String(modeField || "")
    .trim()
    .toUpperCase();

const formatCimaxScoreValue = (value) =>
  String(Math.max(0, Math.round(Number(value) || 0)));

const toRoundedCimaxValue = (value, fallback = NaN) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.round(parsed));
};

const buildMoveDataPipeFromObject = (rawValue, fallbackPlayerIndex = 0) => {
  const payload = toObjectPayload(rawValue);
  if (!payload) return "";

  const pickString = (...keys) => {
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      const value = payload[key];
      const text = String(value || "").trim();
      if (text) return text;
    }
    return "";
  };
  const pickNumber = (...keys) => {
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      const value = Number(payload[key]);
      if (Number.isFinite(value)) return Math.floor(value);
    }
    const fallback = Number(fallbackPlayerIndex);
    return Number.isFinite(fallback) ? Math.max(0, Math.floor(fallback)) : 0;
  };
  const pickFiniteNumber = (...keys) => {
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      const value = Number(payload[key]);
      if (Number.isFinite(value)) return value;
    }
    return NaN;
  };

  const playerIndex = pickNumber(
    "playerIndex",
    "playerIndexNumber",
    "playerSlot",
    "turnIndex",
    "index"
  );
  const cubeNumber = pickString(
    "cubeNumber",
    "cube",
    "cubeNo",
    "cubeIndex",
    "dice",
    "value",
    "selectedCube",
    "selectedCubeId",
    "cubeId",
    "cubeName",
    "name",
    "id"
  );
  const targetPointId = pickString("targetPointId", "targetId");
  const targetPointRaw = pickString(
    "targetPoint",
    "target",
    "toPoint",
    "targetPosition"
  );
  const cubePointRaw = pickString("cubePoint", "sourcePoint", "fromPoint", "startPoint");
  const roundIndex = pickString("roundIndex", "round", "turnRound", "step");
  const turnUnitRaw = pickFiniteNumber("turnUnit", "turn_unit", "unit");
  const turnUnit = Number.isFinite(turnUnitRaw) ? String(turnUnitRaw) : "";
  const targetPoint = targetPointRaw || (targetPointId ? "0,0,0" : "");
  const cubePoint = cubePointRaw || (targetPointId ? "0,0,0" : "");

  if (!cubeNumber) return "";
  return `${playerIndex}|${cubeNumber}|${targetPoint}|${cubePoint}|${targetPointId}|${roundIndex}|${turnUnit}`;
};

const extractMoveDataPayload = (rawInput, fallbackPlayerIndex = 0) => {
  const envelope = resolveMetricsEnvelope(rawInput);
  const rootPayload = envelope.root || {};
  const payload = envelope.payload || {};

  const pipeCandidates = [
    rawInput,
    payload.raw,
    payload.value,
    payload.data,
    payload.JsonData,
    payload.jsonData,
    rootPayload.raw,
    rootPayload.value,
    rootPayload.data,
    rootPayload.JsonData,
    rootPayload.jsonData,
  ];

  for (let index = 0; index < pipeCandidates.length; index += 1) {
    const candidate = pipeCandidates[index];
    const parsedPipe = parseMoveDataPipePayload(candidate);
    if (parsedPipe) {
      const parts = String(candidate || "").split("|");
      const targetPointId = String(parts[4] || "").trim();
      const roundIndex = String(parts[5] || "").trim();
      const turnUnit = String(parts[6] || "").trim();
      return `${parsedPipe.playerIndex}|${parsedPipe.cubeNumber}|${parsedPipe.targetPoint}|${parsedPipe.cubePoint}|${targetPointId}|${roundIndex}|${turnUnit}`;
    }
  }

  const objectCandidates = [payload, rootPayload, rawInput];
  for (let index = 0; index < objectCandidates.length; index += 1) {
    const built = buildMoveDataPipeFromObject(objectCandidates[index], fallbackPlayerIndex);
    if (built) return built;
  }

  return "";
};

const parseMoveSequenceFromPayload = (rawPayload) => {
  const payload = String(rawPayload || "").trim();
  if (!payload) return 0;
  const parts = payload.split("|");
  if (parts.length < 7) return 0;
  const parsed = Number(parts[6]);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.trunc(parsed);
};

const parseMoveRoundIndexFromPayload = (rawPayload) => {
  const payload = String(rawPayload || "").trim();
  if (!payload) return NaN;
  const parts = payload.split("|");
  if (parts.length < 6) return NaN;
  const parsed = Number(parts[5]);
  if (!Number.isFinite(parsed)) return NaN;
  return Math.trunc(parsed);
};

const orderMovePayloadsBySequence = (rawMoveLog, maxEntries = MAX_PUZZLE_MOVE_LOG_SIZE) => {
  const normalizedMoveLog = (Array.isArray(rawMoveLog) ? rawMoveLog : [])
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

  if (normalizedMoveLog.length <= 1) {
    if (maxEntries > 0 && normalizedMoveLog.length > maxEntries) {
      return normalizedMoveLog.slice(-maxEntries);
    }
    return normalizedMoveLog;
  }

  const withMeta = normalizedMoveLog.map((payload, index) => ({
    payload,
    index,
    sequence: parseMoveSequenceFromPayload(payload),
    roundIndex: parseMoveRoundIndexFromPayload(payload),
  }));

  withMeta.sort((left, right) => {
    const leftHasSequence = left.sequence > 0;
    const rightHasSequence = right.sequence > 0;

    if (leftHasSequence && rightHasSequence) {
      if (left.sequence !== right.sequence) {
        return left.sequence - right.sequence;
      }

      const leftHasRound = Number.isFinite(left.roundIndex);
      const rightHasRound = Number.isFinite(right.roundIndex);
      if (leftHasRound && rightHasRound && left.roundIndex !== right.roundIndex) {
        return left.roundIndex - right.roundIndex;
      }
      if (leftHasRound !== rightHasRound) {
        return leftHasRound ? -1 : 1;
      }
      return left.index - right.index;
    }

    if (leftHasSequence !== rightHasSequence) {
      return leftHasSequence ? -1 : 1;
    }

    return left.index - right.index;
  });

  const ordered = withMeta.map((entry) => entry.payload);
  if (maxEntries > 0 && ordered.length > maxEntries) {
    return ordered.slice(-maxEntries);
  }
  return ordered;
};

const buildNotationFromMovePipe = (pipePayload, boardOrigin = { x: 0, z: 0 }) => {
  if (!pipePayload) return "";

  const targetSquare =
    extractSquareToken(pipePayload.targetPoint) ||
    vectorToSquareToken(parseMoveVector3(pipePayload.targetPoint), boardOrigin);
  if (!targetSquare) return "";

  const fromSquare =
    extractSquareToken(pipePayload.cubePoint) ||
    vectorToSquareToken(parseMoveVector3(pipePayload.cubePoint), boardOrigin);

  if (fromSquare && fromSquare !== targetSquare) {
    return `${fromSquare}${targetSquare}`;
  }

  const cubeNumberText = String(pipePayload.cubeNumber || "")
    .trim()
    .toLowerCase();
  const cubeNumberValue = Number(cubeNumberText);
  const isSpecialPlacement =
    Number.isFinite(cubeNumberValue) && cubeNumberValue === 0
      ? true
      : cubeNumberText.includes("special");

  return `${isSpecialPlacement ? "=" : "-"}${targetSquare}`;
};

const resolveNotationEntry = (rawInput, fallbackPlayerIndex = 0) => {
  const envelope = resolveMetricsEnvelope(rawInput);
  const rootPayload = envelope.root || {};
  const payload = envelope.payload || {};

  const parsePlayerIndex = (rawValue) => {
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : null;
  };

  const indexedCandidates = [
    payload.playerIndex,
    payload.playerIndexNumber,
    payload.playerSlot,
    payload.turnIndex,
    payload.index,
    rootPayload.playerIndex,
    rootPayload.playerIndexNumber,
    rootPayload.playerSlot,
    rootPayload.turnIndex,
    rootPayload.index,
  ];
  let resolvedPlayerIndex = parsePlayerIndex(fallbackPlayerIndex) ?? 0;
  for (let i = 0; i < indexedCandidates.length; i += 1) {
    const candidate = parsePlayerIndex(indexedCandidates[i]);
    if (candidate === null) continue;
    resolvedPlayerIndex = candidate;
    break;
  }

  const explicitNotationCandidates = [
    payload.notation,
    payload.moveNotation,
    payload.move_notation,
    payload.moveText,
    payload.move_text,
    payload.move,
    payload.value,
    payload.notationText,
    rootPayload.notation,
    rootPayload.moveNotation,
    rootPayload.move_notation,
    rootPayload.moveText,
    rootPayload.move_text,
    rootPayload.move,
    rootPayload.value,
    rootPayload.notationText,
  ];
  for (let i = 0; i < explicitNotationCandidates.length; i += 1) {
    const notation = normalizeXimaNotation(explicitNotationCandidates[i]);
    if (!notation) continue;
    return { notation, playerIndex: resolvedPlayerIndex };
  }

  const nestedCandidates = [
    payload.JsonData,
    payload.jsonData,
    payload.raw,
    rootPayload.JsonData,
    rootPayload.jsonData,
    rootPayload.raw,
  ];
  for (let i = 0; i < nestedCandidates.length; i += 1) {
    const nested = toObjectPayload(nestedCandidates[i]);
    if (!nested) continue;
    const nestedNotation = normalizeXimaNotation(
      nested.notation ||
        nested.moveNotation ||
        nested.move_notation ||
        nested.moveText ||
        nested.move_text ||
        nested.move ||
        nested.value
    );
    if (!nestedNotation) continue;
    const nestedPlayerIndex =
      parsePlayerIndex(
        nested.playerIndex ??
          nested.playerIndexNumber ??
          nested.playerSlot ??
          nested.turnIndex ??
          nested.index
      ) ?? resolvedPlayerIndex;
    return { notation: nestedNotation, playerIndex: nestedPlayerIndex };
  }

  const pipeCandidates = [
    rawInput,
    payload.raw,
    payload.value,
    payload.data,
    rootPayload.raw,
    rootPayload.value,
    rootPayload.data,
  ];
  for (let i = 0; i < pipeCandidates.length; i += 1) {
    const parsedPipe = parseMoveDataPipePayload(pipeCandidates[i]);
    if (!parsedPipe) continue;
    const parsedNotation = buildNotationFromMovePipe(parsedPipe, { x: 0, z: 0 });
    if (!parsedNotation) continue;
    return {
      notation: parsedNotation,
      playerIndex:
        parsePlayerIndex(parsedPipe.playerIndex) ?? resolvedPlayerIndex,
    };
  }

  return null;
};

const resolveMoveActorIndex = (rawInput, fallbackPlayerIndex = 0) => {
  const parsePlayerIndex = (rawValue) => {
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : null;
  };

  const envelope = resolveMetricsEnvelope(rawInput);
  const rootPayload = envelope.root || {};
  const payload = envelope.payload || {};

  const directCandidates = [
    payload.playerIndex,
    payload.playerIndexNumber,
    payload.playerSlot,
    payload.turnIndex,
    payload.index,
    rootPayload.playerIndex,
    rootPayload.playerIndexNumber,
    rootPayload.playerSlot,
    rootPayload.turnIndex,
    rootPayload.index,
    fallbackPlayerIndex,
  ];
  for (let i = 0; i < directCandidates.length; i += 1) {
    const candidate = parsePlayerIndex(directCandidates[i]);
    if (candidate !== null) return candidate;
  }

  const nestedCandidates = [
    payload.JsonData,
    payload.jsonData,
    payload.raw,
    rootPayload.JsonData,
    rootPayload.jsonData,
    rootPayload.raw,
  ];
  for (let i = 0; i < nestedCandidates.length; i += 1) {
    const nested = toObjectPayload(nestedCandidates[i]);
    if (!nested) continue;
    const nestedCandidate = parsePlayerIndex(
      nested.playerIndex ??
        nested.playerIndexNumber ??
        nested.playerSlot ??
        nested.turnIndex ??
        nested.index
    );
    if (nestedCandidate !== null) return nestedCandidate;
  }

  const pipeCandidates = [
    rawInput,
    payload.raw,
    payload.value,
    payload.data,
    payload.JsonData,
    payload.jsonData,
    rootPayload.raw,
    rootPayload.value,
    rootPayload.data,
    rootPayload.JsonData,
    rootPayload.jsonData,
  ];
  for (let i = 0; i < pipeCandidates.length; i += 1) {
    const parsedPipe = parseMoveDataPipePayload(pipeCandidates[i]);
    if (!parsedPipe) continue;
    const parsedPipeIndex = parsePlayerIndex(parsedPipe.playerIndex);
    if (parsedPipeIndex !== null) return parsedPipeIndex;
  }

  return parsePlayerIndex(fallbackPlayerIndex) ?? 0;
};

const hasIndexedRuntimeFields = (payload) => {
  if (!payload || typeof payload !== "object") return false;

  for (let slot = 1; slot <= 4; slot += 1) {
    if (
      payload[`time${slot}`] !== undefined ||
      payload[`time_${slot}`] !== undefined ||
      payload[`clock${slot}`] !== undefined ||
      payload[`clock_${slot}`] !== undefined ||
      payload[`remaining${slot}`] !== undefined ||
      payload[`remaining_${slot}`] !== undefined ||
      payload[`force${slot}`] !== undefined ||
      payload[`force_${slot}`] !== undefined ||
      payload[`fg${slot}`] !== undefined ||
      payload[`fg_${slot}`] !== undefined
    ) {
      return true;
    }
  }

  return (
    Array.isArray(payload.time) ||
    Array.isArray(payload.times) ||
    Array.isArray(payload.clock) ||
    Array.isArray(payload.clocks) ||
    Array.isArray(payload.force) ||
    Array.isArray(payload.forces) ||
    Array.isArray(payload.fg)
  );
};

// Color de bloque de los bots de Modo Diseñar; vacío = color de su posición.
const BOT_BLOCK_COLOR_IDS = new Set(["blue", "red", "yellow", "green", "white", "gray"]);
const normalizeBotBlockColorId = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return BOT_BLOCK_COLOR_IDS.has(normalized) ? normalized : "";
};

const sanitizeIncomingProfile = (profile) => {
  const rawProfile = profile && typeof profile === "object" ? profile : {};
  const rawSpecialBlockId = normalizeSpecialBlockId(rawProfile.specialBlockId);
  const commonBlockThemeId = normalizeCommonBlockThemeId(rawProfile.commonBlockThemeId);
  const blockMaterialTextureId = normalizeOptionalBlockMaterialTextureId(
    rawProfile.blockMaterialTextureId
  );
  const blockAnimationId = normalizeOptionalBlockAnimationId(rawProfile.blockAnimationId);
  const blockColorId = normalizeBotBlockColorId(rawProfile.blockColorId);

  return {
    avatarUrl: String(rawProfile.avatarUrl || "").trim(),
    displayName: extractPrimaryName(rawProfile.displayName),
    specialBlockId: rawSpecialBlockId,
    commonBlockThemeId,
    blockMaterialTextureId,
    blockAnimationId,
    blockColorId,
    cimaxScores:
      rawProfile.cimaxScores || rawProfile.cimax
        ? normalizeCimaxScores(rawProfile.cimaxScores || rawProfile.cimax)
        : null,
    updatedAt: Number(rawProfile.updatedAt) || Date.now(),
  };
};

const normalizeProfileMap = (rawProfiles) => {
  if (!rawProfiles || typeof rawProfiles !== "object") return {};
  const normalized = {};

  Object.keys(rawProfiles).forEach((rawKey) => {
    const key = normalizeProfileId(rawKey);
    if (!key) return;
    normalized[key] = sanitizeIncomingProfile(rawProfiles[rawKey]);
  });

  return normalized;
};

const mergeProfileMaps = (prevMap, incomingProfiles) => {
  const current = prevMap && typeof prevMap === "object" ? prevMap : {};
  const incoming = normalizeProfileMap(incomingProfiles);
  const next = { ...current };

  Object.keys(incoming).forEach((key) => {
    const existing = sanitizeIncomingProfile(current[key] || {});
    const profile = incoming[key];
    next[key] = {
      avatarUrl: profile.avatarUrl || existing.avatarUrl,
      displayName: profile.displayName || existing.displayName,
      // All profile maps received during a match are server-effective. Defaults
      // must replace stale local cosmetics instead of falling back to them.
      specialBlockId: profile.specialBlockId || DEFAULT_SPECIAL_BLOCK_ID,
      commonBlockThemeId: profile.commonBlockThemeId || DEFAULT_COMMON_BLOCK_THEME_ID,
      blockMaterialTextureId:
        profile.blockMaterialTextureId || DEFAULT_BLOCK_MATERIAL_TEXTURE_ID,
      blockAnimationId: profile.blockAnimationId || DEFAULT_BLOCK_ANIMATION_ID,
      blockColorId: profile.blockColorId || "",
      cimaxScores:
        profile.cimaxScores && typeof profile.cimaxScores === "object"
          ? profile.cimaxScores
          : existing.cimaxScores,
      updatedAt: Number(profile.updatedAt) || Date.now(),
    };
  });

  return next;
};

const getSpecialBlockFromStorage = (userEmail) => {
  const normalizedEmail = String(userEmail || "guest").trim().toLowerCase();
  const userScopedValue = normalizeSpecialBlockId(
    localStorage.getItem(`${SPECIAL_BLOCK_STORAGE_PREFIX}${normalizedEmail}`) || ""
  );
  const globalValue = normalizeSpecialBlockId(localStorage.getItem(GLOBAL_SPECIAL_BLOCK_KEY) || "");

  if (userScopedValue) return userScopedValue;
  if (globalValue) return globalValue;
  return DEFAULT_SPECIAL_BLOCK_ID;
};

const getCommonBlockFromStorage = (userEmail) => {
  const normalizedEmail = String(userEmail || "guest").trim().toLowerCase();
  const userScopedValue = normalizeCommonBlockThemeId(
    localStorage.getItem(`${COMMON_BLOCK_STORAGE_PREFIX}${normalizedEmail}`) || ""
  );
  const globalValue = normalizeCommonBlockThemeId(
    localStorage.getItem(GLOBAL_COMMON_BLOCK_THEME_KEY) || ""
  );

  if (userScopedValue) return userScopedValue;
  if (globalValue) return globalValue;
  return DEFAULT_COMMON_BLOCK_THEME_ID;
};

const getBlockMaterialTextureFromStorage = () =>
  normalizeBlockMaterialTextureId(
    localStorage.getItem(GLOBAL_BLOCK_MATERIAL_TEXTURE_KEY) || ""
  );
const getBlockAnimationFromStorage = () =>
  normalizeBlockAnimationId(localStorage.getItem(GLOBAL_BLOCK_ANIMATION_KEY) || "");

const formatChatUser = (value) => {
  const alias = extractPrimaryName(value);
  return alias || "Player";
};

const normalizeWinnerIds = (rawWinnerIds) => {
  const seen = new Set();
  const unique = [];
  (Array.isArray(rawWinnerIds) ? rawWinnerIds : []).forEach((value) => {
    const userId = String(value || "").trim();
    const normalized = userId.toLowerCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    unique.push(userId);
  });
  return unique;
};

const extractWinnerIdsFromEndPayload = (rawPayload = {}) => {
  const payload = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  const directWinnerIds = Array.isArray(payload.winnerIds) ? payload.winnerIds : [];
  const nestedWinner = payload.winner && typeof payload.winner === "object" ? payload.winner : {};
  const aggregatedWinnerIds = [
    ...directWinnerIds,
    payload.winnerId,
    payload.winnerUserId,
    nestedWinner.id,
    nestedWinner.userId,
    nestedWinner.email,
  ];
  return normalizeWinnerIds(aggregatedWinnerIds);
};

const extractWinnerPlayerIndexFromEndPayload = (rawPayload = {}) => {
  const payload = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  const nestedWinner = payload.winner && typeof payload.winner === "object" ? payload.winner : {};
  const parsedWinnerIndex = Number(
    payload.winnerPlayerIndex ??
      payload.winnerIndex ??
      payload.winnerSlot ??
      nestedWinner.playerIndex ??
      nestedWinner.index ??
      nestedWinner.slot
  );
  if (!Number.isFinite(parsedWinnerIndex)) return null;
  return Math.max(0, Math.floor(parsedWinnerIndex));
};

const buildWinnerAnnouncement = (rawWinnerIds, profileMap = {}) => {
  const winnerIds = normalizeWinnerIds(rawWinnerIds);
  const winnerNames = winnerIds
    .map((winnerId) => {
      const normalizedWinnerId = String(winnerId || "").trim().toLowerCase();
      const profile = profileMap[normalizedWinnerId];
      const profileName = extractPrimaryName(profile?.displayName);
      return profileName || formatChatUser(winnerId);
    })
    .filter(Boolean);

  if (winnerNames.length === 0) return "La partida ha terminado.";
  if (winnerNames.length === 1) return `Ganador: ${winnerNames[0]}.`;
  if (winnerNames.length === 2)
    return `Ganadores: ${winnerNames[0]} y ${winnerNames[1]}.`;
  const lastWinner = winnerNames[winnerNames.length - 1];
  const previousWinners = winnerNames.slice(0, -1).join(", ");
  return `Ganadores: ${previousWinners} y ${lastWinner}.`;
};

const decodeUserFromToken = () => {
  try {
    const token = String(getStoredAuthToken() || "");
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return "";
    const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    const payload = JSON.parse(decoded);
    return String(payload?.email || payload?.userId || payload?.sub || "").trim();
  } catch (error) {
    return "";
  }
};

const Tournament = () => {
  const [open, setOpen] = useState(false);
  const [roomId, setRoomId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [matchmaking, setMatchmaking] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [forcePostGameActions, setForcePostGameActions] = useState(false);
  const [postGameMessage, setPostGameMessage] = useState("");
  const [autoPlayConsumed, setAutoPlayConsumed] = useState(false);
  const [puzzleMetadataState, setPuzzleMetadataState] = useState({
    puzzleId: "",
    status: PUZZLE_METADATA_STATUS.IDLE,
  });
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [chatFloatingPosition, setChatFloatingPosition] = useState({ x: 0, y: 0 });
  const [chatFloatingSize, setChatFloatingSize] = useState({
    width: CHAT_FLOATING_DEFAULT_WIDTH,
    height: CHAT_FLOATING_DEFAULT_HEIGHT,
  });
  const [puzzleEndActionButtonPosition, setPuzzleEndActionButtonPosition] = useState(
    readPuzzleEndActionButtonPosition
  );
  const [expired, setExpired] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [moveNotations, setMoveNotations] = useState([]);
  useEffect(() => {
    moveNotationsRef.current = Array.isArray(moveNotations) ? moveNotations : [];
  }, [moveNotations]);
  const [roomUsers, setRoomUsers] = useState([]);
  const [allBotsSeatName, setAllBotsSeatName] = useState("");
  const [roomProfiles, setRoomProfiles] = useState({});
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [currentUserCimaxScores, setCurrentUserCimaxScores] = useState(() =>
    normalizeCimaxScores({})
  );
  const [specialBlockId, setSpecialBlockId] = useState(DEFAULT_SPECIAL_BLOCK_ID);
  const [commonBlockThemeId, setCommonBlockThemeId] = useState(
    DEFAULT_COMMON_BLOCK_THEME_ID
  );
  const [blockMaterialTextureId, setBlockMaterialTextureId] = useState(
    DEFAULT_BLOCK_MATERIAL_TEXTURE_ID
  );
  const [blockAnimationId, setBlockAnimationId] = useState(
    DEFAULT_BLOCK_ANIMATION_ID
  );
  const [activeTurnIndex, setActiveTurnIndex] = useState(0);
  const [botTurnSlotIndexes, setBotTurnSlotIndexes] = useState([]);
  const [localRuntimeTurnIndex, setLocalRuntimeTurnIndex] = useState(null);
  const [serverTurnClock, setServerTurnClock] = useState({
    turnIndex: 0,
    turnStartedAtMs: 0,
    turnDurationMs: 0,
    remainingMsByIndex: [],
  });
  const [matchPhase, setMatchPhase] = useState("waiting");
  const [matchIntroEndsAtMs, setMatchIntroEndsAtMs] = useState(0);
  const [serverNowMs, setServerNowMs] = useState(() => Date.now());
  const [serverClockOffsetMs, setServerClockOffsetMs] = useState(0);
  const [cimaxDeltaByUserId, setCimaxDeltaByUserId] = useState({});
  const [cimaxInitialByUserId, setCimaxInitialByUserId] = useState({});
  const [cimaxFinalByUserId, setCimaxFinalByUserId] = useState({});
  const [playerRuntimeMap, setPlayerRuntimeMap] = useState({});
  const [indexedRuntimeBySlot, setIndexedRuntimeBySlot] = useState({});
  const [outUsersById, setOutUsersById] = useState({});
  const [disconnectedPlayersById, setDisconnectedPlayersById] = useState({});
  const [connectionState, setConnectionState] = useState("connected");
  const [manualPositionRefreshLoading, setManualPositionRefreshLoading] = useState(false);
  const [manualRefreshUnlockedByReconnect, setManualRefreshUnlockedByReconnect] = useState(false);
  const [unityIframeLoaded, setUnityIframeLoaded] = useState(false);
  const [boardArrowsActive, setBoardArrowsActive] = useState(false);
  const [boardArrowColor, setBoardArrowColor] = useState("blue");
  const [isGame7Layout, setIsGame7Layout] = useState(readGame7LayoutMatch);
  const [unitySessionNonce, setUnitySessionNonce] = useState(0);
  const [savingPuzzleSnapshot, setSavingPuzzleSnapshot] = useState(false);
  const [loadingPuzzleSnapshot, setLoadingPuzzleSnapshot] = useState(false);
  const [puzzleNameModalOpen, setPuzzleNameModalOpen] = useState(false);
  const [puzzleNameDraft, setPuzzleNameDraft] = useState("");
  const [puzzleNameTouchedDraft, setPuzzleNameTouchedDraft] = useState(false);
  const [puzzleTurnLimitDraft, setPuzzleTurnLimitDraft] = useState(
    DEFAULT_PUZZLE_TURN_LIMIT
  );
  const [puzzleRewardValueDraft, setPuzzleRewardValueDraft] = useState(
    DEFAULT_PUZZLE_REWARD_VALUE
  );
  const [puzzleSolveTimeLimitEnabledDraft, setPuzzleSolveTimeLimitEnabledDraft] =
    useState(false);
  const [puzzleSolveTimeLimitMinutesDraft, setPuzzleSolveTimeLimitMinutesDraft] = useState(
    DEFAULT_PUZZLE_SOLVE_TIME_MINUTES
  );
  const [puzzleAttemptCount, setPuzzleAttemptCount] = useState(0);
  const [puzzleFailedByTurnLimit, setPuzzleFailedByTurnLimit] = useState(false);
  const [puzzleFailedBySolveTimeLimit, setPuzzleFailedBySolveTimeLimit] = useState(false);
  const [puzzleSolveTimerStartedAtMs, setPuzzleSolveTimerStartedAtMs] = useState(0);
  const [puzzleSolveTimerNowMs, setPuzzleSolveTimerNowMs] = useState(() => Date.now());
  const [lastGameEndReason, setLastGameEndReason] = useState("");
  const [openingNextPuzzle, setOpeningNextPuzzle] = useState(false);
  const [retryingPuzzle, setRetryingPuzzle] = useState(false);
  const [puzzleModeSequenceNumber, setPuzzleModeSequenceNumber] = useState(null);
  const { user } = useAuth();
  const { id } = useParams();
  const [activePuzzleTournamentId, setActivePuzzleTournamentId] = useState(String(id || "").trim());
  const reconnectingRef = useRef(false);
  const reconnectHttpInFlightRef = useRef(false);
  const reconnectPollingTimerRef = useRef(null);
  const lastMoveSequenceRef = useRef(0);
  const reconnectNoticeShownRef = useRef(false);
  const tournamentOneMinuteAlertPlayedRef = useRef("");
  const roomUsersResyncAtRef = useRef(0);
  const preStartSyncInFlightRef = useRef(false);
  const preStartSyncAtRef = useRef(0);
  const previousLocalClockBySlotRef = useRef({});
  const puzzleEndActionButtonPanelRef = useRef(null);
  const puzzleEndActionButtonDragRef = useRef(null);
  const chatFloatingScrollRef = useRef(null);
  const chatFloatingDragRef = useRef(null);
  const chatFloatingResizeRef = useRef(null);
  const notationScrollRef = useRef(null);
  const unityViewportRef = useRef(null);
  const unityIframeRef = useRef(null);
  const unityIframeLoadedRef = useRef(false);
  const voiceControlRef = useRef(null);
  const voiceRuntimeStateRef = useRef({});
  const hasRequestedExitRef = useRef(false);
  const hasGameEndedRef = useRef(false);
  const localRuntimeClockHadPositiveRef = useRef({});
  const hasAutoResignedOnExitRef = useRef(false);
  const clientFailureReportedRef = useRef(false);
  const solvedPuzzleRewardReportedRef = useRef({});
  const failedPuzzleRewardReportedRef = useRef({});
  const roomIdRef = useRef(0);
  const activePuzzleTournamentIdRef = useRef(String(id || "").trim());
  const roomSessionTournamentIdRef = useRef(String(id || "").trim());
  const roomProfilesRef = useRef({});
  const roomCosmeticAuthorityIdRef = useRef("local");
  const roomCosmeticRevisionRef = useRef(0);
  const cimaxDeltaByUserIdRef = useRef({});
  const cimaxInitialByUserIdRef = useRef({});
  const cimaxFinalByUserIdRef = useRef({});
  const puzzleMoveLogRef = useRef([]);
  const moveNotationsRef = useRef([]);
  const puzzleSnapshotAppliedRef = useRef(false);
  const puzzleSnapshotLoadedKeyRef = useRef("");
  const puzzleSnapshotLoadingKeyRef = useRef("");
  const puzzleHasLiveProgressRef = useRef(false);
  const puzzleLoadCycleRef = useRef(0);
  const lastHandledGameStartRoomKeyRef = useRef("");
  const puzzleSolverPlayerIndexRef = useRef(null);
  const puzzleSolverMoveCountRef = useRef(0);
  const puzzleSolverDiceAttemptMaskRef = useRef(0);
  const lastPuzzleTurnEventKeyRef = useRef("");
  const lastPuzzleTurnNotationKeyRef = useRef("");
  const lastPuzzleTurnEventAtMsRef = useRef(0);
  const unityMoveEventCounterRef = useRef(0);
  const lastLocalHistoryMoveKeyRef = useRef("");
  const lastLocalHistoryMoveAtRef = useRef(0);
  const lastLocalHistoryRollKeyRef = useRef("");
  const lastLocalHistoryRollAtRef = useRef(0);
  const localHistoryEndSentKeyRef = useRef("");
  const pendingPuzzleSnapshotRequestsRef = useRef({});
  const pendingPuzzleSnapshotApplyRef = useRef({});
  const pendingUnityMatchLoadRef = useRef({});
  const puzzleAutoLoadBlockedByIdRef = useRef({});
  const cargarPartidaRef = useRef(null);
  const unityMatchLoadInFlightRef = useRef(null);
  const manualReentryHandledKeyRef = useRef("");
  const previousConnectionStateRef = useRef("connected");
  const activePuzzleTransitionRef = useRef({
    requestId: "",
    puzzleTournamentId: "",
    roomTournamentId: "",
    forceReset: true,
  });
  const acceptAuthoritativeRoomProfiles = useCallback(
    (rawProfiles, rawRevision, rawAuthorityId = "") => {
      if (!rawProfiles || typeof rawProfiles !== "object" || Array.isArray(rawProfiles)) {
        return false;
      }

      const activeRoomId = String(roomIdRef.current || "").trim();
      const incomingAuthorityId =
        String(rawAuthorityId || "").trim() ||
        (activeRoomId ? `${String(id || "").trim()}:${activeRoomId}` : "local");
      if (incomingAuthorityId !== roomCosmeticAuthorityIdRef.current) {
        roomCosmeticAuthorityIdRef.current = incomingAuthorityId;
        roomCosmeticRevisionRef.current = 0;
      }

      const incomingRevision = normalizeRoomCosmeticRevision(rawRevision);
      if (
        !shouldAcceptRoomCosmeticRevision({
          currentRevision: roomCosmeticRevisionRef.current,
          incomingRevision,
        })
      ) {
        return false;
      }

      roomCosmeticRevisionRef.current = Math.max(
        roomCosmeticRevisionRef.current,
        incomingRevision
      );
      setRoomProfiles((prev) => mergeProfileMaps(prev, rawProfiles));
      return true;
    },
    [id]
  );
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language);
  const t = useCallback(
    (key, fallback = key) => getLanguageCopy(language, translations[key], fallback),
    [language]
  );
  const tutorialUiText = useCallback(
    (key) => getLanguageCopy(language, GUIDED_TUTORIAL_UI_COPY[key], key),
    [language]
  );
  const setLayoutNavigationLocked =
    typeof outletContext.setLayoutNavigationLocked === "function"
      ? outletContext.setLayoutNavigationLocked
      : null;
  // Salir de una partida en curso: antes se pregunta (abandonar = rendirse y perder).
  const [leaveGameRequest, setLeaveGameRequest] = useState(null);
  const [tourInfo, setTourInfo] = useState({
    name: "",
    mode: "",
    start: "",
    members: 0,
    havingTime: 0,
    botDifficulty: "",
    duration: 0,
    users: [],
    localPlayers: [],
    ranking: 0,
    createdBy: "",
    isIndependentMatch: false,
    isQuickMatch: false,
    hasMatchPassword: false,
    isPuzzle: false,
    tournamentSequenceNumber: 0,
    tournamentNumber: 0,
    displayNumber: 0,
    puzzleMoveCount: 0,
    puzzleTurnLimit: 0,
    puzzleRewardValue: DEFAULT_PUZZLE_REWARD_VALUE,
    puzzleSolveTimeLimitSeconds: 0,
    freeBoxBaseMode: "free",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const tutorialLevel = useMemo(() => {
    const state =
      location.state && typeof location.state === "object" && !Array.isArray(location.state)
        ? location.state
        : {};
    const search = new URLSearchParams(location.search);
    return Number(state.tutorialLevel || search.get("tutorialLevel") || 0);
  }, [location.search, location.state]);
  const isTutorialLevel1 = tutorialLevel === 1;
  const isTutorialLevel2 = tutorialLevel === 2;
  const isGuidedTutorial = tutorialLevel > 0;
  const [tournamentSequenceNumber, setTournamentSequenceNumber] = useState(0);
  const navigationTournamentSequenceNumber = useMemo(() => {
    const state =
      location.state && typeof location.state === "object" && !Array.isArray(location.state)
        ? location.state
        : null;
    return (
      normalizePublicSequenceNumberValue(state?.tournamentUiSequenceNumber) ||
      normalizePublicSequenceNumberValue(state?.tournamentSequenceNumber) ||
      0
    );
  }, [location.state]);
  const userEmail = user?.email || "";
  const normalizedUserEmail = useMemo(
    () => String(userEmail || "guest").trim().toLowerCase(),
    [userEmail]
  );
  const storedBlockAnimationSpeedPreference = useMemo(() => {
    if (typeof window === "undefined") return DEFAULT_PREFERENCES.blockAnimationSpeed;
    const storageKey = getPreferencesStorageKey(userEmail);
    const storedPreferences = parsePreferences(window.localStorage.getItem(storageKey));
    const speed = normalizeBlockAnimationSpeed(
      storedPreferences?.blockAnimationSpeed ?? DEFAULT_PREFERENCES.blockAnimationSpeed
    );
    writeGlobalBlockAnimationSpeed(speed);
    return speed;
  }, [userEmail]);
  // Se puede cambiar durante la partida (tarjeta «Velocidad de animación»);
  // usa el mismo ajuste guardado que Ajustes generales.
  const [blockAnimationSpeedPreference, setBlockAnimationSpeedPreference] = useState(
    storedBlockAnimationSpeedPreference
  );
  useEffect(() => {
    setBlockAnimationSpeedPreference(storedBlockAnimationSpeedPreference);
  }, [storedBlockAnimationSpeedPreference]);
  const changeBlockAnimationSpeedFromMatch = useCallback(
    (rawValue) => {
      const speed = normalizeBlockAnimationSpeed(rawValue);
      setBlockAnimationSpeedPreference(speed);
      writeUserPreference(userEmail, "blockAnimationSpeed", speed);
    },
    [userEmail]
  );
  const [cameraFollowEnabled, setCameraFollowEnabled] = useState(() =>
    readCameraFollowPreference(userEmail)
  );
  const [voiceControlEnabled, setVoiceControlEnabled] = useState(() =>
    readVoiceControlPreference(userEmail)
  );
  const [voiceControlStatus, setVoiceControlStatus] = useState(
    readVoiceControlPreference(userEmail)
      ? VOICE_CONTROL_STATUS.AWAITING_PERMISSION
      : VOICE_CONTROL_STATUS.DISABLED
  );
  const [voiceControlActivating, setVoiceControlActivating] = useState(false);
  const [voiceControlDebugText, setVoiceControlDebugText] = useState("");

  useEffect(() => {
    setCameraFollowEnabled(readCameraFollowPreference(userEmail));
    const nextVoiceControlEnabled = readVoiceControlPreference(userEmail);
    setVoiceControlEnabled(nextVoiceControlEnabled);
    setVoiceControlStatus(
      nextVoiceControlEnabled
        ? VOICE_CONTROL_STATUS.AWAITING_PERMISSION
        : VOICE_CONTROL_STATUS.DISABLED
    );
  }, [userEmail]);
  const isCurrentUserTournamentCreator = useMemo(
    () =>
      Boolean(normalizedUserEmail) &&
      String(tourInfo?.createdBy || "").trim().toLowerCase() === normalizedUserEmail,
    [normalizedUserEmail, tourInfo?.createdBy]
  );
  const isCurrentUserJoinedTournament = useMemo(() => {
    if (!normalizedUserEmail) return false;
    return (Array.isArray(tourInfo?.users) ? tourInfo.users : []).some(
      (entry) => String(entry?.userId || entry?.email || entry || "").trim().toLowerCase() === normalizedUserEmail
    );
  }, [normalizedUserEmail, tourInfo?.users]);
  const shouldAskForProtectedMatchPassword = useMemo(
    () =>
      Boolean(tourInfo?.hasMatchPassword) &&
      !isCurrentUserTournamentCreator &&
      !isCurrentUserJoinedTournament,
    [isCurrentUserJoinedTournament, isCurrentUserTournamentCreator, tourInfo?.hasMatchPassword]
  );
  const isAutoPlayRequested = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return search.get("autoplay") === "1";
  }, [location.search]);
  const isSpectatorRequested = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return search.get("spectator") === "1";
  }, [location.search]);
  const requestedRoomId = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return String(search.get("roomId") || "").trim();
  }, [location.search]);
  const protectedMatchPasswordFromNavigation = useMemo(() => {
    const state =
      location.state && typeof location.state === "object" && !Array.isArray(location.state)
        ? location.state
        : null;
    return String(state?.protectedMatchPassword || "").trim();
  }, [location.state]);
  const manualReentryRequest = useMemo(() => {
    const state =
      location.state && typeof location.state === "object" && !Array.isArray(location.state)
        ? location.state
        : null;
    if (!state) return null;

    const matchData =
      state.manualReentryMatchData &&
      typeof state.manualReentryMatchData === "object" &&
      !Array.isArray(state.manualReentryMatchData)
        ? state.manualReentryMatchData
        : null;
    if (!matchData) return null;

    const routeTournamentId = String(id || "").trim();
    const incomingTournamentId = String(matchData.matchID || matchData.tournamentId || "").trim();
    if (!incomingTournamentId || !routeTournamentId || incomingTournamentId !== routeTournamentId) {
      return null;
    }

    const roomIdFromPayload = String(matchData.roomID || matchData.roomId || "").trim();
    const requestKey =
      String(state.manualReentryRequestKey || "").trim() ||
      `manual-reentry:${incomingTournamentId}:${roomIdFromPayload}`;

    if (!requestKey) return null;
    return { requestKey, matchData };
  }, [id, location.state]);
  const navigationBotDifficulty = useMemo(() => {
    const state =
      location.state && typeof location.state === "object" && !Array.isArray(location.state)
        ? location.state
        : null;
    return normalizeRuntimeBotDifficulty(
      state?.botGamePreferences?.botDifficulty ||
        state?.lobbyCreatePreferences?.botDifficulty ||
        state?.botDifficulty
    );
  }, [location.state]);
  const isPuzzleRequested = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return search.get("puzzle") === "1";
  }, [location.search]);
  const activePuzzleIdForAutoplay = String(
    activePuzzleTournamentId || id || ""
  ).trim();
  const puzzleMetadataReadyForAutoplay = isPuzzleMetadataReadyForAutoplay({
    isPuzzleRequested,
    activePuzzleId: activePuzzleIdForAutoplay,
    metadataPuzzleId: puzzleMetadataState.puzzleId,
    metadataStatus: puzzleMetadataState.status,
  });
  const unityIframePassthroughQuery = useMemo(() => {
    const search = new URLSearchParams(location.search);
    const browserSearch =
      typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const passthrough = new URLSearchParams();
    ["logicalEngineLocalActive", "botCoreLogicalLocalActive"].forEach((key) => {
      if (search.get(key) === "1" || browserSearch?.get(key) === "1") {
        passthrough.set(key, "1");
      }
    });
    return passthrough.toString();
  }, [location.search]);
  const normalizedMode = useMemo(
    () => normalizeTournamentModeAlias(tourInfo.mode),
    [tourInfo.mode]
  );
  const isBotLocalMode = useMemo(
    () => isBotLocalModeValue(normalizedMode),
    [normalizedMode]
  );
  const isLocalFriendsMode = useMemo(
    () => isLocalFriendsModeValue(normalizedMode),
    [normalizedMode]
  );
  const isPuzzleTournament = Boolean(tourInfo?.isPuzzle);
  const isPuzzleMode = isPuzzleTournament || isPuzzleRequested;
  const puzzleModeField = useMemo(
    () => resolveCimaxModeFieldFromMode(tourInfo?.mode, tourInfo?.freeBoxBaseMode) || "ciencia",
    [tourInfo?.freeBoxBaseMode, tourInfo?.mode]
  );
  const puzzleModeTranslationKey =
    PUZZLE_MODE_TRANSLATION_KEYS[puzzleModeField] || PUZZLE_MODE_TRANSLATION_KEYS.ciencia;
  const isPuzzleAzarMode = useMemo(() => {
    const normalizedBaseMode = normalizeFreeBoxBaseMode(
      tourInfo?.freeBoxBaseMode,
      "free"
    );
    return normalizedMode === "freebox" && normalizedBaseMode === "dice";
  }, [normalizedMode, tourInfo?.freeBoxBaseMode]);
  const canCreatePuzzleSnapshots = useMemo(() => {
    const roleCandidates = [
      user?.role,
      user?.type,
      user?.permission,
      user?.accountType,
      user?.accessLevel,
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
    return roleCandidates.some((value) => value === "admin" || value === "superuser");
  }, [user?.accessLevel, user?.accountType, user?.permission, user?.role, user?.type]);
  const shouldBlockPuzzleRightClickRuleBreak = useMemo(
    () =>
      normalizedMode === "freebox" &&
      !isSpectatorRequested &&
      isPuzzleMode,
    [isPuzzleMode, isSpectatorRequested, normalizedMode]
  );
  const isOnlineMode = useMemo(
    () => ["dice", "cienzar", "sciencevsazar", "free"].includes(normalizedMode),
    [normalizedMode]
  );
  const shouldRecordLocalMatchHistory = useMemo(
    () =>
      !isSpectatorRequested &&
      !isPuzzleMode &&
      normalizedMode !== "freebox" &&
      (isBotLocalMode || isLocalFriendsMode),
    [isBotLocalMode, isLocalFriendsMode, isPuzzleMode, isSpectatorRequested, normalizedMode]
  );
  useEffect(() => {
    setTournamentSequenceNumber(navigationTournamentSequenceNumber);
  }, [id, navigationTournamentSequenceNumber]);
  useEffect(() => {
    const directSequenceNumber = resolvePublicSequenceNumber(tourInfo || {});
    if (directSequenceNumber <= 0) return;
    setTournamentSequenceNumber((current) => (current > 0 ? current : directSequenceNumber));
  }, [tourInfo]);
  useEffect(() => {
    const tournamentId = String(id || "").trim();
    if (!tournamentId || isPuzzleMode || tournamentSequenceNumber > 0) return undefined;
    let cancelled = false;

    axios
      .get("/tournaments")
      .then((response) => {
        if (cancelled) return;
        const payload = response?.data;
        const entries = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.tournaments)
          ? payload.tournaments
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        const resolvedSequenceNumber = resolveTournamentFallbackSequenceNumberFromList(
          entries,
          tournamentId
        );
        if (resolvedSequenceNumber > 0) {
          setTournamentSequenceNumber(resolvedSequenceNumber);
          setTourInfo((previous) => ({
            ...previous,
            tournamentUiSequenceNumber: resolvedSequenceNumber,
            tournamentSequenceNumber: resolvedSequenceNumber,
            tournamentNumber: resolvedSequenceNumber,
            displayNumber: resolvedSequenceNumber,
          }));
        }
      })
      .catch((error) => console.log(error));

    return () => {
      cancelled = true;
    };
  }, [id, isPuzzleMode, tournamentSequenceNumber]);
  const quickChatEnabled = useMemo(
    () => ["dice", "cienzar", "sciencevsazar", "free"].includes(normalizedMode),
    [normalizedMode]
  );
  const useLocalMoveTurnFallback = false;
  const useLocalBotTurnIndicatorFallback =
    isBotLocalMode && !isOnlineMode && !isSpectatorRequested;
  const rightPanelTotalPlayers = useMemo(() => {
    const candidates = [
      Number(tourInfo.memberSize),
      Number(tourInfo.members),
      Array.isArray(roomUsers) ? roomUsers.length : 0,
      Array.isArray(tourInfo.users) ? tourInfo.users.length : 0,
    ].map((value) => (Number.isFinite(value) ? Math.floor(value) : 0));

    let resolved = candidates.find((value) => value > 0) || 0;
    if (isBotLocalMode) {
      // En botlocal memberSize puede llegar como 1 (solo humano), pero visualmente es 1 vs 1.
      if (resolved < 2) resolved = 2;
    }

    if (resolved <= 0) resolved = 2;
    return Math.max(2, Math.min(4, resolved));
  }, [isBotLocalMode, roomUsers, tourInfo.memberSize, tourInfo.members, tourInfo.users]);
  const rightPanelPlayersLabel = useMemo(() => {
    const opponents = Math.max(1, rightPanelTotalPlayers - 1);
    return `1 vs ${opponents}`;
  }, [rightPanelTotalPlayers]);
  const rightPanelTimeLabel = useMemo(() => {
    const puzzleSolveLimitSeconds = resolvePuzzleSolveTimeLimitSecondsFromRecord(tourInfo);
    if (isPuzzleMode) {
      return puzzleSolveLimitSeconds > 0 ? "Con tiempo limite" : "Sin tiempo";
    }

    if (isBotLocalMode || normalizedMode === "freebox") {
      return "Sin tiempo";
    }

    const havingTime = Number(tourInfo.havingTime);
    if (!Number.isFinite(havingTime) || havingTime <= 0) return "Sin tiempo";
    return `${Math.floor(havingTime)} min`;
  }, [isBotLocalMode, isPuzzleMode, normalizedMode, tourInfo]);
  const rightPanelModeLabel = useMemo(() => {
    const modeLabel = isPuzzleMode
      ? t(puzzleModeTranslationKey, "Puzzle")
      : formatModeLabel(tourInfo.mode || "");
    return String(modeLabel || "").trim().toUpperCase();
  }, [isPuzzleMode, puzzleModeTranslationKey, t, tourInfo.mode]);
  const rightPanelModeIconSrc = useMemo(
    () =>
      resolveTournamentModeIconSrc(
        isPuzzleMode ? puzzleModeField : normalizedMode,
        tourInfo?.freeBoxBaseMode
      ),
    [isPuzzleMode, normalizedMode, puzzleModeField, tourInfo?.freeBoxBaseMode]
  );
  const rightPanelPlayersIconSrc = useMemo(
    () => PLAYER_COUNT_ICON_SRC_MAP[rightPanelTotalPlayers] || PLAYER_COUNT_ICON_SRC_MAP[2],
    [rightPanelTotalPlayers]
  );
  const rightPanelTimeIconSrc = useMemo(
    () => resolveTournamentTimeIconSrc(tourInfo.havingTime),
    [tourInfo.havingTime]
  );
  const notationPlayerCount = useMemo(() => {
    const playersFromUsers = Array.isArray(tourInfo.users) ? tourInfo.users.length : 0;
    const playersFromMembers = Number(tourInfo.members) || 0;
    const resolved = playersFromUsers || playersFromMembers || 2;
    return Math.max(2, Math.min(4, resolved));
  }, [tourInfo.members, tourInfo.users]);
  const notationRows = useMemo(
    () => buildNotationRows(moveNotations, notationPlayerCount),
    [moveNotations, notationPlayerCount]
  );
  const chatUserId = useMemo(() => {
    const fromContext = String(user?.email || "").trim();
    if (fromContext) return fromContext;
    return decodeUserFromToken();
  }, [user?.email]);
  const canSendQuickChat =
    Boolean(roomId) &&
    quickChatEnabled &&
    Boolean(chatUserId) &&
    connectionState === "connected" &&
    !isSpectatorRequested;
  const hasActiveRoom = Boolean(String(roomId || "").trim());
  const hasLocalBotRuntimeMatch =
    isBotLocalMode && String(matchPhase || "").trim().toLowerCase() === "playing";
  const isMatchInProgress = (hasActiveRoom || hasLocalBotRuntimeMatch) && !gameEnded;
  const shouldShowManualRefreshButton =
    !isSpectatorRequested && isMatchInProgress && manualRefreshUnlockedByReconnect;
  const isPlayButtonDisabled = loading || expired || matchmaking || isMatchInProgress;
  const hasGameFinishedForHomeAction =
    gameEnded || forcePostGameActions || hasGameEndedRef.current;
  const shouldShowPostGameActions =
    hasGameFinishedForHomeAction && !isSpectatorRequested;
  const puzzleTurnLimit = useMemo(
    () => normalizePuzzleTurnLimit(tourInfo?.puzzleTurnLimit),
    [tourInfo?.puzzleTurnLimit]
  );
  const puzzleRewardValue = useMemo(
    () => resolvePuzzleRewardValueFromRecord(tourInfo),
    [tourInfo]
  );
  const puzzleSolveTimeLimitSeconds = useMemo(
    () => resolvePuzzleSolveTimeLimitSecondsFromRecord(tourInfo),
    [tourInfo]
  );
  const puzzleAttemptLimit = puzzleTurnLimit;
  const puzzleAttemptDisplayCount =
    puzzleAttemptLimit > 0 ? Math.min(puzzleAttemptCount, puzzleAttemptLimit) : 0;
  const puzzleAttemptRemainingCount =
    puzzleAttemptLimit > 0 ? Math.max(0, puzzleAttemptLimit - puzzleAttemptDisplayCount) : 0;
  const puzzleSolveTimeRemainingSeconds = useMemo(() => {
    if (puzzleSolveTimeLimitSeconds <= 0) return 0;
    if (puzzleSolveTimerStartedAtMs <= 0) return puzzleSolveTimeLimitSeconds;
    const elapsedSeconds = Math.max(
      0,
      Math.floor((Number(puzzleSolveTimerNowMs) - Number(puzzleSolveTimerStartedAtMs)) / 1000)
    );
    return Math.max(0, puzzleSolveTimeLimitSeconds - elapsedSeconds);
  }, [puzzleSolveTimeLimitSeconds, puzzleSolveTimerNowMs, puzzleSolveTimerStartedAtMs]);
  const puzzleSolveTimeRemainingLabel = useMemo(
    () => formatPuzzleSolveCountdownLabel(puzzleSolveTimeRemainingSeconds),
    [puzzleSolveTimeRemainingSeconds]
  );
  const puzzleSolveTimerVisible =
    isPuzzleMode &&
    puzzleSolveTimeLimitSeconds > 0 &&
    isMatchInProgress &&
    !isSpectatorRequested;
  const puzzleSolveTimeNearEnd =
    puzzleSolveTimerVisible && puzzleSolveTimeRemainingSeconds <= 10;
  const normalizedLastGameEndReason = String(lastGameEndReason || "")
    .trim()
    .toLowerCase();
  const canShowNextPuzzleButton =
    !isSpectatorRequested &&
    isPuzzleMode &&
    gameEnded &&
    normalizedLastGameEndReason === "winner_reached_goal";
  const puzzleRetryReason =
    normalizedLastGameEndReason === "puzzle_turn_limit_reached" ||
    normalizedLastGameEndReason === "puzzle_solve_time_expired";
  const canShowRetryPuzzleButton =
    !isSpectatorRequested &&
    isPuzzleMode &&
    (puzzleFailedByTurnLimit || puzzleFailedBySolveTimeLimit || puzzleRetryReason) &&
    gameEnded &&
    puzzleRetryReason;
  const canShowPuzzleEndActionButton = canShowNextPuzzleButton || canShowRetryPuzzleButton;
  const shouldUseCompactPuzzleEndActionLayout =
    canShowPuzzleEndActionButton &&
    isPuzzleMode &&
    !isSpectatorRequested &&
    isCompactPuzzleLayoutDevice();
  const puzzleViewportRect = unityViewportRef.current?.getBoundingClientRect?.();
  const resolvedPuzzleEndActionButtonPosition = shouldUseCompactPuzzleEndActionLayout
    ? getDefaultPuzzleEndActionButtonPosition(
        PUZZLE_END_ACTION_BUTTON_DEFAULT_WIDTH,
        PUZZLE_END_ACTION_BUTTON_DEFAULT_HEIGHT,
        {
          forceMobilePuzzleLayout: true,
          viewportWidth:
            Number.isFinite(Number(puzzleViewportRect?.width)) &&
            Number(puzzleViewportRect.width) > 0
              ? Math.round(puzzleViewportRect.width)
              : undefined,
          viewportHeight:
            Number.isFinite(Number(puzzleViewportRect?.height)) &&
            Number(puzzleViewportRect.height) > 0
              ? Math.round(puzzleViewportRect.height)
              : undefined,
        }
      )
    : null;
  const shouldShowFreeBoxGoToPuzzleButton =
    !isSpectatorRequested &&
    normalizedMode === "freebox" &&
    !isPuzzleMode;
  useEffect(() => {
    if (isGuidedTutorial || !canShowNextPuzzleButton) return;
    const solvedPuzzleId = String(activePuzzleTournamentId || id || "").trim();
    if (!solvedPuzzleId) return;
    markPuzzleAsSolvedForUser(normalizedUserEmail, solvedPuzzleId);
    const solvedReportMap =
      solvedPuzzleRewardReportedRef.current &&
      typeof solvedPuzzleRewardReportedRef.current === "object"
        ? solvedPuzzleRewardReportedRef.current
        : {};
    if (solvedReportMap[solvedPuzzleId] === true) return;
    solvedReportMap[solvedPuzzleId] = true;
    solvedPuzzleRewardReportedRef.current = solvedReportMap;
    axios
      .post("/tournaments/puzzles/mark-solved-reward", {
        puzzleId: solvedPuzzleId,
      })
      .catch((error) => {
        solvedPuzzleRewardReportedRef.current[solvedPuzzleId] = false;
        console.log(error);
      });
  }, [activePuzzleTournamentId, canShowNextPuzzleButton, id, isGuidedTutorial, normalizedUserEmail]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const reconcilePuzzleActionButtonPosition = () => {
      setPuzzleEndActionButtonPosition((previousPosition) => {
        const panelRect = puzzleEndActionButtonPanelRef.current?.getBoundingClientRect?.();
        const panelWidth = Math.max(
          PUZZLE_END_ACTION_BUTTON_DEFAULT_WIDTH,
          Math.round(panelRect?.width || 0)
        );
        const panelHeight = Math.max(
          PUZZLE_END_ACTION_BUTTON_DEFAULT_HEIGHT,
          Math.round(panelRect?.height || 0)
        );

        if (!previousPosition) {
          return getDefaultPuzzleEndActionButtonPosition(panelWidth, panelHeight);
        }

        const clampedPosition = clampPuzzleEndActionButtonPosition(
          previousPosition,
          panelWidth,
          panelHeight
        );
        return clampedPosition.x === previousPosition.x && clampedPosition.y === previousPosition.y
          ? previousPosition
          : clampedPosition;
      });
    };

    reconcilePuzzleActionButtonPosition();
    window.addEventListener("resize", reconcilePuzzleActionButtonPosition);
    return () => window.removeEventListener("resize", reconcilePuzzleActionButtonPosition);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !puzzleEndActionButtonPosition) return;
    window.localStorage.setItem(
      PUZZLE_END_ACTION_BUTTON_POSITION_STORAGE_KEY,
      JSON.stringify(puzzleEndActionButtonPosition)
    );
  }, [puzzleEndActionButtonPosition]);

  const isBotLocalOrFreeBoxMode =
    isBotLocalMode || normalizedMode === "freebox";
  const shouldShowBottomHomeAction =
    !hasGameFinishedForHomeAction && (!isMatchInProgress || isBotLocalOrFreeBoxMode);
  const isIndependentMatchMode = !isOnlineMode;
  const quickMatchExpiresAtMs = useMemo(() => {
    const explicitMs = new Date(tourInfo?.quickMatchExpiresAt || 0).valueOf();
    if (Number.isFinite(explicitMs) && explicitMs > 0) return explicitMs;
    return 0;
  }, [tourInfo?.quickMatchExpiresAt]);
  const tournamentEndsAtMs = useMemo(() => {
    const explicitCandidates = [
      tourInfo?.end,
      tourInfo?.endAt,
      tourInfo?.endDate,
      tourInfo?.endsAt,
      tourInfo?.expiresAt,
      tourInfo?.expireAt,
      tourInfo?.quickMatchExpiresAt,
    ];
    for (let index = 0; index < explicitCandidates.length; index += 1) {
      const resolved = resolveTimestampMs(explicitCandidates[index]);
      if (resolved > 0) return resolved;
    }

    const startMs = resolveTimestampMs(tourInfo?.start);
    const durationDays = Number(tourInfo?.duration);
    if (
      Number.isFinite(startMs) &&
      startMs > 0 &&
      Number.isFinite(durationDays) &&
      durationDays > 0
    ) {
      return Math.floor(startMs + durationDays * 24 * 60 * 60 * 1000);
    }
    return 0;
  }, [
    tourInfo?.end,
    tourInfo?.endAt,
    tourInfo?.endDate,
    tourInfo?.endsAt,
    tourInfo?.expiresAt,
    tourInfo?.expireAt,
    tourInfo?.quickMatchExpiresAt,
    tourInfo?.start,
    tourInfo?.duration,
  ]);
  const tournamentRemainingSeconds = useMemo(() => {
    if (!Number.isFinite(tournamentEndsAtMs) || tournamentEndsAtMs <= 0) return NaN;
    const nowMs = Number.isFinite(serverNowMs) ? serverNowMs : Date.now();
    const remainingMs = tournamentEndsAtMs - nowMs;
    return Math.max(0, Math.ceil(remainingMs / 1000));
  }, [serverNowMs, tournamentEndsAtMs]);
  const tournamentRemainingLabel = useMemo(
    () => formatTournamentCountdownLabel(tournamentRemainingSeconds),
    [tournamentRemainingSeconds]
  );
  const shouldShowTournamentRemaining = useMemo(
    () =>
      isOnlineMode &&
      isMatchInProgress &&
      Number.isFinite(tournamentRemainingSeconds) &&
      tournamentRemainingSeconds >= 0,
    [isMatchInProgress, isOnlineMode, tournamentRemainingSeconds]
  );
  const tournamentRemainingTextClass = useMemo(() => {
    if (!Number.isFinite(tournamentRemainingSeconds)) return "text-cyan-100";
    if (tournamentRemainingSeconds <= 60) return "text-rose-300";
    if (tournamentRemainingSeconds <= 300) return "text-amber-300";
    return "text-emerald-200";
  }, [tournamentRemainingSeconds]);
  const isIndependentChallengeMatch = Boolean(tourInfo?.isIndependentMatch);
  const isIndependentQuickMatch =
    Boolean(tourInfo?.isQuickMatch) && quickMatchExpiresAtMs <= 0;
  const shouldShowCreateGamePostGameButton =
    isIndependentChallengeMatch || isIndependentQuickMatch;
  const shouldHidePostGameQueueButtons =
    isIndependentMatchMode || shouldShowCreateGamePostGameButton;
  const postGameActionHelpText = shouldShowCreateGamePostGameButton
    ? "Puedes volver a jugar o ir al inicio."
    : isIndependentMatchMode
    ? "Puedes ir al inicio."
    : "Puedes volver a jugar, volver a la sala de espera o ir al inicio.";
  const currentUserId = useMemo(() => {
    const fromContext = String(
      user?.loginIdentifier || user?.username || user?.userName || user?.email || ""
    ).trim();
    if (fromContext) return fromContext;
    return chatUserId;
  }, [chatUserId, user?.email, user?.loginIdentifier, user?.userName, user?.username]);
  const normalizedCurrentPlayerId = useMemo(
    () => normalizeProfileId(currentUserId || userEmail || user?.email || ""),
    [currentUserId, user?.email, userEmail]
  );
  const isCurrentCienzarPlayerEliminated = Boolean(
    normalizedMode === "cienzar" &&
      normalizedCurrentPlayerId &&
      outUsersById?.[normalizedCurrentPlayerId] === true
  );
  const emitLocalMatchEndForHistory = useCallback(
    (payload = {}) => {
      if (!shouldRecordLocalMatchHistory) return false;

      const activeTournamentId = String(
        payload?.tournamentId ||
          roomSessionTournamentIdRef.current ||
          id ||
          ""
      ).trim();
      const activeRoomId = String(payload?.roomId || roomIdRef.current || roomId || "").trim();
      if (!activeTournamentId || !activeRoomId) return false;

      const winnerPlayerIndex = extractWinnerPlayerIndexFromEndPayload(payload);
      const endReason = String(payload?.endReason || payload?.reason || "local_game_end")
        .trim()
        .toLowerCase();
      const dedupeKey = [
        activeTournamentId,
        activeRoomId,
        endReason,
        winnerPlayerIndex == null ? "" : winnerPlayerIndex,
      ].join("|");
      if (localHistoryEndSentKeyRef.current === dedupeKey) return false;

      const socket = window.socket || getSharedSocket();
      if (!socket) return false;

      localHistoryEndSentKeyRef.current = dedupeKey;
      socket.emit(
        "LOCAL_MATCH_END",
        JSON.stringify({
          tournamentId: activeTournamentId,
          roomId: activeRoomId,
          userId: currentUserId || userEmail || user?.email || "",
          winnerId: String(payload?.winnerId || "").trim(),
          winnerIds: Array.isArray(payload?.winnerIds) ? payload.winnerIds : [],
          winnerPlayerIndex: winnerPlayerIndex == null ? -1 : winnerPlayerIndex,
          winnerMessage: String(payload?.winnerMessage || payload?.message || "").trim(),
          endReason: endReason || "local_game_end",
        })
      );
      return true;
    },
    [
      currentUserId,
      id,
      roomId,
      shouldRecordLocalMatchHistory,
      user?.email,
      userEmail,
    ]
  );
  const currentUserIdentityKeys = useMemo(
    () =>
      uniqueList(
        [
          currentUserId,
          chatUserId,
          user?.loginIdentifier,
          user?.username,
          user?.userName,
          user?.email,
          userEmail,
          user?.contactEmail,
          user?.playerCode,
          user?.userId,
          user?.id,
          user?._id,
        ].map(normalizeProfileId)
      ),
    [
      chatUserId,
      currentUserId,
      user?._id,
      user?.contactEmail,
      user?.email,
      user?.id,
      user?.loginIdentifier,
      user?.playerCode,
      user?.userId,
      user?.userName,
      user?.username,
      userEmail,
    ]
  );
  const isCurrentUserIdentity = useCallback(
    (value) => {
      const normalized = normalizeProfileId(value);
      return Boolean(normalized && currentUserIdentityKeys.includes(normalized));
    },
    [currentUserIdentityKeys]
  );
  const currentUserName = useMemo(() => {
    const firstName = extractPrimaryName(user?.firstName);
    if (firstName) return firstName;
    if (currentUserId) return formatChatUser(currentUserId);
    return "Player";
  }, [currentUserId, user?.firstName]);
  const currentUserAvatarUrl = useMemo(() => {
    const fromContext = String(user?.avatar || user?.avatarUrl || user?.image || "").trim();
    if (fromContext) return resolveAvatarSrc(fromContext);
    return resolveAvatarSrc(getStoredAvatarByEmail(currentUserId));
  }, [currentUserId, user?.avatar, user?.avatarUrl, user?.image]);
  const rememberActiveMatchSession = useCallback(
    (rawRoomId = roomIdRef.current || roomId, rawTournamentId = id) => {
      const activeRoomId = String(rawRoomId || "").trim();
      const activeTournamentId = String(rawTournamentId || id || "").trim();
      const activeUserId = currentUserId || userEmail || user?.email || "";
      if (!activeRoomId || !activeTournamentId || !activeUserId) return false;

      return writeStoredActiveMatchSession({
        tournamentId: activeTournamentId,
        roomId: activeRoomId,
        userId: activeUserId,
      });
    },
    [currentUserId, id, roomId, user?.email, userEmail]
  );
  const clearStoredActiveMatchSessionForCurrentUser = useCallback(
    (rawTournamentId = id) => {
      const activeTournamentId = String(rawTournamentId || id || "").trim();
      const activeUserId = currentUserId || userEmail || user?.email || "";
      return clearStoredActiveMatchSession({
        tournamentId: activeTournamentId,
        userId: activeUserId,
      });
    },
    [currentUserId, id, user?.email, userEmail]
  );
  useEffect(() => {
    if (!isOnlineMode || isPuzzleMode || isSpectatorRequested) return;
    if (!isCurrentCienzarPlayerEliminated || hasGameEndedRef.current) return;

    clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);
    hasGameEndedRef.current = true;
    setGameEnded(true);
    setLastGameEndReason("eliminated");
    setForcePostGameActions(true);
    setServerTurnClock({
      turnIndex: 0,
      turnStartedAtMs: 0,
      turnDurationMs: 0,
      remainingMsByIndex: [],
    });
    setServerClockOffsetMs(0);
    setPostGameMessage((previousMessage) =>
      String(previousMessage || "").trim()
        ? previousMessage
        : `Has sido eliminado. ${postGameActionHelpText}`
    );
    setChatModalOpen(false);
    setOpen(false);
    setMatchmaking(false);
    setLoading(false);
  }, [
    clearStoredActiveMatchSessionForCurrentUser,
    id,
    isCurrentCienzarPlayerEliminated,
    isOnlineMode,
    isPuzzleMode,
    isSpectatorRequested,
    postGameActionHelpText,
  ]);
  useEffect(() => {
    if (!isMatchInProgress) {
      localRuntimeClockHadPositiveRef.current = {};
      return;
    }

    if (isOnlineMode || isSpectatorRequested || hasGameEndedRef.current) return;
    if (rightPanelTotalPlayers > 2) return;

    const activeSlotCount = Math.max(1, Math.min(2, rightPanelTotalPlayers));
    const activeClockEntries = Array.from({ length: activeSlotCount }, (_, index) => ({
      index,
      seconds: parseClockSeconds(indexedRuntimeBySlot?.[index]?.clockSeconds),
    })).filter((entry) => Number.isFinite(entry.seconds));

    if (activeClockEntries.length < activeSlotCount) return;

    const positiveClockSlots = {
      ...(localRuntimeClockHadPositiveRef.current &&
      typeof localRuntimeClockHadPositiveRef.current === "object"
        ? localRuntimeClockHadPositiveRef.current
        : {}),
    };
    activeClockEntries.forEach((entry) => {
      if (entry.seconds > 0) positiveClockSlots[entry.index] = true;
    });
    localRuntimeClockHadPositiveRef.current = positiveClockSlots;

    const allActiveSlotsHadPositive = activeClockEntries.every(
      (entry) => positiveClockSlots[entry.index] === true
    );
    if (!allActiveSlotsHadPositive) return;
    if (!activeClockEntries.some((entry) => entry.seconds <= 0)) return;

    clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);
    hasGameEndedRef.current = true;
    setGameEnded(true);
    setLastGameEndReason("timeout");
    setForcePostGameActions(true);
    setServerTurnClock({
      turnIndex: 0,
      turnStartedAtMs: 0,
      turnDurationMs: 0,
      remainingMsByIndex: [],
    });
    setServerClockOffsetMs(0);
    setPostGameMessage(`Se acabo el tiempo. ${postGameActionHelpText}`);
    setChatModalOpen(false);
    setOpen(false);
    setMatchmaking(false);
    setLoading(false);
  }, [
    clearStoredActiveMatchSessionForCurrentUser,
    id,
    indexedRuntimeBySlot,
    isMatchInProgress,
    isOnlineMode,
    isSpectatorRequested,
    postGameActionHelpText,
    rightPanelTotalPlayers,
  ]);
  useEffect(() => {
    const contextScores = user?.cimaxScores || user?.cimax;
    if (!contextScores) return;
    setCurrentUserCimaxScores(normalizeCimaxScores(contextScores));
  }, [user?.cimax, user?.cimaxScores]);
  const applySpecialBlockPreference = useCallback(
    (rawSkinId, explicitImage = "") => {
      const selected = normalizeSpecialBlockId(rawSkinId) || DEFAULT_SPECIAL_BLOCK_ID;
      setSpecialBlockId(selected);
      localStorage.setItem(`${SPECIAL_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`, selected);
      localStorage.setItem(GLOBAL_SPECIAL_BLOCK_KEY, selected);
      localStorage.setItem(
        GLOBAL_SPECIAL_BLOCK_IMAGE_KEY,
        resolveStoredSpecialBlockImage(
          selected,
          explicitImage || localStorage.getItem(GLOBAL_SPECIAL_BLOCK_IMAGE_KEY)
        )
      );
      return selected;
    },
    [normalizedUserEmail]
  );
  const applyCommonBlockPreference = useCallback(
    (rawSkinId) => {
      const selected = normalizeCommonBlockThemeId(rawSkinId);
      setCommonBlockThemeId(selected);
      localStorage.setItem(`${COMMON_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`, selected);
      localStorage.setItem(GLOBAL_COMMON_BLOCK_THEME_KEY, selected);
      return selected;
    },
    [normalizedUserEmail]
  );
  const applyBlockMaterialPreference = useCallback((rawTextureId) => {
    const selected = normalizeBlockMaterialTextureId(rawTextureId);
    setBlockMaterialTextureId(selected);
    localStorage.setItem(GLOBAL_BLOCK_MATERIAL_TEXTURE_KEY, selected);
    return selected;
  }, []);
  const applyBlockAnimationPreference = useCallback((rawAnimationId) => {
    const selected = normalizeBlockAnimationId(rawAnimationId);
    setBlockAnimationId(selected);
    localStorage.setItem(GLOBAL_BLOCK_ANIMATION_KEY, selected);
    return selected;
  }, []);
  const persistSkinPreferencesToServer = useCallback(async (specialId, commonId, materialId, animationId) => {
    const token = String(getStoredAuthToken() || "").trim();
    if (!token) return null;

    const payload = {
      specialBlockId: normalizeSpecialBlockId(specialId) || DEFAULT_SPECIAL_BLOCK_ID,
      commonBlockThemeId: normalizeCommonBlockThemeId(commonId),
      blockMaterialTextureId: normalizeBlockMaterialTextureId(materialId),
      blockAnimationId: normalizeBlockAnimationId(animationId),
    };

    try {
      const response = await axios.patch("/auth/profile/skins", payload);
      return response?.data || null;
    } catch (error) {
      console.log(error);
      return null;
    }
  }, []);
  const normalizedRoomUsers = useMemo(
    () =>
      uniqueList(
        (Array.isArray(roomUsers) ? roomUsers : [])
          .map((entry) => normalizeParticipantIdentity(entry))
          .filter(Boolean)
      ),
    [roomUsers]
  );
  const reportClientFailureAsTimeout = useCallback(
    (reason, detail = "") => {
      if (hasGameEndedRef.current) return false;
      if (isSpectatorRequested) return false;

      const activeTournamentId = String(id || "").trim();
      const activeRoomId = String(roomIdRef.current || roomId || "").trim();
      if (!activeTournamentId || !activeRoomId) return false;
      if (clientFailureReportedRef.current) return false;

      const socket = getSharedSocket();
      if (!socket) return false;

      clientFailureReportedRef.current = true;
      socket.emit("CLIENT_FAILURE", {
        tournamentId: activeTournamentId,
        roomId: activeRoomId,
        reason: String(reason || "client_error")
          .trim()
          .toLowerCase()
          .slice(0, 64),
        detail: String(detail || "")
          .trim()
          .slice(0, 280),
        at: Date.now(),
      });
      return true;
    },
    [id, isSpectatorRequested, roomId]
  );
  const normalizedTournamentUsers = useMemo(
    () =>
      uniqueList(
        (Array.isArray(tourInfo.users) ? tourInfo.users : [])
          .map((entry) => normalizeParticipantIdentity(entry))
          .filter(Boolean)
      ),
    [tourInfo.users]
  );
  const normalizedLocalPlayers = useMemo(
    () =>
      uniqueList(
        (Array.isArray(tourInfo.localPlayers) ? tourInfo.localPlayers : [])
          .map((entry) => normalizeParticipantIdentity(entry))
          .filter(Boolean)
      ),
    [tourInfo.localPlayers]
  );
  const orderedParticipantUsers = useMemo(() => {
    const localFallbackUsers = uniqueList([
      currentUserId || currentUserName,
      ...normalizedLocalPlayers,
    ]).map((entry) => normalizeParticipantIdentity(entry));
    const onlineFallbackUsers = Array.from(
      { length: Math.max(2, Math.min(4, rightPanelTotalPlayers || 2)) },
      (_, index) => {
        if (index === 0 && currentUserId) return currentUserId;
        return `Player ${index + 1}`;
      }
    );
    const sourceUsersRaw = isOnlineMode
      ? normalizedRoomUsers.length
        ? normalizedRoomUsers
        : onlineFallbackUsers
      : normalizedRoomUsers.length
      ? normalizedRoomUsers
      : normalizedTournamentUsers.length
      ? normalizedTournamentUsers
      : localFallbackUsers.length
      ? localFallbackUsers
      : currentUserId
      ? [currentUserId]
      : [];

    return sourceUsersRaw
      .map((entry) => normalizeParticipantIdentity(entry))
      .filter(Boolean);
  }, [
    currentUserId,
    currentUserName,
    isOnlineMode,
    normalizedLocalPlayers,
    normalizedRoomUsers,
    normalizedTournamentUsers,
    rightPanelTotalPlayers,
  ]);
  const activeParticipantUsers = useMemo(() => {
    const localFallbackUsers = uniqueList([
      currentUserId || currentUserName,
      ...normalizedLocalPlayers,
    ])
      .map((entry) => normalizeParticipantIdentity(entry))
      .filter(Boolean);

    const onlineUsers = normalizedRoomUsers.length
      ? normalizedRoomUsers
      : normalizedTournamentUsers.length
      ? normalizedTournamentUsers
      : currentUserId
      ? [currentUserId]
      : [];

    const localUsers = normalizedRoomUsers.length
      ? normalizedRoomUsers
      : normalizedTournamentUsers.length
      ? normalizedTournamentUsers
      : localFallbackUsers;

    const sourceUsers = isOnlineMode ? onlineUsers : localUsers;

    return uniqueList(sourceUsers)
      .map((entry) => normalizeParticipantIdentity(entry))
      .filter(Boolean)
      .slice(0, 4);
  }, [
    currentUserId,
    currentUserName,
    isOnlineMode,
    normalizedLocalPlayers,
    normalizedRoomUsers,
    normalizedTournamentUsers,
  ]);
  const localTurnPlayerCount = useMemo(() => {
    const fromOrderedPlayers = orderedParticipantUsers.length;
    const fromTournamentUsers = Array.isArray(tourInfo.users) ? tourInfo.users.length : 0;
    const fromMembers = Number(tourInfo.members) || 0;
    const resolved = fromOrderedPlayers || fromTournamentUsers || fromMembers || 2;
    return Math.max(2, Math.min(4, resolved));
  }, [orderedParticipantUsers.length, tourInfo.members, tourInfo.users]);
  const puzzleLocalPlayerIndex = useMemo(() => {
    const normalizedCurrentUserId = normalizeProfileId(
      currentUserId || userEmail || user?.email || ""
    );
    if (!normalizedCurrentUserId) return -1;

    for (let index = 0; index < orderedParticipantUsers.length; index += 1) {
      if (
        normalizeProfileId(orderedParticipantUsers[index]) === normalizedCurrentUserId
      ) {
        return index;
      }
    }

    return -1;
  }, [currentUserId, orderedParticipantUsers, user?.email, userEmail]);

  useEffect(() => {
    if (!isPuzzleMode || isSpectatorRequested) return;
    if (puzzleSolverPlayerIndexRef.current != null) return;
    puzzleSolverPlayerIndexRef.current = resolvePuzzleHumanPlayerIndex();
  }, [isPuzzleMode, isSpectatorRequested]);

  const serverAuthoritativeRemainingSeconds = useMemo(() => {
    if (gameEnded || hasGameEndedRef.current) return NaN;
    if (!USE_SERVER_AUTHORITATIVE_TURN_CLOCK || !isOnlineMode) return NaN;
    if (matchPhase !== "playing") return NaN;
    const startedAtMs = Number(serverTurnClock.turnStartedAtMs);
    const turnDurationMs = Number(serverTurnClock.turnDurationMs);
    if (!Number.isFinite(startedAtMs) || startedAtMs <= 0) return NaN;
    if (!Number.isFinite(turnDurationMs) || turnDurationMs <= 0) return NaN;
    const normalizedOffset = Number.isFinite(serverClockOffsetMs) ? serverClockOffsetMs : 0;
    const estimatedServerNowMs = serverNowMs - normalizedOffset;
    return Math.max(0, (startedAtMs + turnDurationMs - estimatedServerNowMs) / 1000);
  }, [
    gameEnded,
    isOnlineMode,
    matchPhase,
    serverClockOffsetMs,
    serverNowMs,
    serverTurnClock.turnDurationMs,
    serverTurnClock.turnStartedAtMs,
  ]);
  const playerPanels = useMemo(() => {
    const cimaxModeField = resolveCimaxModeFieldFromMode(
      tourInfo?.mode,
      tourInfo?.freeBoxBaseMode
    );
    return activeParticipantUsers.map((participantId, index) => {
      const normalizedParticipantId = normalizeProfileId(participantId);
      const profile = roomProfiles[normalizedParticipantId] || {};
      const runtimeByIdentity = playerRuntimeMap[normalizedParticipantId] || {};
      const runtimeBySlot = indexedRuntimeBySlot[index] || {};
      const forceValue =
        runtimeBySlot.force !== undefined ? runtimeBySlot.force : runtimeByIdentity.force;
      const normalizedActiveTurn = ((Number(activeTurnIndex) % 4) + 4) % 4;
      const isOutPlayer = outUsersById[normalizedParticipantId] === true;
      const isCurrentUserPanel =
        normalizeProfileId(currentUserId || userEmail || user?.email || "") === normalizedParticipantId;
      const serverSnapshotMs = Number(
        Array.isArray(serverTurnClock.remainingMsByIndex)
          ? serverTurnClock.remainingMsByIndex[index]
          : NaN
      );
      const serverSnapshotSeconds = Number.isFinite(serverSnapshotMs)
        ? Math.max(0, serverSnapshotMs / 1000)
        : NaN;
      const clockValue =
        isOutPlayer
          ? 0
          : USE_SERVER_AUTHORITATIVE_TURN_CLOCK && isOnlineMode
          ? index === normalizedActiveTurn && Number.isFinite(serverAuthoritativeRemainingSeconds)
            ? serverAuthoritativeRemainingSeconds
            : serverSnapshotSeconds
          : NaN;

      return {
        id: normalizedParticipantId || `panel-${index}`,
        slotIndex: index,
        isActiveTurn: !isOutPlayer && index === normalizedActiveTurn,
        isOut: isOutPlayer,
        displayName:
          String(profile.displayName || "").trim() || formatChatUser(participantId) || `Player ${index + 1}`,
        avatarUrl: String(profile.avatarUrl || "").trim(),
        cimaxLabel: formatCimaxScoreValue(
          profile?.cimaxScores?.[cimaxModeField] ??
            (isCurrentUserPanel ? currentUserCimaxScores?.[cimaxModeField] : 0)
        ),
        scoreLabel: Number.isFinite(Number(forceValue)) ? Number(forceValue).toFixed(2) : "0.00",
        clockLabel: formatClockLabel(clockValue),
        colors: PLAYER_PANEL_COLORS[index] || PLAYER_PANEL_COLORS[0],
      };
    });
  }, [
    activeParticipantUsers,
    activeTurnIndex,
    indexedRuntimeBySlot,
    isOnlineMode,
    outUsersById,
    playerRuntimeMap,
    roomProfiles,
    tourInfo?.freeBoxBaseMode,
    tourInfo?.mode,
    currentUserCimaxScores,
    currentUserId,
    serverTurnClock.remainingMsByIndex,
    serverAuthoritativeRemainingSeconds,
    user?.email,
    userEmail,
  ]);
  const voiceChatPlayers = useMemo(() => {
    const sourceUsers =
      Array.isArray(roomUsers) && roomUsers.length > 0 ? roomUsers : activeParticipantUsers;

    return Array.from({ length: 4 }, (_, index) => {
      const participantId = String(sourceUsers[index] || "").trim();
      const normalizedParticipantId = normalizeProfileId(participantId);
      const profile = roomProfiles[normalizedParticipantId] || {};
      const displayName =
        String(profile.displayName || "").trim() ||
        formatChatUser(participantId) ||
        `Jugador ${index + 1}`;
      const isBotParticipant =
        isBotLikeName(displayName) || isBotLikeName(participantId);
      const isOutPlayer =
        Boolean(normalizedParticipantId) && outUsersById[normalizedParticipantId] === true;

      return {
        slotIndex: index,
        userId: participantId,
        displayName,
        active:
          Boolean(participantId) &&
          index < rightPanelTotalPlayers &&
          !isOutPlayer &&
          !isBotParticipant,
      };
    });
  }, [
    activeParticipantUsers,
    outUsersById,
    rightPanelTotalPlayers,
    roomProfiles,
    roomUsers,
  ]);
  const shouldShowPlayerVoiceChat =
    isOnlineMode && isMatchInProgress && !gameEnded && !isSpectatorRequested;
  const rightPanelCimaxRows = useMemo(() => {
    const modeField = resolveCimaxModeFieldFromMode(tourInfo?.mode, tourInfo?.freeBoxBaseMode) || "ciencia";
    return activeParticipantUsers.slice(0, 4).map((participantId, index) => {
      const normalizedParticipantId = normalizeProfileId(participantId);
      const profile = roomProfiles[normalizedParticipantId] || {};
      const displayName =
        String(profile.displayName || "").trim() || formatChatUser(participantId) || `Player ${index + 1}`;
      const isBotRow = isBotLikeName(displayName) || isBotLikeName(participantId);
      const profileModeValue = Number(profile?.cimaxScores?.[modeField]);
      const isCurrentUserRow =
        normalizeProfileId(currentUserId || userEmail || user?.email || "") === normalizedParticipantId;
      const currentUserModeValue = Number(currentUserCimaxScores?.[modeField]);
      const initialFromState = toRoundedCimaxValue(cimaxInitialByUserId[normalizedParticipantId], NaN);
      const finalFromState = toRoundedCimaxValue(cimaxFinalByUserId[normalizedParticipantId], NaN);
      const delta = Math.round(Number(cimaxDeltaByUserId[normalizedParticipantId]) || 0);
      const initialFromFinal = Number.isFinite(finalFromState)
        ? Math.max(0, finalFromState - delta)
        : NaN;
      const initialNumeric = Number.isFinite(initialFromState)
        ? Math.max(0, initialFromState)
        : Number.isFinite(initialFromFinal)
        ? Math.max(0, initialFromFinal)
        : Number.isFinite(profileModeValue)
        ? Math.max(0, Math.round(profileModeValue))
        : isCurrentUserRow && Number.isFinite(currentUserModeValue)
        ? Math.max(0, Math.round(currentUserModeValue))
        : NaN;
      const shouldUseFinalFromState =
        Number.isFinite(finalFromState) &&
        !(finalFromState === 0 && delta !== 0 && Number.isFinite(initialNumeric));
      const finalNumeric = shouldUseFinalFromState
        ? Math.max(0, finalFromState)
        : Number.isFinite(initialNumeric)
        ? Math.max(0, initialNumeric + delta)
        : NaN;
      const initialLabel = isBotRow
        ? "N/A"
        : Number.isFinite(initialNumeric)
        ? String(initialNumeric)
        : "-";
      const gpLabel = isBotRow ? "Sin CIMAX" : delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : "0";
      const finalLabel = isBotRow
        ? "N/A"
        : Number.isFinite(finalNumeric)
        ? String(finalNumeric)
        : "-";
      const formulaLabel =
        !isBotRow && Number.isFinite(initialNumeric) && Number.isFinite(finalNumeric)
          ? `${initialNumeric} ${delta >= 0 ? "+" : "-"} ${Math.abs(delta)} = ${finalNumeric}`
          : "";
      return {
        id: normalizedParticipantId || `cimax-${index}`,
        displayName,
        initialLabel,
        gpLabel,
        finalLabel,
        formulaLabel,
        isBotRow,
      };
    });
  }, [
    activeParticipantUsers,
    cimaxDeltaByUserId,
    cimaxFinalByUserId,
    cimaxInitialByUserId,
    currentUserId,
    currentUserCimaxScores,
    roomProfiles,
    tourInfo?.freeBoxBaseMode,
    tourInfo?.mode,
    user?.email,
    userEmail,
  ]);
  useEffect(() => {
    if (!gameEnded || isPuzzleMode) return;

    const modeField = resolveCimaxModeFieldFromMode(tourInfo?.mode, tourInfo?.freeBoxBaseMode) || "ciencia";
    const normalizedCurrentUserId = normalizeProfileId(
      currentUserId || userEmail || user?.email || ""
    );
    const fallbackModeBaseScore = Math.max(
      0,
      Number(DEFAULT_CIMAX_SCORES?.[modeField] || DEFAULT_CIMAX_SCORES.ciencia || 0)
    );
    const participantIds = uniqueList(activeParticipantUsers)
      .map((entry) => normalizeProfileId(normalizeParticipantIdentity(entry)))
      .filter(Boolean)
      .slice(0, 4);
    if (participantIds.length === 0) return;

    const prevInitial =
      cimaxInitialByUserIdRef.current && typeof cimaxInitialByUserIdRef.current === "object"
        ? cimaxInitialByUserIdRef.current
        : {};
    const prevFinal =
      cimaxFinalByUserIdRef.current && typeof cimaxFinalByUserIdRef.current === "object"
        ? cimaxFinalByUserIdRef.current
        : {};
    const prevDelta =
      cimaxDeltaByUserIdRef.current && typeof cimaxDeltaByUserIdRef.current === "object"
        ? cimaxDeltaByUserIdRef.current
        : {};
    const nextInitial = { ...prevInitial };
    const nextFinal = { ...prevFinal };
    const nextDelta = { ...prevDelta };
    let changed = false;

    participantIds.forEach((participantId) => {
      const profileModeValue = toRoundedCimaxValue(
        roomProfilesRef.current?.[participantId]?.cimaxScores?.[modeField],
        NaN
      );
      const currentUserModeValue =
        participantId === normalizedCurrentUserId
          ? toRoundedCimaxValue(currentUserCimaxScores?.[modeField], NaN)
          : NaN;
      const prevInitialValue = toRoundedCimaxValue(prevInitial[participantId], NaN);
      const prevFinalValue = toRoundedCimaxValue(prevFinal[participantId], NaN);
      const prevDeltaValue = Math.round(Number(prevDelta[participantId]) || 0);
      const fallbackInitial = Number.isFinite(prevInitialValue)
        ? prevInitialValue
        : Number.isFinite(prevFinalValue)
        ? Math.max(0, prevFinalValue - prevDeltaValue)
        : Number.isFinite(profileModeValue)
        ? profileModeValue
        : Number.isFinite(currentUserModeValue)
        ? currentUserModeValue
        : Math.round(fallbackModeBaseScore);

      if (!Object.prototype.hasOwnProperty.call(nextDelta, participantId)) {
        nextDelta[participantId] = 0;
        changed = true;
      }
      if (!Number.isFinite(prevInitialValue)) {
        nextInitial[participantId] = fallbackInitial;
        changed = true;
      }
      if (!Number.isFinite(prevFinalValue)) {
        const resolvedDelta = Math.round(Number(nextDelta[participantId]) || 0);
        nextFinal[participantId] = Math.max(0, fallbackInitial + resolvedDelta);
        changed = true;
      }
    });

    if (!changed) return;
    setCimaxDeltaByUserId(nextDelta);
    setCimaxInitialByUserId(nextInitial);
    setCimaxFinalByUserId(nextFinal);
  }, [
    activeParticipantUsers,
    currentUserCimaxScores,
    currentUserId,
    gameEnded,
    isPuzzleMode,
    tourInfo?.freeBoxBaseMode,
    tourInfo?.mode,
    user?.email,
    userEmail,
  ]);
  const shouldShowMatchIntroOverlay = useMemo(() => {
    if (!isOnlineMode || gameEnded || !roomId) return false;
    if (matchPhase === "playing") return false;
    if (!Number.isFinite(matchIntroEndsAtMs) || matchIntroEndsAtMs <= 0) return false;
    const estimatedServerNowMs =
      (Number.isFinite(serverNowMs) ? serverNowMs : Date.now()) -
      (Number.isFinite(serverClockOffsetMs) ? serverClockOffsetMs : 0);
    return estimatedServerNowMs <= matchIntroEndsAtMs + 250;
  }, [
    gameEnded,
    isOnlineMode,
    matchIntroEndsAtMs,
    matchPhase,
    roomId,
    serverClockOffsetMs,
    serverNowMs,
  ]);
  const isMatchIntroOverlayActive =
    shouldShowMatchIntroOverlay && playerPanels.length >= 2;
  const wasMatchIntroOverlayActiveRef = useRef(false);
  const blockIntroOverlayPointerEvent = useCallback((event) => {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (event && typeof event.stopPropagation === "function") event.stopPropagation();
    console.log("[XIMA_INTRO_OVERLAY] blocking pointer event");
  }, []);
  useEffect(() => {
    if (isMatchIntroOverlayActive && !wasMatchIntroOverlayActiveRef.current) {
      console.log("[XIMA_INTRO_OVERLAY] overlay active");
    } else if (!isMatchIntroOverlayActive && wasMatchIntroOverlayActiveRef.current) {
      console.log("[XIMA_INTRO_OVERLAY] overlay inactive");
    }
    wasMatchIntroOverlayActiveRef.current = isMatchIntroOverlayActive;
  }, [isMatchIntroOverlayActive]);
  const resolveIntroAvatarUrl = useCallback((panel, panelIndex) => {
    const profileAvatar = String(panel?.avatarUrl || "").trim();
    if (profileAvatar) return profileAvatar;
    if (isBotLikeName(panel?.displayName)) {
      return BOT_INTRO_AVATAR_DATA_URIS[panelIndex % BOT_INTRO_AVATAR_DATA_URIS.length];
    }
    return "";
  }, []);
  const turnIndicatorState = useMemo(() => {
    if (!isMatchInProgress || shouldShowPostGameActions) return null;

    const runtimeTurnIndex = Number(localRuntimeTurnIndex);
    const effectiveTurnIndex =
      useLocalBotTurnIndicatorFallback && Number.isFinite(runtimeTurnIndex)
        ? runtimeTurnIndex
        : activeTurnIndex;
    const activeSlotIndex = ((Number(effectiveTurnIndex) % 4) + 4) % 4;
    const activeUserId =
      activeParticipantUsers[activeSlotIndex] || orderedParticipantUsers[activeSlotIndex] || "";
    const normalizedActiveUserId = normalizeProfileId(activeUserId);
    const activeProfile = normalizedActiveUserId ? roomProfiles[normalizedActiveUserId] || {} : {};
    const activeDisplayName =
      String(activeProfile.displayName || "").trim() ||
      formatChatUser(activeUserId) ||
      `Player ${activeSlotIndex + 1}`;
    const normalizedCurrentUserId = normalizeProfileId(
      currentUserId || userEmail || user?.email || ""
    );
    const isMyTurn =
      !isSpectatorRequested &&
      Boolean(normalizedCurrentUserId) &&
      Boolean(normalizedActiveUserId) &&
      normalizedCurrentUserId === normalizedActiveUserId;
    const puzzleDisplayName = String(tourInfo?.name || "").trim();
    const showPuzzleNameAsMyTurnLabel =
      (isPuzzleTournament || isPuzzleRequested) && Boolean(puzzleDisplayName);
    const myTurnLabel = showPuzzleNameAsMyTurnLabel ? puzzleDisplayName : "Es tu turno";
    const isBotTurnForLocalBotMatch =
      useLocalBotTurnIndicatorFallback &&
      (botTurnSlotIndexes.includes(activeSlotIndex) ||
        isBotLikeName(activeUserId) ||
        isBotLikeName(activeDisplayName));

    return {
      label: isBotTurnForLocalBotMatch
        ? "ES EL TURNO DEL BOT"
        : isMyTurn
        ? myTurnLabel
        : `Le toca jugar a: ${activeDisplayName}`,
      colorClass: TURN_INDICATOR_COLORS[activeSlotIndex] || TURN_INDICATOR_COLORS[0],
    };
  }, [
    activeParticipantUsers,
    activeTurnIndex,
    botTurnSlotIndexes,
    currentUserId,
    isMatchInProgress,
    isPuzzleRequested,
    isPuzzleTournament,
    isSpectatorRequested,
    localRuntimeTurnIndex,
    orderedParticipantUsers,
    roomProfiles,
    shouldShowPostGameActions,
    tourInfo?.name,
    useLocalBotTurnIndicatorFallback,
    user?.email,
    userEmail,
  ]);
  const disconnectedPlayerAlerts = useMemo(() => {
    if (!isOnlineMode || !isMatchInProgress || gameEnded) return [];
    if (
      !disconnectedPlayersById ||
      typeof disconnectedPlayersById !== "object" ||
      Array.isArray(disconnectedPlayersById)
    ) {
      return [];
    }

    const estimatedNowMs =
      (Number.isFinite(serverNowMs) ? serverNowMs : Date.now()) -
      (Number.isFinite(serverClockOffsetMs) ? serverClockOffsetMs : 0);

    return Object.keys(disconnectedPlayersById)
      .map((normalizedUserId) => {
        const entry = disconnectedPlayersById[normalizedUserId] || {};
        const reconnectByMs = resolveTimestampMs(entry?.reconnectByMs || entry?.reconnectBy);
        if (!Number.isFinite(reconnectByMs) || reconnectByMs <= 0) return null;

        const remainingSeconds = Math.max(0, Math.ceil((reconnectByMs - estimatedNowMs) / 1000));
        const profile = roomProfiles[normalizedUserId] || {};
        const displayName =
          String(profile.displayName || "").trim() ||
          formatChatUser(entry?.userId || normalizedUserId);

        return {
          id: normalizedUserId,
          displayName,
          remainingSeconds,
          reconnectByMs,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.reconnectByMs - b.reconnectByMs);
  }, [
    disconnectedPlayersById,
    gameEnded,
    isMatchInProgress,
    isOnlineMode,
    roomProfiles,
    serverClockOffsetMs,
    serverNowMs,
  ]);
  const syncServerTurnClockFromPayload = useCallback(
    (rawPayload) => {
      if (hasGameEndedRef.current) return;
      const payload = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
      const incomingPhase = String(payload.phase || "").trim().toLowerCase();
      if (incomingPhase) {
        setMatchPhase(incomingPhase);
      }
      if (Object.prototype.hasOwnProperty.call(payload, "matchIntroEndsAtMs")) {
        const nextIntroEndMs = Number(payload.matchIntroEndsAtMs);
        setMatchIntroEndsAtMs(Number.isFinite(nextIntroEndMs) && nextIntroEndMs > 0 ? nextIntroEndMs : 0);
      } else if (incomingPhase === "playing") {
        setMatchIntroEndsAtMs(0);
      }
      if (Object.prototype.hasOwnProperty.call(payload, "isOut")) {
        setOutUsersById(normalizeOutUsersMap(payload.isOut));
      }
      const nextTurnIndex = Number(
        payload.turn ??
          payload.nextTurnIndex ??
          payload.currentTurnIndex ??
          payload.turnIndex
      );
      const turnStartedAtMs = Number(
        payload.turnStartedAt ?? payload.turnStartedAtMs ?? payload.startedAt
      );
      const serverNowFromPayload = Number(
        payload.serverNowMs ?? payload.serverNow ?? payload.nowMs
      );
      const explicitDurationMs = Number(payload.turnDurationMs);
      const explicitRemainingByIndex = Array.isArray(payload.remainingMsByIndex)
        ? payload.remainingMsByIndex
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value >= 0)
        : [];
      const fallbackDurationMs =
        Number(payload.havingTime ?? tourInfo.havingTime) > 0
          ? Number(payload.havingTime ?? tourInfo.havingTime) * 60 * 1000
          : NaN;
      const turnDurationMs =
        Number.isFinite(explicitDurationMs) && explicitDurationMs > 0
          ? explicitDurationMs
          : fallbackDurationMs;

      if (Number.isFinite(nextTurnIndex) && nextTurnIndex >= 0) {
        setActiveTurnIndex(Math.floor(nextTurnIndex));
      }

      if (Number.isFinite(serverNowFromPayload) && serverNowFromPayload > 0) {
        const nextOffset = Date.now() - serverNowFromPayload;
        setServerClockOffsetMs((prev) => {
          if (!Number.isFinite(prev)) return nextOffset;
          return prev * 0.8 + nextOffset * 0.2;
        });
      }

      if (
        Number.isFinite(turnStartedAtMs) &&
        turnStartedAtMs > 0 &&
        Number.isFinite(turnDurationMs) &&
        turnDurationMs > 0
      ) {
        setServerTurnClock((prev) => ({
          turnIndex:
            Number.isFinite(nextTurnIndex) && nextTurnIndex >= 0
              ? Math.floor(nextTurnIndex)
              : prev.turnIndex,
          turnStartedAtMs: Math.floor(turnStartedAtMs),
          turnDurationMs: Math.floor(turnDurationMs),
          remainingMsByIndex:
            explicitRemainingByIndex.length > 0
              ? explicitRemainingByIndex.map((value) => Math.floor(value))
              : Array.isArray(prev.remainingMsByIndex)
              ? prev.remainingMsByIndex
              : [],
        }));
      } else if (incomingPhase === "match_intro" && explicitRemainingByIndex.length > 0) {
        setServerTurnClock((prev) => ({
          ...prev,
          remainingMsByIndex: explicitRemainingByIndex.map((value) => Math.floor(value)),
        }));
      }
    },
    [tourInfo.havingTime]
  );

  useEffect(() => {
    if (!roomId || !isOnlineMode || gameEnded || hasGameEndedRef.current) return undefined;
    const timer = setInterval(() => {
      setServerNowMs(Date.now());
    }, 250);
    return () => clearInterval(timer);
  }, [gameEnded, isOnlineMode, roomId]);

  useEffect(() => {
    if (!shouldShowTournamentRemaining) return;
    if (!Number.isFinite(tournamentRemainingSeconds)) return;
    if (tournamentRemainingSeconds <= 0 || tournamentRemainingSeconds > 60) return;

    const alertKey = `${String(id || "").trim()}::${String(roomId || "").trim()}::${Math.floor(
      tournamentEndsAtMs
    )}`;
    if (!alertKey || tournamentOneMinuteAlertPlayedRef.current === alertKey) return;

    tournamentOneMinuteAlertPlayedRef.current = alertKey;
    playTournamentOneMinuteAlertTone();
  }, [
    id,
    roomId,
    shouldShowTournamentRemaining,
    tournamentEndsAtMs,
    tournamentRemainingSeconds,
  ]);

  const requestTurnStateSync = useCallback(
    (reason = "poll") => {
      const socket = getSharedSocket();
      if (!socket) return;
      if (!roomId || !isOnlineMode) return;
      if (hasGameEndedRef.current || gameEnded) return;
      const activeRoomId = String(roomIdRef.current || roomId || "").trim();
      if (!activeRoomId) return;
      socket.emit("TURN_STATE_SYNC_REQUEST", {
        tournamentId: id,
        roomId: activeRoomId,
        reason,
        lastMoveSequence: Math.max(0, Number(lastMoveSequenceRef.current) || 0),
        replayWindow: 500,
      });
    },
    [gameEnded, id, isOnlineMode, roomId]
  );

  useEffect(() => {
    if (!roomId || !isOnlineMode || gameEnded || hasGameEndedRef.current) return undefined;

    requestTurnStateSync("mounted");
    const timer = setInterval(() => {
      requestTurnStateSync("interval");
    }, 2000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestTurnStateSync("visible");
      }
    };
    const handleFocus = () => requestTurnStateSync("focus");

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [gameEnded, id, isOnlineMode, requestTurnStateSync, roomId]);

  const unityIframeSrc = useMemo(() => {
    const base = `${UNITY_IFRAME_BASE_PATH}?v=${encodeURIComponent(
      UNITY_DEPLOY_VERSION
    )}&session=${unitySessionNonce}`;
    const withPassthrough = unityIframePassthroughQuery
      ? `${base}&${unityIframePassthroughQuery}`
      : base;
    if (isSpectatorRequested) return withPassthrough;
    return `${withPassthrough}&sb=${encodeURIComponent(specialBlockId)}`;
  }, [isSpectatorRequested, specialBlockId, unityIframePassthroughQuery, unitySessionNonce]);

  useEffect(() => {
    unityIframeLoadedRef.current = unityIframeLoaded;
  }, [unityIframeLoaded]);

  useEffect(() => {
    roomProfilesRef.current =
      roomProfiles && typeof roomProfiles === "object" ? roomProfiles : {};
  }, [roomProfiles]);

  useEffect(() => {
    cimaxDeltaByUserIdRef.current =
      cimaxDeltaByUserId && typeof cimaxDeltaByUserId === "object" ? cimaxDeltaByUserId : {};
  }, [cimaxDeltaByUserId]);

  useEffect(() => {
    cimaxInitialByUserIdRef.current =
      cimaxInitialByUserId && typeof cimaxInitialByUserId === "object"
        ? cimaxInitialByUserId
        : {};
  }, [cimaxInitialByUserId]);

  useEffect(() => {
    cimaxFinalByUserIdRef.current =
      cimaxFinalByUserId && typeof cimaxFinalByUserId === "object" ? cimaxFinalByUserId : {};
  }, [cimaxFinalByUserId]);

  useEffect(() => {
    setUnityIframeLoaded(false);
    unityIframeLoadedRef.current = false;
  }, [unityIframeSrc]);

  const waitForUnityIframeReady = useCallback(async (timeoutMs = 14000) => {
    const hasUsableUnityInstance = () => {
      const iframeElement = unityIframeRef.current || null;
      const iframeWindow = iframeElement?.contentWindow || null;
      const candidates = [
        iframeWindow?.unityInstance,
        iframeWindow?.window?.unityInstance,
        iframeWindow?.gameInstance,
      ];
      return candidates.some(
        (candidate) => candidate && typeof candidate.SendMessage === "function"
      );
    };

    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (unityIframeLoadedRef.current && hasUsableUnityInstance()) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 120));
    }
    return false;
  }, []);

  const waitForUnityMatchStateApplied = useCallback(async (requestId, options = {}) => {
    const normalizedRequestId = String(requestId || "").trim();
    if (!normalizedRequestId) {
      throw new Error("No hay identificador para confirmar la carga de Unity.");
    }

    const timeoutMs = Math.max(
      3000,
      Number.isFinite(Number(options?.timeoutMs))
        ? Math.floor(Number(options.timeoutMs))
        : 30000
    );
    const expectedTournamentId = String(options?.tournamentId || "").trim();
    const expectedRoomId = String(options?.roomId || "").trim();
    const startedAt = Date.now();
    const handleMatchStateAppliedEvent = (rawEvent) => {
      const envelope = resolveMetricsEnvelope(rawEvent?.detail ?? rawEvent?.data ?? rawEvent ?? null);
      const rootPayload = envelope.root || {};
      const payload = envelope.payload || rootPayload;
      if (!payload || typeof payload !== "object") return;
      const eventType = String(
        payload.type || payload.event || rootPayload.type || rootPayload.event || ""
      )
        .trim()
        .toUpperCase();
      if (eventType !== MATCH_STATE_APPLIED_EVENT) return;
      const payloadRequestId = String(payload.requestId || rootPayload.requestId || "").trim();
      if (payloadRequestId !== normalizedRequestId) return;
      pendingUnityMatchLoadRef.current[normalizedRequestId] = payload;
    };
    const bindMatchStateAppliedListener = (target) => {
      if (!target || typeof target.addEventListener !== "function") return () => {};
      target.addEventListener("message", handleMatchStateAppliedEvent);
      target.addEventListener(MATCH_STATE_APPLIED_EVENT, handleMatchStateAppliedEvent);
      return () => {
        if (!target || typeof target.removeEventListener !== "function") return;
        target.removeEventListener("message", handleMatchStateAppliedEvent);
        target.removeEventListener(MATCH_STATE_APPLIED_EVENT, handleMatchStateAppliedEvent);
      };
    };
    const cleanupWindow = bindMatchStateAppliedListener(window);
    const cleanupIframe = bindMatchStateAppliedListener(unityIframeRef.current?.contentWindow || null);

    try {
      while (Date.now() - startedAt < timeoutMs) {
        const payload = pendingUnityMatchLoadRef.current[normalizedRequestId];
        if (payload === "__ERROR__") break;
        if (payload && typeof payload === "object") {
          const payloadTournamentId = String(payload.tournamentId || "").trim();
          const payloadRoomId = String(payload.roomId || "").trim();
          const matchesTournament =
            !expectedTournamentId || !payloadTournamentId || payloadTournamentId === expectedTournamentId;
          const matchesRoom = !expectedRoomId || !payloadRoomId || payloadRoomId === expectedRoomId;

          if (!matchesTournament || !matchesRoom) {
            delete pendingUnityMatchLoadRef.current[normalizedRequestId];
            throw new Error("Unity confirmo una sala distinta a la solicitada.");
          }

          delete pendingUnityMatchLoadRef.current[normalizedRequestId];
          if (payload.success === false) {
            throw new Error(
              String(payload.message || "Unity rechazo la carga del estado de la partida.")
            );
          }
          return payload;
        }
        await new Promise((resolve) => setTimeout(resolve, 80));
      }
    } finally {
      cleanupWindow();
      cleanupIframe();
    }

    delete pendingUnityMatchLoadRef.current[normalizedRequestId];
    throw new Error("Unity no confirmo que el tablero y los bloques esten listos a tiempo.");
  }, []);

  const focusUnitySurface = useCallback(() => {
    try {
      const iframeElement = unityIframeRef.current || null;
      const iframeWindow = iframeElement?.contentWindow || null;
      if (iframeElement && typeof iframeElement.focus === "function") iframeElement.focus();
      if (iframeWindow && typeof iframeWindow.focus === "function") iframeWindow.focus();
      const canvas = iframeWindow?.document?.getElementById("unity-canvas");
      if (canvas && typeof canvas.focus === "function") canvas.focus();
    } catch (error) {
      console.log(error);
    }
  }, []);

  const canProcessVoiceCommand = useCallback((command = null) => {
    const state = voiceRuntimeStateRef.current || {};
    if (state.isGuidedTutorial === true) return false;

    const activeElement =
      typeof document !== "undefined" ? document.activeElement || null : null;
    const commandType = String(command?.type || "").toLowerCase();
    const isCameraCommand = commandType === "camera" || commandType === "rotation";
    const isBoardMoveCommand = commandType === "board_move";
    const isDiceCommand = commandType === "dice";
    const hasUnityTarget =
      state.unityIframeLoaded === true ||
      Boolean(unityIframeRef.current?.contentWindow);

    if (isCameraCommand) {
      return (
        state.voiceControlEnabled === true &&
        state.gameEnded !== true &&
        state.hasGameEnded !== true &&
        state.expired !== true &&
        state.isSpectatorRequested !== true &&
        hasUnityTarget &&
        !isTextEntryElement(activeElement)
      );
    }

    if (isBoardMoveCommand || isDiceCommand) {
      return true;
    }

    const hasRequiredMatchContext =
      isCameraCommand || isBoardMoveCommand || isDiceCommand || state.hasActiveRoom === true;
    const phaseAllowsCommand =
      isCameraCommand || isBoardMoveCommand || isDiceCommand || state.matchPhase === "playing";
    const unityAllowsCommand = isBoardMoveCommand || isDiceCommand ? hasUnityTarget : state.unityIframeLoaded === true;

    return (
      state.voiceControlEnabled === true &&
      hasRequiredMatchContext &&
      state.gameEnded !== true &&
      state.hasGameEnded !== true &&
      state.open !== true &&
      state.chatModalOpen !== true &&
      state.puzzleNameModalOpen !== true &&
      state.loadingPuzzleSnapshot !== true &&
      state.matchmaking !== true &&
      state.loading !== true &&
      state.expired !== true &&
      unityAllowsCommand &&
      phaseAllowsCommand &&
      state.isMatchIntroOverlayActive !== true &&
      state.isSpectatorRequested !== true &&
      !isTextEntryElement(activeElement)
    );
  }, []);

  const handleVoiceTranscript = useCallback((detail = {}) => {
    const heardText = detail.normalizedTranscript || detail.transcript || "";
    const commandName = detail.command?.label || detail.command?.id || "";
    if (detail.stage === "interim_unmatched") {
      setVoiceControlDebugText(
        heardText ? `Chrome esta oyendo: "${heardText}".` : ""
      );
      return;
    }
    if (detail.stage === "unmatched") {
      setVoiceControlDebugText(
        heardText ? `Escuche: "${heardText}". No encontre un comando XIMA.` : ""
      );
      return;
    }
    if (detail.stage === "blocked") {
      setVoiceControlDebugText(
        commandName ? `Reconocido: ${commandName}. Bloqueado por el estado del juego.` : ""
      );
      return;
    }
    if (detail.stage === "matched") {
      setVoiceControlDebugText(
        commandName ? `Reconocido: ${commandName}. Enviando a Unity...` : ""
      );
      return;
    }
    if (detail.stage === "matched_interim") {
      setVoiceControlDebugText(
        commandName ? `Reconocido rapido: ${commandName}. Enviando a Unity...` : ""
      );
    }
  }, []);

  const handleVoiceEngineEvent = useCallback((detail = {}) => {
    const stage = String(detail.stage || "").trim().toLowerCase();
    const heardText = detail.normalizedTranscript || detail.transcript || "";
    if (stage === "start_requested") {
      setVoiceControlDebugText("Chrome recibio la orden de escuchar.");
      return;
    }
    if (stage === "permission_granted") {
      setVoiceControlDebugText("Permiso recibido. Iniciando escucha de Chrome...");
      return;
    }
    if (stage === "recognition_started") {
      setVoiceControlDebugText("Microfono escuchando. Di: rotar derecha.");
      return;
    }
    if (stage === "speech_started") {
      setVoiceControlDebugText("Detecte voz, esperando texto de Chrome...");
      return;
    }
    if (stage === "forcing_result") {
      setVoiceControlDebugText("Cerrando escucha para que Chrome entregue el texto...");
      return;
    }
    if (stage === "result_received") {
      setVoiceControlDebugText(
        heardText
          ? `Chrome entendio: "${heardText}".`
          : "Chrome envio texto vacio. Repite el comando."
      );
      return;
    }
    if (stage === "speech_ended") {
      setVoiceControlDebugText("Voz recibida, esperando texto de Chrome...");
      return;
    }
    if (stage === "ended_without_result") {
      setVoiceControlDebugText("Chrome oyo voz pero no entrego texto. Toca Escuchar comando y repite.");
      return;
    }
    if (stage === "no_speech") {
      setVoiceControlDebugText("No detecte voz. Toca Escuchar comando y repite cerca del microfono.");
      return;
    }
    if (stage === "aborted") {
      setVoiceControlDebugText("Chrome pauso el microfono. Toca Escuchar comando otra vez.");
      return;
    }
    if (stage === "recognition_ended") {
      setVoiceControlDebugText("Escucha finalizada. Si no respondio, toca Escuchar comando.");
      return;
    }
    if (stage === "start_failed") {
      setVoiceControlDebugText("Chrome no inicio la escucha. Toca Escuchar comando dentro del juego.");
      return;
    }
    if (stage === "permission_denied") {
      setVoiceControlDebugText("Chrome rechazo el microfono.");
      return;
    }
    if (stage === "recognition_error") {
      setVoiceControlDebugText("Error del reconocimiento de voz de Chrome.");
    }
  }, []);

  const handleVoiceCommand = useCallback(
    (command, transcript) => {
      const commandName = command?.label || command?.id || "comando";
      if (!canProcessVoiceCommand(command)) {
        setVoiceControlDebugText(`${commandName}: bloqueado por el estado del juego.`);
        return false;
      }

      const delivered = dispatchVoiceCommandToUnity({
        iframeElement: unityIframeRef.current,
        command,
        transcript,
      });

      if (delivered) {
        focusUnitySurface();
      }

      setVoiceControlDebugText(
        delivered
          ? `${commandName}: enviado al iframe de Unity.`
          : `${commandName}: no se pudo enviar al iframe de Unity.`
      );

      return delivered;
    },
    [canProcessVoiceCommand, focusUnitySurface]
  );

  const ensureVoiceControl = useCallback(() => {
    if (!voiceControlRef.current) {
      voiceControlRef.current = createVoiceControlController({
        language: getVoiceRecognitionLanguage(language),
        onStatusChange: setVoiceControlStatus,
        onCommand: handleVoiceCommand,
        onTranscript: handleVoiceTranscript,
        onEngineEvent: handleVoiceEngineEvent,
        shouldProcessCommand: canProcessVoiceCommand,
      });
    } else {
      voiceControlRef.current.updateOptions({
        language: getVoiceRecognitionLanguage(language),
        onStatusChange: setVoiceControlStatus,
        onCommand: handleVoiceCommand,
        onTranscript: handleVoiceTranscript,
        onEngineEvent: handleVoiceEngineEvent,
        shouldProcessCommand: canProcessVoiceCommand,
      });
    }

    return voiceControlRef.current;
  }, [
    canProcessVoiceCommand,
    handleVoiceCommand,
    handleVoiceEngineEvent,
    handleVoiceTranscript,
    language,
  ]);

  useEffect(() => {
    const handleVoiceCommandAck = (event) => {
      if (event?.type === "message") {
        const iframeWindow = unityIframeRef.current?.contentWindow || null;
        if (iframeWindow && event?.source && event.source !== iframeWindow) return;
      }

      const payload = event?.detail ?? event?.data ?? null;
      if (!payload || typeof payload !== "object") return;

      const eventType = String(payload.type || payload.event || "")
        .trim()
        .toUpperCase();
      if (eventType !== "XIMA_VOICE_COMMAND_ACK") return;

      const commandId = String(payload.commandId || "").trim();
      const bridge = String(payload.bridge || "").trim();
      const stage = String(payload.stage || "").trim().toLowerCase();
      const bridgeSuffix = bridge ? ` (${bridge})` : "";
      if (commandId.startsWith("voice_help_")) return;

      if (stage === "received") {
        setVoiceControlDebugText(`Unity HTML recibio: ${commandId}.`);
        return;
      }
      if (stage === "sent_to_unity") {
        setVoiceControlDebugText(`Unity recibio orden: ${commandId}${bridgeSuffix}.`);
        return;
      }
      if (stage === "handled") {
        if (
          commandId.startsWith("move_cell:") ||
          commandId.startsWith("move_origin:") ||
          commandId.startsWith("board_hint_cell:") ||
          commandId.startsWith("special_cell:") ||
          commandId.startsWith("incorporate_cell:") ||
          commandId.startsWith("incorporate_special_cell:") ||
          commandId === "incorporate_common" ||
          commandId === "incorporate_special"
        ) {
          return;
        }
        setVoiceControlDebugText(`Unity proceso: ${commandId}.`);
        return;
      }
      if (stage === "move_feedback") {
        const message = String(payload.message || "").trim();
        setVoiceControlDebugText(message || `Unity proceso: ${commandId}.`);
        return;
      }
      if (stage === "send_failed") {
        setVoiceControlDebugText(`Unity no acepto: ${commandId}.`);
        return;
      }
      if (stage === "not_handled") {
        setVoiceControlDebugText(`Unity no pudo procesar: ${commandId}.`);
      }
    };

    window.addEventListener("message", handleVoiceCommandAck);
    return () => {
      window.removeEventListener("message", handleVoiceCommandAck);
    };
  }, []);

  useEffect(() => {
    if (!unityIframeLoaded) return undefined;

    const hiddenStatuses = new Set([
      VOICE_CONTROL_STATUS.DISABLED,
      VOICE_CONTROL_STATUS.PERMISSION_DENIED,
      VOICE_CONTROL_STATUS.UNAVAILABLE,
      VOICE_CONTROL_STATUS.UNSUPPORTED,
      VOICE_CONTROL_STATUS.ERROR,
    ]);
    const shouldShowVoiceHelp =
      voiceControlEnabled === true &&
      isGuidedTutorial !== true &&
      gameEnded !== true &&
      isSpectatorRequested !== true &&
      !hiddenStatuses.has(voiceControlStatus);

    const sendVoiceHelpVisibility = () => {
      dispatchVoiceCommandToUnity({
        iframeElement: unityIframeRef.current,
        command: {
          id: shouldShowVoiceHelp ? "voice_help_show" : "voice_help_hide",
          type: "voice_ui",
          label: "Ayuda de voz",
          keyData: null,
        },
        transcript: "",
      });
    };

    sendVoiceHelpVisibility();
    if (!shouldShowVoiceHelp) return undefined;

    const retryDelaysMs = [450, 1200, 2600, 5200];
    const timers = retryDelaysMs.map((delayMs) =>
      window.setTimeout(sendVoiceHelpVisibility, delayMs)
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [
    gameEnded,
    isSpectatorRequested,
    isGuidedTutorial,
    unityIframeLoaded,
    voiceControlEnabled,
    voiceControlStatus,
  ]);

  useEffect(() => {
    voiceRuntimeStateRef.current = {
      voiceControlEnabled,
      hasActiveRoom,
      gameEnded,
      hasGameEnded: hasGameEndedRef.current,
      open,
      chatModalOpen,
      puzzleNameModalOpen,
      loadingPuzzleSnapshot,
      matchmaking,
      loading,
      expired,
      unityIframeLoaded,
      matchPhase,
      isMatchIntroOverlayActive,
      isSpectatorRequested,
      isGuidedTutorial,
    };

    voiceControlRef.current?.setCommandAllowed(canProcessVoiceCommand());
  }, [
    canProcessVoiceCommand,
    chatModalOpen,
    expired,
    gameEnded,
    hasActiveRoom,
    isMatchIntroOverlayActive,
    isSpectatorRequested,
    isGuidedTutorial,
    loading,
    loadingPuzzleSnapshot,
    matchPhase,
    matchmaking,
    open,
    puzzleNameModalOpen,
    unityIframeLoaded,
    voiceControlEnabled,
  ]);

  useEffect(
    () => () => {
      voiceControlRef.current?.destroy();
      voiceControlRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (isGuidedTutorial) {
      voiceControlRef.current?.stop();
      setVoiceControlStatus(VOICE_CONTROL_STATUS.DISABLED);
      setVoiceControlDebugText("");
      return undefined;
    }

    if (!voiceControlEnabled) {
      voiceControlRef.current?.stop();
      setVoiceControlStatus(VOICE_CONTROL_STATUS.DISABLED);
      return undefined;
    }
    if (voiceControlActivating) return undefined;

    const controller = ensureVoiceControl();
    let cancelled = false;
    controller.start({ requestPermission: false }).then(() => {
      if (cancelled) controller.stop();
    });

    return () => {
      cancelled = true;
    };
  }, [ensureVoiceControl, isGuidedTutorial, voiceControlActivating, voiceControlEnabled]);

  const confirmVoiceMicrophonePermissionFromMatch = useCallback(
    () =>
      new Promise((resolve) => {
        Modal.confirm({
          title: "Activar control de voz",
          content:
            "XIMA usara el microfono para escuchar comandos de juego. No guardamos audio.",
          okText: "Activar microfono",
          cancelText: "Ahora no",
          centered: true,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    []
  );

  const activateVoiceControlFromMatch = useCallback(async () => {
    if (voiceControlActivating || isGuidedTutorial) return;

    setVoiceControlActivating(true);

    try {
      if (voiceControlEnabled || voiceControlStatus !== VOICE_CONTROL_STATUS.DISABLED) {
        writeUserPreference(userEmail, "voiceControlEnabled", true);
        setVoiceControlEnabled(true);
        setVoiceControlDebugText("Reiniciando escucha desde el boton...");

        const controller = ensureVoiceControl();
        controller.stop();
        await new Promise((resolve) => setTimeout(resolve, 120));
        const started = await controller.start({ requestPermission: true });
        if (started) {
          notification.success({
            message: "Control de voz listo",
            description: "Di el comando ahora: rotar derecha.",
          });
        } else {
          notification.warning({
            message: "Control de voz",
            description: "Chrome no inicio la escucha. Toca el boton otra vez dentro del juego.",
          });
        }
        return;
      }

      const confirmed = await confirmVoiceMicrophonePermissionFromMatch();
      if (!confirmed) {
        writeUserPreference(userEmail, "voiceControlEnabled", false);
        setVoiceControlEnabled(false);
        setVoiceControlStatus(VOICE_CONTROL_STATUS.DISABLED);
        notification.info({
          message: "Control de voz",
          description: "No se activo el microfono.",
        });
        return;
      }

      setVoiceControlStatus(VOICE_CONTROL_STATUS.REQUESTING_PERMISSION);
      writeUserPreference(userEmail, "voiceControlEnabled", true);
      setVoiceControlEnabled(true);

      const controller = ensureVoiceControl();
      const started = await controller.start({ requestPermission: true });
      const currentStatus = controller.status;

      if (started) {
        notification.success({
          message: "Control de voz activado",
          description: "El microfono quedo listo para escuchar durante la partida.",
        });
        return;
      }

      writeUserPreference(userEmail, "voiceControlEnabled", false);
      setVoiceControlEnabled(false);
      if (currentStatus === VOICE_CONTROL_STATUS.PERMISSION_DENIED) {
        notification.warning({
          message: "Permiso del microfono rechazado",
          description: "El microfono no se activo porque el navegador rechazo el permiso.",
        });
      } else if (
        currentStatus === VOICE_CONTROL_STATUS.UNSUPPORTED ||
        currentStatus === VOICE_CONTROL_STATUS.UNAVAILABLE
      ) {
        notification.error({
          message: "Control de voz no disponible",
          description: "Este navegador no permite iniciar el reconocimiento de voz en este momento.",
        });
      }
    } catch (error) {
      console.log(error);
      setVoiceControlStatus(VOICE_CONTROL_STATUS.ERROR);
      notification.error({
        message: "No se pudo activar el control de voz",
        description: error?.message || "Intentalo de nuevo desde el boton del panel derecho.",
      });
    } finally {
      setVoiceControlActivating(false);
    }
  }, [
    confirmVoiceMicrophonePermissionFromMatch,
    ensureVoiceControl,
    isGuidedTutorial,
    userEmail,
    voiceControlActivating,
    voiceControlEnabled,
    voiceControlStatus,
  ]);

  const deactivateVoiceControlFromMatch = useCallback(() => {
    voiceControlRef.current?.stop();
    writeUserPreference(userEmail, "voiceControlEnabled", false);
    setVoiceControlEnabled(false);
    setVoiceControlStatus(VOICE_CONTROL_STATUS.DISABLED);
    setVoiceControlDebugText("Control de voz desactivado desde el panel.");
    notification.info({
      message: "Control de voz desactivado",
      description: "Puedes volver a activarlo desde este mismo boton o desde Ajustes.",
    });
  }, [userEmail]);

  const toggleVoiceControlFromMatch = useCallback(async () => {
    if (voiceControlActivating) return;

    const canDeactivate = [
      VOICE_CONTROL_STATUS.ACTIVE,
      VOICE_CONTROL_STATUS.LISTENING,
      VOICE_CONTROL_STATUS.PROCESSING,
    ].includes(voiceControlStatus);

    if (voiceControlEnabled && canDeactivate) {
      deactivateVoiceControlFromMatch();
      return;
    }

    await activateVoiceControlFromMatch();
  }, [
    activateVoiceControlFromMatch,
    deactivateVoiceControlFromMatch,
    voiceControlActivating,
    voiceControlEnabled,
    voiceControlStatus,
  ]);

  const waitForPuzzleMoveLogStability = useCallback(
    async (stableMs = 500, timeoutMs = 4000) => {
      const startedAt = Date.now();
      let lastSignature = "";
      let lastChangeAt = Date.now();

      const readSignature = () => {
        const entries = Array.isArray(puzzleMoveLogRef.current) ? puzzleMoveLogRef.current : [];
        const tail = String(entries[entries.length - 1] || "");
        return `${entries.length}::${tail}`;
      };

      while (Date.now() - startedAt < timeoutMs) {
        const signature = readSignature();
        if (signature !== lastSignature) {
          lastSignature = signature;
          lastChangeAt = Date.now();
        } else if (Date.now() - lastChangeAt >= stableMs) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 120));
      }
    },
    []
  );

  const resolveUnityInstances = useCallback(() => {
    const iframeElement = unityIframeRef.current || null;
    const iframeWindow = iframeElement?.contentWindow || null;
    const globalWindow = typeof window !== "undefined" ? window : null;

    return [
      iframeWindow?.unityInstance,
      iframeWindow?.window?.unityInstance,
      iframeWindow?.gameInstance,
      globalWindow?.unityInstance,
      globalWindow?.gameInstance,
    ].filter((candidate, index, arr) => {
      if (!candidate || typeof candidate.SendMessage !== "function") return false;
      return arr.indexOf(candidate) === index;
    });
  }, []);

  const requestImmediateUnityTurnStateSync = useCallback(
    (rawReason = "frontend_manual_refresh") => {
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) return false;

      const reason = String(rawReason || "frontend_manual_refresh").trim();
      const payloadReason = reason || "frontend_manual_refresh";
      let delivered = false;
      unityInstances.forEach((instance) => {
        try {
          instance.SendMessage(
            "OnlineMultiplayerBridge",
            "RequestImmediateTurnStateSync",
            payloadReason
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });

      return delivered;
    },
    [resolveUnityInstances]
  );

  const postBlockAnimationSpeedToUnityIframe = useCallback((rawValue) => {
    const iframeWindow = unityIframeRef.current?.contentWindow || null;
    if (!iframeWindow || typeof iframeWindow.postMessage !== "function") return false;

    const speed = normalizeBlockAnimationSpeed(rawValue);
    const targetOrigin =
      typeof window !== "undefined" && window.location?.origin ? window.location.origin : "*";

    try {
      iframeWindow.postMessage(
        {
          type: "XIMA_BLOCK_ANIMATION_SPEED",
          speed,
          blockAnimationSpeed: speed,
        },
        targetOrigin
      );
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, []);

  const syncBlockAnimationSpeedToUnity = useCallback(
    (rawValue) => {
      const speed = normalizeBlockAnimationSpeed(rawValue);
      writeGlobalBlockAnimationSpeed(speed);
      let delivered = postBlockAnimationSpeedToUnityIframe(speed);
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) return delivered;

      const payload = speed.toString();
      unityInstances.forEach((instance) => {
        try {
          instance.SendMessage(
            "OnlineMultiplayerBridge",
            "SetBlockMoveAnimationSpeed",
            payload
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });

      return delivered;
    },
    [postBlockAnimationSpeedToUnityIframe, resolveUnityInstances]
  );

  const postCameraFollowToUnityIframe = useCallback((enabled) => {
    const iframeWindow = unityIframeRef.current?.contentWindow || null;
    if (!iframeWindow || typeof iframeWindow.postMessage !== "function") return false;

    const targetOrigin =
      typeof window !== "undefined" && window.location?.origin ? window.location.origin : "*";

    try {
      iframeWindow.postMessage(
        {
          type: "XIMA_CAMERA_FOLLOW",
          enabled: enabled === true,
          cameraFollowEnabled: enabled === true,
        },
        targetOrigin
      );
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, []);

  const syncCameraFollowToUnity = useCallback(
    (enabled) => {
      let delivered = postCameraFollowToUnityIframe(enabled);
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) return delivered;

      const payload = enabled ? "1" : "0";
      unityInstances.forEach((instance) => {
        ["OnlineMultiplayerBridge", "Virtual Camera"].forEach((targetObjectName) => {
          try {
            instance.SendMessage(targetObjectName, "SetCameraFollowEnabled", payload);
            delivered = true;
          } catch (error) {
            console.log(error);
          }
        });
      });

      return delivered;
    },
    [postCameraFollowToUnityIframe, resolveUnityInstances]
  );

  // Flechas del tablero en la partida (mismo sistema que el replay).
  const postBoardArrowEventsToUnity = useCallback((events) => {
    const iframeWindow = unityIframeRef.current?.contentWindow || null;
    if (!iframeWindow || typeof iframeWindow.postMessage !== "function") return;
    const targetOrigin =
      typeof window !== "undefined" && window.location?.origin ? window.location.origin : "*";
    try {
      iframeWindow.postMessage({ type: "XIMA_REPLAY_EVENTS", events }, targetOrigin);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const toggleBoardArrows = useCallback(() => {
    setBoardArrowsActive((prev) => !prev);
    focusUnitySurface();
  }, [focusUnitySurface]);

  const clearBoardArrows = useCallback(() => {
    postBoardArrowEventsToUnity([{ eventName: "REPLAY_ARROWS_CLEAR", payload: "" }]);
    focusUnitySurface();
  }, [focusUnitySurface, postBoardArrowEventsToUnity]);

  // Unity recibe el boton y el color al cambiar y cada poco (por si el tablero se recarga).
  useEffect(() => {
    if (!unityIframeLoaded) return undefined;
    const send = () =>
      postBoardArrowEventsToUnity([
        { eventName: "REPLAY_ARROWS", payload: boardArrowsActive ? "1" : "0" },
        { eventName: "REPLAY_ARROWS_COLOR", payload: boardArrowColor },
      ]);
    send();
    const intervalId = setInterval(send, 2000);
    return () => clearInterval(intervalId);
  }, [boardArrowColor, boardArrowsActive, postBoardArrowEventsToUnity, unityIframeLoaded, unitySessionNonce]);

  // Teclas de color mantenidas con el foco fuera del tablero (Unity las detecta
  // solo cuando tiene el foco): Shift rojo, Tab verde, Ctrl azul.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const held = { shift: false, tab: false, ctrl: false };
    let lastSent = "";
    const send = () => {
      const color = held.shift ? "red" : held.tab ? "green" : held.ctrl ? "blue" : "";
      if (color === lastSent) return;
      lastSent = color;
      postBoardArrowEventsToUnity([{ eventName: "REPLAY_ARROWS_KEY", payload: color }]);
    };
    const isTypingTarget = (target) => {
      const tagName = String(target?.tagName || "").toLowerCase();
      return tagName === "input" || tagName === "textarea" || Boolean(target?.isContentEditable);
    };
    const update = (event, down) => {
      if (isTypingTarget(event.target)) return;
      if (event.key === "Shift") held.shift = down;
      if (event.key === "Control") held.ctrl = down;
      if (event.key === "Tab") {
        held.tab = down;
        event.preventDefault();
      }
      send();
    };
    const handleKeyDown = (event) => update(event, true);
    const handleKeyUp = (event) => update(event, false);
    const handleBlur = () => {
      held.shift = false;
      held.tab = false;
      held.ctrl = false;
      send();
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      handleBlur();
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [postBoardArrowEventsToUnity]);

  const toggleCameraFollowDuringMatch = useCallback(() => {
    const nextEnabled = !cameraFollowEnabled;
    setCameraFollowEnabled(nextEnabled);
    writeCameraFollowPreference(userEmail, nextEnabled);
    syncCameraFollowToUnity(nextEnabled);
    focusUnitySurface();
  }, [cameraFollowEnabled, focusUnitySurface, syncCameraFollowToUnity, userEmail]);

  const triggerUnityReplayRefresh = useCallback(
    (rawReason = "manual_refresh") => {
      if (!hasActiveRoom || hasGameEndedRef.current || gameEnded) return;

      const reason = String(rawReason || "manual_refresh").trim() || "manual_refresh";
      const pulses = [
        { delayMs: 0, reasonLabel: reason },
        { delayMs: 350, reasonLabel: `${reason}_pulse_1` },
        { delayMs: 900, reasonLabel: `${reason}_pulse_2` },
        { delayMs: 1700, reasonLabel: `${reason}_pulse_3` },
      ];

      pulses.forEach(({ delayMs, reasonLabel }) => {
        setTimeout(() => {
          if (!String(roomIdRef.current || roomId || "").trim()) return;
          if (hasGameEndedRef.current || gameEnded) return;

          // Matches the outside-click behavior: return focus to Unity and force a sync burst.
          focusUnitySurface();
          if (isPuzzleMode) {
            return;
          }
          requestImmediateUnityTurnStateSync(reasonLabel);
          requestTurnStateSync(reasonLabel);
        }, delayMs);
      });
    },
    [
      focusUnitySurface,
      gameEnded,
      hasActiveRoom,
      isPuzzleMode,
      requestImmediateUnityTurnStateSync,
      requestTurnStateSync,
      roomId,
    ]
  );

  useEffect(() => {
    const previousConnectionState = previousConnectionStateRef.current;
    previousConnectionStateRef.current = connectionState;

    if (previousConnectionState === "connected") return;
    if (connectionState !== "connected") return;
    if (!hasActiveRoom || gameEnded || hasGameEndedRef.current) return;

    setManualRefreshUnlockedByReconnect(true);
    triggerUnityReplayRefresh("reconnect_auto_refresh");
  }, [connectionState, gameEnded, hasActiveRoom, triggerUnityReplayRefresh]);

  useEffect(() => {
    if (hasActiveRoom && !gameEnded) return;
    setManualRefreshUnlockedByReconnect(false);
  }, [gameEnded, hasActiveRoom, roomId]);

  const syncPlayerSkinsToUnity = useCallback(
    (rawUsers, rawProfiles) => {
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) return false;

      const normalizedCurrentUserId = normalizeProfileId(
        currentUserId || userEmail || user?.email || ""
      );
      const fallbackUser = normalizeParticipantIdentity(
        currentUserId || userEmail || user?.email || "Player 1"
      );
      const users = (
        Array.isArray(rawUsers) && rawUsers.length > 0 ? rawUsers : [fallbackUser]
      )
        .map((entry) => normalizeParticipantIdentity(entry))
        .filter(Boolean)
        .slice(0, 4);

      const profiles = rawProfiles && typeof rawProfiles === "object" ? rawProfiles : {};
      const shouldForceDefaultPuzzleSkins = isPuzzleMode;
      const activeCosmeticRoomId = String(roomIdRef.current || roomId || "").trim();
      const activeCosmeticTournamentId = String(
        roomSessionTournamentIdRef.current || id || ""
      ).trim();
      const payload = {
        cosmeticAuthorityId:
          hasActiveRoom && activeCosmeticRoomId
            ? `${activeCosmeticTournamentId}:${activeCosmeticRoomId}`
            : "local",
        cosmeticRevision: hasActiveRoom ? roomCosmeticRevisionRef.current : 0,
        players: users.map((rawUserId, index) => {
          const normalizedUserId = normalizeProfileId(rawUserId);
          const profile = profiles[normalizedUserId] || {};
          const isCurrentUser =
            normalizedUserId && normalizedUserId === normalizedCurrentUserId;
          const localFallbackEnabled = isCurrentUser && !hasActiveRoom;

          const specialSkin =
            shouldForceDefaultPuzzleSkins
              ? DEFAULT_SPECIAL_SKIN_NAME
              : normalizeSpecialBlockId(
                  resolveRoomCosmeticValue({
                    hasActiveRoom,
                    authoritativeValue: profile.specialBlockId,
                    localValue: localFallbackEnabled ? specialBlockId : "",
                    fallbackValue: DEFAULT_SPECIAL_SKIN_NAME,
                  })
                ) || DEFAULT_SPECIAL_SKIN_NAME;

          const commonSkin =
            shouldForceDefaultPuzzleSkins
              ? DEFAULT_COMMON_BLOCK_THEME_ID
              : normalizeCommonBlockThemeId(
                  resolveRoomCosmeticValue({
                    hasActiveRoom,
                    authoritativeValue: profile.commonBlockThemeId,
                    localValue: localFallbackEnabled ? commonBlockThemeId : "",
                    fallbackValue: DEFAULT_COMMON_BLOCK_THEME_ID,
                  })
                ) || DEFAULT_COMMON_BLOCK_THEME_ID;
          const rawBlockMaterialTexture = resolveRoomCosmeticValue({
            hasActiveRoom,
            authoritativeValue: profile.blockMaterialTextureId,
            localValue: localFallbackEnabled ? blockMaterialTextureId : "",
            fallbackValue: DEFAULT_BLOCK_MATERIAL_TEXTURE_ID,
          });
          const blockMaterialTexture = shouldForceDefaultPuzzleSkins
            ? DEFAULT_BLOCK_MATERIAL_TEXTURE_ID
            : normalizeOptionalBlockMaterialTextureId(rawBlockMaterialTexture);
          const rawBlockAnimation = resolveRoomCosmeticValue({
            hasActiveRoom,
            authoritativeValue: profile.blockAnimationId,
            localValue: localFallbackEnabled ? blockAnimationId : "",
            fallbackValue: DEFAULT_BLOCK_ANIMATION_ID,
          });
          const blockAnimation = shouldForceDefaultPuzzleSkins
            ? DEFAULT_BLOCK_ANIMATION_ID
            : normalizeOptionalBlockAnimationId(rawBlockAnimation);

          // Color de bloque de los bots de Modo Diseñar ("" = color de su posición).
          const blockColor = shouldForceDefaultPuzzleSkins
            ? ""
            : normalizeBotBlockColorId(profile.blockColorId);

          return {
            playerIndex: index,
            userId: rawUserId,
            specialSkin,
            commonSkin,
            blockMaterialTexture,
            blockAnimation,
            blockColor,
          };
        }),
      };

      let delivered = false;
      const payloadJson = JSON.stringify(payload);
      unityInstances.forEach((instance) => {
        try {
          instance.SendMessage(
            "PlayerSkinRuntimeManager",
            "SetPlayerSkinsFromFrontend",
            payloadJson
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });

      return delivered;
    },
    [
      commonBlockThemeId,
      blockMaterialTextureId,
      blockAnimationId,
      currentUserId,
      hasActiveRoom,
      id,
      isPuzzleMode,
      resolveUnityInstances,
      roomId,
      specialBlockId,
      user?.email,
      userEmail,
    ]
  );

  const syncPlayerAvatarsToUnity = useCallback(
    (rawUsers, rawProfiles) => {
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) return false;

      const normalizedCurrentUserId = normalizeProfileId(
        currentUserId || userEmail || user?.email || ""
      );
      const fallbackUser = normalizeParticipantIdentity(
        currentUserId || userEmail || user?.email || "Player 1"
      );
      const users = (
        Array.isArray(rawUsers) && rawUsers.length > 0 ? rawUsers : [fallbackUser]
      )
        .map((entry) => normalizeParticipantIdentity(entry))
        .filter(Boolean)
        .slice(0, 4);

      const profiles = rawProfiles && typeof rawProfiles === "object" ? rawProfiles : {};
      const payload = {
        players: users.map((rawUserId, index) => {
          const normalizedUserId = normalizeProfileId(rawUserId);
          const profile = profiles[normalizedUserId] || {};
          const isCurrentUser =
            normalizedUserId && normalizedUserId === normalizedCurrentUserId;
          const rawAvatarUrl = String(
            profile.avatarUrl ||
              profile.avatar ||
              profile.image ||
              (isCurrentUser ? currentUserAvatarUrl : "")
          ).trim();
          const safeAvatarUrl =
            rawAvatarUrl.startsWith("data:image/svg") || /\.svg(?:$|\?)/i.test(rawAvatarUrl)
              ? ""
              : rawAvatarUrl;
          const avatarUrl =
            safeAvatarUrl && typeof window !== "undefined"
              ? new URL(safeAvatarUrl, window.location.origin).toString()
              : safeAvatarUrl;

          return {
            playerIndex: index,
            userId: rawUserId,
            avatarUrl,
          };
        }),
      };

      let delivered = false;
      const payloadJson = JSON.stringify(payload);
      unityInstances.forEach((instance) => {
        try {
          instance.SendMessage(
            "PlayPanelLiveOverlay",
            "SetPlayerAvatarsFromFrontend",
            payloadJson
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });

      return delivered;
    },
    [
      currentUserAvatarUrl,
      currentUserId,
      resolveUnityInstances,
      user?.email,
      userEmail,
    ]
  );

  const syncPlayerCimaxToUnity = useCallback(
    (rawUsers, rawProfiles) => {
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) return false;

      const modeField = resolveCimaxModeFieldFromMode(
        normalizedMode,
        tourInfo?.freeBoxBaseMode
      );
      const modeLabel = formatCimaxModeLabel(modeField || "ciencia");
      const normalizedCurrentUserId = normalizeProfileId(
        currentUserId || userEmail || user?.email || ""
      );
      const fallbackUser = normalizeParticipantIdentity(
        currentUserId || userEmail || user?.email || "Player 1"
      );
      const users = (
        Array.isArray(rawUsers) && rawUsers.length > 0 ? rawUsers : [fallbackUser]
      )
        .map((entry) => normalizeParticipantIdentity(entry))
        .filter(Boolean)
        .slice(0, 4);

      const profiles = rawProfiles && typeof rawProfiles === "object" ? rawProfiles : {};
      const payload = {
        mode: modeField,
        modeLabel,
        players: users.map((rawUserId, index) => {
          const normalizedUserId = normalizeProfileId(rawUserId);
          const profile = profiles[normalizedUserId] || {};
          const isCurrentUser =
            normalizedUserId && normalizedUserId === normalizedCurrentUserId;
          const scores =
            profile.cimaxScores && typeof profile.cimaxScores === "object"
              ? normalizeCimaxScores(profile.cimaxScores)
              : isCurrentUser
                ? normalizeCimaxScores(currentUserCimaxScores)
                : normalizeCimaxScores({});
          const score = modeField ? scores[modeField] : 0;
          const cimaxValue = formatCimaxScoreValue(score);

          return {
            playerIndex: index,
            userId: rawUserId,
            mode: modeField,
            modeLabel,
            cimaxScore: Number(cimaxValue),
            cimaxValue,
            cimaxLabel: `CIMAX ${modeLabel}: ${cimaxValue}`,
          };
        }),
      };

      let delivered = false;
      const payloadJson = JSON.stringify(payload);
      unityInstances.forEach((instance) => {
        try {
          instance.SendMessage(
            "PlayPanelLiveOverlay",
            "SetPlayerCimaxFromFrontend",
            payloadJson
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });

      return delivered;
    },
    [
      currentUserCimaxScores,
      currentUserId,
      normalizedMode,
      resolveUnityInstances,
      tourInfo?.freeBoxBaseMode,
      user?.email,
      userEmail,
    ]
  );

  const syncPuzzleRuntimeReadyToUnity = useCallback(
    (ready, puzzleId = "") => {
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) return false;

      let delivered = false;
      const payload = JSON.stringify({
        ready: Boolean(ready),
        puzzleId: String(puzzleId || "").trim(),
        humanPlayerIndex: PUZZLE_HUMAN_PLAYER_INDEX,
      });
      const tutorialPayload = JSON.stringify({
        enabled: Boolean(ready) && isTutorialLevel1,
        puzzleId: String(puzzleId || "").trim(),
        language,
      });
      const tutorialLevel2Payload = JSON.stringify({
        enabled: Boolean(ready) && isTutorialLevel2,
        puzzleId: String(puzzleId || "").trim(),
        language,
      });
      unityInstances.forEach((instance) => {
        try {
          instance.SendMessage(
            "OnlineMultiplayerBridge",
            "SetPuzzleRuntimeReady",
            payload
          );
          instance.SendMessage(
            "OnlineMultiplayerBridge",
            "SetTutorialLevel1",
            tutorialPayload
          );
          instance.SendMessage(
            "OnlineMultiplayerBridge",
            "SetTutorialLevel2",
            tutorialLevel2Payload
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });

      return delivered;
    },
    [isTutorialLevel1, isTutorialLevel2, language, resolveUnityInstances]
  );

  const normalizeFreeBoxBaseModeForUnity = useCallback((rawMode) => {
    if (!String(rawMode || "").trim()) return "";
    return normalizeFreeBoxBaseMode(rawMode, "free");
  }, []);

  const syncPuzzleRuntimeContextToUnity = useCallback(
    ({ isPuzzle = false, freeBoxBaseMode = "", puzzleId = "", roomTournamentId = "" } = {}) => {
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) return false;

      let delivered = false;
      const payload = JSON.stringify({
        isPuzzle: Boolean(isPuzzle),
        freeBoxBaseMode: normalizeFreeBoxBaseModeForUnity(freeBoxBaseMode),
        puzzleId: String(puzzleId || "").trim(),
        roomTournamentId: String(roomTournamentId || "").trim(),
      });
      unityInstances.forEach((instance) => {
        try {
          instance.SendMessage(
            "OnlineMultiplayerBridge",
            "SetPuzzleRuntimeContext",
            payload
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });

      return delivered;
    },
    [normalizeFreeBoxBaseModeForUnity, resolveUnityInstances]
  );

  useEffect(() => {
    if (!unityIframeLoaded) return undefined;

    // En "solo bots" el puesto 0 es un bot: no se envia el perfil del admin.
    const unityRoomUsers =
      allBotsSeatName && Array.isArray(roomUsers) && roomUsers.length > 0
        ? [allBotsSeatName, ...roomUsers.slice(1)]
        : roomUsers;
    const applyPlayerSkins = () => {
      syncPlayerSkinsToUnity(unityRoomUsers, roomProfiles);
      syncPlayerAvatarsToUnity(unityRoomUsers, roomProfiles);
      syncPlayerCimaxToUnity(unityRoomUsers, roomProfiles);
    };

    applyPlayerSkins();
    const timer = setTimeout(applyPlayerSkins, 450);
    return () => clearTimeout(timer);
  }, [
    allBotsSeatName,
    roomProfiles,
    roomUsers,
    syncPlayerAvatarsToUnity,
    syncPlayerCimaxToUnity,
    syncPlayerSkinsToUnity,
    unityIframeLoaded,
  ]);

  useEffect(() => {
    if (!unityIframeLoaded) return undefined;

    const applyRuntimeSync = () => {
      syncBlockAnimationSpeedToUnity(blockAnimationSpeedPreference);
      syncCameraFollowToUnity(cameraFollowEnabled);
      if (!isPuzzleRequested || puzzleMetadataReadyForAutoplay) {
        syncPuzzleRuntimeContextToUnity({
          isPuzzle: shouldBlockPuzzleRightClickRuleBreak,
          freeBoxBaseMode:
            normalizedMode === "freebox" ? tourInfo?.freeBoxBaseMode : "",
          puzzleId: activePuzzleTournamentIdRef.current || id,
          roomTournamentId: roomSessionTournamentIdRef.current || id,
        });
      }
    };

    applyRuntimeSync();
    const timer = setTimeout(applyRuntimeSync, 450);
    return () => clearTimeout(timer);
  }, [
    blockAnimationSpeedPreference,
    cameraFollowEnabled,
    id,
    isPuzzleRequested,
    normalizedMode,
    puzzleMetadataReadyForAutoplay,
    roomId,
    syncBlockAnimationSpeedToUnity,
    syncCameraFollowToUnity,
    shouldBlockPuzzleRightClickRuleBreak,
    syncPuzzleRuntimeContextToUnity,
    tourInfo?.freeBoxBaseMode,
    unityIframeLoaded,
  ]);

  const applyPuzzleMoveLogToUnity = useCallback(
    async (rawMoveLog) => {
      const moveLog = orderMovePayloadsBySequence(rawMoveLog, MAX_PUZZLE_MOVE_LOG_SIZE);
      if (!moveLog.length) return 0;

      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) {
        throw new Error("Unity no esta listo para aplicar la posicion del puzzle.");
      }

      focusUnitySurface();
      const waitForMoveAck = async (baselineCounter, timeoutMs = 3500) => {
        const startedAt = Date.now();
        while (Date.now() - startedAt < timeoutMs) {
          if (unityMoveEventCounterRef.current > baselineCounter) return true;
          await new Promise((resolve) => setTimeout(resolve, 60));
        }
        return false;
      };

      let appliedCount = 0;
      for (let index = 0; index < moveLog.length; index += 1) {
        const payload = moveLog[index];
        const parsedPipe = parseMoveDataPipePayload(payload);
        const replayPlayerIndex = Number(parsedPipe?.playerIndex);
        const replayUserId =
          Number.isFinite(replayPlayerIndex) && replayPlayerIndex >= 0
            ? String(roomUsers[replayPlayerIndex] || "").trim()
            : "";
        const replayUserLabel = replayUserId || `slot_${Number.isFinite(replayPlayerIndex) ? replayPlayerIndex : "?"}`;
        const replayCubeLabel = String(parsedPipe?.cubeNumber || "").trim() || "?";
        const replaySequence = parseMoveSequenceFromPayload(payload);
        console.log(
          `[RECONNECT_REBUILD] replaying move user=${replayUserLabel} cube=${replayCubeLabel} seq=${replaySequence}`
        );
        const normalizedReplayUserId = normalizeProfileId(replayUserId);
        if (normalizedReplayUserId && outUsersById[normalizedReplayUserId] === true) {
          console.log(
            `[RECONNECT_REBUILD] user isOut=true but replaying previous block position user=${replayUserLabel} cube=${replayCubeLabel}`
          );
        }
        let moveApplied = false;

        for (let attempt = 0; attempt < 2 && !moveApplied; attempt += 1) {
          const baselineCounter = unityMoveEventCounterRef.current;
          let delivered = false;

          unityInstances.forEach((instance) => {
            try {
              instance.SendMessage("OnlineMultiplayerBridge", "ApplyMoveData", payload);
              delivered = true;
            } catch (error) {
              console.log(error);
            }
          });

          if (!delivered) break;

          const acked = await waitForMoveAck(baselineCounter);
          if (acked) {
            moveApplied = true;
            appliedCount += 1;
          } else {
            await new Promise((resolve) => setTimeout(resolve, 180));
          }
        }
      }

      if (appliedCount !== moveLog.length) {
        throw new Error(
          `No se pudo reconstruir toda la posicion (${appliedCount}/${moveLog.length}).`
        );
      }

      return appliedCount;
    },
    [focusUnitySurface, outUsersById, resolveUnityInstances, roomUsers]
  );

  const requestPuzzlePositionSnapshotFromUnity = useCallback(async (options = {}) => {
    const normalizedTimeoutMs = Math.max(
      700,
      Number.isFinite(Number(options?.timeoutMs))
        ? Math.max(700, Math.floor(Number(options.timeoutMs)))
        : MAX_PUZZLE_SNAPSHOT_WAIT_MS
    );
    const unityInstances = resolveUnityInstances();
    if (unityInstances.length === 0) {
      throw new Error("Unity no esta listo para capturar la posicion del puzzle.");
    }

    const requestId = `puzzle-snapshot-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    pendingPuzzleSnapshotRequestsRef.current[requestId] = null;
    focusUnitySurface();

    let delivered = false;
    unityInstances.forEach((instance) => {
      try {
        instance.SendMessage("OnlineMultiplayerBridge", "EmitPuzzlePositionSnapshot", requestId);
        delivered = true;
      } catch (error) {
        console.log(error);
      }
    });

    if (!delivered) {
      delete pendingPuzzleSnapshotRequestsRef.current[requestId];
      throw new Error("No se pudo solicitar el snapshot de posicion a Unity.");
    }

    const startedAt = Date.now();
    while (Date.now() - startedAt < normalizedTimeoutMs) {
      const snapshot = pendingPuzzleSnapshotRequestsRef.current[requestId];
      if (snapshot === "__ERROR__") {
        delete pendingPuzzleSnapshotRequestsRef.current[requestId];
        throw new Error("Unity no envio un snapshot de posicion valido.");
      }
      if (snapshot && typeof snapshot === "object") {
        delete pendingPuzzleSnapshotRequestsRef.current[requestId];
        if (snapshot.__puzzleSnapshotError || snapshot.success === false) {
          throw new Error(
            String(
              snapshot.message ||
                snapshot.reason ||
                snapshot.code ||
                "Unity aun no puede capturar la posicion del puzzle."
            )
          );
        }
        return snapshot;
      }
      await new Promise((resolve) => setTimeout(resolve, 80));
    }

    delete pendingPuzzleSnapshotRequestsRef.current[requestId];
    throw new Error("No se recibio el snapshot de posicion desde Unity a tiempo.");
  }, [focusUnitySurface, resolveUnityInstances]);

  const waitForUnityPuzzleBoardReady = useCallback(
    async (options = {}) => {
      const timeoutMs = Math.max(
        1000,
        Number.isFinite(Number(options?.timeoutMs))
          ? Math.floor(Number(options.timeoutMs))
          : MAX_PUZZLE_BOARD_READY_WAIT_MS
      );
      const startedAt = Date.now();
      let lastReadyError = null;

      while (Date.now() - startedAt < timeoutMs) {
        const remainingMs = timeoutMs - (Date.now() - startedAt);
        try {
          const iframeReady = await waitForUnityIframeReady(
            Math.max(700, Math.min(1800, remainingMs))
          );
          if (!iframeReady) {
            throw new Error("unity_instance_not_ready");
          }

          const snapshot = await requestPuzzlePositionSnapshotFromUnity({
            timeoutMs: Math.max(900, Math.min(2200, remainingMs)),
          });
          const normalizedSnapshot = normalizePuzzleSnapshotPayload(snapshot);
          if (normalizedSnapshot) {
            return normalizedSnapshot;
          }
          throw new Error("snapshot_unavailable");
        } catch (error) {
          lastReadyError = error;
        }

        await new Promise((resolve) =>
          setTimeout(resolve, PUZZLE_BOARD_READY_PROBE_INTERVAL_MS)
        );
      }

      throw new Error(
        `Unity no termino de preparar el tablero del puzzle${
          lastReadyError?.message ? ` (${lastReadyError.message})` : ""
        }.`
      );
    },
    [requestPuzzlePositionSnapshotFromUnity, waitForUnityIframeReady]
  );

  const verifyPuzzleSnapshotRenderedInUnity = useCallback(
    async (expectedActiveBlocks, timeoutMs = 5000) => {
      const normalizedExpected = Math.max(0, Number(expectedActiveBlocks) || 0);
      if (normalizedExpected <= 0) {
        return {
          success: true,
          observedActiveBlocks: 0,
          minExpectedBlocks: 0,
        };
      }

      const minExpectedBlocks = normalizedExpected;

      let bestObservedBlocks = 0;
      const startedAt = Date.now();
      const requestTimeoutMs = Math.max(700, Math.floor(Math.min(timeoutMs, 2200)));
      while (Date.now() - startedAt < timeoutMs) {
        try {
          const liveSnapshot = await requestPuzzlePositionSnapshotFromUnity({
            timeoutMs: requestTimeoutMs,
          });
          const observedBlocks = countPuzzleSnapshotActiveBlocks(
            normalizePuzzleSnapshotPayload(liveSnapshot)
          );
          bestObservedBlocks = Math.max(bestObservedBlocks, observedBlocks);
          if (observedBlocks >= minExpectedBlocks) {
            return {
              success: true,
              observedActiveBlocks: observedBlocks,
              minExpectedBlocks,
            };
          }
        } catch (error) {
          console.log(error);
        }

        await new Promise((resolve) => setTimeout(resolve, 180));
      }

      return {
        success: false,
        observedActiveBlocks: bestObservedBlocks,
        minExpectedBlocks,
      };
    },
    [requestPuzzlePositionSnapshotFromUnity]
  );

  const applyPuzzlePositionSnapshotToUnity = useCallback(
    async (rawSnapshot, options = {}) => {
      const snapshot = normalizePuzzleSnapshotPayload(rawSnapshot);
      if (!snapshot) {
        throw new Error("El puzzle no tiene una posicion valida para aplicar.");
      }

      const outsideStackOffsetRaw = Number(options?.outsideStackOffset);
      const outsideStackOffset = Number.isFinite(outsideStackOffsetRaw)
        ? Math.floor(outsideStackOffsetRaw)
        : 0;
      const snapshotForApply =
        outsideStackOffset === 0
          ? snapshot
          : {
              ...snapshot,
              players: (Array.isArray(snapshot.players) ? snapshot.players : []).map((playerEntry) => ({
                ...playerEntry,
                cubes: (Array.isArray(playerEntry?.cubes) ? playerEntry.cubes : []).map((cubeEntry) => {
                  if (!cubeEntry || typeof cubeEntry !== "object") return cubeEntry;
                  if (cubeEntry.active === false) return cubeEntry;
                  const cubeState = String(cubeEntry.state || "")
                    .trim()
                    .toLowerCase();
                  if (cubeState !== "outside") return cubeEntry;
                  const rawStack = Number(cubeEntry.stackIndex);
                  if (!Number.isFinite(rawStack)) return cubeEntry;
                  return {
                    ...cubeEntry,
                    stackIndex: Math.max(-1, Math.floor(rawStack) + outsideStackOffset),
                  };
                }),
              })),
            };

      const snapshotJson = JSON.stringify(snapshotForApply);
      const expectedActiveBlocks = countPuzzleSnapshotActiveBlocks(snapshotForApply);
      const unityInstances = resolveUnityInstances();
      if (unityInstances.length === 0) {
        throw new Error("Unity no esta listo para aplicar la posicion del puzzle.");
      }

      const requestId = `puzzle-apply-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
      pendingPuzzleSnapshotApplyRef.current[requestId] = null;
      focusUnitySurface();

      const messagePayload = JSON.stringify({
        requestId,
        snapshotJson,
        expectedActiveBlocks,
        loadKey: String(options?.loadKey || "").trim(),
        forceApply: options?.forceApply === true,
      });
      let delivered = false;
      unityInstances.forEach((instance) => {
        try {
          instance.SendMessage(
            "OnlineMultiplayerBridge",
            "ApplyPuzzlePositionSnapshot",
            messagePayload
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });

      if (!delivered) {
        delete pendingPuzzleSnapshotApplyRef.current[requestId];
        throw new Error("No se pudo enviar el snapshot a Unity.");
      }

      const startedAt = Date.now();
      while (Date.now() - startedAt < MAX_PUZZLE_SNAPSHOT_WAIT_MS) {
        const applyResult = pendingPuzzleSnapshotApplyRef.current[requestId];
        if (applyResult === "__ERROR__") break;
        if (applyResult && typeof applyResult === "object") {
          delete pendingPuzzleSnapshotApplyRef.current[requestId];
          if (applyResult.success === false) {
            throw new Error(
              String(applyResult.message || "Unity rechazo la aplicacion del snapshot.")
            );
          }
          return applyResult;
        }
        await new Promise((resolve) => setTimeout(resolve, 80));
      }

      delete pendingPuzzleSnapshotApplyRef.current[requestId];
      throw new Error("Unity no confirmo la carga del snapshot a tiempo.");
    },
    [focusUnitySurface, resolveUnityInstances]
  );

  useEffect(() => {
    if (!hasActiveRoom || gameEnded) return undefined;
    if (puzzleNameModalOpen) return undefined;
    if (typeof window === "undefined") return undefined;

    const handlePointerDownCapture = (event) => {
      const viewport = unityViewportRef.current;
      const targetNode = event?.target;
      if (!viewport || !targetNode || !(targetNode instanceof Node)) return;
      if (viewport.contains(targetNode)) return;
      setTimeout(() => {
        focusUnitySurface();
      }, 0);
    };

    window.addEventListener("pointerdown", handlePointerDownCapture, true);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDownCapture, true);
    };
  }, [focusUnitySurface, gameEnded, hasActiveRoom, puzzleNameModalOpen]);

  useEffect(() => {
    if (!hasActiveRoom || gameEnded) return undefined;
    if (puzzleNameModalOpen) return undefined;
    if (!open && !chatModalOpen) return undefined;

    focusUnitySurface();
    const timer = setInterval(() => {
      focusUnitySurface();
    }, 250);

    return () => clearInterval(timer);
  }, [chatModalOpen, focusUnitySurface, gameEnded, hasActiveRoom, open, puzzleNameModalOpen]);

  const upsertPlayerRuntime = useCallback((rawUserId, rawPayload) => {
    const normalizedUserId = normalizeProfileId(rawUserId);
    if (!normalizedUserId) return;
    const payload = rawPayload && typeof rawPayload === "object" ? rawPayload : {};

    const runtimeClockCandidates = [
      payload.clockSeconds,
      payload.remainingSeconds,
      payload.timeLeftSeconds,
      payload.timeLeft,
      payload.clock,
    ];
    const runtimeForceCandidates = [
      payload.force,
      payload.fg,
      payload.force1,
      payload.gameForce,
      payload.strength,
    ];

    const clockSecondsRaw = runtimeClockCandidates.find((value) => value !== undefined);
    const forceRaw = runtimeForceCandidates.find((value) => value !== undefined);
    const nextClockSeconds = parseClockSeconds(clockSecondsRaw);
    const nextForce = parseForceValue(forceRaw);

    setPlayerRuntimeMap((prev) => {
      const current = prev && typeof prev === "object" ? prev : {};
      const existing = current[normalizedUserId] || {};
      const nextClockValue = Number.isFinite(nextClockSeconds)
        ? nextClockSeconds
        : existing.clockSeconds;
      const nextForceValue = Number.isFinite(nextForce) ? nextForce : existing.force;

      return {
        ...current,
        [normalizedUserId]: {
          clockSeconds: nextClockValue,
          force: nextForceValue,
          updatedAt: Date.now(),
        },
      };
    });
  }, []);

  const ingestIndexedRuntimePayload = useCallback(
    (rawPayload) => {
      const envelope = resolveMetricsEnvelope(rawPayload);
      const payload = envelope.payload;
      if (!payload) return;

      const timeArray = Array.isArray(payload.times)
        ? payload.times
        : Array.isArray(payload.time)
        ? payload.time
        : Array.isArray(payload.clocks)
        ? payload.clocks
        : Array.isArray(payload.clock)
        ? payload.clock
        : null;
      const forceArray = Array.isArray(payload.forces)
        ? payload.forces
        : Array.isArray(payload.force)
        ? payload.force
        : Array.isArray(payload.fg)
        ? payload.fg
        : null;

      const parsedSlots = [];
      for (let index = 0; index < 4; index += 1) {
        const slot = index + 1;
        const rawTimeValue =
          payload[`time${slot}`] ??
          payload[`time_${slot}`] ??
          payload[`clock${slot}`] ??
          payload[`clock_${slot}`] ??
          payload[`remaining${slot}`] ??
          payload[`remaining_${slot}`] ??
          (timeArray ? timeArray[index] : undefined);
        const rawForceValue =
          payload[`force${slot}`] ??
          payload[`force_${slot}`] ??
          payload[`fg${slot}`] ??
          payload[`fg_${slot}`] ??
          (forceArray ? forceArray[index] : undefined);

        const parsedClock = parseClockSeconds(rawTimeValue);
        const parsedForce = parseForceValue(rawForceValue);
        if (!Number.isFinite(parsedClock) && !Number.isFinite(parsedForce)) continue;

        parsedSlots.push({
          index,
          clockSeconds: Number.isFinite(parsedClock) ? parsedClock : undefined,
          force: Number.isFinite(parsedForce) ? parsedForce : undefined,
        });
      }

      if (!parsedSlots.length) return;

      setIndexedRuntimeBySlot((prev) => {
        const current = prev && typeof prev === "object" ? prev : {};
        const next = { ...current };
        let changed = false;

        parsedSlots.forEach(({ index, clockSeconds, force }) => {
          const existing = next[index] || {};
          const nextClockSeconds =
            clockSeconds !== undefined ? clockSeconds : existing.clockSeconds;
          const nextForce = force !== undefined ? force : existing.force;
          if (
            existing.clockSeconds !== nextClockSeconds ||
            existing.force !== nextForce
          ) {
            changed = true;
          }
          next[index] = {
            ...existing,
            ...(clockSeconds !== undefined ? { clockSeconds } : {}),
            ...(force !== undefined ? { force } : {}),
            updatedAt: Date.now(),
          };
        });

        return changed ? next : prev;
      });

      parsedSlots.forEach(({ index, clockSeconds, force }) => {
        const runtimeUserId = orderedParticipantUsers[index];
        if (!runtimeUserId) return;
        upsertPlayerRuntime(runtimeUserId, {
          ...(clockSeconds !== undefined ? { clockSeconds } : {}),
          ...(force !== undefined ? { force } : {}),
        });
      });
    },
    [orderedParticipantUsers, upsertPlayerRuntime]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const socket = getSharedSocket();
    if (!socket) return undefined;

    const handleRuntimeError = (event) => {
      const targetTag = String(event?.target?.tagName || "")
        .trim()
        .toUpperCase();
      const isMediaError = targetTag === "AUDIO" || targetTag === "VIDEO";
      const isScriptError = targetTag === "SCRIPT";
      const hasRuntimeErrorObject = Boolean(event?.error);
      if (!isMediaError && !isScriptError && !hasRuntimeErrorObject) {
        return;
      }

      const reason = isMediaError
        ? "audio_decode_error"
        : isScriptError
        ? "script_missing"
        : "client_error";
      const detail = isMediaError
        ? String(event?.target?.error?.message || event?.message || "media_error")
        : isScriptError
        ? String(event?.target?.src || event?.message || "script_load_error")
        : String(event?.error?.message || event?.message || "runtime_error");

      if (shouldIgnoreClientFailureReport(reason, detail)) return;
      reportClientFailureAsTimeout(reason, detail);
    };

    const handleUnhandledRejection = (event) => {
      const reasonValue = event?.reason;
      const detail =
        reasonValue && typeof reasonValue === "object"
          ? String(reasonValue.message || reasonValue.stack || "unhandled_rejection")
          : String(reasonValue || "unhandled_rejection");
      if (shouldIgnoreClientFailureReport("client_error", detail)) return;
      reportClientFailureAsTimeout("client_error", detail);
    };

    const handleExplicitClientFailure = (event) => {
      const payload = event?.detail && typeof event.detail === "object" ? event.detail : {};
      const reason = String(payload.reason || payload.code || "client_error");
      const detail = String(payload.detail || payload.message || "");
      if (shouldIgnoreClientFailureReport(reason, detail)) return;
      reportClientFailureAsTimeout(reason, detail);
    };

    window.addEventListener("error", handleRuntimeError, true);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("XIMA_CLIENT_FAILURE", handleExplicitClientFailure);

    return () => {
      window.removeEventListener("error", handleRuntimeError, true);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("XIMA_CLIENT_FAILURE", handleExplicitClientFailure);
    };
  }, [reportClientFailureAsTimeout]);

  const resetPuzzleRoundState = useCallback((options = {}) => {
    const preservePuzzleLoadGuard = options?.preservePuzzleLoadGuard === true;
    puzzleLoadCycleRef.current += 1;
    if (!preservePuzzleLoadGuard) {
      syncPuzzleRuntimeReadyToUnity(false, "");
      lastHandledGameStartRoomKeyRef.current = "";
    }
    setPuzzleNameModalOpen(false);
    setPuzzleNameDraft("");
    setPuzzleNameTouchedDraft(false);
    setPuzzleTurnLimitDraft(DEFAULT_PUZZLE_TURN_LIMIT);
    setPuzzleRewardValueDraft(DEFAULT_PUZZLE_REWARD_VALUE);
    setPuzzleSolveTimeLimitEnabledDraft(false);
    setPuzzleSolveTimeLimitMinutesDraft(DEFAULT_PUZZLE_SOLVE_TIME_MINUTES);
    setLoadingPuzzleSnapshot(false);
    setPuzzleAttemptCount(0);
    setPuzzleFailedByTurnLimit(false);
    setPuzzleFailedBySolveTimeLimit(false);
    setPuzzleSolveTimerStartedAtMs(0);
    setPuzzleSolveTimerNowMs(Date.now());
    setRetryingPuzzle(false);
    setOpeningNextPuzzle(false);
    setLastGameEndReason("");
    setCimaxDeltaByUserId({});
    setCimaxInitialByUserId({});
    setCimaxFinalByUserId({});
    setDisconnectedPlayersById({});
    if (!preservePuzzleLoadGuard) {
      puzzleSnapshotAppliedRef.current = false;
      puzzleSnapshotLoadedKeyRef.current = "";
      puzzleSnapshotLoadingKeyRef.current = "";
      puzzleHasLiveProgressRef.current = false;
    }
    puzzleMoveLogRef.current = [];
    puzzleSolverPlayerIndexRef.current = null;
    puzzleSolverMoveCountRef.current = 0;
    puzzleSolverDiceAttemptMaskRef.current = 0;
    lastPuzzleTurnEventKeyRef.current = "";
    lastPuzzleTurnNotationKeyRef.current = "";
    lastPuzzleTurnEventAtMsRef.current = 0;
    unityMoveEventCounterRef.current = 0;
    lastLocalHistoryMoveKeyRef.current = "";
    lastLocalHistoryMoveAtRef.current = 0;
    lastLocalHistoryRollKeyRef.current = "";
    lastLocalHistoryRollAtRef.current = 0;
    localHistoryEndSentKeyRef.current = "";
    lastMoveSequenceRef.current = 0;
    pendingPuzzleSnapshotRequestsRef.current = {};
    pendingPuzzleSnapshotApplyRef.current = {};
    pendingUnityMatchLoadRef.current = {};
    if (!preservePuzzleLoadGuard) {
      puzzleAutoLoadBlockedByIdRef.current = {};
    }
  }, [syncPuzzleRuntimeReadyToUnity]);

  useEffect(() => {
    if (!window.socket) return undefined;
    let runtimeSyncTimerA = null;
    let runtimeSyncTimerB = null;
    const applyPuzzleRuntimeSync = () => {
      if (!isPuzzleRequested || puzzleMetadataReadyForAutoplay) {
        syncPuzzleRuntimeContextToUnity({
          isPuzzle: shouldBlockPuzzleRightClickRuleBreak,
          freeBoxBaseMode:
            normalizedMode === "freebox" ? tourInfo?.freeBoxBaseMode : "",
          puzzleId: activePuzzleTournamentIdRef.current || id,
          roomTournamentId: roomSessionTournamentIdRef.current || id,
        });
      }
      syncPuzzleRuntimeReadyToUnity(
        isPuzzleMode && puzzleSnapshotAppliedRef.current,
        activePuzzleTournamentIdRef.current || id
      );
    };

    const handleGameStart = (rawData) => {
      try {
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        if (String(parse?.tournamentId || "") !== String(id)) return;

        const incomingRoomId = String(parse?.roomId || "").trim();
        const activeRoomId = String(roomIdRef.current || "").trim();
        if (activeRoomId && incomingRoomId && incomingRoomId !== activeRoomId) return;
        const incomingUsers = Array.isArray(parse?.users) ? parse.users : [];
        const normalizedCurrentUserId = normalizeProfileId(
          currentUserId || userEmail || user?.email || ""
        );
        const normalizedPayloadMe = normalizeProfileId(parse?.me || "");
        const isSpectatorPayload =
          parse?.isSpectator === true || String(parse?.spectatorId || "").trim();
        const includesCurrentUser =
          normalizedCurrentUserId &&
          incomingUsers.some((entry) => isCurrentUserIdentity(entry));
        if (
          !isSpectatorPayload &&
          normalizedCurrentUserId &&
          incomingUsers.length > 0 &&
          !includesCurrentUser
        ) {
          console.warn(
            "[GAME_START_IGNORED] current user is not in room users",
            {
              tournamentId: String(parse?.tournamentId || ""),
              roomId: incomingRoomId,
              currentUserId: normalizedCurrentUserId,
              payloadMe: normalizedPayloadMe,
              users: incomingUsers,
            }
          );
          return;
        }
        if (
          !isSpectatorPayload &&
          currentUserIdentityKeys.length > 0 &&
          normalizedPayloadMe &&
          !isCurrentUserIdentity(normalizedPayloadMe)
        ) {
          console.warn(
            "[GAME_START_IGNORED] payload owner does not match current user",
            {
              tournamentId: String(parse?.tournamentId || ""),
              roomId: incomingRoomId,
              currentUserId: normalizedCurrentUserId,
              currentUserIdentityKeys,
              payloadMe: normalizedPayloadMe,
            }
          );
          return;
        }
        const roomKey = `${String(id)}::${incomingRoomId || activeRoomId}`;
        const sameActiveRoom =
          Boolean(activeRoomId) &&
          Boolean(incomingRoomId) &&
          incomingRoomId === activeRoomId;
        const alreadyHandledSameRoom =
          sameActiveRoom && lastHandledGameStartRoomKeyRef.current === roomKey;
        const puzzleLoadGuardActive =
          isPuzzleMode &&
          sameActiveRoom &&
          (puzzleSnapshotAppliedRef.current ||
            Boolean(puzzleSnapshotLoadedKeyRef.current) ||
            puzzleHasLiveProgressRef.current);

        hasGameEndedRef.current = false;
        clientFailureReportedRef.current = false;
        // Solo evitamos resetear si ya procesamos GAME_START de esta misma sala en este ciclo.
        // El primer GAME_START de la sala siempre debe limpiar estado para evitar residuos.
        if (!alreadyHandledSameRoom && !puzzleLoadGuardActive) {
          resetPuzzleRoundState();
        }
        lastHandledGameStartRoomKeyRef.current = roomKey;
        setGameEnded(false);
        setForcePostGameActions(false);
        setLastGameEndReason("");

        if (!activeRoomId && incomingRoomId) {
          roomIdRef.current = incomingRoomId;
          setRoomId(incomingRoomId);
        }
        if (incomingRoomId || activeRoomId) {
          roomSessionTournamentIdRef.current = String(parse?.tournamentId || id || "").trim();
          rememberActiveMatchSession(incomingRoomId || activeRoomId, parse?.tournamentId || id);
        }

        if (incomingUsers.length > 0) {
          setRoomUsers(incomingUsers);
        }
        setAllBotsSeatName(
          parse?.allBots === true ? String(parse?.allBotsSeatName || "").trim() : ""
        );
        setBotTurnSlotIndexes(
          isBotLocalMode ? resolveBotSlotIndexesFromPayload(parse, incomingUsers) : []
        );
        setLocalRuntimeTurnIndex(null);
        setOutUsersById(normalizeOutUsersMap(parse?.isOut));

        // Fallback defensivo: en algunos accesos por enlace (invitado),
        // USER_START puede no sincronizar a tiempo en frontend.
        // Si ya llego GAME_START para esta sala, cerramos espera y matchmaking.
        setOpen(false);
        setMatchmaking(false);

        // GAME_START is also used as a reconnect bootstrap. Once this room has
        // already been handled, the events below can refresh users, clocks and
        // board state without destroying the Unity iframe mid-download.
        if (
          !alreadyHandledSameRoom &&
          !isSpectatorPayload &&
          isOnlineMode &&
          !isPuzzleMode
        ) {
          const cargarPartida = cargarPartidaRef.current;
          if (typeof cargarPartida === "function") {
            const bridgeTurnState = {
              ...parse,
              turn: parse?.turn,
              turnStartedAt: parse?.turnStartedAt,
              turnStartedAtMs: parse?.turnStartedAt,
              turnDurationMs: parse?.turnDurationMs,
              serverNowMs: parse?.serverNowMs,
              remainingMsByIndex: Array.isArray(parse?.remainingMsByIndex)
                ? parse.remainingMsByIndex
                : [],
              isOut:
                parse?.isOut && typeof parse.isOut === "object" && !Array.isArray(parse.isOut)
                  ? parse.isOut
                  : {},
            };
            const bridgeBoardState = {
              ...(parse?.estadoDelTablero &&
              typeof parse.estadoDelTablero === "object" &&
              !Array.isArray(parse.estadoDelTablero)
                ? parse.estadoDelTablero
                : {}),
              isOut: bridgeTurnState.isOut,
              ...(parse?.boardSnapshot ? { boardSnapshot: parse.boardSnapshot } : {}),
              ...(parse?.boardSnapshotJson ? { boardSnapshotJson: parse.boardSnapshotJson } : {}),
            };
            const bridgeMatchData = {
              ...parse,
              matchID: parse?.tournamentId || id,
              tournamentId: parse?.tournamentId || id,
              roomID: incomingRoomId || activeRoomId,
              roomId: incomingRoomId || activeRoomId,
              modoDeJuego: parse?.mode || tourInfo?.mode || "",
              mode: parse?.mode || tourInfo?.mode || "",
              users: incomingUsers,
              jugadoresEnPartida: incomingUsers,
              userProfiles: parse?.userProfiles || {},
              turnState: bridgeTurnState,
              estadoDelTablero: bridgeBoardState,
              havingTime: parse?.havingTime || tourInfo?.havingTime || 1,
              botDifficulty:
                parse?.botDifficulty || navigationBotDifficulty || tourInfo?.botDifficulty || "",
            };
            setTimeout(() => {
              cargarPartida(bridgeMatchData).catch((error) => {
                console.log(error);
                setManualRefreshUnlockedByReconnect(true);
              });
            }, 0);
          }
        }

        if (!String(parse?.phase || "").trim()) {
          setMatchPhase("playing");
        }
        syncServerTurnClockFromPayload(parse);
        if (!Number.isFinite(Number(parse?.turn))) {
          setActiveTurnIndex(0);
        }

        const incomingProfiles =
          parse?.userProfiles && typeof parse.userProfiles === "object"
            ? parse.userProfiles
            : null;
        if (incomingProfiles) {
          acceptAuthoritativeRoomProfiles(
            incomingProfiles,
            parse?.cosmeticRevision,
            parse?.cosmeticAuthorityId
          );
          const modeField =
            resolveCimaxModeFieldFromMode(parse?.mode || tourInfo?.mode, tourInfo?.freeBoxBaseMode) ||
            "ciencia";
          const nextInitial = {};
          Object.keys(incomingProfiles).forEach((rawUserId) => {
            const normalized = normalizeProfileId(rawUserId);
            if (!normalized) return;
            const profile = incomingProfiles[rawUserId] || {};
            const rawScores =
              profile?.cimaxScores && typeof profile.cimaxScores === "object"
                ? profile.cimaxScores
                : profile?.cimax && typeof profile.cimax === "object"
                ? profile.cimax
                : null;
            if (!rawScores) return;
            const scores = normalizeCimaxScores(rawScores);
            nextInitial[normalized] = Math.max(0, Math.round(Number(scores?.[modeField]) || 0));
          });
          if (Object.keys(nextInitial).length > 0) {
            setCimaxInitialByUserId(nextInitial);
            setCimaxFinalByUserId({});
            setCimaxDeltaByUserId({});
          }
        }

        const incomingRuntime =
          parse?.playerRuntime && typeof parse.playerRuntime === "object"
            ? parse.playerRuntime
            : parse?.runtime && typeof parse.runtime === "object"
            ? parse.runtime
            : null;
        if (incomingRuntime) {
          Object.keys(incomingRuntime).forEach((runtimeUserId) => {
            upsertPlayerRuntime(runtimeUserId, incomingRuntime[runtimeUserId]);
          });
        }

        // Unity reinicia parte del estado al comenzar la sala.
        // Reaplicamos estas banderas para que el fin local de puzzle
        // (winner_reached_goal) se emita correctamente.
        applyPuzzleRuntimeSync();
        if (runtimeSyncTimerA) clearTimeout(runtimeSyncTimerA);
        if (runtimeSyncTimerB) clearTimeout(runtimeSyncTimerB);
        runtimeSyncTimerA = setTimeout(applyPuzzleRuntimeSync, 250);
        runtimeSyncTimerB = setTimeout(applyPuzzleRuntimeSync, 700);
      } catch (error) {
        console.log(error);
      }
    };

    window.socket.on("GAME_START", handleGameStart);
    return () => {
      window.socket.off("GAME_START", handleGameStart);
      if (runtimeSyncTimerA) clearTimeout(runtimeSyncTimerA);
      if (runtimeSyncTimerB) clearTimeout(runtimeSyncTimerB);
    };
  }, [
    acceptAuthoritativeRoomProfiles,
    id,
    currentUserId,
    currentUserIdentityKeys,
    isCurrentUserIdentity,
    isBotLocalMode,
    isOnlineMode,
    isPuzzleMode,
    isPuzzleRequested,
    navigationBotDifficulty,
    normalizedMode,
    puzzleMetadataReadyForAutoplay,
    shouldBlockPuzzleRightClickRuleBreak,
    syncPuzzleRuntimeContextToUnity,
    syncPuzzleRuntimeReadyToUnity,
    syncServerTurnClockFromPayload,
    tourInfo?.botDifficulty,
    tourInfo?.freeBoxBaseMode,
    tourInfo?.havingTime,
    tourInfo?.mode,
    resetPuzzleRoundState,
    rememberActiveMatchSession,
    upsertPlayerRuntime,
    user?.email,
    userEmail,
  ]);

  useEffect(() => {
    if (!window.socket) return undefined;

    const handleCimaxResult = (rawData) => {
      try {
        const payload = typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        if (String(payload?.tournamentId || "") !== String(id)) return;
        const incomingRoomId = String(payload?.roomId || "").trim();
        const activeRoomId = String(roomIdRef.current || roomId || "").trim();
        if (activeRoomId && incomingRoomId && incomingRoomId !== activeRoomId) return;
        const updates = Array.isArray(payload?.updates) ? payload.updates : [];
        const modeField =
          resolveCimaxModeFieldFromMode(payload?.mode || tourInfo?.mode, tourInfo?.freeBoxBaseMode) ||
          "ciencia";
        setCimaxDeltaByUserId((prev) => {
          const next = { ...(prev && typeof prev === "object" ? prev : {}) };
          updates.forEach((entry) => {
            const key = normalizeProfileId(entry?.userId);
            if (!key) return;
            next[key] = Math.round(Number(entry?.delta) || 0);
          });
          return next;
        });
        setCimaxFinalByUserId((prev) => {
          const next = { ...(prev && typeof prev === "object" ? prev : {}) };
          updates.forEach((entry) => {
            const key = normalizeProfileId(entry?.userId);
            if (!key) return;
            const scores =
              entry?.cimaxScores && typeof entry.cimaxScores === "object" ? entry.cimaxScores : {};
            const finalValue = Math.max(0, Math.round(Number(scores?.[modeField]) || 0));
            next[key] = finalValue;
          });
          return next;
        });
        setCimaxInitialByUserId((prev) => {
          const next = { ...(prev && typeof prev === "object" ? prev : {}) };
          updates.forEach((entry) => {
            const key = normalizeProfileId(entry?.userId);
            if (!key) return;
            const delta = Math.round(Number(entry?.delta) || 0);
            const scores =
              entry?.cimaxScores && typeof entry.cimaxScores === "object" ? entry.cimaxScores : {};
            const finalValue = toRoundedCimaxValue(scores?.[modeField], NaN);
            if (!Number.isFinite(finalValue)) return;
            next[key] = Math.max(0, finalValue - delta);
          });
          return next;
        });
      } catch (error) {
        console.log(error);
      }
    };

    window.socket.on("CIMAX_RESULT", handleCimaxResult);
    return () => {
      window.socket.off("CIMAX_RESULT", handleCimaxResult);
    };
  }, [id, roomId, tourInfo?.freeBoxBaseMode, tourInfo?.mode]);

  useEffect(() => {
    if (!window.socket) return undefined;

    const handleRoomUsersSync = (rawData) => {
      try {
        if (hasGameEndedRef.current) return;
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        if (String(parse?.tournamentId || "") !== String(id)) return;

        const incomingRoomId = String(parse?.roomId || "").trim();
        const activeRoomId = String(roomIdRef.current || "").trim();
        if (activeRoomId && incomingRoomId && incomingRoomId !== activeRoomId) return;

        if (!activeRoomId && incomingRoomId) {
          roomIdRef.current = incomingRoomId;
          setRoomId(incomingRoomId);
        }

        const incomingUsers = Array.isArray(parse?.users) ? parse.users : [];
        if (incomingUsers.length > 0) {
          setRoomUsers(incomingUsers);
        }
        if (Object.prototype.hasOwnProperty.call(parse || {}, "isOut")) {
          setOutUsersById(normalizeOutUsersMap(parse?.isOut));
        }
      } catch (error) {
        console.log(error);
      }
    };

    window.socket.on("ROOM_USERS_SYNC", handleRoomUsersSync);
    return () => {
      window.socket.off("ROOM_USERS_SYNC", handleRoomUsersSync);
    };
  }, [id]);

  useEffect(() => {
    if (!window.socket) return undefined;

    const handleRoomProfileSync = (rawData) => {
      try {
        if (hasGameEndedRef.current) return;
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        if (String(parse?.tournamentId || "") !== String(id)) return;

        const incomingRoomId = String(parse?.roomId || "").trim();
        const activeRoomId = String(roomIdRef.current || roomId || "").trim();
        if (activeRoomId && incomingRoomId && incomingRoomId !== activeRoomId) return;
        if (!activeRoomId && incomingRoomId) return;

        const fullMap =
          parse?.userProfiles && typeof parse.userProfiles === "object"
            ? parse.userProfiles
            : null;
        if (fullMap) {
          acceptAuthoritativeRoomProfiles(
            fullMap,
            parse?.cosmeticRevision,
            parse?.cosmeticAuthorityId
          );
          return;
        }

        const profileKey = normalizeProfileId(parse?.userId);
        if (!profileKey) return;
        acceptAuthoritativeRoomProfiles(
          { [profileKey]: parse?.profile || {} },
          parse?.cosmeticRevision,
          parse?.cosmeticAuthorityId
        );
      } catch (error) {
        console.log(error);
      }
    };

    window.socket.on("ROOM_PROFILE_SYNC", handleRoomProfileSync);
    return () => {
      window.socket.off("ROOM_PROFILE_SYNC", handleRoomProfileSync);
    };
  }, [acceptAuthoritativeRoomProfiles, id, roomId]);

  useEffect(() => {
    if (!window.socket) return undefined;

    const isCurrentRoomPayload = (payload) => {
      if (!payload || typeof payload !== "object") return true;
      if (payload.tournamentId && String(payload.tournamentId) !== String(id)) return false;
      if (roomId && payload.roomId && String(payload.roomId) !== String(roomId)) return false;
      return true;
    };

    const handleTurnNext = (rawData) => {
      let parsed = rawData;
      if (typeof rawData === "string") {
        try {
          parsed = JSON.parse(rawData);
        } catch {
          return;
        }
      }

      if (!parsed || typeof parsed !== "object") return;
      if (!isCurrentRoomPayload(parsed)) return;
      if (hasGameEndedRef.current) return;
      syncServerTurnClockFromPayload(parsed);
    };

    const handlePlayerTimeout = (rawData) => {
      try {
        const envelope = resolveMetricsEnvelope(rawData);
        const rootPayload = envelope.root || {};
        const parse = envelope.payload || {};
        if (!isCurrentRoomPayload(parse) && !isCurrentRoomPayload(rootPayload)) return;
        syncServerTurnClockFromPayload(parse);
        const incomingIsOutMap =
          parse?.isOut && typeof parse.isOut === "object"
            ? parse.isOut
            : rootPayload?.isOut && typeof rootPayload.isOut === "object"
            ? rootPayload.isOut
            : null;
        if (incomingIsOutMap) {
          setOutUsersById(normalizeOutUsersMap(incomingIsOutMap));
        }

        if (isSpectatorRequested) return;
        if (hasGameEndedRef.current) return;

        const myNormalizedUserId = normalizeProfileId(
          currentUserId || userEmail || user?.email || ""
        );
        if (!myNormalizedUserId) return;

        const timedOutUserId =
          parse?.timedOutUserId ||
          parse?.timedOutUser ||
          parse?.failedUserId ||
          parse?.userId ||
          parse?.playerId ||
          parse?.email ||
          rootPayload?.timedOutUserId ||
          rootPayload?.timedOutUser ||
          rootPayload?.failedUserId ||
          rootPayload?.userId ||
          rootPayload?.playerId ||
          rootPayload?.email ||
          "";

        let isCurrentUserTimedOut =
          Boolean(timedOutUserId) &&
          normalizeProfileId(timedOutUserId) === myNormalizedUserId;

        if (!isCurrentUserTimedOut) {
          const timedOutIndexRaw =
            parse?.timedOutPlayerIndex ??
            parse?.timedOutIndex ??
            parse?.playerIndex ??
            rootPayload?.timedOutPlayerIndex ??
            rootPayload?.timedOutIndex ??
            rootPayload?.playerIndex;
          const timedOutIndex = Number(timedOutIndexRaw);
          if (Number.isFinite(timedOutIndex) && timedOutIndex >= 0) {
            const indexedUser = orderedParticipantUsers[Math.floor(timedOutIndex)];
            if (indexedUser && normalizeProfileId(indexedUser) === myNormalizedUserId) {
              isCurrentUserTimedOut = true;
            }
          }
        }

        if (!isCurrentUserTimedOut) {
          const outById =
            parse?.isOut && typeof parse.isOut === "object" ? parse.isOut : {};
          if (outById[myNormalizedUserId] === true) {
            isCurrentUserTimedOut = true;
          }
        }

        if (!isCurrentUserTimedOut) return;

        clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);
        hasGameEndedRef.current = true;
        setGameEnded(true);
        setLastGameEndReason("timeout");
        setForcePostGameActions(true);
        setServerTurnClock({
          turnIndex: 0,
          turnStartedAtMs: 0,
          turnDurationMs: 0,
          remainingMsByIndex: [],
        });
        setServerClockOffsetMs(0);
        setPostGameMessage(`Se te acabo el tiempo. ${postGameActionHelpText}`);
        setChatModalOpen(false);
        setOpen(false);
        setMatchmaking(false);
        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    };

    const ingestMetricsPayload = (rawData) => {
      try {
        const envelope = resolveMetricsEnvelope(rawData);
        const rootPayload = envelope.root || {};
        const parse = envelope.payload || {};

        if (!isCurrentRoomPayload(parse) && !isCurrentRoomPayload(rootPayload)) return;
        if (hasGameEndedRef.current) return;

        const singleUserId =
          parse?.userId ||
          parse?.playerId ||
          parse?.email ||
          parse?.participantId ||
          rootPayload?.userId ||
          rootPayload?.playerId ||
          rootPayload?.email ||
          rootPayload?.participantId;
        if (singleUserId) {
          upsertPlayerRuntime(singleUserId, parse);
        }

        const players = Array.isArray(parse?.players)
          ? parse.players
          : Array.isArray(rootPayload?.players)
          ? rootPayload.players
          : [];
        players.forEach((entry) => {
          const entryEnvelope = resolveMetricsEnvelope(entry);
          const playerPayload = entryEnvelope.payload || {};
          const runtimeUserId =
            playerPayload?.userId ||
            playerPayload?.playerId ||
            playerPayload?.email ||
            playerPayload?.participantId;
          if (!runtimeUserId) return;
          upsertPlayerRuntime(runtimeUserId, playerPayload);
        });

        ingestIndexedRuntimePayload(parse);
      } catch (error) {
        console.log(error);
      }
    };

    window.socket.on("TURN_NEXT", handleTurnNext);
    window.socket.on("PLAYER_TIMEOUT", handlePlayerTimeout);
    window.socket.on("PLAYER_DISCONNECT_TIMEOUT", handlePlayerTimeout);
    if (SHOW_FRONTEND_PLAYER_PANELS && !USE_SERVER_AUTHORITATIVE_TURN_CLOCK) {
      window.socket.on("PLAYER_CLOCK", ingestMetricsPayload);
      window.socket.on("PLAYER_FORCE", ingestMetricsPayload);
      window.socket.on("PLAYER_METRICS", ingestMetricsPayload);
    }

    return () => {
      window.socket.off("TURN_NEXT", handleTurnNext);
      window.socket.off("PLAYER_TIMEOUT", handlePlayerTimeout);
      window.socket.off("PLAYER_DISCONNECT_TIMEOUT", handlePlayerTimeout);
      if (SHOW_FRONTEND_PLAYER_PANELS && !USE_SERVER_AUTHORITATIVE_TURN_CLOCK) {
        window.socket.off("PLAYER_CLOCK", ingestMetricsPayload);
        window.socket.off("PLAYER_FORCE", ingestMetricsPayload);
        window.socket.off("PLAYER_METRICS", ingestMetricsPayload);
      }
    };
  }, [
    clearStoredActiveMatchSessionForCurrentUser,
    currentUserId,
    id,
    ingestIndexedRuntimePayload,
    isSpectatorRequested,
    orderedParticipantUsers,
    postGameActionHelpText,
    roomId,
    syncServerTurnClockFromPayload,
    user?.email,
    userEmail,
    upsertPlayerRuntime,
  ]);

  useEffect(() => {
    // Los paneles frontend pueden permanecer ocultos y el reloj online seguir
    // siendo autoritativo, pero las partidas locales contra bots necesitan
    // escuchar el reloj emitido por Unity para identificar el slot que juega.
    if (
      (!SHOW_FRONTEND_PLAYER_PANELS || USE_SERVER_AUTHORITATIVE_TURN_CLOCK) &&
      !useLocalBotTurnIndicatorFallback
    )
      return undefined;

    const handleWindowMetrics = (event) => {
      if (event?.type === "message") {
        const iframeWindow = unityIframeRef.current?.contentWindow || null;
        if (iframeWindow && event?.source && event.source !== iframeWindow) return;
      }

      const envelope = resolveMetricsEnvelope(event?.detail ?? event?.data ?? null);
      const rootPayload = envelope.root || {};
      const incoming = envelope.payload || {};
      if (!incoming || typeof incoming !== "object") return;

      const eventType = String(
        incoming.type || incoming.event || rootPayload.type || rootPayload.event || ""
      )
        .trim()
        .toUpperCase();

      const embeddedTurn = Number(
        incoming.turnIndex ??
          incoming.turn ??
          incoming.currentTurnIndex ??
          incoming.turn_order ??
          incoming.turnOrder
      );
      const hasExplicitTurnIndex = Number.isFinite(embeddedTurn);
      const embeddedTurnSlot = Number(
        incoming.turnPlayerSlot ??
          incoming.playerSlot ??
          incoming.currentPlayerSlot ??
          incoming.playerIndex ??
          incoming.playerIndexNumber
      );
      if (
        eventType === "XIMA_TURN_INDEX" ||
        eventType === "TURN_INDEX" ||
        eventType.includes("TURN") ||
        Number.isFinite(embeddedTurn)
      ) {
        let turnIndex = Number.isFinite(embeddedTurn)
          ? embeddedTurn
          : Number(rootPayload.turnIndex ?? rootPayload.turn);
        if (!Number.isFinite(turnIndex) && Number.isFinite(embeddedTurnSlot)) {
          const slotValue = Math.floor(embeddedTurnSlot);
          if (slotValue >= 0) {
            const safePlayerCount = Math.max(
              2,
              Math.min(4, Number(localTurnPlayerCount) || 2)
            );
            const normalizedSlot =
              !hasExplicitTurnIndex &&
              slotValue >= 1 &&
              slotValue <= safePlayerCount
                ? slotValue - 1
                : slotValue;
            turnIndex =
              ((normalizedSlot % safePlayerCount) + safePlayerCount) %
              safePlayerCount;
          }
        }
        if (Number.isFinite(turnIndex)) {
          setActiveTurnIndex(Math.floor(turnIndex));
        }
      }

      const looksLikeMetrics =
        eventType === "XIMA_PLAYER_METRICS" ||
        eventType === "PLAYER_METRICS" ||
        eventType === "XIMA_PLAYER_CLOCK" ||
        eventType === "XIMA_PLAYER_FORCE" ||
        hasIndexedRuntimeFields(incoming) ||
        Array.isArray(incoming.players) ||
        hasIndexedRuntimeFields(rootPayload) ||
        Array.isArray(rootPayload.players);
      if (!looksLikeMetrics) return;

      const singleUserId =
        incoming.userId ||
        incoming.playerId ||
        incoming.email ||
        incoming.participantId ||
        rootPayload.userId ||
        rootPayload.playerId ||
        rootPayload.email ||
        rootPayload.participantId;
      if (singleUserId) {
        upsertPlayerRuntime(singleUserId, incoming);
      }

      const players = Array.isArray(incoming.players)
        ? incoming.players
        : Array.isArray(rootPayload.players)
        ? rootPayload.players
        : [];
      players.forEach((entry) => {
        const entryEnvelope = resolveMetricsEnvelope(entry);
        const playerPayload = entryEnvelope.payload || {};
        const runtimeUserId =
          playerPayload?.userId ||
          playerPayload?.playerId ||
          playerPayload?.email ||
          playerPayload?.participantId;
        if (!runtimeUserId) return;
        upsertPlayerRuntime(runtimeUserId, playerPayload);
      });

      ingestIndexedRuntimePayload(incoming);
      if (incoming !== rootPayload) {
        ingestIndexedRuntimePayload(rootPayload);
      }
    };

    const bindMetricsListeners = (target) => {
      if (!target || typeof target.addEventListener !== "function") {
        return () => {};
      }

      target.addEventListener("message", handleWindowMetrics);
      target.addEventListener("xima-player-metrics", handleWindowMetrics);
      target.addEventListener("XIMA_PLAYER_METRICS", handleWindowMetrics);
      target.addEventListener("XIMA_PLAYER_CLOCK", handleWindowMetrics);
      target.addEventListener("XIMA_PLAYER_FORCE", handleWindowMetrics);
      target.addEventListener("XIMA_TURN_INDEX", handleWindowMetrics);
      target.addEventListener("xima-turn-index", handleWindowMetrics);

      return () => {
        if (!target || typeof target.removeEventListener !== "function") return;
        target.removeEventListener("message", handleWindowMetrics);
        target.removeEventListener("xima-player-metrics", handleWindowMetrics);
        target.removeEventListener("XIMA_PLAYER_METRICS", handleWindowMetrics);
        target.removeEventListener("XIMA_PLAYER_CLOCK", handleWindowMetrics);
        target.removeEventListener("XIMA_PLAYER_FORCE", handleWindowMetrics);
        target.removeEventListener("XIMA_TURN_INDEX", handleWindowMetrics);
        target.removeEventListener("xima-turn-index", handleWindowMetrics);
      };
    };

    const unbindWindow = bindMetricsListeners(window);
    let iframeTarget = unityIframeRef.current?.contentWindow || null;
    let unbindIframe = bindMetricsListeners(iframeTarget);
    const iframeElement = unityIframeRef.current;

    const handleIframeLoad = () => {
      unbindIframe();
      iframeTarget = unityIframeRef.current?.contentWindow || null;
      unbindIframe = bindMetricsListeners(iframeTarget);
    };

    if (iframeElement && typeof iframeElement.addEventListener === "function") {
      iframeElement.addEventListener("load", handleIframeLoad);
    }

    return () => {
      unbindWindow();
      unbindIframe();
      if (iframeElement && typeof iframeElement.removeEventListener === "function") {
        iframeElement.removeEventListener("load", handleIframeLoad);
      }
    };
  }, [
    ingestIndexedRuntimePayload,
    localTurnPlayerCount,
    upsertPlayerRuntime,
    useLocalBotTurnIndicatorFallback,
  ]);

  useEffect(() => {
    const syncSelectedSpecialBlock = () => {
      const selected = getSpecialBlockFromStorage(userEmail);
      applySpecialBlockPreference(
        selected,
        localStorage.getItem(GLOBAL_SPECIAL_BLOCK_IMAGE_KEY)
      );
    };

    syncSelectedSpecialBlock();

    const handleSpecialBlockChange = (event) => {
      const incomingName = normalizeSpecialBlockId(
        event?.detail?.imageName || event?.detail?.id || ""
      );
      const selected = incomingName || getSpecialBlockFromStorage(userEmail);
      applySpecialBlockPreference(selected, event?.detail?.image);
    };

    window.addEventListener("xima-special-block-change", handleSpecialBlockChange);
    return () => {
      window.removeEventListener("xima-special-block-change", handleSpecialBlockChange);
    };
  }, [applySpecialBlockPreference, userEmail]);

  useEffect(() => {
    const syncCommonTheme = () => {
      const selected = getCommonBlockFromStorage(userEmail);
      applyCommonBlockPreference(selected);
    };

    syncCommonTheme();

    const handleThemeChange = (event) => {
      const incomingThemeId = String(event?.detail?.id || event?.detail?.imageName || "")
        .trim();
      const resolvedThemeId = normalizeCommonBlockThemeId(incomingThemeId);
      applyCommonBlockPreference(resolvedThemeId);
    };

    window.addEventListener("xima-common-block-theme-change", handleThemeChange);
    return () => {
      window.removeEventListener("xima-common-block-theme-change", handleThemeChange);
    };
  }, [applyCommonBlockPreference, userEmail]);

  useEffect(() => {
    const syncMaterialTexture = (event) => {
      const incoming = event?.detail?.blockMaterialTextureId;
      applyBlockMaterialPreference(
        incoming || getBlockMaterialTextureFromStorage()
      );
    };
    syncMaterialTexture();
    window.addEventListener("xima-material-design-change", syncMaterialTexture);
    return () => {
      window.removeEventListener("xima-material-design-change", syncMaterialTexture);
    };
  }, [applyBlockMaterialPreference]);

  useEffect(() => {
    const syncBlockAnimation = (event) => {
      const incoming = event?.detail?.blockAnimationId;
      applyBlockAnimationPreference(incoming || getBlockAnimationFromStorage());
    };
    syncBlockAnimation();
    window.addEventListener("xima-block-animation-design-change", syncBlockAnimation);
    return () => {
      window.removeEventListener("xima-block-animation-design-change", syncBlockAnimation);
    };
  }, [applyBlockAnimationPreference]);

  useEffect(() => {
    let cancelled = false;

    const syncSkinPreferencesFromServer = async () => {
      const token = String(getStoredAuthToken() || "").trim();
      if (!token) return;

      const localSpecial = getSpecialBlockFromStorage(userEmail);
      const localCommon = getCommonBlockFromStorage(userEmail);
      const localMaterial = getBlockMaterialTextureFromStorage();
      const localAnimation = getBlockAnimationFromStorage();

      try {
        const response = await axios.get("/auth/profile");
        if (cancelled) return;

        const serverSpecial = normalizeSpecialBlockId(response?.data?.specialBlockId || "");
        const serverCommonRaw = normalizeSkinId(response?.data?.commonBlockThemeId || "", "");
        const resolvedSpecial = serverSpecial || localSpecial || DEFAULT_SPECIAL_BLOCK_ID;
        const resolvedCommon = normalizeCommonBlockThemeId(
          serverCommonRaw || localCommon || DEFAULT_COMMON_BLOCK_THEME_ID
        );
        const serverMaterial = normalizeOptionalBlockMaterialTextureId(
          response?.data?.blockMaterialTextureId || ""
        );
        const resolvedMaterial =
          serverMaterial || localMaterial || DEFAULT_BLOCK_MATERIAL_TEXTURE_ID;
        const serverAnimation = normalizeOptionalBlockAnimationId(
          response?.data?.blockAnimationId || ""
        );
        const resolvedAnimation =
          serverAnimation || localAnimation || DEFAULT_BLOCK_ANIMATION_ID;
        setCurrentUserCimaxScores(
          normalizeCimaxScores(response?.data?.cimaxScores || response?.data?.cimax)
        );

        applySpecialBlockPreference(resolvedSpecial);
        applyCommonBlockPreference(resolvedCommon);
        applyBlockMaterialPreference(resolvedMaterial);
        applyBlockAnimationPreference(resolvedAnimation);

        if (!serverSpecial || !serverCommonRaw || !response?.data?.blockMaterialTextureId || !response?.data?.blockAnimationId) {
          persistSkinPreferencesToServer(
            resolvedSpecial,
            resolvedCommon,
            resolvedMaterial,
            resolvedAnimation
          ).catch((error) =>
            console.log(error)
          );
        }
      } catch (error) {
        console.log(error);
      }
    };

    syncSkinPreferencesFromServer().catch((error) => console.log(error));
    return () => {
      cancelled = true;
    };
  }, [
    applyCommonBlockPreference,
    applyBlockMaterialPreference,
    applyBlockAnimationPreference,
    applySpecialBlockPreference,
    persistSkinPreferencesToServer,
    userEmail,
  ]);

  useEffect(() => {
    if (!window.socket) return;
    if (!roomId) return;
    if (!currentUserId) return;
    if (isSpectatorRequested) return;
    let cancelled = false;

    const sendProfileSync = async () => {
      const normalizedAvatar = await normalizeAvatarForSocket(currentUserAvatarUrl);
      if (cancelled) return;

      const profilePayload = {
        avatarUrl: normalizedAvatar,
        displayName: currentUserName,
        specialBlockId,
        commonBlockThemeId,
        blockMaterialTextureId,
        blockAnimationId,
        cimaxScores: currentUserCimaxScores,
      };

      // Do not optimistically insert saved cosmetics into the effective room
      // profile. The server response applies CIMAX conflict arbitration and is
      // the only cosmetic authority while this online room is active.
      window.socket.emit("ROOM_PROFILE_SYNC", {
        tournamentId: String(id),
        roomId: String(roomId),
        profile: profilePayload,
      });
    };

    sendProfileSync().catch((error) => console.log(error));

    return () => {
      cancelled = true;
    };
  }, [
    currentUserAvatarUrl,
    currentUserId,
    currentUserName,
    id,
    isSpectatorRequested,
    roomId,
    specialBlockId,
    commonBlockThemeId,
    blockMaterialTextureId,
    blockAnimationId,
    currentUserCimaxScores,
  ]);

  const leaveTournament = useCallback(async (options = {}) => {
    try {
      await axios.post("/tournaments/leave", {
        id,
        ...(options?.cancelFindingUser === true ? { cancelFindingUser: true } : {}),
      });
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  const resolvePuzzleRewardReportId = useCallback(
    (rawPuzzleId = "") =>
      String(rawPuzzleId || activePuzzleTournamentIdRef.current || id || "").trim(),
    [id]
  );

  const shouldForfeitPuzzleRewardOnExit = useCallback(
    (rawPuzzleId = "") => {
      const puzzleId = resolvePuzzleRewardReportId(rawPuzzleId);
      if (!puzzleId) return false;
      if (isGuidedTutorial) return false;
      if (!isPuzzleMode || isSpectatorRequested) return false;
      if (String(normalizedLastGameEndReason || "").trim().toLowerCase() === "winner_reached_goal") {
        return false;
      }
      return true;
    },
    [
      isPuzzleMode,
      isGuidedTutorial,
      isSpectatorRequested,
      normalizedLastGameEndReason,
      resolvePuzzleRewardReportId,
    ]
  );

  const reportPuzzleRewardFailure = useCallback(
    async (rawPuzzleId, failureReason = "puzzle_failed", options = {}) => {
      if (isGuidedTutorial) return false;
      const failedPuzzleId = resolvePuzzleRewardReportId(rawPuzzleId);
      if (!failedPuzzleId) return false;

      const reportMap =
        failedPuzzleRewardReportedRef.current &&
        typeof failedPuzzleRewardReportedRef.current === "object"
          ? failedPuzzleRewardReportedRef.current
          : {};
      if (reportMap[failedPuzzleId] === true && options?.force !== true) return true;
      reportMap[failedPuzzleId] = true;
      failedPuzzleRewardReportedRef.current = reportMap;

      const normalizedReason =
        String(failureReason || "").trim().toLowerCase() || "puzzle_failed";
      const payload = {
        puzzleId: failedPuzzleId,
        reason: normalizedReason,
      };

      try {
        if (
          options?.useKeepaliveFetch === true &&
          typeof window !== "undefined" &&
          typeof window.fetch === "function"
        ) {
          const token = String(getStoredAuthToken() || "").trim();
          const headers = {
            "Content-Type": "application/json",
          };
          if (token && token !== "0") {
            headers.Authorization = `Bearer ${token}`;
          }
          const response = await window.fetch("/tournaments/puzzles/mark-failed-reward", {
            method: "POST",
            headers,
            credentials: "same-origin",
            keepalive: true,
            body: JSON.stringify(payload),
          });
          if (!response.ok) {
            throw new Error(`Failed with status ${response.status}`);
          }
        } else {
          await axios.post("/tournaments/puzzles/mark-failed-reward", payload);
        }
        return true;
      } catch (error) {
        failedPuzzleRewardReportedRef.current[failedPuzzleId] = false;
        if (options?.silent !== true) {
          console.log(error);
        }
        return false;
      }
    },
    [isGuidedTutorial, resolvePuzzleRewardReportId]
  );

  const emitOutTournament = useCallback(
    (forcedRoomId = roomId) => {
      if (!window.socket) return;
      window.socket.emit(
        "OUT_TOURNAMENT",
        JSON.stringify({
          tournamentId: id,
          roomId: forcedRoomId,
          userId: currentUserId || user?.email || "",
        })
      );
    },
    [currentUserId, id, roomId, user?.email]
  );

  const clearLiveMatchUiState = useCallback(() => {
    setOpen(false);
    setMatchmaking(false);
    setLoading(false);
    setForcePostGameActions(false);
    setChatModalOpen(false);
    setChatMessages([]);
    setMoveNotations([]);
    setRoomUsers([]);
    setAllBotsSeatName("");
    roomCosmeticAuthorityIdRef.current = "local";
    roomCosmeticRevisionRef.current = 0;
    setRoomProfiles({});
    setPlayerRuntimeMap({});
    setIndexedRuntimeBySlot({});
    setOutUsersById({});
    setSpectatorCount(0);
    setActiveTurnIndex(0);
    setBotTurnSlotIndexes([]);
    setLocalRuntimeTurnIndex(null);
    setMatchPhase("waiting");
    setServerTurnClock({
      turnIndex: 0,
      turnStartedAtMs: 0,
      turnDurationMs: 0,
      remainingMsByIndex: [],
    });
    setServerClockOffsetMs(0);
    setConnectionState("connected");
    setSavingPuzzleSnapshot(false);
    resetPuzzleRoundState();
  }, [resetPuzzleRoundState]);

  const CargarPartida = useCallback(
    async (rawMatchData, loadOptions = {}) => {
      const matchData =
        rawMatchData && typeof rawMatchData === "object" && !Array.isArray(rawMatchData)
          ? rawMatchData
          : {};
      const resolvedMatchId = String(matchData.matchID || matchData.tournamentId || id || "").trim();
      const resolvedRoomId = String(matchData.roomID || matchData.roomId || "").trim();
      if (!resolvedMatchId || !resolvedRoomId) {
        throw new Error("No hay datos suficientes para restaurar la partida.");
      }

      const loadKey = `${resolvedMatchId}::${resolvedRoomId}`;
      while (unityMatchLoadInFlightRef.current) {
        const activeLoad = unityMatchLoadInFlightRef.current;
        await activeLoad.done;
        if (activeLoad.key === loadKey) {
          if (activeLoad.error) throw activeLoad.error;
          return activeLoad.result;
        }
      }

      let resolveLoadDone;
      const loadOperation = {
        key: loadKey,
        result: false,
        error: null,
        done: new Promise((resolve) => {
          resolveLoadDone = resolve;
        }),
      };
      unityMatchLoadInFlightRef.current = loadOperation;

      try {

      const incomingUsers = Array.isArray(matchData.jugadoresEnPartida)
        ? matchData.jugadoresEnPartida
        : Array.isArray(matchData.users)
        ? matchData.users
        : [];
      const roomUsersNormalized = incomingUsers
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
        .slice(0, 4);
      const reconstructedMoveLog = orderMovePayloadsBySequence(
        Array.isArray(matchData.posicionDePiezas)
          ? matchData.posicionDePiezas
          : Array.isArray(matchData.fullMoveLog)
          ? matchData.fullMoveLog
          : [],
        MAX_PUZZLE_MOVE_LOG_SIZE
      );
      const turnState =
        matchData.turnState && typeof matchData.turnState === "object" ? matchData.turnState : {};
      const unityRuntimeMode = String(
        matchData.modoDeJuego || matchData.mode || tourInfo.mode || ""
      )
        .trim()
        .toLowerCase();
      const restoredTournamentMode = normalizeTournamentModeAlias(unityRuntimeMode);
      const isRestoredBotLocalMode = isBotLocalModeValue(restoredTournamentMode);
      const havingTime = Math.max(
        1,
        Number(matchData.havingTime || tourInfo.havingTime || 1) || 1
      );
      const resolvedBotDifficulty =
        [
          matchData.botDifficulty,
          matchData.roomData?.botDifficulty,
          turnState?.botDifficulty,
          turnState?.runtimeState?.botDifficulty,
          matchData.estadoDelTablero?.botDifficulty,
          matchData.estadoDelTablero?.runtimeState?.botDifficulty,
          navigationBotDifficulty,
          tourInfo.botDifficulty,
        ]
          .map(normalizeRuntimeBotDifficulty)
          .find(Boolean) || "medium";
      const memberSize = Math.max(
        2,
        Number(matchData.memberSize || roomUsersNormalized.length || tourInfo.members || 2) || 2
      );
      const currentMe = String(
        matchData.me || currentUserId || userEmail || user?.email || ""
      ).trim();
      const normalizedCurrentMe = normalizeProfileId(currentMe);
      const currentUserIsInRoom =
        normalizedCurrentMe &&
        roomUsersNormalized.some(
          (entry) => normalizeProfileId(entry) === normalizedCurrentMe
        );
      if (
        !isSpectatorRequested &&
        roomUsersNormalized.length > 0 &&
        normalizedCurrentMe &&
        !currentUserIsInRoom
      ) {
        clearStoredActiveMatchSessionForCurrentUser(resolvedMatchId);
        roomIdRef.current = 0;
        setRoomId(0);
        throw new Error(
          "La sala guardada no corresponde a este usuario. Vuelve a entrar desde la sala de espera."
        );
      }

      hasGameEndedRef.current = false;
      clientFailureReportedRef.current = false;
      clearLiveMatchUiState();
      lastHandledGameStartRoomKeyRef.current = loadKey;
      setLoading(true);
      setGameEnded(false);
      setForcePostGameActions(false);
      setPostGameMessage("");
      setLastGameEndReason("");
      setOpen(false);
      setMatchmaking(false);
      setConnectionState("connected");

      roomIdRef.current = resolvedRoomId;
      setRoomId(resolvedRoomId);
      roomSessionTournamentIdRef.current = resolvedMatchId;
      rememberActiveMatchSession(resolvedRoomId, resolvedMatchId);
      if (roomUsersNormalized.length > 0) {
        setRoomUsers(roomUsersNormalized);
      }
      setBotTurnSlotIndexes(
        isRestoredBotLocalMode
          ? resolveBotSlotIndexesFromPayload(
              {
                botSlots:
                  matchData.botSlots ??
                  matchData.roomData?.botSlots ??
                  turnState?.botSlots,
              },
              roomUsersNormalized
            )
          : []
      );
      setLocalRuntimeTurnIndex(null);
      if (matchData.userProfiles && typeof matchData.userProfiles === "object") {
        acceptAuthoritativeRoomProfiles(
          matchData.userProfiles,
          matchData.cosmeticRevision,
          matchData.cosmeticAuthorityId || `${resolvedMatchId}:${resolvedRoomId}`
        );
      }
      const manualIsOutMap = Object.prototype.hasOwnProperty.call(matchData, "isOut")
        ? normalizeOutUsersMap(matchData.isOut)
        : matchData.estadoDelTablero && typeof matchData.estadoDelTablero === "object"
        ? normalizeOutUsersMap(matchData.estadoDelTablero.isOut)
        : {};
      setOutUsersById(manualIsOutMap);

      const manualActiveTurnUsers = roomUsersNormalized.filter((entry) => {
        const normalizedUserId = normalizeProfileId(entry);
        if (!normalizedUserId) return false;
        return manualIsOutMap[normalizedUserId] !== true;
      });
      const manualFrozenBlocksCount = countFrozenBlocksFromMoveLog(
        reconstructedMoveLog,
        roomUsersNormalized,
        manualIsOutMap
      );
      const manualActiveTurnUsersText =
        manualActiveTurnUsers.length > 0 ? manualActiveTurnUsers.join(",") : "(none)";
      console.log(`[RECONNECT_REBUILD] active turn users only=${manualActiveTurnUsersText}`);
      console.log(`[RECONNECT_REBUILD] frozen users blocks count=${manualFrozenBlocksCount}`);
      if (manualFrozenBlocksCount > 0) {
        console.log("[RECONNECT_REBUILD] board occupancy includes forfeited user blocks");
      }

      setTourInfo((prev) => ({
        ...prev,
        mode: restoredTournamentMode || normalizeTournamentModeAlias(prev.mode) || prev.mode,
        members: memberSize,
        havingTime,
        botDifficulty: resolvedBotDifficulty,
        users: roomUsersNormalized.length > 0 ? roomUsersNormalized : prev.users,
      }));

      const shouldReloadUnity = loadOptions?.reloadUnity !== false;
      if (shouldReloadUnity) {
        setUnitySessionNonce((prev) => prev + 1);
      }
      await ensureSocketSessionReady();

      const iframeReady = await waitForUnityIframeReady(30000);
      if (!iframeReady) {
        setLoading(false);
        throw new Error("Unity sigue cargando. Intenta nuevamente en unos segundos.");
      }

      const roomTurn =
        Number.isFinite(Number(turnState?.turn)) && Number(turnState.turn) >= 0
          ? Number(turnState.turn)
          : Number.isFinite(Number(matchData.turnoActual)) && Number(matchData.turnoActual) >= 0
          ? Number(matchData.turnoActual)
          : 0;
      const roomTurnStartedAt = Math.max(
        0,
        Number(turnState?.turnStartedAtMs || turnState?.turnStartedAt || 0) || 0
      );
      const roomTurnDurationMs = Math.max(
        1,
        Number(turnState?.turnDurationMs || 0) || havingTime * 60 * 1000
      );
      const roomServerNowMs = Math.max(
        0,
        Number(turnState?.serverNowMs || matchData.serverNowMs || Date.now()) || Date.now()
      );
      const manualLoadRequestId = `match-load-${Date.now()}-${Math.floor(
        Math.random() * 1000000
      )}`;
      const unityFreeBoxBaseMode =
        restoredTournamentMode === "freebox"
          ? normalizeFreeBoxBaseMode(
              matchData.freeBoxBaseMode || tourInfo?.freeBoxBaseMode,
              "free"
            )
          : "";
      const unityIsPuzzle = Boolean(matchData.isPuzzle || isPuzzleMode);

      const unityManualPayload = {
        requestId: manualLoadRequestId,
        matchID: resolvedMatchId,
        roomID: resolvedRoomId,
        modoDeJuego: unityRuntimeMode,
        botDifficulty: resolvedBotDifficulty,
        freeBoxBaseMode: unityFreeBoxBaseMode,
        isPuzzle: unityIsPuzzle,
        turnoActual: Math.floor(roomTurn),
        estadoDelTablero:
          matchData.estadoDelTablero && typeof matchData.estadoDelTablero === "object"
            ? matchData.estadoDelTablero
            : {},
        posicionDePiezas: reconstructedMoveLog,
        jugadoresEnPartida: roomUsersNormalized,
        turnState,
        roomData: {
          me: currentMe,
          users: roomUsersNormalized,
          mode: unityRuntimeMode,
          havingTime,
          botDifficulty: resolvedBotDifficulty,
          freeBoxBaseMode: unityFreeBoxBaseMode,
          isPuzzle: unityIsPuzzle,
          roomId: resolvedRoomId,
          tournamentId: resolvedMatchId,
          isSpectator:
            matchData.isSpectator === true || isSpectatorRequested,
          spectatorId: String(matchData.spectatorId || ""),
          turn: roomTurn,
          turnStartedAt: roomTurnStartedAt,
          turnStartedAtMs: roomTurnStartedAt,
          turnDurationMs: roomTurnDurationMs,
          serverNowMs: roomServerNowMs,
          remainingMsByIndex: Array.isArray(turnState?.remainingMsByIndex)
            ? turnState.remainingMsByIndex
            : [],
          freeBoxRules: Boolean(matchData.freeBoxRules),
          reconnecting: false,
          botControllerId: String(matchData.botControllerId || turnState?.botControllerId || ""),
          hasBots:
            Boolean(matchData.hasBots) ||
            (Array.isArray(matchData.botSlots) && matchData.botSlots.length > 0) ||
            (Array.isArray(turnState?.botSlots) && turnState.botSlots.length > 0),
          botSlots: Array.isArray(matchData.botSlots)
            ? matchData.botSlots
            : Array.isArray(turnState?.botSlots)
            ? turnState.botSlots
            : [],
          boardSnapshotJson: String(matchData.boardSnapshotJson || ""),
        },
      };

      const serializedManualPayload = JSON.stringify(unityManualPayload);
      pendingUnityMatchLoadRef.current[manualLoadRequestId] = null;
      const matchStateAppliedPromise = waitForUnityMatchStateApplied(manualLoadRequestId, {
        tournamentId: resolvedMatchId,
        roomId: resolvedRoomId,
        timeoutMs: 3500,
      });
      const dispatchManualLoadToUnity = async (timeoutMs = 7000) => {
        const startedAt = Date.now();
        while (Date.now() - startedAt < timeoutMs) {
          const currentInstances = resolveUnityInstances();
          let delivered = false;
          currentInstances.forEach((instance) => {
            try {
              instance.SendMessage("OnlineMultiplayerBridge", "CargarPartida", serializedManualPayload);
              delivered = true;
            } catch (error) {
              console.log(error);
            }
          });
          if (delivered) return true;
          await new Promise((resolve) => setTimeout(resolve, 160));
        }
        return false;
      };

      const manualLoadDispatched = await dispatchManualLoadToUnity();

      if (!manualLoadDispatched) {
        pendingUnityMatchLoadRef.current[manualLoadRequestId] = "__ERROR__";
        await matchStateAppliedPromise.catch(() => {});
        delete pendingUnityMatchLoadRef.current[manualLoadRequestId];
        setLoading(false);
        throw new Error("No se pudo enviar el estado de la partida a Unity.");
      }

      try {
        await matchStateAppliedPromise;
      } catch (matchStateAckError) {
        const normalizedAckMessage = String(matchStateAckError?.message || "").toLowerCase();
        const ackTimedOut =
          normalizedAckMessage.includes("no confirmo") ||
          normalizedAckMessage.includes("tiempo");
        if (!ackTimedOut) {
          throw matchStateAckError;
        }
        console.log(
          "Unity no envio confirmacion de tablero listo; continuamos con compatibilidad legacy."
        );
      }

      syncPuzzleRuntimeContextToUnity({
        isPuzzle: unityIsPuzzle && !isSpectatorRequested,
        freeBoxBaseMode: unityFreeBoxBaseMode,
        puzzleId: resolvedMatchId,
        roomTournamentId: resolvedMatchId,
      });

      if (turnState && typeof turnState === "object") {
        syncServerTurnClockFromPayload(turnState);
      }

      const latestMoveSequence = Number(matchData.latestMoveSequence);
      if (Number.isFinite(latestMoveSequence) && latestMoveSequence > 0) {
        lastMoveSequenceRef.current = Math.max(
          Number(lastMoveSequenceRef.current) || 0,
          Math.trunc(latestMoveSequence)
        );
      } else if (reconstructedMoveLog.length > 0) {
        const lastMoveParts = String(
          reconstructedMoveLog[reconstructedMoveLog.length - 1] || ""
        ).split("|");
        const tailMoveSequence = Number(lastMoveParts[6]);
        if (Number.isFinite(tailMoveSequence) && tailMoveSequence > 0) {
          lastMoveSequenceRef.current = Math.max(
            Number(lastMoveSequenceRef.current) || 0,
            Math.trunc(tailMoveSequence)
          );
        }
      }

      setLoading(false);
      loadOperation.result = true;
      return true;
      } catch (error) {
        loadOperation.error = error;
        if (lastHandledGameStartRoomKeyRef.current === loadKey) {
          lastHandledGameStartRoomKeyRef.current = "";
        }
        throw error;
      } finally {
        if (unityMatchLoadInFlightRef.current === loadOperation) {
          unityMatchLoadInFlightRef.current = null;
        }
        resolveLoadDone();
      }
    },
    [
      acceptAuthoritativeRoomProfiles,
      clearLiveMatchUiState,
      clearStoredActiveMatchSessionForCurrentUser,
      currentUserId,
      id,
      isPuzzleMode,
      isSpectatorRequested,
      navigationBotDifficulty,
      rememberActiveMatchSession,
      resolveUnityInstances,
      syncPuzzleRuntimeContextToUnity,
      syncServerTurnClockFromPayload,
      tourInfo.botDifficulty,
      tourInfo.havingTime,
      tourInfo.members,
      tourInfo.mode,
      tourInfo.freeBoxBaseMode,
      user?.email,
      userEmail,
      waitForUnityIframeReady,
      waitForUnityMatchStateApplied,
    ]
  );

  useEffect(() => {
    cargarPartidaRef.current = CargarPartida;
    return () => {
      if (cargarPartidaRef.current === CargarPartida) {
        cargarPartidaRef.current = null;
      }
    };
  }, [CargarPartida]);

  const applyLiveBoardSnapshotToUnity = useCallback(
    async (rawSnapshot) => {
      const snapshotJson =
        typeof rawSnapshot === "string"
          ? rawSnapshot.trim()
          : rawSnapshot && typeof rawSnapshot === "object"
          ? JSON.stringify(rawSnapshot)
          : "";
      if (!snapshotJson) return false;

      const iframeReady = await waitForUnityIframeReady(10000);
      if (!iframeReady) return false;

      let delivered = false;
      resolveUnityInstances().forEach((instance) => {
        try {
          instance.SendMessage(
            "OnlineMultiplayerBridge",
            "ApplyLiveBoardSnapshot",
            snapshotJson
          );
          delivered = true;
        } catch (error) {
          console.log(error);
        }
      });
      return delivered;
    },
    [resolveUnityInstances, waitForUnityIframeReady]
  );

  const unlinkFromCurrentMatch = useCallback(
    (forcedRoomId = roomIdRef.current || roomId) => {
      const activeRoomId = String(forcedRoomId || "").trim();
      if (activeRoomId) {
        emitOutTournament(activeRoomId);
      }
      clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);

      roomIdRef.current = 0;
      setRoomId(0);
      hasRequestedExitRef.current = false;
      hasGameEndedRef.current = false;
      setGameEnded(false);
      clearLiveMatchUiState();
      setUnitySessionNonce((prev) => prev + 1);
    },
    [clearLiveMatchUiState, clearStoredActiveMatchSessionForCurrentUser, emitOutTournament, id, roomId]
  );

  const exitTournamentSession = useCallback(async (options = {}) => {
    if (hasRequestedExitRef.current) return;
    hasRequestedExitRef.current = true;

    if (shouldForfeitPuzzleRewardOnExit()) {
      void reportPuzzleRewardFailure("", "puzzle_abandoned_exit", {
        silent: true,
      });
    }

    const activeRoomId = roomIdRef.current || roomId;
    emitOutTournament(activeRoomId);
    if (!isSpectatorRequested) {
      await leaveTournament({
        cancelFindingUser: options?.cancelFindingUser === true,
      });
    }
    clearLiveMatchUiState();
  }, [
    clearLiveMatchUiState,
    emitOutTournament,
    isSpectatorRequested,
    leaveTournament,
    reportPuzzleRewardFailure,
    roomId,
    shouldForfeitPuzzleRewardOnExit,
  ]);

  useEffect(() => {
    roomIdRef.current = roomId;
    hasRequestedExitRef.current = false;
    hasGameEndedRef.current = false;
    hasAutoResignedOnExitRef.current = false;
    clientFailureReportedRef.current = false;
    resetPuzzleRoundState();
    setSavingPuzzleSnapshot(false);
    setGameEnded(false);
    setForcePostGameActions(false);
    setPostGameMessage("");
    setOutUsersById({});
    setPuzzleAttemptCount(0);
    puzzleSolverPlayerIndexRef.current = null;
    puzzleSolverMoveCountRef.current = 0;
    puzzleSolverDiceAttemptMaskRef.current = 0;
    lastPuzzleTurnEventKeyRef.current = "";
    lastPuzzleTurnNotationKeyRef.current = "";
    lastPuzzleTurnEventAtMsRef.current = 0;
    lastMoveSequenceRef.current = 0;
  }, [id, roomId, resetPuzzleRoundState]);

  const syncActivePuzzleTournamentId = useCallback((rawPuzzleId) => {
    const resolvedPuzzleId = String(rawPuzzleId || "").trim();
    if (!resolvedPuzzleId) return;
    activePuzzleTournamentIdRef.current = resolvedPuzzleId;
    setActivePuzzleTournamentId((current) =>
      String(current || "").trim() === resolvedPuzzleId ? current : resolvedPuzzleId
    );
  }, []);

  useEffect(() => {
    const routePuzzleId = String(id || "").trim();
    if (routePuzzleId) {
      roomSessionTournamentIdRef.current = routePuzzleId;
      syncActivePuzzleTournamentId(routePuzzleId);
    }
  }, [id, syncActivePuzzleTournamentId]);

  const resolveActivePuzzleId = useCallback((rawPuzzleId) => {
    return String(rawPuzzleId || activePuzzleTournamentIdRef.current || id || "").trim();
  }, [id]);

  const resolvePuzzleSequenceMode = useCallback(
    (rawMode = "") =>
      normalizeFreeBoxBaseMode(
        rawMode || tourInfo?.freeBoxBaseMode || tourInfo?.mode || "free",
        "free"
      ),
    [tourInfo?.freeBoxBaseMode, tourInfo?.mode]
  );

  const getOrderedPuzzlesForMode = useCallback(
    async (rawMode = "", options = {}) => {
      const mode = resolvePuzzleSequenceMode(rawMode);
      const forceRefresh = options?.forceRefresh === true;
      const now = Date.now();
      const cachedEntry = PUZZLE_PRELOAD_STATE.orderedByMode[mode];
      if (
        !forceRefresh &&
        cachedEntry &&
        Array.isArray(cachedEntry.entries) &&
        now - Number(cachedEntry.cachedAt || 0) < PUZZLE_PRELOAD_LIST_TTL_MS
      ) {
        return cachedEntry.entries;
      }

      const puzzlesResponse = await axios.get("/tournaments/puzzles", {
        params: { limit: 300 },
      });
      const orderedEntries = toOrderedPuzzleSequenceEntries(
        puzzlesResponse?.data?.puzzles
      ).filter(
        (entry) =>
          resolvePuzzleSequenceMode(entry?.freeBoxBaseMode || entry?.mode || mode) === mode
      );

      PUZZLE_PRELOAD_STATE.orderedByMode[mode] = {
        entries: orderedEntries,
        cachedAt: now,
      };
      return orderedEntries;
    },
    [resolvePuzzleSequenceMode]
  );
  useEffect(() => {
    let cancelled = false;

    const syncPuzzleModeSequenceNumber = async () => {
      if (!isPuzzleMode) {
        setPuzzleModeSequenceNumber(null);
        return;
      }

      const currentPuzzleId = resolveActivePuzzleId(activePuzzleTournamentId);
      if (!currentPuzzleId) {
        setPuzzleModeSequenceNumber(null);
        return;
      }

      try {
        const currentPuzzleMode = resolvePuzzleSequenceMode(
          tourInfo?.freeBoxBaseMode || tourInfo?.mode || ""
        );
        let orderedPuzzles = await getOrderedPuzzlesForMode(currentPuzzleMode);
        let currentIndex = orderedPuzzles.findIndex(
          (entry) => getPuzzleSequenceEntryId(entry) === currentPuzzleId
        );
        if (currentIndex < 0) {
          orderedPuzzles = await getOrderedPuzzlesForMode(currentPuzzleMode, {
            forceRefresh: true,
          });
          currentIndex = orderedPuzzles.findIndex(
            (entry) => getPuzzleSequenceEntryId(entry) === currentPuzzleId
          );
        }

        if (cancelled) return;
        setPuzzleModeSequenceNumber(currentIndex >= 0 ? currentIndex + 1 : null);
      } catch (error) {
        if (cancelled) return;
        setPuzzleModeSequenceNumber(null);
      }
    };

    syncPuzzleModeSequenceNumber().catch(() => {
      if (cancelled) return;
      setPuzzleModeSequenceNumber(null);
    });

    return () => {
      cancelled = true;
    };
  }, [
    activePuzzleTournamentId,
    getOrderedPuzzlesForMode,
    isPuzzleMode,
    resolveActivePuzzleId,
    resolvePuzzleSequenceMode,
    tourInfo?.freeBoxBaseMode,
    tourInfo?.mode,
  ]);

  const updatePuzzleCertificationInCache = useCallback((rawPuzzleId, patch = {}) => {
    const puzzleId = String(rawPuzzleId || "").trim();
    if (!puzzleId) return;

    const safePatch = patch && typeof patch === "object" ? patch : {};
    const orderedModes = Object.keys(PUZZLE_PRELOAD_STATE.orderedByMode || {});
    orderedModes.forEach((modeKey) => {
      const modeEntry = PUZZLE_PRELOAD_STATE.orderedByMode[modeKey];
      if (!modeEntry || !Array.isArray(modeEntry.entries)) return;
      modeEntry.entries = modeEntry.entries.map((entry) => {
        if (getPuzzleSequenceEntryId(entry) !== puzzleId) return entry;
        return { ...entry, ...safePatch };
      });
    });

    const payloadEntry = PUZZLE_PRELOAD_STATE.payloadByPuzzleId[puzzleId];
    if (payloadEntry && payloadEntry.payload && typeof payloadEntry.payload === "object") {
      payloadEntry.payload = {
        ...payloadEntry.payload,
        ...safePatch,
      };
    }
  }, []);

  const reportPuzzleLoadFailure = useCallback(
    async (rawPuzzleId, reason, detail = "") => {
      const puzzleId = String(rawPuzzleId || "").trim();
      if (!puzzleId) return null;

      try {
        const response = await axios.post("/tournaments/puzzles/report-load-failure", {
          puzzleTournamentId: puzzleId,
          reason: String(reason || "snapshot_mismatch").trim() || "snapshot_mismatch",
          detail: String(detail || "").trim().slice(0, 240),
        });
        const puzzleMeta = response?.data?.puzzle || null;
        if (puzzleMeta && typeof puzzleMeta === "object") {
          updatePuzzleCertificationInCache(puzzleId, puzzleMeta);
        }
        return puzzleMeta;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    [updatePuzzleCertificationInCache]
  );

  const findNextReliablePuzzleAhead = useCallback(
    async (rawCurrentPuzzleId, rawMode = "") => {
      const currentPuzzleId = String(rawCurrentPuzzleId || "").trim();
      if (!currentPuzzleId) return "";

      const mode = resolvePuzzleSequenceMode(rawMode);
      let orderedEntries = await getOrderedPuzzlesForMode(mode);
      let currentIndex = orderedEntries.findIndex(
        (entry) => getPuzzleSequenceEntryId(entry) === currentPuzzleId
      );
      if (currentIndex < 0) {
        orderedEntries = await getOrderedPuzzlesForMode(mode, { forceRefresh: true });
        currentIndex = orderedEntries.findIndex(
          (entry) => getPuzzleSequenceEntryId(entry) === currentPuzzleId
        );
      }
      if (currentIndex < 0) return "";

      for (let index = currentIndex + 1; index < orderedEntries.length; index += 1) {
        const candidateEntry = orderedEntries[index];
        const candidatePuzzleId = getPuzzleSequenceEntryId(candidateEntry);
        if (!candidatePuzzleId) continue;
        if (!isReliablePuzzlePlanAEntry(candidateEntry)) continue;
        return candidatePuzzleId;
      }

      return "";
    },
    [getOrderedPuzzlesForMode, resolvePuzzleSequenceMode]
  );

  const preloadUpcomingPuzzles = useCallback(
    async (rawFromPuzzleId, desiredCount = PUZZLE_PRELOAD_INITIAL_AHEAD_COUNT) => {
      if (!isPuzzleMode || isSpectatorRequested) return;

      const fromPuzzleId = String(rawFromPuzzleId || resolveActivePuzzleId()).trim();
      const preloadCount = Math.max(0, Math.floor(Number(desiredCount) || 0));
      if (!fromPuzzleId || preloadCount <= 0) return;

      const activeRoomId = String(roomIdRef.current || roomId || "").trim();
      const roomTournamentId = String(
        roomSessionTournamentIdRef.current ||
          resolveActivePuzzleId() ||
          id ||
          fromPuzzleId
      ).trim();
      if (!activeRoomId || !roomTournamentId) return;

      const mode = resolvePuzzleSequenceMode();
      let orderedEntries = [];
      try {
        orderedEntries = await getOrderedPuzzlesForMode(mode);
      } catch (error) {
        console.log(error);
        return;
      }

      const currentIndex = orderedEntries.findIndex(
        (entry) => getPuzzleSequenceEntryId(entry) === fromPuzzleId
      );
      if (currentIndex < 0) return;

      const targetPuzzleIds = [];
      const now = Date.now();
      for (
        let index = currentIndex + 1;
        index < orderedEntries.length && targetPuzzleIds.length < preloadCount;
        index += 1
      ) {
        const candidatePuzzleId = getPuzzleSequenceEntryId(orderedEntries[index]);
        if (!candidatePuzzleId || candidatePuzzleId === fromPuzzleId) continue;

        const cachedPayloadEntry = PUZZLE_PRELOAD_STATE.payloadByPuzzleId[candidatePuzzleId];
        const isCacheFresh =
          cachedPayloadEntry &&
          cachedPayloadEntry.payload &&
          now - Number(cachedPayloadEntry.cachedAt || 0) < PUZZLE_PRELOAD_PAYLOAD_TTL_MS;
        if (isCacheFresh) continue;
        if (PUZZLE_PRELOAD_STATE.inFlightByPuzzleId[candidatePuzzleId]) continue;
        targetPuzzleIds.push(candidatePuzzleId);
      }

      if (targetPuzzleIds.length === 0) return;

      await Promise.all(
        targetPuzzleIds.map(async (candidatePuzzleId) => {
          PUZZLE_PRELOAD_STATE.inFlightByPuzzleId[candidatePuzzleId] = true;
          try {
            const preloadResponse = await axios.post("/tournaments/puzzles/load", {
              roomTournamentId,
              puzzleTournamentId: candidatePuzzleId,
              roomId: activeRoomId,
              forceReset: false,
              dryRun: true,
            });
            const rawPayload = preloadResponse?.data || {};
            const normalizedSnapshot = normalizePuzzleSnapshotPayload(
              rawPayload?.positionSnapshot ?? rawPayload?.puzzlePositionSnapshot
            );
            const normalizedMoveLog = Array.isArray(rawPayload?.moveLog)
              ? rawPayload.moveLog
                  .map((entry) => String(entry || "").trim())
                  .filter(Boolean)
                  .slice(-MAX_PUZZLE_MOVE_LOG_SIZE)
              : [];
            const normalizedMoveNotations = Array.isArray(rawPayload?.moveNotations)
              ? rawPayload.moveNotations
                  .map((entry) => normalizePuzzleNotationPayloadEntry(entry))
                  .filter((entry) => entry && entry.notation)
                  .slice(-MAX_PUZZLE_MOVE_NOTATION_COUNT)
              : [];

            PUZZLE_PRELOAD_STATE.payloadByPuzzleId[candidatePuzzleId] = {
              payload: {
                ...rawPayload,
                positionSnapshot: normalizedSnapshot,
                moveLog: normalizedMoveLog,
                moveNotations: normalizedMoveNotations,
              },
              cachedAt: Date.now(),
            };

            const payloadEntries = Object.entries(PUZZLE_PRELOAD_STATE.payloadByPuzzleId)
              .sort(
                (a, b) =>
                  Number(b?.[1]?.cachedAt || 0) - Number(a?.[1]?.cachedAt || 0)
              );
            if (payloadEntries.length > PUZZLE_PRELOAD_MAX_CACHED_PAYLOADS) {
              payloadEntries
                .slice(PUZZLE_PRELOAD_MAX_CACHED_PAYLOADS)
                .forEach(([stalePuzzleId]) => {
                  delete PUZZLE_PRELOAD_STATE.payloadByPuzzleId[stalePuzzleId];
                });
            }
          } catch (error) {
            console.log(error);
          } finally {
            delete PUZZLE_PRELOAD_STATE.inFlightByPuzzleId[candidatePuzzleId];
          }
        })
      );
    },
    [
      getOrderedPuzzlesForMode,
      id,
      isPuzzleMode,
      isSpectatorRequested,
      resolveActivePuzzleId,
      resolvePuzzleSequenceMode,
      roomId,
    ]
  );

  const loadPuzzleMetadata = useCallback(
    async (rawPuzzleId, options = {}) => {
      const puzzleId = String(rawPuzzleId || "").trim();
      if (!puzzleId) return false;

      const shouldNavigateOnFailure = options.navigateOnFailure === true;
      setPuzzleMetadataState({
        puzzleId,
        status: PUZZLE_METADATA_STATUS.LOADING,
      });

      try {
        const response = await axios.get(`/tournaments/${puzzleId}`);
        const data = response?.data || {};

        if (data.success === false) {
          setPuzzleMetadataState((current) =>
            current.puzzleId === puzzleId
              ? { puzzleId, status: PUZZLE_METADATA_STATUS.ERROR }
              : current
          );
          if (shouldNavigateOnFailure) {
            navigate("/game");
          }
          return false;
        }

        const currentPuzzleId = String(
          activePuzzleTournamentIdRef.current || id || ""
        ).trim();
        if (currentPuzzleId && currentPuzzleId !== puzzleId) {
          return false;
        }

        setTourInfo(data);
        setPuzzleMetadataState((current) =>
          current.puzzleId === puzzleId
            ? { puzzleId, status: PUZZLE_METADATA_STATUS.READY }
            : current
        );

        const startingTimeValue = new Date(data.start).valueOf();
        if (Number.isFinite(startingTimeValue)) {
          const currentTime = moment();
          const endingTime = moment(
            startingTimeValue + Number(data.duration) * 3600 * 24 * 1000
          );
          setExpired(currentTime.isAfter(endingTime));
        } else {
          setExpired(false);
        }
        return true;
      } catch (error) {
        console.log(error);
        setPuzzleMetadataState((current) =>
          current.puzzleId === puzzleId
            ? { puzzleId, status: PUZZLE_METADATA_STATUS.ERROR }
            : current
        );
        if (shouldNavigateOnFailure) {
          navigate("/game");
        }
        return false;
      }
    },
    [id, navigate]
  );

  useEffect(() => {
    if (!setLayoutNavigationLocked) return undefined;
    const shouldLock = isMatchInProgress && !isSpectatorRequested;
    setLayoutNavigationLocked(shouldLock);
    return () => {
      setLayoutNavigationLocked(false);
    };
  }, [isMatchInProgress, isSpectatorRequested, setLayoutNavigationLocked]);

  useEffect(() => {
    const handleUnityNavigateHome = () => {
      // Cuando la partida ya termino mostramos acciones post-partida en este panel
      // y evitamos navegar automaticamente fuera de la vista.
      if (hasGameEndedRef.current || gameEnded) return;
      const goHome = () =>
        exitTournamentSession()
          .catch((error) => console.log(error))
          .finally(() => navigate("/game"));
      if (isMatchInProgress && !isSpectatorRequested) {
        setLeaveGameRequest({ run: goHome });
        return;
      }
      goHome();
    };

    window.addEventListener("XIMA_NAVIGATE_HOME", handleUnityNavigateHome);
    return () => {
      window.removeEventListener("XIMA_NAVIGATE_HOME", handleUnityNavigateHome);
    };
  }, [exitTournamentSession, gameEnded, isMatchInProgress, isSpectatorRequested, navigate]);

  // Menú de la app (Inicio, Campeonato, Torneos, Ayuda…) durante una partida: pregunta antes.
  useEffect(() => {
    const handleLeaveRequest = (event) => {
      const run = typeof event?.detail?.run === "function" ? event.detail.run : null;
      if (!isMatchInProgress || isSpectatorRequested || hasGameEndedRef.current) {
        run?.();
        return;
      }
      setLeaveGameRequest({ run });
    };
    window.addEventListener("xima:request-leave-game", handleLeaveRequest);
    return () => window.removeEventListener("xima:request-leave-game", handleLeaveRequest);
  }, [isMatchInProgress, isSpectatorRequested]);

  // Botón Atrás del navegador o del teléfono durante una partida: también pregunta.
  useEffect(() => {
    if (!isMatchInProgress || isSpectatorRequested) return undefined;
    window.history.pushState({ ximaLeaveGuard: true }, "", window.location.href);
    const handlePopState = () => {
      if (hasGameEndedRef.current) return;
      window.history.pushState({ ximaLeaveGuard: true }, "", window.location.href);
      setLeaveGameRequest({ run: () => navigate("/game/lobby") });
    };
    const warnBeforeClosing = (event) => {
      if (hasGameEndedRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", warnBeforeClosing);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", warnBeforeClosing);
    };
  }, [isMatchInProgress, isSpectatorRequested, navigate]);

  const goToTournamentWaitingRoom = useCallback(
    (options = {}) => {
      const autoPlay = options?.autoPlay === true;
      unlinkFromCurrentMatch();
      setAutoPlayConsumed(true);
      navigate("/game", {
        replace: true,
        state: {
          openTournamentWaitingId: String(id),
          openTournamentWaitingRequestKey: `${String(id)}-${Date.now()}`,
          ...(autoPlay ? { openTournamentWaitingAutoPlay: true } : {}),
        },
      });
    },
    [id, navigate, unlinkFromCurrentMatch]
  );

  // Al acabar la partida se vuelve a la pantalla por la que se entró (Campeonato,
  // lobby…). Se recuerda por pestaña para que no se pierda al recargar.
  const returnToStorageKey = `xima_return_to_${String(id || "")}`;
  const [returnTo, setReturnTo] = useState(() => {
    const fromState =
      location.state?.returnTo ||
      (location.state?.fromLobbySource === "championship" ? "/game/championship" : "");
    if (fromState) return fromState;
    try {
      return window.sessionStorage.getItem(`xima_return_to_${String(id || "")}`) || "";
    } catch (error) {
      return "";
    }
  });
  useEffect(() => {
    if (!returnTo) return;
    try {
      window.sessionStorage.setItem(returnToStorageKey, returnTo);
    } catch (error) {
      // Without storage the return target only lasts while the page is open.
    }
  }, [returnTo, returnToStorageKey]);
  // Partidas del Campeonato (jugar, mirar en vivo): sus botones de salida vuelven allí.
  const isChampionshipRoom = returnTo === "/game/championship";
  useEffect(() => {
    if (!id) return undefined;
    let canceled = false;
    axios
      .get(`/tournaments/${id}`)
      .then((response) => {
        if (!canceled && response?.data?.championshipMatchId) setReturnTo("/game/championship");
      })
      .catch(() => {});
    return () => {
      canceled = true;
    };
  }, [id]);
  const returnToLabel = isChampionshipRoom
    ? "Volver al Campeonato"
    : returnTo === "/game/lobby"
    ? "Volver al lobby"
    : "";
  const goToChampionship = useCallback(async () => {
    try {
      await exitTournamentSession();
    } catch (error) {
      console.log(error);
    }
    navigate("/game/championship", { replace: true });
  }, [exitTournamentSession, navigate]);

  const goToGameHome = useCallback(async () => {
    if (isChampionshipRoom) {
      await goToChampionship();
      return;
    }
    await exitTournamentSession();
    navigate(returnTo || "/game");
  }, [exitTournamentSession, goToChampionship, isChampionshipRoom, navigate, returnTo]);

  const goToIndependentMatchesLobby = useCallback(async () => {
    try {
      await exitTournamentSession();
    } catch (error) {
      console.log(error);
    }
    navigate("/game/create-game", {
      replace: true,
      state: {
        fromLobbySource: "postGame",
        source: "postGameCreateButton",
      },
    });
  }, [exitTournamentSession, navigate]);

  // «Volver a jugar»: otra partida del mismo modo (la busca o la crea el lobby).
  const playAgainSameMode = useCallback(async () => {
    const mode = normalizeTournamentModeAlias(tourInfo?.mode);
    if (!["free", "dice", "cienzar", "sciencevsazar"].includes(mode)) {
      await goToIndependentMatchesLobby();
      return;
    }
    try {
      await exitTournamentSession();
    } catch (error) {
      console.log(error);
    }
    navigate("/game/lobby", {
      replace: true,
      state: { quickPlayAgainMode: mode, quickPlayAgainKey: `${mode}-${Date.now()}` },
    });
  }, [exitTournamentSession, goToIndependentMatchesLobby, navigate, tourInfo?.mode]);

  const goToPuzzleSection = useCallback(async () => {
    try {
      await exitTournamentSession();
    } catch (error) {
      console.log(error);
    }
    const requestKey = `daily-puzzles-${Date.now()}`;
    navigate(`/game?quickView=daily-puzzles&openMatchmakingQuickViewRequestKey=${requestKey}`, {
      replace: true,
      state: {
        openMatchmakingQuickView: "daily-puzzles",
        openMatchmakingQuickViewRequestKey: requestKey,
      },
    });
  }, [exitTournamentSession, navigate]);

  const openPuzzleFromHomeFlow = useCallback(
    async (targetPuzzleId, flowLabel = "puzzle") => {
      const safePuzzleId = String(targetPuzzleId || "").trim();
      if (!safePuzzleId) return false;

      // Replica la transicion manual: salir a Inicio y luego abrir Resolver.
      try {
        await exitTournamentSession();
      } catch (error) {
        console.log(error);
      }

      const now = Date.now();
      navigate("/game", {
        replace: true,
        state: {
          openMatchmakingQuickView: "puzzles",
          openMatchmakingQuickViewRequestKey: `${flowLabel}-view-${now}`,
          openPuzzleId: safePuzzleId,
          openPuzzleRequestKey: `${flowLabel}-open-${safePuzzleId}-${now}`,
        },
      });
      return true;
    },
    [exitTournamentSession, navigate]
  );

  const resetPuzzleTransitionState = useCallback(() => {
    puzzleLoadCycleRef.current += 1;
    syncPuzzleRuntimeReadyToUnity(false, "");
    setLoadingPuzzleSnapshot(false);
    setPuzzleAttemptCount(0);
    setPuzzleFailedByTurnLimit(false);
    setPuzzleFailedBySolveTimeLimit(false);
    setPuzzleSolveTimerStartedAtMs(0);
    setPuzzleSolveTimerNowMs(Date.now());
    setLastGameEndReason("");
    setGameEnded(false);
    setForcePostGameActions(false);
    setPostGameMessage("");
    hasGameEndedRef.current = false;
    puzzleSnapshotAppliedRef.current = false;
    puzzleSnapshotLoadedKeyRef.current = "";
    puzzleSnapshotLoadingKeyRef.current = "";
    puzzleHasLiveProgressRef.current = false;
    puzzleMoveLogRef.current = [];
    puzzleSolverPlayerIndexRef.current = null;
    puzzleSolverMoveCountRef.current = 0;
    puzzleSolverDiceAttemptMaskRef.current = 0;
    lastPuzzleTurnEventKeyRef.current = "";
    lastPuzzleTurnNotationKeyRef.current = "";
    lastPuzzleTurnEventAtMsRef.current = 0;
    pendingPuzzleSnapshotRequestsRef.current = {};
    pendingPuzzleSnapshotApplyRef.current = {};
    pendingUnityMatchLoadRef.current = {};
    activePuzzleTransitionRef.current = {
      requestId: "",
      puzzleTournamentId: "",
      roomTournamentId: "",
      forceReset: true,
    };
  }, [syncPuzzleRuntimeReadyToUnity]);

  const triggerPuzzleTurnLimitFailure = useCallback((options = {}) => {
    const {
      message = isPuzzleAzarMode ? "No has completado el puzzle" : "Lo siento has fallado",
      reason = "puzzle_turn_limit_reached",
    } = options || {};
    if (!isPuzzleMode || isSpectatorRequested) return;
    if (hasGameEndedRef.current) return;

    clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);
    hasGameEndedRef.current = true;
    setGameEnded(true);
    setPuzzleFailedByTurnLimit(true);
    setPuzzleFailedBySolveTimeLimit(false);
    setLastGameEndReason(String(reason || "puzzle_turn_limit_reached"));
    setForcePostGameActions(true);
    setServerTurnClock({
      turnIndex: 0,
      turnStartedAtMs: 0,
      turnDurationMs: 0,
      remainingMsByIndex: [],
    });
    setServerClockOffsetMs(0);
    setPostGameMessage(
      String(
        message ||
          (isPuzzleAzarMode ? "No has completado el puzzle" : "Lo siento has fallado")
      )
    );
    setChatModalOpen(false);
    setOpen(false);
    setMatchmaking(false);
    setLoading(false);
    void reportPuzzleRewardFailure("", String(reason || "puzzle_turn_limit_reached"));
  }, [
    clearStoredActiveMatchSessionForCurrentUser,
    id,
    isPuzzleAzarMode,
    isPuzzleMode,
    isSpectatorRequested,
    reportPuzzleRewardFailure,
  ]);


  const triggerPuzzleSolveTimeLimitFailure = useCallback(() => {
    if (!isPuzzleMode || isSpectatorRequested) return;
    if (hasGameEndedRef.current) return;

    clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);
    hasGameEndedRef.current = true;
    setGameEnded(true);
    setPuzzleFailedByTurnLimit(false);
    setPuzzleFailedBySolveTimeLimit(true);
    setLastGameEndReason("puzzle_solve_time_expired");
    setForcePostGameActions(true);
    setServerTurnClock({
      turnIndex: 0,
      turnStartedAtMs: 0,
      turnDurationMs: 0,
      remainingMsByIndex: [],
    });
    setServerClockOffsetMs(0);
    setPostGameMessage("Se acabo el tiempo. Intenta de nuevo.");
    setChatModalOpen(false);
    setOpen(false);
    setMatchmaking(false);
    setLoading(false);
    void reportPuzzleRewardFailure("", "puzzle_solve_time_expired");
  }, [clearStoredActiveMatchSessionForCurrentUser, id, isPuzzleMode, isSpectatorRequested, reportPuzzleRewardFailure]);

  useEffect(() => {
    if (!puzzleFailedByTurnLimit && !puzzleFailedBySolveTimeLimit) return;
    if (typeof window === "undefined") return;
    window.XimaSoundManager?.playPuzzleFailed?.();
  }, [puzzleFailedBySolveTimeLimit, puzzleFailedByTurnLimit]);

  useEffect(() => {
    if (!isPuzzleMode || isSpectatorRequested) return undefined;
    if (loadingPuzzleSnapshot || gameEnded || hasGameEndedRef.current) return undefined;
    if (puzzleSolveTimeLimitSeconds <= 0 || puzzleSolveTimerStartedAtMs <= 0) return undefined;

    const updateNow = () => setPuzzleSolveTimerNowMs(Date.now());
    updateNow();
    const timer = setInterval(updateNow, 250);
    return () => clearInterval(timer);
  }, [
    gameEnded,
    isPuzzleMode,
    isSpectatorRequested,
    loadingPuzzleSnapshot,
    puzzleSolveTimeLimitSeconds,
    puzzleSolveTimerStartedAtMs,
  ]);

  useEffect(() => {
    if (!isPuzzleMode || isSpectatorRequested) return;
    if (loadingPuzzleSnapshot || gameEnded || hasGameEndedRef.current) return;
    if (puzzleSolveTimeLimitSeconds <= 0 || puzzleSolveTimerStartedAtMs <= 0) return;
    if (puzzleSolveTimeRemainingSeconds > 0) return;
    triggerPuzzleSolveTimeLimitFailure();
  }, [
    gameEnded,
    isPuzzleMode,
    isSpectatorRequested,
    loadingPuzzleSnapshot,
    puzzleSolveTimeLimitSeconds,
    puzzleSolveTimeRemainingSeconds,
    puzzleSolveTimerStartedAtMs,
    triggerPuzzleSolveTimeLimitFailure,
  ]);

  const registerPuzzleSolverTurn = useCallback(
    (rawPlayerIndex, rawMovePayload = "", rawNotation = "", rawEventInput = null) => {
      if (!isPuzzleMode || isSpectatorRequested) return;
      if (puzzleAttemptLimit <= 0) return;
      if (loadingPuzzleSnapshot) return;
      if (!puzzleSnapshotAppliedRef.current) return;
      if (hasGameEndedRef.current || gameEnded) return;

      const playerIndex = Number(rawPlayerIndex);
      if (!Number.isFinite(playerIndex)) return;
      const normalizedPlayerIndex = Math.max(0, Math.floor(playerIndex));

      const movePayload = String(rawMovePayload || "").trim();
      const notation = String(rawNotation || "")
        .trim()
        .toUpperCase();
      const eventKey = movePayload || `${normalizedPlayerIndex}|${notation}`;
      const notationKey = notation ? `${normalizedPlayerIndex}|${notation}` : "";
      const nowMs = Date.now();
      const duplicatedByKey =
        eventKey && lastPuzzleTurnEventKeyRef.current === eventKey;
      const duplicatedByNotation =
        notationKey && lastPuzzleTurnNotationKeyRef.current === notationKey;
      const duplicatedInsideWindow =
        (duplicatedByKey || duplicatedByNotation) &&
        nowMs - Number(lastPuzzleTurnEventAtMsRef.current || 0) < 350;
      if (duplicatedInsideWindow) return;
      if (eventKey) {
        lastPuzzleTurnEventKeyRef.current = eventKey;
      }
      if (notationKey) {
        lastPuzzleTurnNotationKeyRef.current = notationKey;
      }
      lastPuzzleTurnEventAtMsRef.current = nowMs;

      if (puzzleSolverPlayerIndexRef.current == null) {
        puzzleSolverPlayerIndexRef.current = resolvePuzzleHumanPlayerIndex();
      }

      if (normalizedPlayerIndex !== puzzleSolverPlayerIndexRef.current) {
        const hadPendingDiceTurn = puzzleSolverDiceAttemptMaskRef.current !== 0;
        if (hadPendingDiceTurn) {
          // Solo visual: si el turno con dados cierra por cambio de jugador,
          // reflejamos el intento ya consumido en el marcador.
          const committedAttempts = Math.min(
            Math.max(0, Number(puzzleSolverMoveCountRef.current) || 0),
            puzzleAttemptLimit
          );
          setPuzzleAttemptCount((previous) =>
            previous === committedAttempts ? previous : committedAttempts
          );
        }
        // Si se agoto el limite y cambia de jugador, cerramos el intento pendiente.
        if (
          puzzleSolverDiceAttemptMaskRef.current !== 0 &&
          puzzleSolverMoveCountRef.current >= puzzleAttemptLimit
        ) {
          window.setTimeout(() => {
            if (hasGameEndedRef.current) return;
            if (puzzleSolverMoveCountRef.current < puzzleAttemptLimit) return;
            triggerPuzzleTurnLimitFailure();
          }, 0);
        }
        // Al pasar a otro jugador, se limpia el estado parcial de dados del solver.
        puzzleSolverDiceAttemptMaskRef.current = 0;
        return;
      }
      puzzleHasLiveProgressRef.current = true;

      const parseFiniteNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : NaN;
      };
      const isValidTurnUnit = (value) => value === 0.5 || value === 1;

      const moveParts = movePayload ? movePayload.split("|") : [];
      const payloadRoundRaw = parseFiniteNumber(moveParts[5]);
      const payloadTurnUnitCandidate = parseFiniteNumber(moveParts[6]);
      const payloadTurnUnitRaw = isValidTurnUnit(payloadTurnUnitCandidate)
        ? payloadTurnUnitCandidate
        : NaN;

      const eventEnvelope = resolveMetricsEnvelope(rawEventInput);
      const eventRoot = eventEnvelope.root || {};
      const eventPayload = eventEnvelope.payload || {};
      const eventRoundRaw = parseFiniteNumber(
        eventPayload.roundIndex ??
          eventPayload.round ??
          eventPayload.turnRound ??
          eventPayload.step ??
          eventRoot.roundIndex ??
          eventRoot.round ??
          eventRoot.turnRound ??
          eventRoot.step
      );
      const eventTurnUnitCandidate = parseFiniteNumber(
        eventPayload.turnUnit ??
          eventPayload.turn_unit ??
          eventPayload.unit ??
          eventRoot.turnUnit ??
          eventRoot.turn_unit ??
          eventRoot.unit
      );
      const eventTurnUnitRaw = isValidTurnUnit(eventTurnUnitCandidate)
        ? eventTurnUnitCandidate
        : NaN;

      const resolvedRoundRaw = Number.isFinite(payloadRoundRaw)
        ? payloadRoundRaw
        : eventRoundRaw;
      const resolvedTurnUnitRaw = Number.isFinite(eventTurnUnitRaw)
        ? eventTurnUnitRaw
        : payloadTurnUnitRaw;
      const resolvedRoundIndex = Number.isFinite(resolvedRoundRaw)
        ? Math.max(0, Math.min(1, Math.floor(resolvedRoundRaw)))
        : null;

      const normalizedBaseMode = normalizeFreeBoxBaseMode(
        tourInfo?.freeBoxBaseMode || tourInfo?.mode,
        "free"
      );
      const hasTurnUnit = Number.isFinite(resolvedTurnUnitRaw);
      let isDiceAction = false;
      if (hasTurnUnit) {
        isDiceAction = resolvedTurnUnitRaw < 1;
      } else {
        // Fallback seguro: en modos con dados (dice/cienzar), tratar el evento como accion de dado
        // para evitar contar doble los 2 movimientos internos de un mismo turno.
        isDiceAction = normalizedBaseMode !== "free";
      }

      let shouldCountAttempt = false;
      if (isDiceAction) {
        let diceMask = puzzleSolverDiceAttemptMaskRef.current;
        if (resolvedRoundIndex !== null) {
          const roundBit = resolvedRoundIndex === 0 ? 1 : 2;
          if ((diceMask & roundBit) !== 0) {
            // Nuevo turno de dados detectado (o evento repetido fuera de la ventana corta).
            diceMask = 0;
          }
          if (diceMask === 0) shouldCountAttempt = true;
          diceMask |= roundBit;
          puzzleSolverDiceAttemptMaskRef.current = diceMask;
        } else {
          // Sin roundIndex: contar solo el primer movimiento del turno actual.
          if (diceMask === 0) {
            shouldCountAttempt = true;
            diceMask = 1;
          }
          puzzleSolverDiceAttemptMaskRef.current = diceMask;
        }
      } else {
        puzzleSolverDiceAttemptMaskRef.current = 0;
        shouldCountAttempt = true;
      }
      const schedulePuzzleTurnLimitFailure = () => {
        window.setTimeout(() => {
          if (hasGameEndedRef.current) return;
          if (puzzleSolverMoveCountRef.current < puzzleAttemptLimit) return;
          triggerPuzzleTurnLimitFailure();
        }, 0);
      };
      const commitAttemptCounterVisual = () => {
        const committedAttempts = Math.min(
          Math.max(0, Number(puzzleSolverMoveCountRef.current) || 0),
          puzzleAttemptLimit
        );
        setPuzzleAttemptCount((previous) =>
          previous === committedAttempts ? previous : committedAttempts
        );
      };

      if (!shouldCountAttempt) {
        if (isDiceAction && resolvedRoundIndex !== null) {
          const diceTurnCompleted = puzzleSolverDiceAttemptMaskRef.current === 3;
          if (diceTurnCompleted) {
            // Solo visual: en turnos de dados el intento se muestra gastado
            // cuando termina el segundo movimiento del turno.
            commitAttemptCounterVisual();
          }
        }
        // Caso especial: ultimo intento en modo dados.
        // Si el contador ya llego al limite, esperamos a que se complete el segundo
        // movimiento del turno de dados (mask 3) antes de mostrar "Intentar de nuevo".
        if (
          isDiceAction &&
          resolvedRoundIndex !== null &&
          puzzleSolverMoveCountRef.current >= puzzleAttemptLimit &&
          puzzleSolverDiceAttemptMaskRef.current === 3
        ) {
          schedulePuzzleTurnLimitFailure();
        }
        return;
      }

      const nextMoveCount = puzzleSolverMoveCountRef.current + 1;
      puzzleSolverMoveCountRef.current = nextMoveCount;
      const shouldDelayDiceAttemptVisual = isDiceAction && resolvedRoundIndex !== null;
      if (!shouldDelayDiceAttemptVisual) {
        setPuzzleAttemptCount(Math.min(nextMoveCount, puzzleAttemptLimit));
      }
      if (nextMoveCount < puzzleAttemptLimit) return;
      // Si el limite se alcanza en el primer movimiento de un turno con dados,
      // deferimos el fallo hasta terminar el segundo movimiento del turno.
      if (
        isDiceAction &&
        resolvedRoundIndex !== null &&
        puzzleSolverDiceAttemptMaskRef.current !== 3
      ) {
        return;
      }
      schedulePuzzleTurnLimitFailure();
    },
    [
      gameEnded,
      isPuzzleMode,
      isSpectatorRequested,
      loadingPuzzleSnapshot,
      puzzleLocalPlayerIndex,
      puzzleAttemptLimit,
      tourInfo?.freeBoxBaseMode,
      tourInfo?.mode,
      triggerPuzzleTurnLimitFailure,
    ]
  );

  const certifySavedPuzzleSnapshot = useCallback(
    async ({ puzzleId, roomTournamentId, activeRoomId } = {}) => {
      const resolvedPuzzleId = String(puzzleId || "").trim();
      const resolvedRoomTournamentId = String(roomTournamentId || "").trim();
      const resolvedRoomId = String(activeRoomId || "").trim();
      if (!resolvedPuzzleId || !resolvedRoomTournamentId || !resolvedRoomId) {
        return { certified: false, reason: "missing_context" };
      }

      let snapshotBeforeCertification = null;
      let shouldRestoreSnapshot = false;
      try {
        snapshotBeforeCertification = normalizePuzzleSnapshotPayload(
          await requestPuzzlePositionSnapshotFromUnity({
            timeoutMs: MAX_PUZZLE_SNAPSHOT_WAIT_MS,
          })
        );
        if (!snapshotBeforeCertification) {
          return { certified: false, reason: "missing_pre_cert_snapshot" };
        }

        const dryRunResponse = await axios.post("/tournaments/puzzles/load", {
          roomTournamentId: resolvedRoomTournamentId,
          puzzleTournamentId: resolvedPuzzleId,
          roomId: resolvedRoomId,
          forceReset: false,
          dryRun: true,
        });
        const dryRunPayload = dryRunResponse?.data || {};
        const dryRunSnapshot = normalizePuzzleSnapshotPayload(
          dryRunPayload?.positionSnapshot ?? dryRunPayload?.puzzlePositionSnapshot
        );
        if (!dryRunSnapshot) {
          return { certified: false, reason: "missing_snapshot" };
        }

        shouldRestoreSnapshot = true;
        await applyPuzzlePositionSnapshotToUnity(dryRunSnapshot);
        const expectedBlocks = countPuzzleSnapshotActiveBlocks(dryRunSnapshot);
        if (expectedBlocks > 0) {
          const rendered = await verifyPuzzleSnapshotRenderedInUnity(
            expectedBlocks,
            MAX_PUZZLE_SNAPSHOT_VERIFICATION_TIMEOUT_MS
          );
          if (!rendered.success) {
            throw new Error(
              rendered.observedActiveBlocks > 0
                ? `Unity no renderizo completo el snapshot de certificacion (${rendered.observedActiveBlocks}/${expectedBlocks}).`
                : "Unity no renderizo el snapshot de certificacion."
            );
          }
        }

        const liveSnapshot = normalizePuzzleSnapshotPayload(
          await requestPuzzlePositionSnapshotFromUnity({
            timeoutMs: MAX_PUZZLE_SNAPSHOT_WAIT_MS,
          })
        );
        if (!liveSnapshot) {
          throw new Error("No se pudo recapturar el snapshot para certificacion.");
        }

        const liveHash = normalizePuzzleSnapshotHash(await computePuzzleSnapshotHash(liveSnapshot));
        const dryRunHash = normalizePuzzleSnapshotHash(
          await computePuzzleSnapshotHash(dryRunSnapshot)
        );
        const storedHash = normalizePuzzleSnapshotHash(dryRunPayload?.puzzleSnapshotHash);
        const targetHash = storedHash || dryRunHash;
        const expectedBoardVersion = normalizePuzzleBoardVersion(
          dryRunPayload?.puzzleBoardVersion || dryRunSnapshot?.boardVersion
        );
        const observedBoardVersion = normalizePuzzleBoardVersion(liveSnapshot?.boardVersion);
        const hashMatch = Boolean(targetHash) && Boolean(liveHash) && liveHash === targetHash;
        const boardMatch =
          !expectedBoardVersion ||
          !observedBoardVersion ||
          observedBoardVersion === expectedBoardVersion;

        if (!hashMatch || !boardMatch) {
          await reportPuzzleLoadFailure(
            resolvedPuzzleId,
            "certification_mismatch",
            `expected_hash=${targetHash || "none"} live_hash=${liveHash || "none"} expected_board=${expectedBoardVersion || "none"} live_board=${observedBoardVersion || "none"}`
          );
          return {
            certified: false,
            reason: "hash_or_board_mismatch",
            expectedHash: targetHash,
            liveHash,
          };
        }

        const certificationResponse = await axios.post("/tournaments/puzzles/certify", {
          puzzleTournamentId: resolvedPuzzleId,
          snapshotHash: liveHash,
          boardVersion: observedBoardVersion || expectedBoardVersion,
        });
        const certifiedPuzzleMeta = certificationResponse?.data?.puzzle || {};
        updatePuzzleCertificationInCache(resolvedPuzzleId, certifiedPuzzleMeta);
        return { certified: true, snapshotHash: liveHash };
      } catch (error) {
        console.log(error);
        await reportPuzzleLoadFailure(
          resolvedPuzzleId,
          "certification_error",
          String(error?.message || "unknown_error")
        );
        return {
          certified: false,
          reason: "certification_error",
          error,
        };
      } finally {
        if (!shouldRestoreSnapshot || !snapshotBeforeCertification) return;
        try {
          await applyPuzzlePositionSnapshotToUnity(snapshotBeforeCertification);
        } catch (restoreError) {
          console.log(restoreError);
        }
      }
    },
    [
      applyPuzzlePositionSnapshotToUnity,
      reportPuzzleLoadFailure,
      requestPuzzlePositionSnapshotFromUnity,
      updatePuzzleCertificationInCache,
      verifyPuzzleSnapshotRenderedInUnity,
    ]
  );

  const saveCurrentPositionAsPuzzle = useCallback(
    async (
      rawPuzzleName,
      rawPuzzleTurnLimit,
      rawPuzzleRewardValue = DEFAULT_PUZZLE_REWARD_VALUE,
      rawPuzzleSolveTimeLimitSeconds = 0
    ) => {
      if (isSpectatorRequested) return false;
      if (!canCreatePuzzleSnapshots) {
        notification.warning({
          message: i18nT("lobby.soloSuperusuarios"),
          description:
            i18nT("tournaments.porAhoraSoloSuperusuariosPuedenGuardarPosicic855e6"),
        });
        return false;
      }
      if (normalizedMode !== "freebox") {
        notification.warning({
          message: i18nT("tournaments.soloEnFreebox"),
          description: i18nT("tournaments.guardarPosicionSoloEstaDisponibleEnPartidasF6773cf"),
        });
        return false;
      }

      const activeRoomId = String(roomIdRef.current || roomId || "").trim();
      if (!activeRoomId) {
        notification.warning({
          message: i18nT("tournaments.partidaNoIniciada"),
          description: i18nT("tournaments.primeroIniciaLaPartidaParaGuardarUnaPosicion"),
        });
        return false;
      }

      const resolvedPuzzleTurnLimit = normalizePuzzleTurnLimit(rawPuzzleTurnLimit);
      if (resolvedPuzzleTurnLimit <= 0) {
        notification.warning({
          message: i18nT("lobby.turnosRequeridos"),
          description: i18nT("tournaments.escribeUnaCantidadDeTurnosValidaMinimo"),
        });
        return false;
      }
      const resolvedPuzzleRewardValue = normalizePuzzleRewardValue(
        rawPuzzleRewardValue,
        DEFAULT_PUZZLE_REWARD_VALUE
      );
      const resolvedPuzzleSolveTimeLimitSeconds = normalizePuzzleSolveTimeLimitSeconds(
        rawPuzzleSolveTimeLimitSeconds,
        0
      );
      const resolvedPuzzleName =
        String(rawPuzzleName || "").trim().slice(0, 120) ||
        buildDefaultPuzzleNameFromRules(
          resolvedPuzzleTurnLimit,
          resolvedPuzzleSolveTimeLimitSeconds
        ).slice(0, 120);

      const allowedTimes = new Set([3, 5, 10, 15, 30, 60]);
      const resolvedBaseMode = normalizeFreeBoxBaseMode(
        tourInfo?.freeBoxBaseMode,
        "free"
      );
      const requestedHavingTime = Number(tourInfo?.havingTime);
      const resolvedHavingTime = allowedTimes.has(requestedHavingTime)
        ? requestedHavingTime
        : 3;

      try {
        setSavingPuzzleSnapshot(true);
        await waitForPuzzleMoveLogStability();

        const rawMoveLog = Array.isArray(puzzleMoveLogRef.current)
          ? puzzleMoveLogRef.current
          : [];
        const coverage = buildPuzzleBlockCoverageSummary(rawMoveLog);
        const moveLog = coverage.sanitizedMoveLog;
        const expectedBlocksFromMoves = Math.max(0, Number(coverage.blockCount) || 0);
        const rawMoveNotations = Array.isArray(moveNotationsRef.current)
          ? moveNotationsRef.current
          : [];
        const moveNotations = rawMoveNotations
          .map((entry) => {
            if (typeof entry === "string") {
              const notation = normalizeXimaNotation(entry);
              return notation ? { notation } : null;
            }

            if (typeof entry !== "object" || !entry) return null;

            const notation = normalizeXimaNotation(entry.notation || "");
            if (!notation) return null;
            const rawPayloadText = String(entry.payload || entry.movePayload || "").trim();
            const payload = parseMoveDataPipePayload(rawPayloadText)
              ? rawPayloadText
              : "";
            const playerIndexRaw = Number(entry.playerIndex);
            const playerIndex = Number.isFinite(playerIndexRaw)
              ? Math.max(0, Math.floor(playerIndexRaw))
              : 0;

            if (!payload) {
              return { notation };
            }

            return {
              notation,
              payload,
              playerIndex,
            };
          })
          .filter(Boolean)
          .slice(-MAX_PUZZLE_MOVE_NOTATION_COUNT);

        let positionSnapshot = null;
        let snapshotActiveBlocks = 0;
        let snapshotCaptureError = null;
        try {
          const maxAttempts = expectedBlocksFromMoves > 0 ? 3 : 1;
          for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            const unitySnapshot = await requestPuzzlePositionSnapshotFromUnity();
            const normalizedSnapshot = normalizePuzzleSnapshotPayload(unitySnapshot);
            const activeBlocksInAttempt = countPuzzleSnapshotActiveBlocks(normalizedSnapshot);

            if (normalizedSnapshot && activeBlocksInAttempt > snapshotActiveBlocks) {
              positionSnapshot = normalizedSnapshot;
              snapshotActiveBlocks = activeBlocksInAttempt;
            }

            if (snapshotActiveBlocks > 0 && snapshotActiveBlocks >= expectedBlocksFromMoves) {
              break;
            }

            if (attempt < maxAttempts - 1) {
              await new Promise((resolve) => setTimeout(resolve, 180));
            }
          }
        } catch (snapshotError) {
          snapshotCaptureError = snapshotError;
          console.log(snapshotError);
        }

        if (positionSnapshot && snapshotActiveBlocks <= 0) {
          positionSnapshot = null;
          snapshotActiveBlocks = 0;
        }

        const snapshotCoverageIncomplete =
          expectedBlocksFromMoves > 0 && snapshotActiveBlocks < expectedBlocksFromMoves;
        if (!positionSnapshot || snapshotActiveBlocks <= 0 || snapshotCoverageIncomplete) {
          const captureReason = String(snapshotCaptureError?.message || "").trim();
          notification.warning({
            message: i18nT("tournaments.posicionIncompleta"),
            description:
              captureReason
                ? `${i18nT(
                    "tournaments.noSePudoCapturarUnSnapshotValidoHaz"
                  )} (${captureReason})`
                : i18nT("tournaments.noSePudoCapturarUnSnapshotValidoHaz"),
          });
          return false;
        }

        const response = await axios.post("/tournaments/puzzles", {
          sourceTournamentId: resolveActivePuzzleId(),
          sourceRoomId: activeRoomId,
          puzzleName: resolvedPuzzleName,
          puzzleTurnLimit: resolvedPuzzleTurnLimit,
          puzzleRewardValue: resolvedPuzzleRewardValue,
          puzzleSolveTimeLimitSeconds: resolvedPuzzleSolveTimeLimitSeconds,
          freeBoxBaseMode: resolvedBaseMode,
          havingTime: resolvedHavingTime,
          memberSize: Math.max(2, Math.min(4, Number(rightPanelTotalPlayers) || 2)),
          positionSnapshot,
          moveLog,
          moveNotations,
        });
        const createdPuzzle = response?.data?.puzzle || {};
        const puzzleId = String(createdPuzzle?.id || "").trim();
        const savedPuzzleName =
          String(createdPuzzle?.tournamentName || resolvedPuzzleName).trim() ||
          resolvedPuzzleName;

        let certificationResult = null;
        if (puzzleId && positionSnapshot) {
          certificationResult = await certifySavedPuzzleSnapshot({
            puzzleId,
            roomTournamentId: resolveActivePuzzleId(),
            activeRoomId,
          });
        }
        const initialStatus = normalizePuzzleCertificationStatus(
          createdPuzzle?.puzzleCertificationStatus,
          positionSnapshot ? "pending" : "fallback"
        );
        const isCertified = Boolean(certificationResult?.certified);
        const certificationLabel = isCertified
          ? "certificado"
          : initialStatus === "fallback"
          ? "fallback"
          : "pendiente";

        notification.success({
          message: i18nT("tournaments.puzzleGuardado"),
          description: puzzleId
            ? snapshotActiveBlocks > 0
              ? `La posicion (${snapshotActiveBlocks} bloques) se guardo como "${savedPuzzleName}" (#${puzzleId.slice(-6)}), recompensa ${resolvedPuzzleRewardValue}X, estado ${certificationLabel}.`
              : `La posicion se guardo como "${savedPuzzleName}" (#${puzzleId.slice(-6)}), recompensa ${resolvedPuzzleRewardValue}X, estado ${certificationLabel}.`
            : `La posicion actual se guardo como "${savedPuzzleName}".`,
        });

        if (positionSnapshot && !isCertified) {
          notification.warning({
            message: i18nT("tournaments.puzzleEnCuarentenaOPendiente"),
            description:
              certificationResult?.reason === "missing_snapshot"
                ? "No se pudo recargar el snapshot para certificar; quedo pendiente."
                : certificationResult?.reason === "hash_or_board_mismatch"
                ? "El hash final no coincidio con el guardado. El puzzle fue marcado como no confiable."
                : "No se pudo certificar en este intento. Quedo pendiente o en cuarentena para revalidar.",
            duration: 6,
          });
        }
        return true;
      } catch (error) {
        console.log(error);
        notification.error({
          message: i18nT("tournaments.noSePudoGuardar"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo guardar la posicion actual como puzzle.",
        });
        return false;
      } finally {
        setSavingPuzzleSnapshot(false);
      }
    },
    [
      canCreatePuzzleSnapshots,
      isSpectatorRequested,
      normalizedMode,
      rightPanelTotalPlayers,
      roomId,
      tourInfo,
      certifySavedPuzzleSnapshot,
      resolveActivePuzzleId,
      requestPuzzlePositionSnapshotFromUnity,
      waitForPuzzleMoveLogStability,
    ]
  );

  const openPuzzleNameModalForSave = useCallback(() => {
    if (isSpectatorRequested) return;
    if (!canCreatePuzzleSnapshots) {
      notification.warning({
        message: i18nT("lobby.soloSuperusuarios"),
        description:
          i18nT("tournaments.porAhoraSoloSuperusuariosPuedenGuardarPosicic855e6"),
      });
      return;
    }
    if (normalizedMode !== "freebox") {
      notification.warning({
        message: i18nT("tournaments.soloEnFreebox"),
        description: i18nT("tournaments.guardarPosicionSoloEstaDisponibleEnPartidasF6773cf"),
      });
      return;
    }

    const activeRoomId = String(roomIdRef.current || roomId || "").trim();
    if (!activeRoomId) {
      notification.warning({
        message: i18nT("tournaments.partidaNoIniciada"),
        description: i18nT("tournaments.primeroIniciaLaPartidaParaGuardarUnaPosicion"),
      });
      return;
    }

    const defaultTurnLimit = Math.max(
      1,
      normalizePuzzleTurnLimit(tourInfo?.puzzleTurnLimit, DEFAULT_PUZZLE_TURN_LIMIT)
    );
    setPuzzleTurnLimitDraft(defaultTurnLimit);
    setPuzzleRewardValueDraft(resolvePuzzleRewardValueFromRecord(tourInfo));
    const defaultSolveTimeLimitSeconds = resolvePuzzleSolveTimeLimitSecondsFromRecord(tourInfo);
    let defaultSolveTimeLimitEnabled = false;
    let defaultSolveTimeLimitMinutes = DEFAULT_PUZZLE_SOLVE_TIME_MINUTES;
    if (defaultSolveTimeLimitSeconds > 0) {
      defaultSolveTimeLimitEnabled = true;
      defaultSolveTimeLimitMinutes = Math.max(
        1,
        Math.min(
          MAX_PUZZLE_SOLVE_TIME_MINUTES,
          Math.ceil(defaultSolveTimeLimitSeconds / 60)
        )
      );
      setPuzzleSolveTimeLimitEnabledDraft(true);
      setPuzzleSolveTimeLimitMinutesDraft(defaultSolveTimeLimitMinutes);
    } else {
      setPuzzleSolveTimeLimitEnabledDraft(false);
      setPuzzleSolveTimeLimitMinutesDraft(DEFAULT_PUZZLE_SOLVE_TIME_MINUTES);
    }
    setPuzzleNameDraft(
      buildDefaultPuzzleNameFromRules(
        defaultTurnLimit,
        defaultSolveTimeLimitEnabled ? defaultSolveTimeLimitMinutes * 60 : 0
      ).slice(0, 120)
    );
    setPuzzleNameTouchedDraft(false);
    setPuzzleNameModalOpen(true);
  }, [
    canCreatePuzzleSnapshots,
    isSpectatorRequested,
    normalizedMode,
    roomId,
    tourInfo,
  ]);

  useEffect(() => {
    if (!puzzleNameModalOpen) return;
    if (puzzleNameTouchedDraft) return;

    const resolvedTurnLimit = Math.max(
      1,
      normalizePuzzleTurnLimit(puzzleTurnLimitDraft, DEFAULT_PUZZLE_TURN_LIMIT)
    );
    const resolvedSolveTimeLimitSeconds = puzzleSolveTimeLimitEnabledDraft
      ? normalizePuzzleSolveTimeLimitSeconds(Number(puzzleSolveTimeLimitMinutesDraft || 0) * 60, 0)
      : 0;
    const nextAutoName = buildDefaultPuzzleNameFromRules(
      resolvedTurnLimit,
      resolvedSolveTimeLimitSeconds
    ).slice(0, 120);

    if (String(puzzleNameDraft || "").trim() === nextAutoName) return;
    setPuzzleNameDraft(nextAutoName);
  }, [
    puzzleNameDraft,
    puzzleNameModalOpen,
    puzzleNameTouchedDraft,
    puzzleSolveTimeLimitEnabledDraft,
    puzzleSolveTimeLimitMinutesDraft,
    puzzleTurnLimitDraft,
  ]);

  const closePuzzleNameModal = useCallback(() => {
    if (savingPuzzleSnapshot) return;
    setPuzzleNameModalOpen(false);
    setPuzzleNameDraft("");
    setPuzzleNameTouchedDraft(false);
    setPuzzleTurnLimitDraft(DEFAULT_PUZZLE_TURN_LIMIT);
    setPuzzleRewardValueDraft(DEFAULT_PUZZLE_REWARD_VALUE);
    setPuzzleSolveTimeLimitEnabledDraft(false);
    setPuzzleSolveTimeLimitMinutesDraft(DEFAULT_PUZZLE_SOLVE_TIME_MINUTES);
  }, [savingPuzzleSnapshot]);

  const confirmPuzzleNameAndSave = useCallback(async () => {
    const resolvedDraftTurnLimit = normalizePuzzleTurnLimit(
      puzzleTurnLimitDraft,
      DEFAULT_PUZZLE_TURN_LIMIT
    );
    if (resolvedDraftTurnLimit <= 0) {
      notification.warning({
        message: i18nT("lobby.turnosRequeridos"),
        description: i18nT("tournaments.escribeUnaCantidadDeTurnosValidaMinimo"),
      });
      return;
    }
    const resolvedDraftRewardValue = normalizePuzzleRewardValue(
      puzzleRewardValueDraft,
      DEFAULT_PUZZLE_REWARD_VALUE
    );
    const resolvedDraftSolveTimeLimitSeconds = puzzleSolveTimeLimitEnabledDraft
      ? normalizePuzzleSolveTimeLimitSeconds(
          Number(puzzleSolveTimeLimitMinutesDraft || 0) * 60,
          0
        )
      : 0;
    if (puzzleSolveTimeLimitEnabledDraft && resolvedDraftSolveTimeLimitSeconds <= 0) {
      notification.warning({
        message: i18nT("lobby.tiempoRequerido"),
        description:
          i18nT("lobby.defineUnTiempoDeSolucionValidoEnMinutos"),
      });
      return;
    }
    const resolvedDraftPuzzleName =
      String(puzzleNameDraft || "").trim() ||
      buildDefaultPuzzleNameFromRules(
        resolvedDraftTurnLimit,
        resolvedDraftSolveTimeLimitSeconds
      ).slice(0, 120);

    const didSave = await saveCurrentPositionAsPuzzle(
      resolvedDraftPuzzleName,
      resolvedDraftTurnLimit,
      resolvedDraftRewardValue,
      resolvedDraftSolveTimeLimitSeconds
    );
    if (!didSave) return;
    setPuzzleNameModalOpen(false);
    setPuzzleNameDraft("");
    setPuzzleNameTouchedDraft(false);
    setPuzzleTurnLimitDraft(DEFAULT_PUZZLE_TURN_LIMIT);
    setPuzzleRewardValueDraft(DEFAULT_PUZZLE_REWARD_VALUE);
    setPuzzleSolveTimeLimitEnabledDraft(false);
    setPuzzleSolveTimeLimitMinutesDraft(DEFAULT_PUZZLE_SOLVE_TIME_MINUTES);
  }, [
    puzzleNameDraft,
    puzzleRewardValueDraft,
    puzzleSolveTimeLimitEnabledDraft,
    puzzleSolveTimeLimitMinutesDraft,
    puzzleTurnLimitDraft,
    saveCurrentPositionAsPuzzle,
  ]);

  const loadPuzzleSnapshotIntoRoom = useCallback(async (options = {}) => {
    if (isSpectatorRequested) return;
    if (normalizedMode !== "freebox") return;
    if (!isPuzzleTournament && !isPuzzleRequested) return;

    const currentLoadCycle = puzzleLoadCycleRef.current;
    const targetPuzzleTournamentId = String(
      options.puzzleTournamentId ||
        activePuzzleTournamentIdRef.current ||
        resolveActivePuzzleId() ||
        id ||
        ""
    ).trim();
    const roomTournamentId = String(
      options.roomTournamentId ||
        roomSessionTournamentIdRef.current ||
        resolveActivePuzzleId() ||
        id ||
        targetPuzzleTournamentId ||
        ""
    ).trim();
    const activeRoomId = String(roomIdRef.current || roomId || "").trim();
    const forceReset = options.forceReset === true;
    const ignoreBlockedAutoLoad = options.ignoreBlockedAutoLoad === true;
    const transitionRequestId = String(options.transitionRequestId || "").trim();
    const loadKey = buildPuzzleSnapshotLoadKey(
      targetPuzzleTournamentId,
      roomTournamentId,
      activeRoomId
    );
    const allowPuzzleReload = forceReset || Boolean(transitionRequestId);
    if (!activeRoomId) return;
    if (!targetPuzzleTournamentId || !roomTournamentId) return;
    if (!loadKey) return;
    if (!allowPuzzleReload) {
      if (puzzleHasLiveProgressRef.current) return;
      if (
        puzzleSnapshotAppliedRef.current &&
        puzzleSnapshotLoadedKeyRef.current === loadKey
      ) {
        return;
      }
      if (puzzleSnapshotLoadingKeyRef.current === loadKey) return;
    }
    if (
      !ignoreBlockedAutoLoad &&
      puzzleAutoLoadBlockedByIdRef.current[targetPuzzleTournamentId] === true
    ) {
      return;
    }
    if (currentLoadCycle !== puzzleLoadCycleRef.current) return;
    if (transitionRequestId) {
      const activeTransition = activePuzzleTransitionRef.current || {};
      if (activeTransition.requestId !== transitionRequestId) return;
      if (
        activeTransition.puzzleTournamentId &&
        activeTransition.puzzleTournamentId !== targetPuzzleTournamentId
      ) {
        return;
      }
      if (
        activeTransition.roomTournamentId &&
        activeTransition.roomTournamentId !== roomTournamentId
      ) {
        return;
      }
    }

    try {
      puzzleSnapshotLoadingKeyRef.current = loadKey;
      syncPuzzleRuntimeContextToUnity({
        isPuzzle: true,
        freeBoxBaseMode: tourInfo?.freeBoxBaseMode,
        puzzleId: targetPuzzleTournamentId,
        roomTournamentId,
      });
      syncPuzzleRuntimeReadyToUnity(false, targetPuzzleTournamentId);
      puzzleHasLiveProgressRef.current = false;
      setLoadingPuzzleSnapshot(true);
      setPuzzleAttemptCount(0);
      setPuzzleFailedByTurnLimit(false);
      setPuzzleFailedBySolveTimeLimit(false);
      setPuzzleSolveTimerStartedAtMs(0);
      setPuzzleSolveTimerNowMs(Date.now());
      puzzleSolverPlayerIndexRef.current = null;
      puzzleSolverMoveCountRef.current = 0;
      puzzleSolverDiceAttemptMaskRef.current = 0;
      lastPuzzleTurnEventKeyRef.current = "";
      lastPuzzleTurnNotationKeyRef.current = "";
      lastPuzzleTurnEventAtMsRef.current = 0;
      const iframeReady = await waitForUnityIframeReady();
      if (!iframeReady) {
        throw new Error("Unity aun no esta listo para aplicar el puzzle.");
      }
      await new Promise((resolve) => setTimeout(resolve, 220));
      const preloadedPayloadEntry =
        PUZZLE_PRELOAD_STATE.payloadByPuzzleId[targetPuzzleTournamentId];
      const preloadedPayloadIsFresh =
        preloadedPayloadEntry &&
        preloadedPayloadEntry.payload &&
        Date.now() - Number(preloadedPayloadEntry.cachedAt || 0) <
          PUZZLE_PRELOAD_PAYLOAD_TTL_MS;
      const preloadedPayloadRoomTournamentId = String(
        preloadedPayloadEntry?.payload?.roomTournamentId ||
          preloadedPayloadEntry?.payload?.tournamentId ||
          ""
      ).trim();
      const preloadMatchesCurrentRoomTournament =
        !preloadedPayloadRoomTournamentId ||
        preloadedPayloadRoomTournamentId === roomTournamentId;
      if (preloadedPayloadIsFresh && !preloadMatchesCurrentRoomTournament) {
        delete PUZZLE_PRELOAD_STATE.payloadByPuzzleId[targetPuzzleTournamentId];
      }
      // Usamos preload solo cuando trae snapshot completo; si solo trae moveLog
      // dejamos que el endpoint oficial sincronice la sala de juego en backend.
      const canUsePreloadedPayload =
        preloadedPayloadIsFresh &&
        preloadMatchesCurrentRoomTournament &&
        preloadedPayloadEntry?.payload?.positionSnapshot;
      const responsePayload = canUsePreloadedPayload
        ? preloadedPayloadEntry.payload
        : (
            await axios.post("/tournaments/puzzles/load", {
              roomTournamentId,
              puzzleTournamentId: targetPuzzleTournamentId,
              roomId: activeRoomId,
              forceReset,
            })
          )?.data || {};
      if (currentLoadCycle !== puzzleLoadCycleRef.current) return;
      if (transitionRequestId) {
        const activeTransition = activePuzzleTransitionRef.current || {};
        if (activeTransition.requestId !== transitionRequestId) return;
      }
      const responsePuzzleTournamentId = String(
        responsePayload?.puzzleTournamentId || ""
      ).trim();
      const responseRoomTournamentId = String(
        responsePayload?.roomTournamentId || responsePayload?.tournamentId || ""
      ).trim();
      if (
        responsePuzzleTournamentId &&
        responsePuzzleTournamentId !== targetPuzzleTournamentId
      ) {
        throw new Error(
          "La respuesta del servidor no corresponde al puzzle solicitado."
        );
      }
      if (responseRoomTournamentId && responseRoomTournamentId !== roomTournamentId) {
        throw new Error(
          "La respuesta del servidor no corresponde a la sala solicitada."
        );
      }

      const responsePositionSnapshot = normalizePuzzleSnapshotPayload(
        responsePayload?.positionSnapshot ?? responsePayload?.puzzlePositionSnapshot
      );
      if (responsePositionSnapshot) {
        puzzleSolverPlayerIndexRef.current = resolvePuzzleHumanPlayerIndex();
      }
      const responseMoveLog = Array.isArray(responsePayload?.moveLog)
        ? responsePayload.moveLog
            .map((entry) => String(entry || "").trim())
            .filter(Boolean)
            .slice(-MAX_PUZZLE_MOVE_LOG_SIZE)
        : [];
      const responseMoveNotations = Array.isArray(responsePayload?.moveNotations)
        ? responsePayload.moveNotations
            .map((entry) => normalizePuzzleNotationPayloadEntry(entry))
            .filter((entry) => entry && entry.notation && entry.playerIndex >= 0)
            .slice(-MAX_PUZZLE_MOVE_NOTATION_COUNT)
        : [];
      const responseMoveLogFromNotations = responseMoveNotations
        .map((entry) => entry.payload || "")
        .filter((payload) => parseMoveDataPipePayload(payload))
        .map((payload) => String(payload).trim())
        .slice(-MAX_PUZZLE_MOVE_LOG_SIZE);
      const replayMoveLog =
        responseMoveLog.length > 0 ? responseMoveLog : responseMoveLogFromNotations;
      const responsePuzzleSnapshotHash = normalizePuzzleSnapshotHash(
        responsePayload?.puzzleSnapshotHash
      );
      const responsePuzzleBoardVersion = normalizePuzzleBoardVersion(
        responsePayload?.puzzleBoardVersion
      );
      const responsePuzzleCertificationStatus = normalizePuzzleCertificationStatus(
        responsePayload?.puzzleCertificationStatus,
        responsePositionSnapshot ? "pending" : "fallback"
      );
      const hasResponsePuzzleTurnLimit =
        Object.prototype.hasOwnProperty.call(responsePayload || {}, "puzzleTurnLimit");
      const responsePuzzleTurnLimit = normalizePuzzleTurnLimit(responsePayload?.puzzleTurnLimit);
      const hasResponsePuzzleRewardValue = [
        "puzzleRewardValue",
        "rewardValue",
        "rewardAmount",
        "reward",
        "rewardX",
      ].some((key) => Object.prototype.hasOwnProperty.call(responsePayload || {}, key));
      const responsePuzzleRewardValue = hasResponsePuzzleRewardValue
        ? normalizePuzzleRewardValue(
            responsePayload?.puzzleRewardValue ??
              responsePayload?.rewardValue ??
              responsePayload?.rewardAmount ??
              responsePayload?.reward ??
              responsePayload?.rewardX,
            puzzleRewardValue
          )
        : puzzleRewardValue;
      const hasResponsePuzzleSolveTimeLimit = [
        "puzzleSolveTimeLimitSeconds",
        "puzzleSolveTimeSeconds",
        "solveTimeSeconds",
        "timeLimitSeconds",
      ].some((key) => Object.prototype.hasOwnProperty.call(responsePayload || {}, key));
      const responsePuzzleSolveTimeLimitSeconds = hasResponsePuzzleSolveTimeLimit
        ? normalizePuzzleSolveTimeLimitSeconds(
            responsePayload?.puzzleSolveTimeLimitSeconds ??
              responsePayload?.puzzleSolveTimeSeconds ??
              responsePayload?.solveTimeSeconds ??
              responsePayload?.timeLimitSeconds,
            0
          )
        : resolvePuzzleSolveTimeLimitSecondsFromRecord(tourInfo);
      if (
        hasResponsePuzzleTurnLimit ||
        hasResponsePuzzleRewardValue ||
        hasResponsePuzzleSolveTimeLimit
      ) {
        setTourInfo((previous) => ({
          ...previous,
          ...(hasResponsePuzzleTurnLimit ? { puzzleTurnLimit: responsePuzzleTurnLimit } : {}),
          ...(hasResponsePuzzleRewardValue
            ? { puzzleRewardValue: responsePuzzleRewardValue }
            : {}),
          ...(hasResponsePuzzleSolveTimeLimit
            ? { puzzleSolveTimeLimitSeconds: responsePuzzleSolveTimeLimitSeconds }
            : {}),
        }));
      }
      if (
        targetPuzzleTournamentId &&
        (responsePuzzleSnapshotHash ||
          responsePuzzleBoardVersion ||
          responsePayload?.puzzleCertificationStatus)
      ) {
        updatePuzzleCertificationInCache(targetPuzzleTournamentId, {
          puzzleSnapshotHash: responsePuzzleSnapshotHash,
          puzzleBoardVersion: responsePuzzleBoardVersion,
          puzzleCertificationStatus: responsePuzzleCertificationStatus,
          puzzleCertifiedAt: responsePayload?.puzzleCertifiedAt || null,
          puzzleFailureCount: Math.max(0, Number(responsePayload?.puzzleFailureCount) || 0),
          puzzleLastFailureAt: responsePayload?.puzzleLastFailureAt || null,
          puzzleLastFailureReason: String(responsePayload?.puzzleLastFailureReason || "").trim(),
        });
      }
      let appliedMoves = Math.max(0, Number(responsePayload?.appliedMoves) || 0);
      let loadedFromSnapshot = false;
      let appliedFromMoveLogPrimary = false;
      let moveLogPrimaryError = null;

      if (responsePositionSnapshot || replayMoveLog.length > 0) {
        await waitForUnityPuzzleBoardReady({
          timeoutMs: MAX_PUZZLE_BOARD_READY_WAIT_MS,
        });
        if (currentLoadCycle !== puzzleLoadCycleRef.current) return;
      }

      // Plan A primero: usar snapshot (si viene) para estado inmediato y preciso del puzzle.
      // Plan B queda como respaldo para puzzles legacy sin snapshot o cuando snapshot no cargue.
      if (replayMoveLog.length > 0 && !responsePositionSnapshot) {
        try {
          if (currentLoadCycle !== puzzleLoadCycleRef.current) return;
          const localAppliedMoves = await applyPuzzleMoveLogToUnity(replayMoveLog);
          appliedMoves = Math.max(appliedMoves, localAppliedMoves);
          puzzleMoveLogRef.current = replayMoveLog;
          appliedFromMoveLogPrimary = true;
        } catch (moveLogError) {
          moveLogPrimaryError = moveLogError;
          console.log(moveLogError);
        }
      }

      // Plan B como fallback: snapshot (si existe) y luego se rompe/corrige si no aplica.
      if (responsePositionSnapshot && !appliedFromMoveLogPrimary) {
        const expectedSnapshotBlocks = countPuzzleSnapshotActiveBlocks(
          responsePositionSnapshot
        );
        const snapshotVersion = Math.max(0, Number(responsePositionSnapshot?.version) || 0);
        const snapshotCubes = (Array.isArray(responsePositionSnapshot?.players)
          ? responsePositionSnapshot.players
          : []
        ).flatMap((playerEntry) =>
          Array.isArray(playerEntry?.cubes) ? playerEntry.cubes : []
        );
        const hasStableCubeIds =
          snapshotCubes.length > 0 &&
          snapshotCubes.every((cubeEntry) =>
            Boolean(normalizePuzzleCubeId(cubeEntry?.cubeId || cubeEntry?.id))
          );
        const snapshotSupportsStrictCertification = snapshotVersion >= 3 && hasStableCubeIds;
        const shouldStrictlyEnforceHash =
          responsePuzzleCertificationStatus === "certified" ||
          snapshotSupportsStrictCertification;
        const expectedSnapshotHash =
          responsePuzzleSnapshotHash ||
          normalizePuzzleSnapshotHash(
            await computePuzzleSnapshotHash(responsePositionSnapshot)
          );
        const expectedBoardVersion =
          responsePuzzleBoardVersion ||
          normalizePuzzleBoardVersion(responsePositionSnapshot?.boardVersion);
        let observedSnapshotHash = "";
        let observedSnapshotBoardVersion = "";
        let lastSnapshotError = null;
        let hasSnapshotLoaded = false;
        let snapshotHashMismatchDetected = false;
        let snapshotHashMismatchDetail = "";

        for (let attempt = 0; attempt < MAX_PUZZLE_SNAPSHOT_APPLY_RETRIES; attempt += 1) {
          if (attempt > 0) {
            await new Promise((resolve) =>
              setTimeout(resolve, PUZZLE_SNAPSHOT_RETRY_DELAY_MS + attempt * 140)
            );
          }
          if (currentLoadCycle !== puzzleLoadCycleRef.current) return;

          try {
            const applyResult = await applyPuzzlePositionSnapshotToUnity(
              responsePositionSnapshot,
              {
                outsideStackOffset: -1,
                loadKey,
                forceApply: allowPuzzleReload,
              }
            );
            appliedMoves = Math.max(appliedMoves, Number(applyResult?.appliedCubeCount) || 0);
            if (expectedSnapshotBlocks > 0) {
              const verificationResult = await verifyPuzzleSnapshotRenderedInUnity(
                expectedSnapshotBlocks,
                MAX_PUZZLE_SNAPSHOT_VERIFICATION_TIMEOUT_MS + attempt * 800
              );
              if (!verificationResult.success) {
                throw new Error(
                  verificationResult.observedActiveBlocks > 0
                    ? `Unity no reflejo la posicion completa del puzzle (${verificationResult.observedActiveBlocks}/${expectedSnapshotBlocks}).`
                    : "Unity no reflejo la posicion del puzzle en el tablero."
                );
              }
            }

            const liveSnapshot = normalizePuzzleSnapshotPayload(
              await requestPuzzlePositionSnapshotFromUnity({
                timeoutMs: MAX_PUZZLE_SNAPSHOT_WAIT_MS,
              })
            );
            if (!liveSnapshot) {
              throw new Error("No se pudo recapturar el snapshot para verificar el hash.");
            }
            const currentObservedSnapshotHash = normalizePuzzleSnapshotHash(
              await computePuzzleSnapshotHash(liveSnapshot)
            );
            const currentObservedBoardVersion = normalizePuzzleBoardVersion(
              liveSnapshot?.boardVersion
            );
            const hashMatches =
              Boolean(expectedSnapshotHash) &&
              Boolean(currentObservedSnapshotHash) &&
              currentObservedSnapshotHash === expectedSnapshotHash;
            const boardMatches =
              !expectedBoardVersion ||
              !currentObservedBoardVersion ||
              currentObservedBoardVersion === expectedBoardVersion;
            if (shouldStrictlyEnforceHash && (!hashMatches || !boardMatches)) {
              snapshotHashMismatchDetected = true;
              snapshotHashMismatchDetail = `expected_hash=${expectedSnapshotHash || "none"} observed_hash=${
                currentObservedSnapshotHash || "none"
              } expected_board=${expectedBoardVersion || "none"} observed_board=${
                currentObservedBoardVersion || "none"
              }`;
              observedSnapshotHash = currentObservedSnapshotHash;
              observedSnapshotBoardVersion = currentObservedBoardVersion;
              throw new Error(
                `hash/board del snapshot no coincide (${snapshotHashMismatchDetail})`
              );
            }
            observedSnapshotHash = currentObservedSnapshotHash;
            observedSnapshotBoardVersion = currentObservedBoardVersion;

            loadedFromSnapshot = true;
            puzzleMoveLogRef.current = [];
            hasSnapshotLoaded = true;
            lastSnapshotError = null;
            break;
          } catch (snapshotApplyError) {
            lastSnapshotError = snapshotApplyError;
            const normalizedErrorMessage = String(snapshotApplyError?.message || "")
              .trim()
              .toLowerCase();
            const isDeterministicHashMismatch =
              normalizedErrorMessage.includes("hash/board del snapshot no coincide");
            if (isDeterministicHashMismatch) {
              // Si por alguna razon llega aqui por mismatch de hash, dejamos
              // que el flujo intente fallback sin romper la apertura.
              break;
            }
            if (attempt < MAX_PUZZLE_SNAPSHOT_APPLY_RETRIES - 1) {
              console.log(
                `Reintento de carga de snapshot (${attempt + 1}/${MAX_PUZZLE_SNAPSHOT_APPLY_RETRIES}).`
              );
              continue;
            }
            throw snapshotApplyError;
          }
        }

        if (!hasSnapshotLoaded && lastSnapshotError) {
          console.log(lastSnapshotError);
          await reportPuzzleLoadFailure(
            targetPuzzleTournamentId,
            "snapshot_apply_failed",
            String(lastSnapshotError?.message || "unknown_error")
          );

          if (replayMoveLog.length > 0) {
            if (currentLoadCycle !== puzzleLoadCycleRef.current) return;
            const localAppliedMoves = await applyPuzzleMoveLogToUnity(replayMoveLog);
            appliedMoves = Math.max(appliedMoves, localAppliedMoves);
            puzzleMoveLogRef.current = replayMoveLog;
            notification.warning({
              message: i18nT("tournaments.puzzleCargadoConRespaldo"),
              description:
                i18nT("tournaments.elSnapshotNoCoincidioConElHashGuardado"),
              duration: 5,
            });
          } else {
            const reliableNextPuzzleId = await findNextReliablePuzzleAhead(
              targetPuzzleTournamentId,
              resolvePuzzleSequenceMode()
            );
            if (reliableNextPuzzleId) {
              notification.warning({
                message: i18nT("tournaments.puzzleNoConfiable"),
                description:
                  i18nT("tournaments.estePuzzleSeMarcoEnCuarentenaAbrimosAutomatif3cc00"),
                duration: 6,
              });
              await openPuzzleFromHomeFlow(reliableNextPuzzleId, "auto-skip-unreliable");
              return;
            }
            throw lastSnapshotError;
          }
        }

        if (snapshotHashMismatchDetected && targetPuzzleTournamentId) {
          try {
            await reportPuzzleLoadFailure(
              targetPuzzleTournamentId,
              "snapshot_hash_mismatch_runtime",
              snapshotHashMismatchDetail || "hash_or_board_mismatch"
            );
          } catch (mismatchReportError) {
            console.log(mismatchReportError);
          }
          notification.warning({
            message: i18nT("tournaments.puzzleCargadoConDiferencias"),
            description:
              i18nT("tournaments.seDetectoDiferenciaDeHashBoardDelSnapshot"),
            duration: 5,
          });
        }

        if (
          hasSnapshotLoaded &&
          observedSnapshotHash &&
          responsePuzzleCertificationStatus !== "certified" &&
          snapshotSupportsStrictCertification
        ) {
          try {
            const certificationResponse = await axios.post("/tournaments/puzzles/certify", {
              puzzleTournamentId: targetPuzzleTournamentId,
              snapshotHash: observedSnapshotHash,
              boardVersion: observedSnapshotBoardVersion || expectedBoardVersion,
            });
            const certifiedMeta = certificationResponse?.data?.puzzle || null;
            if (certifiedMeta && typeof certifiedMeta === "object") {
              updatePuzzleCertificationInCache(targetPuzzleTournamentId, certifiedMeta);
            }
          } catch (certifyError) {
            console.log(certifyError);
          }
        }
      } else if (replayMoveLog.length > 0 && !appliedFromMoveLogPrimary) {
        if (currentLoadCycle !== puzzleLoadCycleRef.current) return;
        if (moveLogPrimaryError) {
          throw moveLogPrimaryError;
        }
        const localAppliedMoves = await applyPuzzleMoveLogToUnity(replayMoveLog);
        appliedMoves = Math.max(appliedMoves, localAppliedMoves);
        puzzleMoveLogRef.current = replayMoveLog;
      } else if (transitionRequestId) {
        console.log(
          "No hay estado inicial almacenado para el puzzle solicitado. Se mantiene el estado visual de Unity."
        );
      }

      if (currentLoadCycle !== puzzleLoadCycleRef.current) return;
      puzzleSnapshotAppliedRef.current = true;
      syncPuzzleRuntimeContextToUnity({
        isPuzzle: true,
        freeBoxBaseMode: tourInfo?.freeBoxBaseMode,
        puzzleId: targetPuzzleTournamentId,
        roomTournamentId,
      });
      syncPuzzleRuntimeReadyToUnity(true, targetPuzzleTournamentId);
      puzzleSnapshotLoadedKeyRef.current = loadKey;
      puzzleSnapshotLoadingKeyRef.current = "";
      const solveTimerStartMs = Date.now();
      setPuzzleSolveTimerNowMs(solveTimerStartMs);
      setPuzzleSolveTimerStartedAtMs(
        responsePuzzleSolveTimeLimitSeconds > 0 ? solveTimerStartMs : 0
      );
      delete puzzleAutoLoadBlockedByIdRef.current[targetPuzzleTournamentId];
      void preloadUpcomingPuzzles(
        targetPuzzleTournamentId,
        PUZZLE_PRELOAD_INITIAL_AHEAD_COUNT
      );
      if (loadedFromSnapshot) {
        notification.success({
          message: i18nT("tournaments.puzzleCargado"),
          description:
            appliedMoves > 0
              ? `Se aplico la posicion guardada con ${appliedMoves} bloques.`
              : "Se aplico la posicion guardada del puzzle.",
          duration: 4,
        });
      } else if (appliedMoves > 0) {
        notification.success({
          message: i18nT("tournaments.puzzleCargado"),
          description: `Se aplicaron ${appliedMoves} jugadas para llegar a la posicion guardada.`,
          duration: 4,
        });
      }
    } catch (error) {
      console.log(error);
      if (currentLoadCycle === puzzleLoadCycleRef.current) {
        syncPuzzleRuntimeReadyToUnity(false, targetPuzzleTournamentId);
      }
      if (targetPuzzleTournamentId) {
        puzzleAutoLoadBlockedByIdRef.current[targetPuzzleTournamentId] = true;
      }
      notification.error({
        message: i18nT("tournaments.errorCargandoPuzzle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          "No se pudo cargar la posicion del puzzle.",
      });
    } finally {
      if (puzzleSnapshotLoadingKeyRef.current === loadKey) {
        puzzleSnapshotLoadingKeyRef.current = "";
      }
      if (currentLoadCycle === puzzleLoadCycleRef.current) {
        setLoadingPuzzleSnapshot(false);
      }
    }
  }, [
    isPuzzleRequested,
    isPuzzleTournament,
    isSpectatorRequested,
    normalizedMode,
    roomId,
    applyPuzzleMoveLogToUnity,
    applyPuzzlePositionSnapshotToUnity,
    findNextReliablePuzzleAhead,
    openPuzzleFromHomeFlow,
    preloadUpcomingPuzzles,
    reportPuzzleLoadFailure,
    resolveActivePuzzleId,
    resolvePuzzleSequenceMode,
    syncPuzzleRuntimeContextToUnity,
    syncPuzzleRuntimeReadyToUnity,
    requestPuzzlePositionSnapshotFromUnity,
    puzzleRewardValue,
    tourInfo,
    updatePuzzleCertificationInCache,
    waitForUnityPuzzleBoardReady,
    verifyPuzzleSnapshotRenderedInUnity,
    waitForUnityIframeReady,
  ]);

  const requestProtectedMatchPassword = useCallback(
    () =>
      new Promise((resolve) => {
        let passwordDraft = "";
        const inputId = `match-password-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
        const matchName = localizeTournamentName(language, tourInfo.name) || tourInfo.name || "Partido";
        Modal.confirm({
          title: i18nT("lobby.partidoConContrasena"),
          content: (
            <div>
              <div className="mb-3 text-sm">
                {i18nT("tournaments.paraJugar")} {matchName}{" "}
                {i18nT("tournaments.debesEscribirLaContrasenaDelPartido")}
              </div>
                <Input.Password
                  id={inputId}
                  autoFocus
                  maxLength={MATCH_PASSWORD_MAX_LENGTH}
                  placeholder={i18nT("lobby.contrasenaDelPartido")}
                  onChange={(event) => {
                    passwordDraft = normalizeMatchPassword(event?.target?.value || "");
                  }}
                  onPressEnter={() => {
                    const activeOkButton = document.querySelector(".ant-modal-confirm-btns .ant-btn-primary");
                    if (activeOkButton && typeof activeOkButton.click === "function") {
                      activeOkButton.click();
                    }
                  }}
                />
            </div>
          ),
          okText: "Jugar",
          cancelText: "Cancelar",
          centered: true,
          onOk: () => {
            const password = String(
              document.getElementById(inputId)?.value || passwordDraft || ""
            ).trim();
            const normalizedPassword = normalizeMatchPassword(password);
            if (!normalizedPassword || !isValidMatchPassword(normalizedPassword)) {
              notification.warning({
                message: i18nT("lobby.contrasenaRequerida"),
                description: i18nT("tournaments.estePartidoEstaProtegidoEscribeLaContrasenaPe46440"),
              });
              return Promise.reject(new Error("MATCH_PASSWORD_REQUIRED"));
            }
            resolve(normalizedPassword);
            return undefined;
          },
          onCancel: () => resolve(null),
        });
      }),
    [language, tourInfo.name]
  );

  const startMatchmaking = useCallback(async () => {
    if (expired || loading || matchmaking || isMatchInProgress) return;
    if (isPuzzleRequested && !puzzleMetadataReadyForAutoplay) return;

    if (gameEnded || hasGameEndedRef.current || hasActiveRoom) {
      unlinkFromCurrentMatch(roomIdRef.current || roomId);
    }

    try {
      setGameEnded(false);
      hasGameEndedRef.current = false;
      setPuzzleAttemptCount(0);
      setPuzzleFailedByTurnLimit(false);
      setPuzzleFailedBySolveTimeLimit(false);
      setPuzzleSolveTimerStartedAtMs(0);
      setPuzzleSolveTimerNowMs(Date.now());
      setForcePostGameActions(false);
      setPostGameMessage("");
      setLastGameEndReason("");
      puzzleSolverPlayerIndexRef.current = null;
      puzzleSolverMoveCountRef.current = 0;
      puzzleSolverDiceAttemptMaskRef.current = 0;
      lastPuzzleTurnEventKeyRef.current = "";
      lastPuzzleTurnNotationKeyRef.current = "";
      lastPuzzleTurnEventAtMsRef.current = 0;
      setMatchmaking(true);
      await ensureSocketSessionReady();
      if (isSpectatorRequested || isPuzzleMode) {
        const iframeReady = await waitForUnityIframeReady(
          14000
        );
        if (!iframeReady) {
          throw new Error(
            isSpectatorRequested
              ? "El visor de espectador aun esta cargando. Intenta de nuevo."
              : "Unity aun esta cargando el puzzle. Intenta de nuevo en unos segundos."
          );
        }
      } else if (isOnlineMode) {
        waitForUnityIframeReady(2500).catch(() => false);
      }
      setOpen(true);
      // Campeonato: sus dos jugadores reciben la contraseña de la sala del servidor
      // y entran directo, sin que se les pregunte.
      const championshipRoomPassword =
        !protectedMatchPasswordFromNavigation && !isSpectatorRequested && shouldAskForProtectedMatchPassword
          ? await axios
              .post(`/championship/rooms/${id}/password`)
              .then((response) => String(response?.data?.matchPassword || "").trim())
              .catch(() => "")
          : "";
      const protectedMatchPassword =
        protectedMatchPasswordFromNavigation ||
        championshipRoomPassword ||
        (!isSpectatorRequested && shouldAskForProtectedMatchPassword
          ? await requestProtectedMatchPassword()
          : "");
      if (!isSpectatorRequested && shouldAskForProtectedMatchPassword && !protectedMatchPassword) {
        setOpen(false);
        setMatchmaking(false);
        // Cancelar no deja la partida a medias: vuelve a la pantalla de donde vino.
        if (location.state?.fromLobbySource === "championship") {
          navigate("/game/championship", { replace: true });
        } else if (window.history.length > 1) {
          navigate(-1);
        } else {
          navigate("/game/lobby", { replace: true });
        }
        return;
      }
      const res = isSpectatorRequested
        ? await axios.post("/tournaments/spectate", {
            tournamentId: id,
            roomId: requestedRoomId,
          })
        : await (async () => {
            await axios.post("/tournaments/join", {
              id,
              matchPassword: protectedMatchPassword,
              tournamentPassword: protectedMatchPassword,
              joinPassword: protectedMatchPassword,
            });
            return axios.post("/tournaments/play", {
              id,
              matchPassword: protectedMatchPassword,
              tournamentPassword: protectedMatchPassword,
              joinPassword: protectedMatchPassword,
            });
          })();
      const responseSequenceNumber = resolvePublicSequenceNumber(res?.data || {});
      if (responseSequenceNumber > 0) {
        const stableSequenceNumber = tournamentSequenceNumber || responseSequenceNumber;
        setTournamentSequenceNumber((current) =>
          current > 0 ? current : responseSequenceNumber
        );
        setTourInfo((previous) => ({
          ...previous,
          tournamentUiSequenceNumber: stableSequenceNumber,
          tournamentSequenceNumber: stableSequenceNumber,
          tournamentNumber: stableSequenceNumber,
          displayNumber: stableSequenceNumber,
        }));
      }
      const incomingRoomId = Number(res?.data?.roomId);
      if (incomingRoomId === -1) {
        throw new Error(
          isSpectatorRequested
            ? "No running room available to spectate."
            : "Socket session is not ready yet. Please try again."
        );
      }
      const assignedRoomId = String(res?.data?.roomId || "").trim();
      roomIdRef.current = assignedRoomId || 0;
      setRoomId(assignedRoomId || 0);
      if (assignedRoomId) {
        roomSessionTournamentIdRef.current = String(id || "").trim();
      }
      setGameEnded(false);
      setPuzzleFailedByTurnLimit(false);
      setPuzzleFailedBySolveTimeLimit(false);
      setPuzzleSolveTimerStartedAtMs(0);
      setPuzzleSolveTimerNowMs(Date.now());
      setForcePostGameActions(false);
      setPostGameMessage("");
      setLastGameEndReason("");
    } catch (error) {
      console.log(error);
      setOpen(false);
      setMatchmaking(false);
      notification.error({
        message: i18nT("tournaments.queueError"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          (isSpectatorRequested
            ? "Could not join this room as spectator."
            : "Could not start matchmaking for this room."),
      });
    }
  }, [
    gameEnded,
    hasActiveRoom,
    expired,
    id,
    isMatchInProgress,
    isPuzzleMode,
    isPuzzleRequested,
    isSpectatorRequested,
    loading,
    location.state,
    matchmaking,
    navigate,
    puzzleMetadataReadyForAutoplay,
    requestProtectedMatchPassword,
    roomId,
    protectedMatchPasswordFromNavigation,
    requestedRoomId,
    rememberActiveMatchSession,
    shouldAskForProtectedMatchPassword,
    unlinkFromCurrentMatch,
    waitForUnityIframeReady,
  ]);

  useEffect(() => {
    if (isSpectatorRequested) return undefined;
    if (normalizedMode !== "freebox") return undefined;
    if (!isPuzzleTournament && !isPuzzleRequested) return undefined;
    if (!roomId) return undefined;
    if (puzzleSnapshotAppliedRef.current || loadingPuzzleSnapshot) return undefined;
    if (open || matchmaking) return undefined;
    const activePuzzleId = resolveActivePuzzleId();
    if (activePuzzleId && puzzleAutoLoadBlockedByIdRef.current[activePuzzleId] === true) {
      return undefined;
    }

    const timer = setTimeout(() => {
      loadPuzzleSnapshotIntoRoom().catch((error) => console.log(error));
    }, 180);

    return () => clearTimeout(timer);
  }, [
    isPuzzleRequested,
    isPuzzleTournament,
    isSpectatorRequested,
    loadPuzzleSnapshotIntoRoom,
    loadingPuzzleSnapshot,
    resolveActivePuzzleId,
    matchmaking,
    normalizedMode,
    open,
    roomId,
  ]);

  const runPreStartQuickSync = useCallback(async () => {
    if (preStartSyncInFlightRef.current) return;
    preStartSyncInFlightRef.current = true;

    try {
      const activeRoomId = String(roomIdRef.current || roomId || "").trim();
      if (!activeRoomId) return;

      const [tourResponse, playResponse] = await Promise.all([
        axios.get(`/tournaments/${id}`),
        axios.post("/tournaments/play", {
          id,
          matchPassword: protectedMatchPasswordFromNavigation,
          tournamentPassword: protectedMatchPasswordFromNavigation,
          joinPassword: protectedMatchPasswordFromNavigation,
        }),
      ]);

      const tourData = tourResponse?.data;
      if (tourData?.success) {
        setTourInfo(tourData);
      }

      const incomingRoomId = String(playResponse?.data?.roomId || "").trim();
      if (incomingRoomId && !String(roomIdRef.current || "").trim()) {
        roomIdRef.current = incomingRoomId;
        setRoomId(incomingRoomId);
      }
    } catch (error) {
      console.log(error);
    } finally {
      preStartSyncInFlightRef.current = false;
    }
  }, [id, protectedMatchPasswordFromNavigation, roomId]);

  useEffect(() => {
    if (!roomId) return undefined;
    if (isSpectatorRequested) return undefined;
    if (!isOnlineMode) return undefined;
    if (gameEnded || hasGameEndedRef.current) return undefined;

    const expectedPlayers = Math.max(
      2,
      Math.min(
        4,
        Number(tourInfo.memberSize) || Number(tourInfo.members) || 2
      )
    );
    const knownPlayers = Array.isArray(roomUsers) ? roomUsers.length : 0;
    if (knownPlayers >= expectedPlayers) return undefined;

    const triggerSync = () => {
      const now = Date.now();
      if (now - preStartSyncAtRef.current < 1200) return;
      preStartSyncAtRef.current = now;
      runPreStartQuickSync().catch((error) => console.log(error));
    };

    triggerSync();
    const timer = setInterval(triggerSync, 1200);
    return () => clearInterval(timer);
  }, [
    isOnlineMode,
    isSpectatorRequested,
    roomId,
    roomUsers,
    runPreStartQuickSync,
    tourInfo.memberSize,
    tourInfo.members,
    gameEnded,
  ]);

  const recoverSession = useCallback(
    async (options = {}) => {
      const silent = options?.silent === true;
      const viaHttpPolling = options?.viaHttpPolling === true;
      const forceUnityReload = options?.forceUnityReload === true;
      if (reconnectingRef.current || hasGameEndedRef.current || gameEnded) return false;
      if (isBotLocalMode && !isSpectatorRequested) {
        setConnectionState("connected");
        return false;
      }
      if (isPuzzleMode && options?.allowPuzzleRecovery !== true) {
        setConnectionState("connected");
        return false;
      }

      reconnectingRef.current = true;
      setConnectionState("reconnecting");

      try {
        if (!viaHttpPolling) {
          await ensureSocketSessionReady();
        }

        const requestedLastMoveSequence = Math.max(0, Number(lastMoveSequenceRef.current) || 0);
        let activeRoomId = String(roomIdRef.current || roomId || "").trim();
        if (!activeRoomId) {
          const activeSessionsResponse = await axios.get("/tournaments/active-sessions");
          const activeSessions = Array.isArray(activeSessionsResponse?.data?.sessions)
            ? activeSessionsResponse.data.sessions
            : [];
          const matchingSession = activeSessions.find(
            (entry) => String(entry?.tournamentId || "").trim() === String(id)
          );
          activeRoomId = String(matchingSession?.roomId || "").trim();
          if (activeRoomId) {
            roomIdRef.current = activeRoomId;
            setRoomId(activeRoomId);
          }
        }

        if (!activeRoomId) {
          setConnectionState("connected");
          if (!silent) {
            notification.warning({
              message: i18nT("tournaments.sinSalaActiva"),
              description: i18nT("tournaments.noSeEncontroUnaPartidaActivaParaReconectar"),
            });
          }
          return false;
        }

        let reconnectResponse;
        try {
          reconnectResponse = await axios.post("/tournaments/reconnect-state", {
            tournamentId: id,
            roomId: activeRoomId,
            lastMoveSequence: requestedLastMoveSequence,
          });
        } catch (reconnectError) {
          const reconnectStatus = Number(reconnectError?.response?.status);
          if (!isSpectatorRequested || reconnectStatus !== 403) throw reconnectError;

          const iframeReady = await waitForUnityIframeReady();
          if (!iframeReady) {
            throw new Error("El visor de espectador aun esta cargando. Intenta de nuevo.");
          }
          await axios.post("/tournaments/spectate", {
            tournamentId: id,
            roomId: activeRoomId,
          });
          setConnectionState("connected");
          return true;
        }
        const reconnectState = reconnectResponse?.data || {};
        if (!reconnectState?.success) {
          throw new Error("El servidor no devolvio un estado valido de reconexion.");
        }

        const incomingRoomId = String(reconnectState?.roomId || activeRoomId).trim();
        if (incomingRoomId && incomingRoomId !== String(roomIdRef.current || "").trim()) {
          roomIdRef.current = incomingRoomId;
          setRoomId(incomingRoomId);
        }
        if (incomingRoomId) {
          roomSessionTournamentIdRef.current = String(reconnectState?.tournamentId || id || "").trim();
          rememberActiveMatchSession(incomingRoomId, reconnectState?.tournamentId || id);
        }

        const incomingUsers = Array.isArray(reconnectState?.users) ? reconnectState.users : [];
        if (incomingUsers.length > 0) {
          setRoomUsers(incomingUsers);
        }
        if (isBotLocalMode) {
          setBotTurnSlotIndexes(
            resolveBotSlotIndexesFromPayload(reconnectState, incomingUsers)
          );
        }

        if (reconnectState?.userProfiles && typeof reconnectState.userProfiles === "object") {
          acceptAuthoritativeRoomProfiles(
            reconnectState.userProfiles,
            reconnectState.cosmeticRevision,
            reconnectState.cosmeticAuthorityId
          );
        }

        const reconnectIsOutMap = Object.prototype.hasOwnProperty.call(reconnectState, "isOut")
          ? normalizeOutUsersMap(reconnectState.isOut)
          : {};
        if (Object.prototype.hasOwnProperty.call(reconnectState, "isOut")) {
          setOutUsersById(reconnectIsOutMap);
        }

        const activeTurnUsers = incomingUsers.filter((entry) => {
          const normalizedUserId = normalizeProfileId(entry);
          if (!normalizedUserId) return false;
          return reconnectIsOutMap[normalizedUserId] !== true;
        });
        const fullMoveLog = Array.isArray(reconnectState?.fullMoveLog)
          ? reconnectState.fullMoveLog
          : [];
        const frozenBlocksCount = countFrozenBlocksFromMoveLog(
          fullMoveLog,
          incomingUsers,
          reconnectIsOutMap
        );
        const activeTurnUsersText = activeTurnUsers.length > 0 ? activeTurnUsers.join(",") : "(none)";
        console.log(`[RECONNECT_REBUILD] active turn users only=${activeTurnUsersText}`);
        console.log(`[RECONNECT_REBUILD] frozen users blocks count=${frozenBlocksCount}`);
        if (frozenBlocksCount > 0) {
          console.log("[RECONNECT_REBUILD] board occupancy includes forfeited user blocks");
        }

        if (reconnectState?.turnState && typeof reconnectState.turnState === "object") {
          syncServerTurnClockFromPayload(reconnectState.turnState);
        }

        const spectatorRoomKey = `${String(id)}::${incomingRoomId || activeRoomId}`;
        const canApplySpectatorSnapshotInPlace =
          isSpectatorRequested &&
          unityIframeLoadedRef.current &&
          lastHandledGameStartRoomKeyRef.current === spectatorRoomKey;
        if (canApplySpectatorSnapshotInPlace) {
          const snapshotApplied = await applyLiveBoardSnapshotToUnity(
            reconnectState?.boardSnapshotJson || reconnectState?.boardSnapshot || null
          );
          if (snapshotApplied || fullMoveLog.length === 0) {
            const latestMoveSequence = Number(reconnectState?.latestMoveSequence);
            if (Number.isFinite(latestMoveSequence) && latestMoveSequence > 0) {
              lastMoveSequenceRef.current = Math.max(
                Number(lastMoveSequenceRef.current) || 0,
                Math.trunc(latestMoveSequence)
              );
            }
            setConnectionState("connected");
            if (!silent) {
              notification.success({
                message: i18nT("tournaments.conexionRestaurada"),
                description: i18nT("tournaments.laPartidaSeSincronizoDeNuevo"),
              });
            }
            return true;
          }
        }

        const shouldReloadUnityFromFullState =
          forceUnityReload ||
          !unityIframeLoadedRef.current ||
          requestedLastMoveSequence <= 0;

        if (shouldReloadUnityFromFullState) {
          const reconnectMatchData = {
            matchID: reconnectState?.tournamentId || id,
            tournamentId: reconnectState?.tournamentId || id,
            roomID: incomingRoomId || activeRoomId,
            roomId: incomingRoomId || activeRoomId,
            modoDeJuego: reconnectState?.mode || tourInfo?.mode || "",
            mode: reconnectState?.mode || tourInfo?.mode || "",
            havingTime: reconnectState?.havingTime || tourInfo?.havingTime || 1,
            botDifficulty:
              normalizeRuntimeBotDifficulty(reconnectState?.botDifficulty) ||
              navigationBotDifficulty ||
              normalizeRuntimeBotDifficulty(tourInfo?.botDifficulty) ||
              "",
            memberSize:
              reconnectState?.memberSize ||
              incomingUsers.length ||
              tourInfo?.memberSize ||
              tourInfo?.members ||
              2,
            users: incomingUsers,
            jugadoresEnPartida: incomingUsers,
            me: reconnectState?.me || incomingUsers[0] || "",
            spectatorId: reconnectState?.spectatorId || "",
            isSpectator: Boolean(reconnectState?.isSpectator || isSpectatorRequested),
            botControllerId: reconnectState?.botControllerId || "",
            hasBots: Boolean(reconnectState?.hasBots),
            botSlots: Array.isArray(reconnectState?.botSlots) ? reconnectState.botSlots : [],
            userProfiles: reconnectState?.userProfiles || {},
            isOut: reconnectIsOutMap,
            estadoDelTablero: {
              isOut: reconnectIsOutMap,
              boardSnapshot: reconnectState?.boardSnapshot || null,
              boardSnapshotJson: reconnectState?.boardSnapshotJson || "",
              botDifficulty:
                normalizeRuntimeBotDifficulty(reconnectState?.botDifficulty) ||
                navigationBotDifficulty ||
                normalizeRuntimeBotDifficulty(tourInfo?.botDifficulty) ||
                "",
            },
            boardSnapshot: reconnectState?.boardSnapshot || null,
            boardSnapshotJson: reconnectState?.boardSnapshotJson || "",
            turnState: reconnectState?.turnState || {},
            serverNowMs: reconnectState?.serverNowMs || Date.now(),
            posicionDePiezas: fullMoveLog,
            fullMoveLog,
            latestMoveSequence: reconnectState?.latestMoveSequence || 0,
            freeBoxRules:
              Boolean(reconnectState?.freeBoxRules) ||
              String(reconnectState?.mode || tourInfo?.mode || "").trim().toLowerCase() ===
                "freebox",
            freeBoxBaseMode: tourInfo?.freeBoxBaseMode || "",
          };

          await CargarPartida(reconnectMatchData, {
            reloadUnity: !isSpectatorRequested,
          });
          setConnectionState("connected");
          if (!silent) {
            notification.success({
              message: i18nT("tournaments.conexionRestaurada"),
              description: i18nT("tournaments.laPartidaSeSincronizoDeNuevo"),
            });
          }
          return true;
        }

        const moveDeltas = orderMovePayloadsBySequence(
          Array.isArray(reconnectState?.moveDeltas) ? reconnectState.moveDeltas : [],
          MAX_PUZZLE_MOVE_LOG_SIZE
        );
        if (moveDeltas.length > 0) {
          try {
            const iframeReady = await waitForUnityIframeReady(9000);
            if (iframeReady) {
              await applyPuzzleMoveLogToUnity(moveDeltas);
            }
          } catch (replayError) {
            console.log(replayError);
          }
        }

        const latestMoveSequence = Number(reconnectState?.latestMoveSequence);
        if (Number.isFinite(latestMoveSequence) && latestMoveSequence > 0) {
          lastMoveSequenceRef.current = Math.max(
            Number(lastMoveSequenceRef.current) || 0,
            Math.trunc(latestMoveSequence)
          );
        }

        setConnectionState("connected");
        if (!silent) {
          notification.success({
            message: i18nT("tournaments.conexionRestaurada"),
            description: i18nT("tournaments.laPartidaSeSincronizoDeNuevo"),
          });
        }
        return true;
      } catch (error) {
        console.log(error);
        const reconnectStatus = Number(error?.response?.status);
        const reconnectCode = String(error?.response?.data?.code || "").trim().toUpperCase();
        const roomNotStarted = reconnectStatus === 409 && reconnectCode === "ROOM_NOT_STARTED";
        if (roomNotStarted) {
          clearStoredActiveMatchSessionForCurrentUser(id);
          setConnectionState("connected");
          return false;
        }
        const activeSessionGone = reconnectStatus === 404 || reconnectStatus === 409;
        if (activeSessionGone) {
          clearStoredActiveMatchSessionForCurrentUser(id);
          roomIdRef.current = 0;
          setRoomId(0);
        }
        setConnectionState(activeSessionGone ? "connected" : "reconnecting");
        if (!silent) {
          notification.error({
            message: i18nT("tournaments.reconnectionError"),
            description:
              error?.response?.data?.message ||
              error?.message ||
              "No se pudo recuperar la partida automaticamente.",
          });
        }
        return false;
      } finally {
        reconnectingRef.current = false;
      }
    },
    [
      acceptAuthoritativeRoomProfiles,
      applyPuzzleMoveLogToUnity,
      applyLiveBoardSnapshotToUnity,
      CargarPartida,
      clearStoredActiveMatchSessionForCurrentUser,
      gameEnded,
      id,
      isBotLocalMode,
      isPuzzleMode,
      isSpectatorRequested,
      navigationBotDifficulty,
      rememberActiveMatchSession,
      roomId,
      syncServerTurnClockFromPayload,
      tourInfo?.botDifficulty,
      tourInfo?.freeBoxBaseMode,
      tourInfo?.havingTime,
      tourInfo?.memberSize,
      tourInfo?.members,
      tourInfo?.mode,
      waitForUnityIframeReady,
    ]
  );

  const handleManualPositionRefresh = useCallback(async () => {
    if (manualPositionRefreshLoading) return;
    if (hasGameEndedRef.current || gameEnded) return;

    setManualPositionRefreshLoading(true);
    try {
      let restored = await recoverSession({
        silent: true,
        viaHttpPolling: false,
        forceUnityReload: true,
      });

      if (!restored) {
        restored = await recoverSession({
          silent: true,
          viaHttpPolling: true,
          forceUnityReload: true,
        });
      }

      triggerUnityReplayRefresh("manual_position_refresh");

      if (restored) {
        notification.success({
          message: i18nT("tournaments.posicionActualizada"),
          description: i18nT("tournaments.seSincronizoManualmenteLaPosicionActualDelTa720c44"),
        });
      } else {
        notification.warning({
          message: i18nT("tournaments.sincronizacionParcial"),
          description:
            i18nT("tournaments.seIntentoActualizarLaPosicionManualmenteSiNo"),
        });
      }
    } catch (error) {
      console.log(error);
      notification.error({
        message: i18nT("tournaments.noSePudoActualizarLaPosicion"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          "No fue posible sincronizar manualmente la posicion de la partida.",
      });
    } finally {
      setManualPositionRefreshLoading(false);
    }
  }, [gameEnded, manualPositionRefreshLoading, recoverSession, triggerUnityReplayRefresh]);

  const stopReconnectHttpPolling = useCallback(() => {
    if (reconnectPollingTimerRef.current) {
      clearInterval(reconnectPollingTimerRef.current);
      reconnectPollingTimerRef.current = null;
    }
    reconnectHttpInFlightRef.current = false;
  }, []);

  const startReconnectHttpPolling = useCallback(() => {
    if (isPuzzleMode) return;
    if (isBotLocalMode && !isSpectatorRequested) return;
    if (reconnectPollingTimerRef.current) return;
    const run = async () => {
      if (reconnectHttpInFlightRef.current) return;
      reconnectHttpInFlightRef.current = true;
      try {
        await recoverSession({
          viaHttpPolling: true,
          silent: true,
        });
      } catch (error) {
        console.log(error);
      } finally {
        reconnectHttpInFlightRef.current = false;
      }
    };

    run().catch((error) => console.log(error));
    reconnectPollingTimerRef.current = setInterval(run, 2500);
  }, [isBotLocalMode, isPuzzleMode, isSpectatorRequested, recoverSession, stopReconnectHttpPolling]);

  useEffect(() => {
    return () => {
      stopReconnectHttpPolling();
    };
  }, [stopReconnectHttpPolling]);

  useEffect(() => {
    let cancelled = false;
    const bootstrapActiveSession = async () => {
      if (hasGameEndedRef.current || gameEnded) return;
      if (String(roomIdRef.current || roomId || "").trim()) return;
      const canRestoreLiveSession =
        isOnlineMode || (isSpectatorRequested && isBotLocalMode);
      if (!canRestoreLiveSession || isPuzzleMode) return;

      try {
        const activeSessionsResponse = await axios.get("/tournaments/active-sessions");
        const activeSessions = Array.isArray(activeSessionsResponse?.data?.sessions)
          ? activeSessionsResponse.data.sessions
          : [];

        const matchingSession = activeSessions.find(
          (entry) => String(entry?.tournamentId || "").trim() === String(id)
        );
        const incomingRoomId = String(matchingSession?.roomId || "").trim();
        if (!incomingRoomId || cancelled) return;

        roomIdRef.current = incomingRoomId;
        setRoomId(incomingRoomId);
        await recoverSession({
          viaHttpPolling: true,
          silent: true,
          forceUnityReload: true,
        });
      } catch (error) {
        console.log(error);
      }
    };

    bootstrapActiveSession().catch((error) => console.log(error));
    return () => {
      cancelled = true;
    };
  }, [
    gameEnded,
    id,
    isBotLocalMode,
    isOnlineMode,
    isPuzzleMode,
    isSpectatorRequested,
    recoverSession,
    roomId,
  ]);

  const cancelAndBackToLobby = useCallback(async () => {
    setOpen(false);
    setMatchmaking(false);
    setLoading(false);
    await exitTournamentSession({ cancelFindingUser: true });
    navigate("/game");
  }, [exitTournamentSession, navigate]);

  const goToNextPuzzle = useCallback(async () => {
    if (openingNextPuzzle) return;
    const currentPuzzleId = resolveActivePuzzleId();
    if (!currentPuzzleId) {
      notification.warning({
        message: i18nT("lobby.siguientePuzzle"),
        description: i18nT("tournaments.noSePudoIdentificarElPuzzleActual"),
      });
      return;
    }

    setOpeningNextPuzzle(true);

    try {
      void preloadUpcomingPuzzles(
        currentPuzzleId,
        PUZZLE_PRELOAD_ROLLING_AHEAD_COUNT
      );
      await new Promise((resolve) => setTimeout(resolve, POST_PUZZLE_ACTION_DELAY_MS));
      const currentPuzzleMode = resolvePuzzleSequenceMode();
      let orderedPuzzles = await getOrderedPuzzlesForMode(currentPuzzleMode);
      let currentIndex = orderedPuzzles.findIndex(
        (entry) => getPuzzleSequenceEntryId(entry) === currentPuzzleId
      );
      if (currentIndex < 0) {
        orderedPuzzles = await getOrderedPuzzlesForMode(currentPuzzleMode, {
          forceRefresh: true,
        });
        currentIndex = orderedPuzzles.findIndex(
          (entry) => getPuzzleSequenceEntryId(entry) === currentPuzzleId
        );
      }

      if (currentIndex < 0) {
        notification.warning({
          message: i18nT("lobby.siguientePuzzle"),
          description:
            i18nT("tournaments.noSeEncontroElPuzzleActualEnLa"),
        });
        navigate("/game", { replace: true });
        return;
      }

      if (currentIndex >= orderedPuzzles.length - 1) {
        notification.info({
          message: i18nT("lobby.noHaySiguientePuzzle"),
          description: i18nT("lobby.yaResolvisteElUltimoPuzzleDisponible"),
        });
        navigate("/game", { replace: true });
        return;
      }

      const aheadEntries = orderedPuzzles.slice(currentIndex + 1);
      const preferredReliableEntry =
        aheadEntries.find((entry) => isReliablePuzzlePlanAEntry(entry)) || null;
      const firstNonQuarantinedEntry =
        aheadEntries.find((entry) => {
          const fallbackStatus = entry?.positionSnapshot ? "pending" : "fallback";
          return (
            normalizePuzzleCertificationStatus(
              entry?.puzzleCertificationStatus,
              fallbackStatus
            ) !== "quarantined"
          );
        }) || null;
      const selectedNextEntry =
        preferredReliableEntry || firstNonQuarantinedEntry || aheadEntries[0] || null;

      const nextPuzzleId = getPuzzleSequenceEntryId(selectedNextEntry);
      if (!nextPuzzleId) {
        throw new Error("No se pudo identificar el siguiente puzzle en la lista.");
      }

      await openPuzzleFromHomeFlow(nextPuzzleId, "next-puzzle");
    } catch (error) {
      console.log(error);
      notification.error({
        message: i18nT("tournaments.noSePudoAbrirElSiguientePuzzle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Intenta nuevamente en unos segundos.",
      });
    } finally {
      setOpeningNextPuzzle(false);
    }
  }, [
    getOrderedPuzzlesForMode,
    preloadUpcomingPuzzles,
    resolveActivePuzzleId,
    resolvePuzzleSequenceMode,
    openingNextPuzzle,
    openPuzzleFromHomeFlow,
  ]);

  const goToNextTutorialLevel = useCallback(async () => {
    try {
      await exitTournamentSession();
    } catch (error) {
      console.log(error);
    }
    const target = buildTutorialLevel2Target();
    navigate(target.pathname, { replace: true, state: target.state });
  }, [exitTournamentSession, navigate]);

  const retryCurrentPuzzle = useCallback(async () => {
    const currentPuzzleId = resolveActivePuzzleId();
    if (!currentPuzzleId || retryingPuzzle) return;

    setRetryingPuzzle(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, POST_PUZZLE_ACTION_DELAY_MS));
      await openPuzzleFromHomeFlow(currentPuzzleId, "retry-puzzle");
    } catch (error) {
      console.log(error);
      notification.error({
        message: i18nT("tournaments.noSePudoReiniciar"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Intenta nuevamente en unos segundos.",
      });
    } finally {
      setRetryingPuzzle(false);
    }
  }, [openPuzzleFromHomeFlow, resolveActivePuzzleId, retryingPuzzle]);

  useEffect(() => {
    let mounted = true;

    const handleUserStart = (userId) => {
      if (isCurrentUserIdentity(userId)) {
        setOpen(false);
        setMatchmaking(false);
        setLoading(true);
      }
    };

    const handleUserAttend = (userId) => {
      if (isCurrentUserIdentity(userId)) {
        setLoading(false);
      }
    };

    const activePuzzleId = resolveActivePuzzleId(activePuzzleTournamentId);
    if (!activePuzzleId) {
      if (!mounted) return;
      navigate("/game");
    } else {
      loadPuzzleMetadata(activePuzzleId, { navigateOnFailure: false }).then((loaded) => {
        if (!mounted) return;
        if (!loaded) {
          navigate("/game");
        }
      });
    }

    if (window.socket) {
      window.socket.on("USER_START", handleUserStart);
      window.socket.on("USER_ATTEND", handleUserAttend);
    } else {
      navigate("/game");
    }

    return () => {
      mounted = false;
      if (window.socket) {
        window.socket.off("USER_START", handleUserStart);
        window.socket.off("USER_ATTEND", handleUserAttend);
      }
    };
  }, [
    activePuzzleTournamentId,
    isCurrentUserIdentity,
    loadPuzzleMetadata,
    navigate,
    resolveActivePuzzleId,
  ]);

  useEffect(() => {
    const handleGameEnd = (rawData) => {
      try {
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        const normalizedEndReason = String(parse?.endReason || "")
          .trim()
          .toLowerCase();

        // Temporal: no cerrar la partida desde frontend cuando el backend
        // emite GAME_END por "resign", para permitir el flujo nuevo de
        // eliminación y salto de turnos dentro del juego.

        const activeRoomId = String(roomIdRef.current || roomId || "").trim();
        const incomingRoomId = String(parse?.roomId || "").trim();
        const incomingTournamentId = String(parse?.tournamentId || "").trim();
        const routeTournamentId = String(id || "").trim();
        const activePuzzleRouteId = String(activePuzzleTournamentIdRef.current || "").trim();
        const isCurrentRouteTournament =
          Boolean(incomingTournamentId) &&
          (incomingTournamentId === routeTournamentId ||
            (Boolean(activePuzzleRouteId) && incomingTournamentId === activePuzzleRouteId));
        const isCurrentRoomGameEnd =
          Boolean(activeRoomId) &&
          Boolean(incomingRoomId) &&
          incomingRoomId === activeRoomId;
        if (!isCurrentRouteTournament && !isCurrentRoomGameEnd) return;

        if (hasGameEndedRef.current) return;
          const winnerIds = extractWinnerIdsFromEndPayload(parse);
          if (normalizedEndReason === "resign" && winnerIds.length === 0) return;
          const participantIds = uniqueList(
            (Array.isArray(parse?.users) ? parse.users : [])
              .map((entry) => normalizeParticipantIdentity(entry))
              .filter(Boolean)
          );
          const myNormalizedUserId = normalizeProfileId(
            currentUserId || userEmail || user?.email || ""
          );
          const iAmOneOfWinners =
            Boolean(myNormalizedUserId) &&
            winnerIds.some(
              (winnerId) =>
                normalizeProfileId(winnerId) === myNormalizedUserId
            );
          const iAmParticipant =
            Boolean(myNormalizedUserId) &&
            participantIds.some(
              (participantId) =>
                normalizeProfileId(participantId) === myNormalizedUserId
            );
          const winnerPlayerIndex = extractWinnerPlayerIndexFromEndPayload(parse);
          const localPlayerIndex = Number(puzzleLocalPlayerIndex);
          const localPlayerIndexKnown =
            Number.isFinite(localPlayerIndex) && localPlayerIndex >= 0;
          const iAmWinnerByPlayerIndex =
            localPlayerIndexKnown &&
            winnerPlayerIndex != null &&
            winnerPlayerIndex === Math.floor(localPlayerIndex);
          const shouldFailPuzzleBecauseOpponentReachedGoal =
            shouldFailPuzzleEndAsOpponentWon({
              mode: normalizedMode,
              isPuzzleMode,
              normalizedEndReason,
              iAmParticipant,
              winnerIds,
              iAmOneOfWinners,
              winnerPlayerIndex,
              localPlayerIndex,
              localPlayerIndexKnown,
            });
          if (shouldFailPuzzleBecauseOpponentReachedGoal) {
            triggerPuzzleTurnLimitFailure({
              message: i18nT("lobby.intentarDeNuevo"),
              reason: "puzzle_turn_limit_reached",
            });
            return;
          }
          if (!isCurrentRoomGameEnd && !iAmParticipant && !iAmOneOfWinners) return;
          if (!activeRoomId && incomingRoomId && (iAmParticipant || iAmOneOfWinners)) {
            roomIdRef.current = incomingRoomId;
            setRoomId(incomingRoomId);
          }

          const fallbackWinnerMessage = buildWinnerAnnouncement(
            winnerIds,
            roomProfilesRef.current || {}
          );
          const winnerMessage =
            String(parse?.winnerMessage || "").trim() || fallbackWinnerMessage;
          clearStoredActiveMatchSessionForCurrentUser(incomingTournamentId || id);
          hasGameEndedRef.current = true;
          setGameEnded(true);
          setForcePostGameActions(true);
          setLastGameEndReason(normalizedEndReason || "unknown");
          setServerTurnClock({
            turnIndex: 0,
            turnStartedAtMs: 0,
            turnDurationMs: 0,
            remainingMsByIndex: [],
          });
          setServerClockOffsetMs(0);
          setPostGameMessage(winnerMessage);
          setChatModalOpen(false);
          notification.success({
            message: i18nT("tournaments.partidaFinalizada"),
            description: winnerMessage,
            duration: 6,
          });
          setLoading(false);
          setMatchmaking(false);
      } catch (err) {
        console.log(err);
      }
    };

    const handleTournamentRemoved = (rawData) => {
      try {
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData;
        if (String(parse?.tournamentId || "") === String(id)) {
          if (hasGameEndedRef.current) return;
          setOpen(false);
          setMatchmaking(false);
          setLoading(false);
          navigate("/game");
        }
      } catch (err) {
        console.log(err);
      }
    };

    const handlePlayerResigned = (rawData) => {
      try {
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        if (String(parse?.tournamentId || "") !== String(id)) return;

        const incomingRoomId = String(parse?.roomId || "").trim();
        const activeRoomId = String(roomIdRef.current || roomId || "").trim();
        if (activeRoomId && incomingRoomId && incomingRoomId !== activeRoomId) return;

        const myNormalizedUserId = normalizeProfileId(
          currentUserId || userEmail || user?.email || ""
        );
        const resignedUserId = normalizeProfileId(parse?.userId || "");
        if (!myNormalizedUserId || resignedUserId !== myNormalizedUserId) return;
        if (hasGameEndedRef.current) return;

        setOutUsersById((prev) => ({
          ...(prev && typeof prev === "object" ? prev : {}),
          [myNormalizedUserId]: true,
        }));
        clearStoredActiveMatchSessionForCurrentUser(id);
        hasGameEndedRef.current = true;
        setGameEnded(true);
        setLastGameEndReason("resign");
        setForcePostGameActions(true);
        setServerTurnClock({
          turnIndex: 0,
          turnStartedAtMs: 0,
          turnDurationMs: 0,
          remainingMsByIndex: [],
        });
        setServerClockOffsetMs(0);
        setPostGameMessage((prev) =>
          String(prev || "").trim()
            ? prev
            : `Te rendiste. ${postGameActionHelpText}`
        );
        setChatModalOpen(false);
        setOpen(false);
        setMatchmaking(false);
        setLoading(false);
        setUnitySessionNonce((prev) => prev + 1);
      } catch (error) {
        console.log(error);
      }
    };

    if (window.socket) {
      window.socket.on("GAME_END", handleGameEnd);
      window.socket.on("TOURNAMENT_REMOVED", handleTournamentRemoved);
      window.socket.on("PLAYER_RESIGNED", handlePlayerResigned);
    } else {
      navigate("/game");
    }

    return () => {
      if (window.socket) {
        window.socket.off("GAME_END", handleGameEnd);
        window.socket.off("TOURNAMENT_REMOVED", handleTournamentRemoved);
        window.socket.off("PLAYER_RESIGNED", handlePlayerResigned);
      }
    };
  }, [
    clearStoredActiveMatchSessionForCurrentUser,
    currentUserId,
    id,
    isPuzzleMode,
    navigate,
    puzzleLocalPlayerIndex,
    postGameActionHelpText,
    roomId,
    triggerPuzzleTurnLimitFailure,
    user?.email,
    userEmail,
  ]);

  useEffect(() => {
    setSpectatorCount(0);
  }, [id, roomId]);

  useEffect(() => {
    const handleRoomStats = (rawData) => {
      try {
        if (hasGameEndedRef.current) return;
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        if (String(parse?.tournamentId || "") !== String(id)) return;

        const incomingRoomId = String(parse?.roomId || "");
        const activeRoomId = String(roomIdRef.current || roomId || "").trim();
        if (activeRoomId && incomingRoomId && incomingRoomId !== activeRoomId) return;
        if (!activeRoomId && incomingRoomId) return;

        const incomingSpectators = Number(parse?.spectators);
        setSpectatorCount(
          Number.isFinite(incomingSpectators) && incomingSpectators >= 0
            ? incomingSpectators
            : 0
        );

        const incomingActivePlayers = Number(parse?.activePlayers);
        const knownPlayers = Array.isArray(roomUsers) ? roomUsers.length : 0;
        const hasRoomAssigned = Boolean(String(roomIdRef.current || roomId || "").trim());
        const looksDesynced =
          hasRoomAssigned &&
          Number.isFinite(incomingActivePlayers) &&
          incomingActivePlayers > 1 &&
          incomingActivePlayers > knownPlayers;

        if (looksDesynced && !reconnectingRef.current) {
          const now = Date.now();
          if (now - roomUsersResyncAtRef.current > 3000) {
            roomUsersResyncAtRef.current = now;
            requestTurnStateSync("room_stats_desync");
          }
        }
      } catch (error) {
        console.log(error);
      }
    };

    if (!window.socket) return undefined;
    window.socket.on("ROOM_STATS", handleRoomStats);
    return () => {
      window.socket.off("ROOM_STATS", handleRoomStats);
    };
  }, [id, requestTurnStateSync, roomId, roomUsers]);

  useEffect(() => {
    if (!window.socket || !isOnlineMode) return undefined;

    const parseSocketPayload = (rawData) => {
      if (typeof rawData === "string") {
        try {
          return JSON.parse(rawData);
        } catch {
          return {};
        }
      }
      return rawData && typeof rawData === "object" ? rawData : {};
    };

    const isCurrentRoomPayload = (payload) => {
      const parse = payload && typeof payload === "object" ? payload : {};
      if (String(parse?.tournamentId || "") !== String(id)) return false;
      const incomingRoomId = String(parse?.roomId || "").trim();
      const activeRoomId = String(roomIdRef.current || roomId || "").trim();
      if (activeRoomId && incomingRoomId && incomingRoomId !== activeRoomId) return false;
      if (!activeRoomId && incomingRoomId) return false;
      return true;
    };

    const resolveEventUserId = (payload) =>
      String(
        payload?.userId ||
          payload?.playerId ||
          payload?.participantId ||
          payload?.email ||
          ""
      ).trim();

    const handlePlayerDisconnected = (rawData) => {
      try {
        const parse = parseSocketPayload(rawData);
        if (!isCurrentRoomPayload(parse)) return;
        if (hasGameEndedRef.current) return;

        const rawUserId = resolveEventUserId(parse);
        const normalizedUserId = normalizeProfileId(rawUserId);
        if (!normalizedUserId || isGuestProfileId(normalizedUserId)) return;

        const reconnectByMs = resolveTimestampMs(parse?.reconnectBy);
        const fallbackReconnectByMs = Date.now() + 60 * 1000;
        const effectiveReconnectByMs =
          Number.isFinite(reconnectByMs) && reconnectByMs > 0
            ? reconnectByMs
            : fallbackReconnectByMs;

        setDisconnectedPlayersById((prev) => ({
          ...(prev && typeof prev === "object" && !Array.isArray(prev) ? prev : {}),
          [normalizedUserId]: {
            userId: rawUserId || normalizedUserId,
            reconnectByMs: effectiveReconnectByMs,
            updatedAt: Date.now(),
          },
        }));
      } catch (error) {
        console.log(error);
      }
    };

    const clearDisconnectedPlayer = (rawData, markAsOut = false) => {
      try {
        const parse = parseSocketPayload(rawData);
        if (!isCurrentRoomPayload(parse)) return;

        const rawUserId = resolveEventUserId(parse);
        const normalizedUserId = normalizeProfileId(rawUserId);
        if (!normalizedUserId || isGuestProfileId(normalizedUserId)) return;

        setDisconnectedPlayersById((prev) => {
          if (!prev || typeof prev !== "object" || Array.isArray(prev)) return {};
          if (!Object.prototype.hasOwnProperty.call(prev, normalizedUserId)) return prev;
          const next = { ...prev };
          delete next[normalizedUserId];
          return next;
        });

        if (markAsOut) {
          setOutUsersById((prev) => ({
            ...(prev && typeof prev === "object" ? prev : {}),
            [normalizedUserId]: true,
          }));
        }
      } catch (error) {
        console.log(error);
      }
    };

    const handlePlayerReconnectedNotice = (rawData) => clearDisconnectedPlayer(rawData, false);
    const handlePlayerDisconnectTimeoutNotice = (rawData) => clearDisconnectedPlayer(rawData, true);

    window.socket.on("PLAYER_DISCONNECTED", handlePlayerDisconnected);
    window.socket.on("PLAYER_RECONNECTED", handlePlayerReconnectedNotice);
    window.socket.on("PLAYER_DISCONNECT_TIMEOUT", handlePlayerDisconnectTimeoutNotice);

    return () => {
      window.socket.off("PLAYER_DISCONNECTED", handlePlayerDisconnected);
      window.socket.off("PLAYER_RECONNECTED", handlePlayerReconnectedNotice);
      window.socket.off("PLAYER_DISCONNECT_TIMEOUT", handlePlayerDisconnectTimeoutNotice);
    };
  }, [id, isOnlineMode, roomId]);

  useEffect(() => {
    setDisconnectedPlayersById((prev) => {
      if (!prev || typeof prev !== "object" || Array.isArray(prev)) return {};
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;

      let changed = false;
      const next = {};
      keys.forEach((key) => {
        if (outUsersById?.[key] === true) {
          changed = true;
          return;
        }
        next[key] = prev[key];
      });
      return changed ? next : prev;
    });
  }, [outUsersById]);

  useEffect(() => {
    if (!window.socket) return undefined;

    const handleSocketDisconnect = () => {
      if (isBotLocalMode && !isSpectatorRequested) {
        setConnectionState("connected");
        return;
      }
      setConnectionState("reconnecting");
      startReconnectHttpPolling();
      if (!reconnectNoticeShownRef.current) {
        reconnectNoticeShownRef.current = true;
        notification.warning({
          message: i18nT("tournaments.sinConexion"),
          description: i18nT("tournaments.intentandoReconectarYSincronizarLaPartida"),
        });
      }
    };

    const handleSocketConnect = () => {
      reconnectNoticeShownRef.current = false;
      stopReconnectHttpPolling();
      if (isBotLocalMode && !isSpectatorRequested) {
        setConnectionState("connected");
        return;
      }
      if (!hasGameEndedRef.current && !gameEnded) {
        recoverSession({
          silent: !Boolean(String(roomIdRef.current || roomId || "").trim()),
        }).catch((error) => console.log(error));
      } else {
        setConnectionState("connected");
      }
    };

    const handlePlayerReconnected = (rawData) => {
      try {
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        if (String(parse?.tournamentId || "") !== String(id)) return;
        const activeRoomId = String(roomIdRef.current || roomId || "").trim();
        if (activeRoomId && String(parse?.roomId || "") !== activeRoomId) return;
        if (!activeRoomId && String(parse?.roomId || "").trim()) return;
        stopReconnectHttpPolling();
        setConnectionState("connected");
      } catch (error) {
        console.log(error);
      }
    };

    window.socket.on("disconnect", handleSocketDisconnect);
    window.socket.on("connect", handleSocketConnect);
    window.socket.on("PLAYER_RECONNECTED", handlePlayerReconnected);

    return () => {
      window.socket.off("disconnect", handleSocketDisconnect);
      window.socket.off("connect", handleSocketConnect);
      window.socket.off("PLAYER_RECONNECTED", handlePlayerReconnected);
      stopReconnectHttpPolling();
    };
  }, [
    gameEnded,
    id,
    isBotLocalMode,
    isSpectatorRequested,
    recoverSession,
    roomId,
    startReconnectHttpPolling,
    stopReconnectHttpPolling,
  ]);

  useEffect(() => {
    const handleChatMessage = (rawData) => {
      try {
        if (hasGameEndedRef.current) return;
        const parse =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData || {};
        if (String(parse?.tournamentId || "") !== String(id)) return;
        const activeRoomId = String(roomIdRef.current || roomId || "").trim();
        if (activeRoomId && String(parse?.roomId || "") !== activeRoomId) return;
        if (!activeRoomId && String(parse?.roomId || "").trim()) return;
        if (!parse?.message) return;

        setChatMessages((prev) => {
          const next = [
            ...prev,
            {
              userId: parse.userId || "Player",
              message: String(parse.message || ""),
              sentAt: Number(parse.sentAt) || Date.now(),
            },
          ];
          return next.slice(-40);
        });
      } catch (error) {
        console.log(error);
      }
    };

    if (!window.socket) return undefined;
    window.socket.on("CHAT_MESSAGE", handleChatMessage);
    return () => {
      window.socket.off("CHAT_MESSAGE", handleChatMessage);
    };
  }, [id, roomId]);

  useEffect(() => {
    resetPuzzleRoundState();
    setChatMessages([]);
    setChatModalOpen(false);
    setMoveNotations([]);
    setPuzzleFailedByTurnLimit(false);
    roomCosmeticAuthorityIdRef.current = "local";
    roomCosmeticRevisionRef.current = 0;
    setRoomProfiles({});
    setPlayerRuntimeMap({});
    setIndexedRuntimeBySlot({});
    setOutUsersById({});
    setActiveTurnIndex(0);
    setBotTurnSlotIndexes([]);
    setLocalRuntimeTurnIndex(null);
    setServerTurnClock({
      turnIndex: 0,
      turnStartedAtMs: 0,
      turnDurationMs: 0,
      remainingMsByIndex: [],
    });
    setServerClockOffsetMs(0);
  }, [id, resetPuzzleRoundState, roomId]);

  useEffect(() => {
    setRoomUsers([]);
  }, [id]);

  useEffect(() => {
    const handleMoveData = (rawPayload) => {
      const normalizedMovePayload = extractMoveDataPayload(rawPayload, activeTurnIndex);
      const incomingMoveSequence = parseMoveSequenceFromPayload(normalizedMovePayload);
      if (incomingMoveSequence > Number(lastMoveSequenceRef.current || 0)) {
        lastMoveSequenceRef.current = incomingMoveSequence;
      }

      let rawPayloadText = normalizedMovePayload;
      if (normalizedMode === "freebox") {
        puzzleMoveLogRef.current = appendPuzzleMovePayload(
          puzzleMoveLogRef.current,
          rawPayloadText
        );
      }

      const entry = resolveNotationEntry(rawPayload, activeTurnIndex);
      const fallbackPlayerIndex = resolveMoveActorIndex(rawPayload, activeTurnIndex);
      const normalizedFreeBoxBaseMode = normalizeFreeBoxBaseMode(
        tourInfo?.freeBoxBaseMode || tourInfo?.mode,
        "free"
      );
      const shouldRegisterSocketMoveForPuzzle =
        normalizedMode !== "freebox" || normalizedFreeBoxBaseMode === "dice";
      if (shouldRegisterSocketMoveForPuzzle && (rawPayloadText || entry)) {
        registerPuzzleSolverTurn(
          entry?.playerIndex ?? fallbackPlayerIndex,
          rawPayloadText,
          entry?.notation || "",
          rawPayload
        );
      }
      if (!entry) return;
      unityMoveEventCounterRef.current += 1;
      setMoveNotations((prev) =>
        appendNotationEntry(prev, {
          playerIndex: entry.playerIndex,
          notation: entry.notation,
          payload: rawPayloadText,
        })
      );
    };

    if (!window.socket) return undefined;
    window.socket.on("MOVE_DATA", handleMoveData);
    return () => {
      window.socket.off("MOVE_DATA", handleMoveData);
    };
  }, [
    activeTurnIndex,
    id,
    normalizedMode,
    registerPuzzleSolverTurn,
    roomId,
    tourInfo?.freeBoxBaseMode,
    tourInfo?.mode,
  ]);

  useEffect(() => {
    if (!useLocalBotTurnIndicatorFallback) {
      previousLocalClockBySlotRef.current = {};
      setLocalRuntimeTurnIndex(null);
      return;
    }

    const safePlayers = Math.max(2, Math.min(4, Number(localTurnPlayerCount) || 2));
    const previousSnapshot = previousLocalClockBySlotRef.current || {};
    const nextSnapshot = {};
    let detectedTurnIndex = NaN;

    for (let index = 0; index < safePlayers; index += 1) {
      const parsedClock = parseClockSeconds(indexedRuntimeBySlot[index]?.clockSeconds);
      if (!Number.isFinite(parsedClock)) continue;

      nextSnapshot[index] = parsedClock;
      const previousClock = Number(previousSnapshot[index]);
      if (!Number.isFinite(previousClock)) continue;
      if (!Number.isFinite(detectedTurnIndex) && parsedClock < previousClock - 0.01) {
        detectedTurnIndex = index;
      }
    }

    previousLocalClockBySlotRef.current = nextSnapshot;
    if (Number.isFinite(detectedTurnIndex)) {
      setLocalRuntimeTurnIndex(Math.floor(detectedTurnIndex));
    }
  }, [indexedRuntimeBySlot, localTurnPlayerCount, useLocalBotTurnIndicatorFallback]);

  useEffect(() => {
    const scrollBox = notationScrollRef.current;
    if (!scrollBox) return;
    scrollBox.scrollLeft = scrollBox.scrollWidth;
  }, [notationRows]);

  const sendQuickChat = useCallback(
    (message) => {
      if (!window.socket) return;
      const cleanMessage = String(message || "").trim();
      if (!cleanMessage) return;
      if (!roomId || !quickChatEnabled || !chatUserId) return;
      if (isSpectatorRequested) return;

      window.socket.emit(
        "CHAT_MESSAGE",
        {
          tournamentId: id,
          roomId: String(roomId),
          userId: chatUserId,
          message: cleanMessage,
        }
      );
    },
    [chatUserId, id, isSpectatorRequested, quickChatEnabled, roomId]
  );

  useEffect(() => {
    const scrollBox = chatFloatingScrollRef.current;
    if (!scrollBox) return;
    scrollBox.scrollTop = scrollBox.scrollHeight;
  }, [chatMessages, chatModalOpen]);

  useEffect(() => {
    if (!chatModalOpen || isSpectatorRequested || typeof window === "undefined") return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxWidth = Math.max(
      CHAT_FLOATING_MIN_WIDTH,
      Math.min(CHAT_FLOATING_MAX_WIDTH, viewportWidth - 12)
    );
    const maxHeight = Math.max(
      CHAT_FLOATING_MIN_HEIGHT,
      Math.min(CHAT_FLOATING_MAX_HEIGHT, viewportHeight - 12)
    );

    const width = clampValue(CHAT_FLOATING_DEFAULT_WIDTH, CHAT_FLOATING_MIN_WIDTH, maxWidth);
    const height = clampValue(CHAT_FLOATING_DEFAULT_HEIGHT, CHAT_FLOATING_MIN_HEIGHT, maxHeight);

    setChatFloatingSize({ width, height });
    setChatFloatingPosition({
      x: clampValue(viewportWidth - width - 16, 0, Math.max(0, viewportWidth - width)),
      y: clampValue(viewportHeight - height - 16, 0, Math.max(0, viewportHeight - height)),
    });
  }, [chatModalOpen, isSpectatorRequested]);

  const handlePuzzleEndActionButtonDragStart = useCallback(
    (event) => {
      if (!canShowPuzzleEndActionButton || isSpectatorRequested) return;
      if (event.button !== 2) return;
      if (typeof window === "undefined") return;

      event.preventDefault();
      event.stopPropagation();

      const panelRect = puzzleEndActionButtonPanelRef.current?.getBoundingClientRect?.();
      const panelWidth = Math.max(
        PUZZLE_END_ACTION_BUTTON_DEFAULT_WIDTH,
        Math.round(panelRect?.width || 0)
      );
      const panelHeight = Math.max(
        PUZZLE_END_ACTION_BUTTON_DEFAULT_HEIGHT,
        Math.round(panelRect?.height || 0)
      );

      puzzleEndActionButtonDragRef.current = {
        startMouseX: event.clientX,
        startMouseY: event.clientY,
        startX: puzzleEndActionButtonPosition?.x ?? 0,
        startY: puzzleEndActionButtonPosition?.y ?? 0,
        panelWidth,
        panelHeight,
      };

      const handleMouseMove = (moveEvent) => {
        const dragData = puzzleEndActionButtonDragRef.current;
        if (!dragData) return;
        const dx = moveEvent.clientX - dragData.startMouseX;
        const dy = moveEvent.clientY - dragData.startMouseY;
        const maxX = Math.max(0, window.innerWidth - dragData.panelWidth);
        const maxY = Math.max(0, window.innerHeight - dragData.panelHeight);

        setPuzzleEndActionButtonPosition({
          x: clampValue(dragData.startX + dx, 0, maxX),
          y: clampValue(dragData.startY + dy, 0, maxY),
        });
      };

      const handleMouseUp = () => {
      puzzleEndActionButtonDragRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [
      canShowPuzzleEndActionButton,
      isSpectatorRequested,
      puzzleEndActionButtonPosition?.x,
      puzzleEndActionButtonPosition?.y,
    ]
  );

  const handleQuickChatFloatingDragStart = useCallback(
    (event) => {
      if (!chatModalOpen || isSpectatorRequested || typeof window === "undefined") return;
      if (event.button !== 0) return;

      const targetElement = event.target instanceof Element ? event.target : null;
      if (targetElement?.closest("button,input,textarea")) return;

      event.preventDefault();

      chatFloatingDragRef.current = {
        startMouseX: event.clientX,
        startMouseY: event.clientY,
        startX: chatFloatingPosition.x,
        startY: chatFloatingPosition.y,
      };

      const handleMouseMove = (moveEvent) => {
        const dragData = chatFloatingDragRef.current;
        if (!dragData) return;

        const dx = moveEvent.clientX - dragData.startMouseX;
        const dy = moveEvent.clientY - dragData.startMouseY;
        const maxX = Math.max(0, window.innerWidth - chatFloatingSize.width);
        const maxY = Math.max(0, window.innerHeight - chatFloatingSize.height);

        setChatFloatingPosition({
          x: clampValue(dragData.startX + dx, 0, maxX),
          y: clampValue(dragData.startY + dy, 0, maxY),
        });
      };

      const handleMouseUp = () => {
        chatFloatingDragRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [
      chatModalOpen,
      chatFloatingPosition.x,
      chatFloatingPosition.y,
      chatFloatingSize.height,
      chatFloatingSize.width,
      isSpectatorRequested,
    ]
  );

  const handleQuickChatFloatingResizeStart = useCallback(
    (corner, event) => {
      if (!chatModalOpen || isSpectatorRequested || typeof window === "undefined") return;
      if (event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      chatFloatingResizeRef.current = {
        corner,
        startMouseX: event.clientX,
        startMouseY: event.clientY,
        startX: chatFloatingPosition.x,
        startY: chatFloatingPosition.y,
        startWidth: chatFloatingSize.width,
        startHeight: chatFloatingSize.height,
      };

      const handleMouseMove = (moveEvent) => {
        const resizeData = chatFloatingResizeRef.current;
        if (!resizeData) return;

        const dx = moveEvent.clientX - resizeData.startMouseX;
        const dy = moveEvent.clientY - resizeData.startMouseY;

        let nextX = resizeData.startX;
        let nextY = resizeData.startY;
        let nextWidth = resizeData.startWidth;
        let nextHeight = resizeData.startHeight;

        if (corner.includes("e")) nextWidth = resizeData.startWidth + dx;
        if (corner.includes("s")) nextHeight = resizeData.startHeight + dy;
        if (corner.includes("w")) {
          nextWidth = resizeData.startWidth - dx;
          nextX = resizeData.startX + dx;
        }
        if (corner.includes("n")) {
          nextHeight = resizeData.startHeight - dy;
          nextY = resizeData.startY + dy;
        }

        const maxWidth = Math.max(
          CHAT_FLOATING_MIN_WIDTH,
          Math.min(CHAT_FLOATING_MAX_WIDTH, window.innerWidth - 8)
        );
        const maxHeight = Math.max(
          CHAT_FLOATING_MIN_HEIGHT,
          Math.min(CHAT_FLOATING_MAX_HEIGHT, window.innerHeight - 8)
        );
        const width = clampValue(nextWidth, CHAT_FLOATING_MIN_WIDTH, maxWidth);
        const height = clampValue(nextHeight, CHAT_FLOATING_MIN_HEIGHT, maxHeight);
        const x = clampValue(nextX, 0, Math.max(0, window.innerWidth - width));
        const y = clampValue(nextY, 0, Math.max(0, window.innerHeight - height));

        setChatFloatingSize({ width, height });
        setChatFloatingPosition({ x, y });
      };

      const handleMouseUp = () => {
        chatFloatingResizeRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [
      chatModalOpen,
      chatFloatingPosition.x,
      chatFloatingPosition.y,
      chatFloatingSize.height,
      chatFloatingSize.width,
      isSpectatorRequested,
    ]
  );

  useEffect(() => {
    const normalizeMoveCells = (value) => {
      if (Array.isArray(value)) {
        return value.map((entry) => String(entry || "").trim()).filter(Boolean);
      }
      return String(value || "")
        .split(",")
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
    };

    const handleUnityLocalMatchMove = (rawEvent) => {
      if (rawEvent?.type === "message") {
        const iframeWindow = unityIframeRef.current?.contentWindow || null;
        if (iframeWindow && rawEvent?.source && rawEvent.source !== iframeWindow) return;
      }

      const rawInput = rawEvent?.detail ?? rawEvent?.data ?? rawEvent ?? null;
      const envelope = resolveMetricsEnvelope(rawInput);
      const rootPayload = envelope.root || {};
      const payload = envelope.payload || rootPayload;
      if (!payload || typeof payload !== "object") return;

      const eventType = String(
        payload.type ||
          payload.event ||
          rootPayload.type ||
          rootPayload.event ||
          rawEvent?.type ||
          ""
      )
        .trim()
        .toUpperCase();
      if (eventType !== LOCAL_MATCH_MOVE_EVENT) return;
      if (!shouldRecordLocalMatchHistory) return;

      const pickValue = (...keys) => {
        const sources = [payload, rootPayload];
        for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex += 1) {
          const source = sources[sourceIndex];
          if (!source || typeof source !== "object") continue;
          for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
            const key = keys[keyIndex];
            if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
            const value = source[key];
            if (value === null || value === undefined) continue;
            return value;
          }
        }
        return "";
      };
      const pickString = (...keys) => String(pickValue(...keys) || "").trim();
      const pickNumber = (...keys) => {
        const parsed = Number(pickValue(...keys));
        return Number.isFinite(parsed) ? Math.trunc(parsed) : NaN;
      };

      const activeTournamentId = String(
        pickValue("tournamentId", "matchId", "matchID") ||
          roomSessionTournamentIdRef.current ||
          id ||
          ""
      ).trim();
      const activeRoomId = String(
        pickValue("roomId", "roomID") || roomIdRef.current || roomId || ""
      ).trim();
      if (!activeTournamentId || !activeRoomId) return;

      const playerIndexNumber = pickNumber(
        "playerIndexNumber",
        "playerIndex",
        "playerSlot",
        "turnIndex",
        "index"
      );
      if (!Number.isFinite(playerIndexNumber)) return;

      const cubeNumber = pickValue(
        "cubeNumber",
        "cube",
        "cubeNo",
        "cubeIndex",
        "selectedCube",
        "selectedCubeId",
        "cubeId"
      );
      const cubeNumberText = String(cubeNumber ?? "").trim();
      if (!cubeNumberText) return;

      const pathCells = normalizeMoveCells(pickValue("pathCells", "path", "movePathCells"));
      const entryPath = normalizeMoveCells(pickValue("entryPath", "entryCells"));
      const outgoingPayload = {
        tournamentId: activeTournamentId,
        roomId: activeRoomId,
        userId: currentUserId || userEmail || user?.email || "",
        playerIndexNumber,
        cubeNumber: cubeNumberText,
        targetPoint: pickString("targetPoint", "target", "toPoint", "targetPosition"),
        targetPointId: pickString("targetPointId", "targetId"),
        cubePoint: pickString("cubePoint", "sourcePoint", "fromPoint", "startPoint"),
        roundIndex: pickValue("roundIndex", "round", "turnRound", "step"),
        turnUnit: pickValue("turnUnit", "turn_unit", "unit"),
        sourceCell: pickString("sourceCell", "fromCell"),
        targetCell: pickString("targetCell", "toCell"),
        pathCells,
        entryPath,
        flexibleAmbiguousDiceFirstMove:
          pickValue(
            "flexibleAmbiguousDiceFirstMove",
            "flexibleAmbiguousDiceFirstMoveUsed"
          ) === true ||
          String(
            pickValue(
              "flexibleAmbiguousDiceFirstMove",
              "flexibleAmbiguousDiceFirstMoveUsed"
            ) || ""
          )
            .trim()
            .toLowerCase() === "true" ||
          String(
            pickValue(
              "flexibleAmbiguousDiceFirstMove",
              "flexibleAmbiguousDiceFirstMoveUsed"
            ) || ""
          ).trim() === "1",
      };

      const dedupeKey = [
        outgoingPayload.tournamentId,
        outgoingPayload.roomId,
        outgoingPayload.playerIndexNumber,
        outgoingPayload.cubeNumber,
        outgoingPayload.targetPointId,
        outgoingPayload.targetPoint,
        outgoingPayload.cubePoint,
        outgoingPayload.roundIndex,
        pathCells.join(","),
      ].join("|");
      const nowMs = Date.now();
      if (
        lastLocalHistoryMoveKeyRef.current === dedupeKey &&
        nowMs - Number(lastLocalHistoryMoveAtRef.current || 0) < 800
      ) {
        return;
      }
      lastLocalHistoryMoveKeyRef.current = dedupeKey;
      lastLocalHistoryMoveAtRef.current = nowMs;

      const socket = window.socket || getSharedSocket();
      if (!socket) return;
      socket.emit("LOCAL_MATCH_MOVE", JSON.stringify(outgoingPayload));
    };

    const handleUnityLocalMatchRollDice = (rawEvent) => {
      if (rawEvent?.type === "message") {
        const iframeWindow = unityIframeRef.current?.contentWindow || null;
        if (iframeWindow && rawEvent?.source && rawEvent.source !== iframeWindow) return;
      }

      const rawInput = rawEvent?.detail ?? rawEvent?.data ?? rawEvent ?? null;
      const envelope = resolveMetricsEnvelope(rawInput);
      const rootPayload = envelope.root || {};
      const payload = envelope.payload || rootPayload;
      if (!payload || typeof payload !== "object") return;

      const eventType = String(
        payload.type ||
          payload.event ||
          rootPayload.type ||
          rootPayload.event ||
          rawEvent?.type ||
          ""
      )
        .trim()
        .toUpperCase();
      if (eventType !== LOCAL_MATCH_ROLL_DICE_EVENT) return;
      if (!shouldRecordLocalMatchHistory) return;

      const pickValue = (...keys) => {
        const sources = [payload, rootPayload];
        for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex += 1) {
          const source = sources[sourceIndex];
          if (!source || typeof source !== "object") continue;
          for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
            const key = keys[keyIndex];
            if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
            const value = source[key];
            if (value === null || value === undefined) continue;
            return value;
          }
        }
        return "";
      };

      const activeTournamentId = String(
        pickValue("tournamentId", "matchId", "matchID") ||
          roomSessionTournamentIdRef.current ||
          id ||
          ""
      ).trim();
      const activeRoomId = String(
        pickValue("roomId", "roomID") || roomIdRef.current || roomId || ""
      ).trim();
      if (!activeTournamentId || !activeRoomId) return;

      const die1 = Math.trunc(Number(pickValue("die1", "dice1", "val1", "value1")));
      const die2 = Math.trunc(Number(pickValue("die2", "dice2", "val2", "value2")));
      if (!Number.isFinite(die1) || !Number.isFinite(die2)) return;
      if (die1 < 1 || die1 > 6 || die2 < 1 || die2 > 6) return;

      const emittedAtMs = String(pickValue("emittedAtMs", "timestamp", "at") || "").trim();
      const dedupeKey = [
        activeTournamentId,
        activeRoomId,
        die1,
        die2,
        emittedAtMs,
      ].join("|");
      const nowMs = Date.now();
      if (
        lastLocalHistoryRollKeyRef.current === dedupeKey &&
        nowMs - Number(lastLocalHistoryRollAtRef.current || 0) < 1200
      ) {
        return;
      }
      lastLocalHistoryRollKeyRef.current = dedupeKey;
      lastLocalHistoryRollAtRef.current = nowMs;

      const socket = window.socket || getSharedSocket();
      if (!socket) return;
      socket.emit(
        "LOCAL_MATCH_ROLL_DICE",
        JSON.stringify({
          tournamentId: activeTournamentId,
          roomId: activeRoomId,
          userId: currentUserId || userEmail || user?.email || "",
          die1,
          die2,
        })
      );
    };

    const bindLocalHistoryMoveListeners = (target) => {
      if (!target || typeof target.addEventListener !== "function") {
        return () => {};
      }

      target.addEventListener(LOCAL_MATCH_MOVE_EVENT, handleUnityLocalMatchMove);
      target.addEventListener(LOCAL_MATCH_ROLL_DICE_EVENT, handleUnityLocalMatchRollDice);
      target.addEventListener("message", handleUnityLocalMatchMove);
      target.addEventListener("message", handleUnityLocalMatchRollDice);

      return () => {
        if (!target || typeof target.removeEventListener !== "function") return;
        target.removeEventListener(LOCAL_MATCH_MOVE_EVENT, handleUnityLocalMatchMove);
        target.removeEventListener(LOCAL_MATCH_ROLL_DICE_EVENT, handleUnityLocalMatchRollDice);
        target.removeEventListener("message", handleUnityLocalMatchMove);
        target.removeEventListener("message", handleUnityLocalMatchRollDice);
      };
    };

    const unbindWindow = bindLocalHistoryMoveListeners(window);
    let iframeTarget = unityIframeRef.current?.contentWindow || null;
    let unbindIframe = bindLocalHistoryMoveListeners(iframeTarget);
    const iframeElement = unityIframeRef.current;

    const handleIframeLoad = () => {
      unbindIframe();
      iframeTarget = unityIframeRef.current?.contentWindow || null;
      unbindIframe = bindLocalHistoryMoveListeners(iframeTarget);
    };

    if (iframeElement && typeof iframeElement.addEventListener === "function") {
      iframeElement.addEventListener("load", handleIframeLoad);
    }

    return () => {
      unbindWindow();
      unbindIframe();
      if (iframeElement && typeof iframeElement.removeEventListener === "function") {
        iframeElement.removeEventListener("load", handleIframeLoad);
      }
    };
  }, [
    currentUserId,
    id,
    roomId,
    shouldRecordLocalMatchHistory,
    user?.email,
    userEmail,
  ]);

  useEffect(() => {
    const handleUnityMoveNotation = (rawEvent) => {
      if (rawEvent?.type === "message") {
        const iframeWindow = unityIframeRef.current?.contentWindow || null;
        if (iframeWindow && rawEvent?.source && rawEvent.source !== iframeWindow) return;
      }

      let movePayload = "";
      if (normalizedMode === "freebox") {
        movePayload = extractMoveDataPayload(
          rawEvent?.detail ?? rawEvent?.data ?? rawEvent ?? null,
          activeTurnIndex
        );
        puzzleMoveLogRef.current = appendPuzzleMovePayload(
          puzzleMoveLogRef.current,
          movePayload
        );
      }

      const rawMoveEventInput = rawEvent?.detail ?? rawEvent?.data ?? rawEvent ?? null;
      const entry = resolveNotationEntry(rawMoveEventInput, activeTurnIndex);
      const fallbackPlayerIndex = resolveMoveActorIndex(rawMoveEventInput, activeTurnIndex);
      if (movePayload || entry) {
        registerPuzzleSolverTurn(
          entry?.playerIndex ?? fallbackPlayerIndex,
          movePayload,
          entry?.notation || "",
          rawMoveEventInput
        );
      }
      if (!entry) return;
      unityMoveEventCounterRef.current += 1;

      setMoveNotations((prev) =>
        appendNotationEntry(prev, {
          playerIndex: entry.playerIndex,
          notation: entry.notation,
          payload: movePayload,
        })
      );

      const safePlayers = Math.max(2, Math.min(4, Number(localTurnPlayerCount) || 2));
      const nextTurn =
        ((entry.playerIndex + 1) % safePlayers + safePlayers) % safePlayers;
      if (useLocalBotTurnIndicatorFallback) {
        setLocalRuntimeTurnIndex(nextTurn);
      }
      if (useLocalMoveTurnFallback) {
        setActiveTurnIndex(nextTurn);
      }
    };

    const bindNotationListeners = (target) => {
      if (!target || typeof target.addEventListener !== "function") {
        return () => {};
      }

      target.addEventListener("XIMA_MOVE_NOTATION", handleUnityMoveNotation);
      target.addEventListener("xima-move-notation", handleUnityMoveNotation);
      target.addEventListener("XIMA_MOVE_NOTATION_EVENT", handleUnityMoveNotation);
      target.addEventListener("message", handleUnityMoveNotation);

      return () => {
        if (!target || typeof target.removeEventListener !== "function") return;
        target.removeEventListener("XIMA_MOVE_NOTATION", handleUnityMoveNotation);
        target.removeEventListener("xima-move-notation", handleUnityMoveNotation);
        target.removeEventListener("XIMA_MOVE_NOTATION_EVENT", handleUnityMoveNotation);
        target.removeEventListener("message", handleUnityMoveNotation);
      };
    };

    const unbindWindow = bindNotationListeners(window);
    let iframeTarget = unityIframeRef.current?.contentWindow || null;
    let unbindIframe = bindNotationListeners(iframeTarget);
    const iframeElement = unityIframeRef.current;

    const handleIframeLoad = () => {
      unbindIframe();
      iframeTarget = unityIframeRef.current?.contentWindow || null;
      unbindIframe = bindNotationListeners(iframeTarget);
    };

    if (iframeElement && typeof iframeElement.addEventListener === "function") {
      iframeElement.addEventListener("load", handleIframeLoad);
    }

    return () => {
      unbindWindow();
      unbindIframe();
      if (iframeElement && typeof iframeElement.removeEventListener === "function") {
        iframeElement.removeEventListener("load", handleIframeLoad);
      }
    };
  }, [
    activeTurnIndex,
    localTurnPlayerCount,
    normalizedMode,
    registerPuzzleSolverTurn,
    useLocalBotTurnIndicatorFallback,
    useLocalMoveTurnFallback,
  ]);

  useEffect(() => {
    const handlePuzzleSnapshotBridgeEvent = (rawEvent) => {
      if (rawEvent?.type === "message") {
        const iframeWindow = unityIframeRef.current?.contentWindow || null;
        if (iframeWindow && rawEvent?.source && rawEvent.source !== iframeWindow) return;
      }

      const envelope = resolveMetricsEnvelope(rawEvent?.detail ?? rawEvent?.data ?? rawEvent ?? null);
      const rootPayload = envelope.root || {};
      const payload = envelope.payload || rootPayload;
      if (!payload || typeof payload !== "object") return;

      const eventType = String(
        payload.type || payload.event || rootPayload.type || rootPayload.event || ""
      )
        .trim()
        .toUpperCase();
      if (!eventType) return;

      const requestId = String(payload.requestId || rootPayload.requestId || "").trim();
      if (eventType === PUZZLE_SNAPSHOT_EVENT) {
        const snapshot = normalizePuzzleSnapshotPayload(
          payload.snapshot || payload.positionSnapshot || payload.snapshotPayload || payload
        );
        if (requestId) {
          const snapshotFailed =
            payload.success === false || rootPayload.success === false;
          pendingPuzzleSnapshotRequestsRef.current[requestId] =
            snapshot ||
            (snapshotFailed
              ? {
                  __puzzleSnapshotError: true,
                  success: false,
                  message: String(
                    payload.message ||
                      rootPayload.message ||
                      payload.reason ||
                      rootPayload.reason ||
                      "snapshot_not_ready"
                  ).trim(),
                }
              : "__ERROR__");
        }
        return;
      }

      if (eventType === PUZZLE_SNAPSHOT_APPLIED_EVENT) {
        if (requestId) {
          pendingPuzzleSnapshotApplyRef.current[requestId] = payload;
        }
        return;
      }

      if (eventType === MATCH_STATE_APPLIED_EVENT) {
        if (requestId) {
          pendingUnityMatchLoadRef.current[requestId] = payload;
        }
        return;
      }

      if (eventType === LOCAL_GAME_END_EVENT) {
        const normalizedEndReason = String(payload.endReason || payload.reason || "unknown")
          .trim()
          .toLowerCase();
        const winnerMessage = String(payload.winnerMessage || payload.message || "").trim();
        emitLocalMatchEndForHistory({
          ...payload,
          endReason: normalizedEndReason || payload.endReason || payload.reason,
          winnerMessage,
        });
        if (hasGameEndedRef.current) {
          if (normalizedEndReason === "timeout") {
            setLastGameEndReason("timeout");
            setPostGameMessage(`Se acabo el tiempo. ${postGameActionHelpText}`);
          } else if (normalizedEndReason === "puzzle_solve_time_expired") {
            setLastGameEndReason("puzzle_solve_time_expired");
            if (winnerMessage) setPostGameMessage(winnerMessage);
          }
          return;
        }

        const winnerIds = extractWinnerIdsFromEndPayload(payload);
        const normalizedLocalUserId = normalizeProfileId(
          currentUserId || userEmail || user?.email || ""
        );
        const iAmWinnerById =
          Boolean(normalizedLocalUserId) &&
          winnerIds.some(
            (winnerId) => normalizeProfileId(winnerId) === normalizedLocalUserId
          );
        const winnerPlayerIndex = extractWinnerPlayerIndexFromEndPayload(payload);
        const localPlayerIndex = Number(puzzleLocalPlayerIndex);
        const localPlayerIndexKnown =
          Number.isFinite(localPlayerIndex) && localPlayerIndex >= 0;
        const iAmWinnerByPlayerIndex =
          localPlayerIndexKnown &&
          winnerPlayerIndex != null &&
          winnerPlayerIndex === Math.floor(localPlayerIndex);
        const shouldFailPuzzleBecauseOpponentReachedGoal =
          shouldFailPuzzleEndAsOpponentWon({
            mode: normalizedMode,
            isPuzzleMode,
            normalizedEndReason,
            iAmParticipant: true,
            winnerIds,
            iAmOneOfWinners: iAmWinnerById,
            winnerPlayerIndex,
            localPlayerIndex,
            localPlayerIndexKnown,
          });
        if (shouldFailPuzzleBecauseOpponentReachedGoal) {
          triggerPuzzleTurnLimitFailure({
            message: i18nT("lobby.intentarDeNuevo"),
            reason: "puzzle_turn_limit_reached",
          });
          return;
        }

        clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);
        hasGameEndedRef.current = true;
        setGameEnded(true);
        setForcePostGameActions(true);
        setLastGameEndReason(normalizedEndReason || "unknown");
        setServerTurnClock({
          turnIndex: 0,
          turnStartedAtMs: 0,
          turnDurationMs: 0,
          remainingMsByIndex: [],
        });
        setServerClockOffsetMs(0);
        if (winnerMessage) {
          setPostGameMessage(winnerMessage);
        }
        setChatModalOpen(false);
        setOpen(false);
        setMatchmaking(false);
        setLoading(false);
      }
    };

    const bindPuzzleSnapshotListeners = (target) => {
      if (!target || typeof target.addEventListener !== "function") {
        return () => {};
      }

      target.addEventListener("message", handlePuzzleSnapshotBridgeEvent);
      target.addEventListener(PUZZLE_SNAPSHOT_EVENT, handlePuzzleSnapshotBridgeEvent);
      target.addEventListener(PUZZLE_SNAPSHOT_APPLIED_EVENT, handlePuzzleSnapshotBridgeEvent);
      target.addEventListener(MATCH_STATE_APPLIED_EVENT, handlePuzzleSnapshotBridgeEvent);
      target.addEventListener(LOCAL_GAME_END_EVENT, handlePuzzleSnapshotBridgeEvent);

      return () => {
        if (!target || typeof target.removeEventListener !== "function") return;
        target.removeEventListener("message", handlePuzzleSnapshotBridgeEvent);
        target.removeEventListener(PUZZLE_SNAPSHOT_EVENT, handlePuzzleSnapshotBridgeEvent);
        target.removeEventListener(PUZZLE_SNAPSHOT_APPLIED_EVENT, handlePuzzleSnapshotBridgeEvent);
        target.removeEventListener(MATCH_STATE_APPLIED_EVENT, handlePuzzleSnapshotBridgeEvent);
        target.removeEventListener(LOCAL_GAME_END_EVENT, handlePuzzleSnapshotBridgeEvent);
      };
    };

    const unbindWindow = bindPuzzleSnapshotListeners(window);
    let iframeTarget = unityIframeRef.current?.contentWindow || null;
    let unbindIframe = bindPuzzleSnapshotListeners(iframeTarget);
    const iframeElement = unityIframeRef.current;

    const handleIframeLoad = () => {
      unbindIframe();
      iframeTarget = unityIframeRef.current?.contentWindow || null;
      unbindIframe = bindPuzzleSnapshotListeners(iframeTarget);
    };

    if (iframeElement && typeof iframeElement.addEventListener === "function") {
      iframeElement.addEventListener("load", handleIframeLoad);
    }

    return () => {
      unbindWindow();
      unbindIframe();
      if (iframeElement && typeof iframeElement.removeEventListener === "function") {
        iframeElement.removeEventListener("load", handleIframeLoad);
      }
    };
  }, [
    currentUserId,
    clearStoredActiveMatchSessionForCurrentUser,
    emitLocalMatchEndForHistory,
    id,
    isPuzzleMode,
    puzzleLocalPlayerIndex,
    postGameActionHelpText,
    triggerPuzzleTurnLimitFailure,
    user?.email,
    userEmail,
  ]);

  const sendUnityResignNow = useCallback((options = {}) => {
    const { silent = false } = options || {};
    if (isSpectatorRequested) return;

    try {
      const unityInstances = resolveUnityInstances();

      if (unityInstances.length === 0) {
        if (!silent) {
          notification.warning({
            message: i18nT("tournaments.unityNoEstaListo"),
            description: i18nT("tournaments.esperaAQueElJuegoCargueCompletamenteE"),
          });
        }
      } else {
        // En la escena actual, GameUI está en el objeto "Play Panel".
        // Usamos solo el método válido para evitar abortos por SendMessage a destinos inexistentes.
        const runResignCommand = () => {
          focusUnitySurface();
          unityInstances.forEach((instance) => {
            try {
              instance.SendMessage("Play Panel", "ResignCurrentPlayer", "");
            } catch (error) {
              console.log(error);
            }
          });
        };

        // Reintentos cortos por si Unity sigue terminando de montar UI/scripts.
        runResignCommand();
        setTimeout(runResignCommand, 120);
        setTimeout(runResignCommand, 320);
      }
    } catch (error) {
      console.log(error);
      if (!silent) {
        notification.error({
          message: i18nT("tournaments.noSePudoRendir"),
          description: i18nT("tournaments.noFuePosibleEnviarLaAccionDeRendirse"),
        });
      }
    }
  }, [focusUnitySurface, isSpectatorRequested, resolveUnityInstances]);

  const emitServerResign = useCallback(() => {
    if (isSpectatorRequested) return;
    if (shouldRecordLocalMatchHistory) return;
    const socket = window.socket || getSharedSocket();
    if (!socket) return;

    const activeRoomId = String(roomIdRef.current || roomId || "").trim();
    if (!activeRoomId) return;

    try {
      socket.emit(
        "RESIGN",
        JSON.stringify({
          tournamentId: id,
          roomId: activeRoomId,
          userId: currentUserId || user?.email || "",
        })
      );
    } catch (error) {
      console.log(error);
    }
  }, [
    currentUserId,
    id,
    isSpectatorRequested,
    roomId,
    shouldRecordLocalMatchHistory,
    user?.email,
  ]);

  useEffect(() => {
    if (!isMatchInProgress || isSpectatorRequested) return undefined;

    const keepMatchOnPageExit = () => {
      if (hasAutoResignedOnExitRef.current) return;
      if (hasGameEndedRef.current) return;
      hasAutoResignedOnExitRef.current = true;
      if (shouldForfeitPuzzleRewardOnExit()) {
        void reportPuzzleRewardFailure("", "puzzle_abandoned_page_exit", {
          useKeepaliveFetch: true,
          silent: true,
        });
      }
    };

    window.addEventListener("beforeunload", keepMatchOnPageExit);
    window.addEventListener("pagehide", keepMatchOnPageExit);

    return () => {
      window.removeEventListener("beforeunload", keepMatchOnPageExit);
      window.removeEventListener("pagehide", keepMatchOnPageExit);
    };
  }, [
    isMatchInProgress,
    isSpectatorRequested,
    reportPuzzleRewardFailure,
    shouldForfeitPuzzleRewardOnExit,
  ]);

  const requestUnityResign = useCallback(() => {
    if (isSpectatorRequested) return;

    let keepFocusTimer = null;
    let keepFocusStopped = false;
    const stopKeepingUnityFocus = () => {
      if (keepFocusStopped) return;
      keepFocusStopped = true;
      if (keepFocusTimer) {
        clearInterval(keepFocusTimer);
        keepFocusTimer = null;
      }
    };

    focusUnitySurface();
    keepFocusTimer = setInterval(() => {
      focusUnitySurface();
    }, 250);

    Modal.confirm({
      className: "xima-resign-confirm",
      title: i18nT("tournaments.confirmarRendirse"),
      content: (
        <span className="xima-resign-confirm-message">
          {i18nT("tournaments.siTeRindesPerderasEstaPartidaDeseasContinuar")}
        </span>
      ),
      okText: "Rendirse",
      cancelText: "Cancelar",
      okButtonProps: { danger: true },
      centered: true,
      afterClose: stopKeepingUnityFocus,
      onCancel: stopKeepingUnityFocus,
      onOk: () => {
        stopKeepingUnityFocus();
        clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);
        hasGameEndedRef.current = true;
        setGameEnded(true);
        setLastGameEndReason("resign");
        setForcePostGameActions(true);
        setServerTurnClock({
          turnIndex: 0,
          turnStartedAtMs: 0,
          turnDurationMs: 0,
          remainingMsByIndex: [],
        });
        setServerClockOffsetMs(0);
        setPostGameMessage(`Te rendiste. ${postGameActionHelpText}`);
        setChatModalOpen(false);
        setOpen(false);
        setMatchmaking(false);
        setLoading(false);
        emitServerResign();
        setTimeout(() => {
          sendUnityResignNow();
        }, 0);
      },
    });
  }, [
    clearStoredActiveMatchSessionForCurrentUser,
    emitServerResign,
    focusUnitySurface,
    id,
    isSpectatorRequested,
    postGameActionHelpText,
    sendUnityResignNow,
  ]);

  // «Sí, abandonar»: se rinde (pierde la partida) y después sale a donde iba.
  const abandonGameAndLeave = useCallback(() => {
    const run = leaveGameRequest?.run;
    setLeaveGameRequest(null);
    clearStoredActiveMatchSessionForCurrentUser(roomSessionTournamentIdRef.current || id);
    hasGameEndedRef.current = true;
    hasAutoResignedOnExitRef.current = true;
    setGameEnded(true);
    setLastGameEndReason("resign");
    emitServerResign();
    sendUnityResignNow({ silent: true });
    if (setLayoutNavigationLocked) setLayoutNavigationLocked(false);
    window.setTimeout(() => {
      if (run) run();
      else navigate("/game/lobby");
    }, 300);
  }, [
    clearStoredActiveMatchSessionForCurrentUser,
    emitServerResign,
    id,
    leaveGameRequest,
    navigate,
    sendUnityResignNow,
    setLayoutNavigationLocked,
  ]);

  useEffect(() => {
    if (!isAutoPlayRequested) return;
    if (autoPlayConsumed) return;
    if (loading || expired || matchmaking || open) return;
    if (isPuzzleRequested && !puzzleMetadataReadyForAutoplay) return;

    setAutoPlayConsumed(true);
    startMatchmaking();
  }, [
    autoPlayConsumed,
    expired,
    isAutoPlayRequested,
    isPuzzleRequested,
    loading,
    matchmaking,
    open,
    puzzleMetadataReadyForAutoplay,
    startMatchmaking,
  ]);

  useEffect(() => {
    if (!manualReentryRequest) return undefined;
    const requestKey = String(manualReentryRequest.requestKey || "").trim();
    if (!requestKey) return undefined;
    if (manualReentryHandledKeyRef.current === requestKey) return undefined;
    manualReentryHandledKeyRef.current = requestKey;

    let cancelled = false;

    CargarPartida(manualReentryRequest.matchData)
      .then(async (loaded) => {
        if (!loaded || cancelled) return;

        // After manual restore, force an active sync burst so the board updates
        // immediately without requiring minimize/focus/manual refresh.
        try {
          await recoverSession({
            silent: true,
            viaHttpPolling: true,
          });
        } catch (error) {
          console.log(error);
        }

        if (cancelled) return;
        triggerUnityReplayRefresh("manual_reentry_bootstrap");

        notification.success({
          message: i18nT("tournaments.partidaRestaurada"),
          description: i18nT("tournaments.seCargoTuPartidaOnlineGuardadaYSe"),
        });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        notification.error({
          message: i18nT("tournaments.errorAlRestaurarLaPartida"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No fue posible cargar la partida manualmente.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [CargarPartida, manualReentryRequest, recoverSession, triggerUnityReplayRefresh]);

  const voiceControlStatusText = getVoiceControlStatusText(voiceControlStatus, language);
  const voiceControlTone = getVoiceControlStatusTone(voiceControlStatus);
  const shouldShowVoiceControlPanel =
    !isGuidedTutorial && !gameEnded && !isSpectatorRequested;
  const isVoiceControlBusy =
    voiceControlActivating || voiceControlStatus === VOICE_CONTROL_STATUS.REQUESTING_PERMISSION;
  const isVoiceControlReady = [
    VOICE_CONTROL_STATUS.ACTIVE,
    VOICE_CONTROL_STATUS.LISTENING,
    VOICE_CONTROL_STATUS.PROCESSING,
  ].includes(voiceControlStatus);
  const canDeactivateVoiceControlFromPanel = voiceControlEnabled && isVoiceControlReady;
  const voiceControlButtonLabel = isVoiceControlBusy
    ? "Activando microfono..."
    : canDeactivateVoiceControlFromPanel
    ? "Desactivar control de voz"
    : "Activar control de voz";
  const voiceControlButtonClass =
    canDeactivateVoiceControlFromPanel
      ? "xima-tournament-danger-button"
      : voiceControlTone === "danger"
      ? "xima-tournament-danger-button"
      : voiceControlStatus === VOICE_CONTROL_STATUS.DISABLED
      ? "xima-tournament-voice-off-button"
      : "xima-tournament-amber-button";
  const isVoiceControlButtonDisabled =
    isVoiceControlBusy ||
    voiceControlStatus === VOICE_CONTROL_STATUS.UNSUPPORTED;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(GAME7_LAYOUT_QUERY);
    const sync = () => setIsGame7Layout(media.matches);
    sync();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle("xima-game7-active", isGame7Layout);
    return () => document.body.classList.remove("xima-game7-active");
  }, [isGame7Layout]);

  // Datos del diseño «Juego 7/8» (solo presentación; las funciones son las mismas).
  const g7Data = (() => {
    const runtimeTurnIndex = Number(localRuntimeTurnIndex);
    const effectiveTurnIndex =
      useLocalBotTurnIndicatorFallback && Number.isFinite(runtimeTurnIndex)
        ? runtimeTurnIndex
        : activeTurnIndex;
    const activeSlot = ((Number(effectiveTurnIndex) % 4) + 4) % 4;
    const normalizedMe = normalizeProfileId(currentUserId || userEmail || user?.email || "");
    let localSlot = playerPanels.findIndex((panel) => panel.id === normalizedMe);
    const isLocalPlayer = localSlot >= 0 && !isSpectatorRequested;
    if (localSlot < 0) localSlot = 0;
    const otherSlots = playerPanels
      .map((panel, index) => index)
      .filter((index) => index !== localSlot);
    const rivalSlot =
      otherSlots.length === 0
        ? -1
        : otherSlots.includes(activeSlot)
        ? activeSlot
        : otherSlots[0];
    const havingTimeMinutes = Number(tourInfo.havingTime);
    const hasClock =
      !isPuzzleMode &&
      !isBotLocalMode &&
      normalizedMode !== "freebox" &&
      Number.isFinite(havingTimeMinutes) &&
      havingTimeMinutes > 0;
    const initialClockLabel = hasClock
      ? `${String(Math.floor(havingTimeMinutes)).padStart(2, "0")}:00`
      : "--:--";
    const isSearching = Boolean(open) && !isMatchInProgress;
    const resolveClock = (slot) => {
      if (slot === localSlot && puzzleSolveTimerVisible) return puzzleSolveTimeRemainingLabel;
      if (!hasClock) return "--:--";
      const panel = slot >= 0 ? playerPanels[slot] : null;
      if (!panel || !isMatchInProgress) return initialClockLabel;
      return panel.clockLabel;
    };
    const localPanel = playerPanels[localSlot] || null;
    const rivalPanel = rivalSlot >= 0 ? playerPanels[rivalSlot] || null : null;
    const avatarBySlot = (slot) => {
      const panel = playerPanels[slot];
      if (!panel) return "";
      const fromPanel = resolveIntroAvatarUrl(panel, slot);
      if (fromPanel) return resolveAvatarSrc(fromPanel) || fromPanel;
      if (slot === localSlot && isLocalPlayer) return currentUserAvatarUrl || "";
      return "";
    };
    const cimaxModeField =
      resolveCimaxModeFieldFromMode(tourInfo?.mode, tourInfo?.freeBoxBaseMode) || "ciencia";
    const myCimaxValue = Number(currentUserCimaxScores?.[cimaxModeField]);
    // Una pastilla por jugada: «4. e2e5», con la raya del color de la columna (jugador).
    const notationLabels = [];
    notationRows.forEach((row, rowIndex) => {
      (Array.isArray(row) ? row : []).forEach((item, column) => {
        const moveText = String(item || "").trim();
        if (!moveText) return;
        notationLabels.push({
          key: `g7-move-${rowIndex}-${column}`,
          label: `${rowIndex + 1}. ${moveText}`,
          slot: column,
        });
      });
    });
    return {
      activeSlot,
      localSlot,
      rivalSlot,
      isLocalPlayer,
      isSearching,
      localPanel,
      rivalPanel,
      localClock: resolveClock(localSlot),
      rivalClock: rivalPanel ? resolveClock(rivalSlot) : initialClockLabel,
      localIsActive:
        Boolean(localPanel) && isMatchInProgress && !gameEnded && activeSlot === localSlot,
      rivalIsActive:
        Boolean(rivalPanel) && isMatchInProgress && !gameEnded && activeSlot === rivalSlot,
      avatarBySlot,
      isPaid: Number(tourInfo?.tournamentRewardValue) > 0,
      cimaxModeLabel: formatCimaxModeLabel(cimaxModeField),
      myCimaxLabel: Number.isFinite(myCimaxValue) ? String(Math.round(myCimaxValue)) : "-",
      notationLabels,
    };
  })();

  // La pantalla Juego 7 se ajusta al alto visible real de la ventana para que la
  // barra JUGADAS (debajo del tablero) nunca quede fuera de la pantalla.
  const matchScreenRef = useRef(null);
  useEffect(() => {
    if (!isGame7Layout || typeof window === "undefined") return undefined;
    const node = matchScreenRef.current;
    if (!node) return undefined;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const top = Math.max(0, node.getBoundingClientRect().top);
        const viewportHeight = window.visualViewport?.height || window.innerHeight || 0;
        const available = Math.floor(viewportHeight - top);
        if (available > 320) node.style.setProperty("--g7-available-height", `${available}px`);
        else node.style.removeProperty("--g7-available-height");
      });
    };
    measure();
    const timer = setInterval(measure, 1500);
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener?.("resize", measure);
    return () => {
      cancelAnimationFrame(frame);
      clearInterval(timer);
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener?.("resize", measure);
      node.style.removeProperty("--g7-available-height");
    };
  }, [isGame7Layout]);

  // «Analizar partida» al terminar: se busca la partida recién guardada en el
  // historial del jugador (misma sala) y se abre su análisis como en el Historial.
  const lastMatchRoomIdRef = useRef("");
  useEffect(() => {
    const currentRoomId = String(roomId || "").trim();
    if (currentRoomId) lastMatchRoomIdRef.current = currentRoomId;
  }, [roomId]);
  const [finishedMatchHistory, setFinishedMatchHistory] = useState(null);
  const [finishedMatchLookupDone, setFinishedMatchLookupDone] = useState(false);
  const showAnalyzeFinishedMatch =
    isGame7Layout &&
    gameEnded &&
    !isSpectatorRequested &&
    !isPuzzleMode &&
    g7Data.isLocalPlayer &&
    playerPanels.length === 2;
  useEffect(() => {
    setFinishedMatchHistory(null);
    setFinishedMatchLookupDone(false);
    if (!showAnalyzeFinishedMatch) return undefined;
    const targetRoomId = String(lastMatchRoomIdRef.current || roomId || "").trim();
    const targetTournamentId = String(id || "").trim();
    let canceled = false;
    let attempts = 0;
    let timer = 0;
    const lookup = () => {
      attempts += 1;
      axios
        .get("/tournaments/history/list", { params: { page: 1, pageSize: 10 } })
        .then((response) => {
          if (canceled) return;
          const matches = Array.isArray(response?.data?.matches) ? response.data.matches : [];
          const found = matches.find(
            (match) =>
              match?.hasReplay &&
              (targetRoomId
                ? String(match?.roomId || "") === targetRoomId
                : String(match?.tournamentId || "") === targetTournamentId)
          );
          if (found) {
            setFinishedMatchHistory(found);
            setFinishedMatchLookupDone(true);
            return;
          }
          if (attempts >= 30) {
            setFinishedMatchLookupDone(true);
            return;
          }
          timer = window.setTimeout(lookup, 3000);
        })
        .catch(() => {
          if (canceled) return;
          if (attempts >= 30) setFinishedMatchLookupDone(true);
          else timer = window.setTimeout(lookup, 3000);
        });
    };
    timer = window.setTimeout(lookup, 1200);
    return () => {
      canceled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnalyzeFinishedMatch, id]);
  const openFinishedMatchAnalysis = useCallback(() => {
    const match = finishedMatchHistory;
    if (!match?.id) return;
    navigate("/game/history", {
      state: {
        replayReturnTo: "/game/history",
        openReplayRequestKey: `game:${match.id}:${Date.now()}`,
        openReplayMatch: {
          id: match.id,
          historyId: match.id,
          tournamentName: match.tournamentName || "Partida",
          mode: match.mode,
          modeLabel: rightPanelModeLabel,
          hasReplay: true,
          replaySteps: match.replaySteps,
          participants: match.participants,
          analyze: true,
        },
      },
    });
  }, [finishedMatchHistory, navigate, rightPanelModeLabel]);

  // ---- «Volver a jugar» y «Revancha» al terminar (pantalla Juego 7) ----
  const POST_GAME_QUICK_MODES = ["free", "dice", "cienzar", "sciencevsazar"];
  const postGameBaseMode = normalizeTournamentModeAlias(tourInfo?.mode);
  const postGameHavingTime = Math.max(1, Math.floor(Number(tourInfo?.havingTime) || 10));
  const postGameRivalSlot = g7Data.rivalSlot;
  const postGameRivalId =
    postGameRivalSlot >= 0 ? String(activeParticipantUsers[postGameRivalSlot] || "").trim() : "";
  const postGameRivalName =
    postGameRivalSlot >= 0 ? String(playerPanels[postGameRivalSlot]?.displayName || "").trim() : "";
  const postGameRivalIsBot =
    Boolean(postGameRivalId) && (isBotLikeName(postGameRivalId) || isBotLikeName(postGameRivalName));
  const postGameKind = (() => {
    if (!isGame7Layout || !gameEnded || isSpectatorRequested || isPuzzleMode) return "";
    if (isChampionshipRoom) return "championship";
    if (isBotLocalMode) return "bot";
    if (!isOnlineMode) return "";
    if (tourInfo?.isIndependentMatch && POST_GAME_QUICK_MODES.includes(postGameBaseMode)) return "quick";
    if (!tourInfo?.isIndependentMatch) return "tournament";
    return "";
  })();
  const postGameTournamentClosed =
    postGameKind === "tournament" &&
    (expired || (Number.isFinite(tournamentRemainingSeconds) && tournamentRemainingSeconds <= 0));
  const postGamePlayAgain = useMemo(
    () =>
      !postGameKind
    ? null
    : postGameKind === "championship"
    ? { disabled: true, reason: "Partida del Campeonato" }
    : postGameTournamentClosed
    ? { disabled: true, reason: "El torneo ya terminó" }
    : {
        disabled: false,
        reason:
          postGameKind === "bot"
            ? "Otra contra el bot"
            : postGameKind === "tournament"
            ? "Siguiente del torneo"
            : "Mismo modo y tiempo",
      },
    [postGameKind, postGameTournamentClosed]
  );
  const postGameRematch = useMemo(
    () =>
      !postGameKind
    ? null
    : postGameKind === "championship"
    ? { disabled: true, reason: "No en el Campeonato" }
    : postGameKind === "tournament"
    ? { disabled: true, reason: "No disponible en torneos" }
    : playerPanels.length !== 2
    ? { disabled: true, reason: "Solo para 2 jugadores" }
    : postGameKind === "bot" || postGameRivalIsBot
    ? { disabled: false, reason: "El bot acepta siempre", withBot: true }
    : !postGameRivalId
    ? { disabled: true, reason: "No hay rival" }
    : { disabled: false, reason: `Contra ${postGameRivalName || "tu rival"}` },
    [playerPanels.length, postGameKind, postGameRivalId, postGameRivalIsBot, postGameRivalName]
  );

  const [postGameBusy, setPostGameBusy] = useState(false);
  const [rematchOutgoing, setRematchOutgoing] = useState(null);
  const [rematchIncoming, setRematchIncoming] = useState(null);
  const [rematchNotice, setRematchNotice] = useState("");
  const [rematchClockMs, setRematchClockMs] = useState(() => Date.now());
  const rematchOutgoingRef = useRef(null);
  const rematchIncomingRef = useRef(null);
  const rematchNavigatingRef = useRef(false);
  const postGameStateRef = useRef({});
  rematchOutgoingRef.current = rematchOutgoing;
  rematchIncomingRef.current = rematchIncoming;
  postGameStateRef.current = {
    id: String(id || ""),
    canAnswer: Boolean(isGame7Layout),
    gameEnded,
    isSpectatorRequested,
    rivalId: normalizeProfileId(postGameRivalId),
  };

  const goToNextMatch = useCallback(
    async (tournamentId, extraState = {}) => {
      const nextId = String(tournamentId || "").trim();
      if (!nextId || rematchNavigatingRef.current) return;
      rematchNavigatingRef.current = true;
      try {
        await exitTournamentSession();
      } catch (error) {
        console.log(error);
      }
      navigate(`/game/${nextId}?autoplay=1`, {
        state: {
          fromLobbySource: location.state?.fromLobbySource || "lobby-modern",
          returnTo: returnTo || "/game/lobby",
          ...extraState,
        },
      });
    },
    [exitTournamentSession, location.state?.fromLobbySource, navigate, returnTo]
  );

  const startBotMatchLikeThis = useCallback(
    async (botMode, difficulty) => {
      const preferences = {
        mode: botMode,
        memberSize: Math.max(2, Math.min(4, Number(tourInfo?.memberSize) || 2)),
        havingTime: postGameHavingTime,
        botDifficulty: difficulty || "medium",
        allBotsMatch: Boolean(tourInfo?.allBotsMatch),
      };
      const response = await axios.post("/tournaments", {
        tournamentName: "",
        ...preferences,
        duration: 1,
        startingTime: new Date().toISOString(),
        isQuickMatch: false,
        isIndependentMatch: false,
        hasMatchPassword: false,
        matchPassword: "",
        tournamentPassword: "",
        joinPassword: "",
        tournamentRewardValue: 0,
        tournamentWinnerPrizeValue: 0,
      });
      const createdId = String(response?.data?._id || "").trim();
      if (!createdId) throw new Error("No se pudo crear la partida contra el bot.");
      await goToNextMatch(createdId, {
        fromLobbySource: "bot",
        source: "lobbyBotInterface",
        botGamePreferences: preferences,
      });
    },
    [goToNextMatch, postGameHavingTime, tourInfo?.allBotsMatch, tourInfo?.memberSize]
  );

  const showPostGameError = useCallback((error, fallback) => {
    notification.error({
      message: fallback,
      description: error?.response?.data?.message || error?.message || "",
    });
  }, []);

  const handlePostGamePlayAgain = useCallback(async () => {
    if (!postGamePlayAgain || postGamePlayAgain.disabled || postGameBusy) return;
    setRematchNotice("");
    if (postGameKind === "tournament") {
      // Sigue en el mismo torneo: vuelve a la cola desde aquí mismo.
      startMatchmaking();
      return;
    }
    setPostGameBusy(true);
    try {
      if (postGameKind === "bot") {
        await startBotMatchLikeThis(
          String(tourInfo?.mode || "").trim().toLowerCase(),
          normalizeRuntimeBotDifficulty(tourInfo?.botDifficulty) || navigationBotDifficulty || "medium"
        );
        return;
      }
      const response = await axios.post("/tournaments/quick-match", {
        mode: postGameBaseMode,
        havingTime: postGameHavingTime,
      });
      const nextId = String(response?.data?.tournamentId || "").trim();
      if (!nextId) throw new Error("No se pudo buscar partida.");
      const sequenceNumber = Math.max(0, Number(response?.data?.tournamentSequenceNumber) || 0);
      await goToNextMatch(nextId, {
        ...(response?.data?.action === "created"
          ? { lobbyCreatedIndependentChallenge: response.data.tournament, createdTournamentId: nextId }
          : {}),
        ...(sequenceNumber > 0
          ? { tournamentUiSequenceNumber: sequenceNumber, tournamentSequenceNumber: sequenceNumber }
          : {}),
      });
    } catch (error) {
      rematchNavigatingRef.current = false;
      showPostGameError(error, "No se pudo volver a jugar");
    } finally {
      setPostGameBusy(false);
    }
  }, [
    goToNextMatch,
    navigationBotDifficulty,
    postGameBaseMode,
    postGameBusy,
    postGameHavingTime,
    postGameKind,
    postGamePlayAgain,
    showPostGameError,
    startBotMatchLikeThis,
    startMatchmaking,
    tourInfo?.botDifficulty,
    tourInfo?.mode,
  ]);

  const handlePostGameRematch = useCallback(async () => {
    if (!postGameRematch || postGameRematch.disabled || postGameBusy) return;
    setRematchNotice("");
    setPostGameBusy(true);
    try {
      if (postGameRematch.withBot) {
        // Contra el bot: acepta siempre, misma configuración y mismo bot.
        const botMode =
          postGameKind === "bot"
            ? String(tourInfo?.mode || "").trim().toLowerCase()
            : `botlocal${postGameBaseMode}`;
        const difficulty =
          postGameKind === "bot"
            ? normalizeRuntimeBotDifficulty(tourInfo?.botDifficulty) || navigationBotDifficulty || "medium"
            : normalizeRuntimeBotDifficulty(tourInfo?.botDifficulty) || "medium";
        await startBotMatchLikeThis(botMode, difficulty);
        return;
      }
      const response = await axios.post("/rematch/request", {
        toUserId: postGameRivalId,
        fromTournamentId: String(id || ""),
        mode: postGameBaseMode,
        havingTime: postGameHavingTime,
      });
      const data = response?.data || {};
      if (data.status === "started" && data.tournamentId) {
        await goToNextMatch(data.tournamentId, { rematchOf: String(id || "") });
        return;
      }
      if (data.status === "pending" && data.requestId) {
        setRematchOutgoing({ requestId: data.requestId, expiresAt: Number(data.expiresAt) || Date.now() + 20000 });
      }
    } catch (error) {
      rematchNavigatingRef.current = false;
      setRematchNotice(error?.response?.data?.message || "No se pudo pedir la revancha.");
    } finally {
      setPostGameBusy(false);
    }
  }, [
    goToNextMatch,
    id,
    navigationBotDifficulty,
    postGameBaseMode,
    postGameBusy,
    postGameHavingTime,
    postGameKind,
    postGameRematch,
    postGameRivalId,
    startBotMatchLikeThis,
    tourInfo?.botDifficulty,
    tourInfo?.mode,
  ]);

  const cancelRematchRequest = useCallback(() => {
    const outgoing = rematchOutgoingRef.current;
    setRematchOutgoing(null);
    if (!outgoing?.requestId) return;
    axios.post("/rematch/cancel", { requestId: outgoing.requestId }).catch(() => {});
  }, []);

  const answerRematchRequest = useCallback(
    async (accept) => {
      const incoming = rematchIncomingRef.current;
      setRematchIncoming(null);
      if (!incoming?.requestId) return;
      if (!accept) {
        axios.post("/rematch/respond", { requestId: incoming.requestId, accept: false }).catch(() => {});
        return;
      }
      setPostGameBusy(true);
      try {
        const response = await axios.post("/rematch/respond", { requestId: incoming.requestId, accept: true });
        const nextId = String(response?.data?.tournamentId || "").trim();
        if (nextId) await goToNextMatch(nextId, { rematchOf: String(id || "") });
      } catch (error) {
        rematchNavigatingRef.current = false;
        setRematchNotice(error?.response?.data?.message || "No se pudo empezar la revancha.");
      } finally {
        setPostGameBusy(false);
      }
    },
    [goToNextMatch, id]
  );

  // Avisos de revancha por socket.
  useEffect(() => {
    const socket = window.socket;
    if (!socket || typeof socket.on !== "function") return undefined;
    const parse = (raw) => {
      if (raw && typeof raw === "object") return raw;
      try {
        return JSON.parse(String(raw || "{}"));
      } catch (error) {
        return {};
      }
    };
    const onRequest = (raw) => {
      const payload = parse(raw);
      const state = postGameStateRef.current || {};
      const fromUserId = normalizeProfileId(payload.fromUserId);
      const isForThisScreen =
        state.gameEnded &&
        !state.isSpectatorRequested &&
        String(payload.fromTournamentId || "") === state.id &&
        (!state.rivalId || state.rivalId === fromUserId);
      if (!isForThisScreen || !state.canAnswer) {
        axios
          .post("/rematch/respond", {
            requestId: payload.requestId,
            accept: false,
            reason: isForThisScreen ? "unsupported" : "busy",
          })
          .catch(() => {});
        return;
      }
      setRematchNotice("");
      setRematchIncoming({
        requestId: payload.requestId,
        fromName: String(payload.fromName || "").trim(),
        expiresAt: Number(payload.expiresAt) || Date.now() + 20000,
      });
    };
    const onDeclined = (raw) => {
      const payload = parse(raw);
      if (rematchOutgoingRef.current?.requestId !== payload.requestId) return;
      setRematchOutgoing(null);
      setRematchNotice(
        payload.reason === "busy"
          ? "Tu rival ya no está en esta partida."
          : payload.reason === "unsupported"
          ? "Tu rival no puede ver la revancha en su pantalla (móvil)."
          : payload.reason === "error"
          ? "No se pudo empezar la revancha."
          : "Tu rival rechazó la revancha."
      );
    };
    const onCancelled = (raw) => {
      const payload = parse(raw);
      if (rematchIncomingRef.current?.requestId !== payload.requestId) return;
      setRematchIncoming(null);
      setRematchNotice("Tu rival canceló la revancha.");
    };
    const onExpired = (raw) => {
      const payload = parse(raw);
      if (rematchOutgoingRef.current?.requestId === payload.requestId) {
        setRematchOutgoing(null);
        setRematchNotice("Tu rival no respondió a tiempo.");
      }
      if (rematchIncomingRef.current?.requestId === payload.requestId) {
        setRematchIncoming(null);
        setRematchNotice("La propuesta de revancha caducó.");
      }
    };
    const onStart = (raw) => {
      const payload = parse(raw);
      const known =
        rematchOutgoingRef.current?.requestId === payload.requestId ||
        rematchIncomingRef.current?.requestId === payload.requestId ||
        (postGameStateRef.current?.gameEnded && payload.requestId);
      if (!known || !payload.tournamentId) return;
      setRematchOutgoing(null);
      setRematchIncoming(null);
      goToNextMatch(payload.tournamentId, { rematchOf: postGameStateRef.current?.id || "" });
    };
    socket.on("REMATCH_REQUEST", onRequest);
    socket.on("REMATCH_DECLINED", onDeclined);
    socket.on("REMATCH_CANCELLED", onCancelled);
    socket.on("REMATCH_EXPIRED", onExpired);
    socket.on("REMATCH_START", onStart);
    return () => {
      socket.off?.("REMATCH_REQUEST", onRequest);
      socket.off?.("REMATCH_DECLINED", onDeclined);
      socket.off?.("REMATCH_CANCELLED", onCancelled);
      socket.off?.("REMATCH_EXPIRED", onExpired);
      socket.off?.("REMATCH_START", onStart);
    };
  }, [goToNextMatch]);

  // Cuenta atrás visible de la propuesta.
  useEffect(() => {
    if (!rematchOutgoing && !rematchIncoming) return undefined;
    const timer = window.setInterval(() => {
      const now = Date.now();
      setRematchClockMs(now);
      if (rematchOutgoingRef.current && rematchOutgoingRef.current.expiresAt + 1500 < now) {
        setRematchOutgoing(null);
        setRematchNotice("Tu rival no respondió a tiempo.");
      }
      if (rematchIncomingRef.current && rematchIncomingRef.current.expiresAt + 1500 < now) {
        setRematchIncoming(null);
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [rematchIncoming, rematchOutgoing]);

  // Al salir de la pantalla con una propuesta viva, se cancela.
  useEffect(
    () => () => {
      const outgoing = rematchOutgoingRef.current;
      if (outgoing?.requestId && !rematchNavigatingRef.current) {
        axios.post("/rematch/cancel", { requestId: outgoing.requestId }).catch(() => {});
      }
    },
    []
  );
  const rematchSecondsLeft = (entry) =>
    entry ? Math.max(0, Math.ceil((entry.expiresAt - rematchClockMs) / 1000)) : 0;

  // Barra de evaluación (motor NNUE del bot, como «Analizar partida»): tras cada
  // jugada se pide la evaluación en segundo plano; el juego no espera.
  const [liveEval, setLiveEval] = useState(null);
  const liveEvalRequestRef = useRef(0);
  // Los dos jugadores no la ven mientras juegan (sería una ayuda del bot);
  // sí los espectadores, y todos al terminar. Para que la vean todos: poner en false.
  const HIDE_LIVE_EVAL_FOR_PLAYERS = true;
  const showLiveEvalBar =
    isGame7Layout &&
    !isPuzzleMode &&
    playerPanels.length === 2 &&
    (isMatchInProgress || gameEnded) &&
    (!HIDE_LIVE_EVAL_FOR_PLAYERS || isSpectatorRequested || !g7Data.isLocalPlayer || gameEnded);
  const liveEvalMovesKey = moveNotations
    .map((entry) => `${entry?.playerIndex}${entry?.notation}`)
    .join(",");
  useEffect(() => {
    if (!showLiveEvalBar) return undefined;
    const requestId = liveEvalRequestRef.current + 1;
    liveEvalRequestRef.current = requestId;
    const moves = moveNotations
      .map((entry) => ({
        playerIndex: Number(entry?.playerIndex) === 1 ? 1 : 0,
        notation: String(entry?.notation || "").trim(),
      }))
      .filter((entry) => entry.notation);
    const timer = setTimeout(() => {
      axios
        .post("/live-eval", { moves, toMove: g7Data.activeSlot === 1 ? 1 : 0 })
        .then((response) => {
          if (liveEvalRequestRef.current !== requestId) return;
          const p0 = Number(response?.data?.p0);
          const score0 = Number(response?.data?.score0);
          if (Number.isFinite(p0)) {
            setLiveEval({ p0, score0: Number.isFinite(score0) ? score0 : 0 });
          }
        })
        .catch(() => {});
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLiveEvalBar, liveEvalMovesKey, g7Data.activeSlot]);
  useEffect(() => {
    setLiveEval(null);
  }, [id, roomId]);
  const liveEvalP0 = liveEval ? Math.min(0.97, Math.max(0.03, liveEval.p0)) : 0.5;
  const liveEvalLeader = !liveEval ? -1 : liveEval.score0 > 0 ? 0 : liveEval.score0 < 0 ? 1 : -1;
  const liveEvalLabel = !liveEval
    ? ""
    : liveEval.p0 >= 0.995 || liveEval.p0 <= 0.005
    ? "GANA"
    : (Math.abs(liveEval.score0) / 100).toFixed(1);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "rgb(59, 130, 246)", // Mystical blue color
        },
        algorithm: theme.darkAlgorithm,
      }}
    >
      <div
        ref={matchScreenRef}
        className={`xima-tournament-match-screen relative flex h-full flex-col gap-2 p-2 md:flex-row md:items-stretch md:gap-2 md:p-2 bg-gradient-to-b from-gray-900 via-gray-800 to-blue-900 flex-items font-fantasy${isGame7Layout ? " xima-g7" : ""}`}
      >
        {isGame7Layout ? (
          <aside className="g7-col g7-col-left" aria-label="Información de la partida">
            <div className={`g7-clock g7-clock-local${g7Data.localIsActive ? " is-active" : ""}`}>
              <div className="g7-clock-value">{g7Data.localClock}</div>
              <div className={`g7-clock-pill${g7Data.localIsActive ? " is-turn" : ""}`}>
                {g7Data.localIsActive
                  ? "● Te toca mover"
                  : g7Data.isSearching
                  ? "Buscando rival…"
                  : "Esperando"}
              </div>
            </div>

            <div className="g7-mode-card">
              <div className="g7-mode-icon" aria-hidden="true">
                {rightPanelModeIconSrc ? <img src={rightPanelModeIconSrc} alt="" /> : <IoCubeOutline />}
              </div>
              <div className="g7-mode-copy">
                <span>Modo de juego</span>
                <strong>{rightPanelModeLabel}</strong>
              </div>
              <em className="g7-mode-pill">{g7Data.isPaid ? "PREMIO" : "FREE"}</em>
            </div>

            <div className="g7-meta-row">
              <div className="g7-meta-card g7-meta-players">
                <strong>
                  <IoPeopleOutline aria-hidden="true" /> {rightPanelPlayersLabel}
                </strong>
                <span>Jugadores</span>
              </div>
              <div className="g7-meta-card g7-meta-time">
                <strong>
                  <IoTimeOutline aria-hidden="true" /> {rightPanelTimeLabel}
                </strong>
                <span>Tiempo</span>
              </div>
            </div>

            {tournamentSequenceNumber > 0 || shouldShowTournamentRemaining ? (
              <div className="g7-tournament-card">
                <div className="g7-tournament-copy">
                  <span>
                    {tournamentSequenceNumber > 0
                      ? formatTournamentSequenceLabel(language, tournamentSequenceNumber)
                      : "Torneo"}
                    {shouldShowTournamentRemaining ? ` · ${i18nT("tournaments.torneoTerminaEn")}` : ""}
                  </span>
                  <strong>{shouldShowTournamentRemaining ? tournamentRemainingLabel : "En curso"}</strong>
                </div>
                <IoTrophyOutline className="g7-tournament-trophy" aria-hidden="true" />
              </div>
            ) : null}

            {isPuzzleMode ? (
              <div className="g7-card g7-puzzle-card">
                <div className="g7-card-title">
                  <IoCubeOutline aria-hidden="true" />
                  <span>{t(puzzleModeTranslationKey)}</span>
                  {Number.isFinite(Number(puzzleModeSequenceNumber)) &&
                  Number(puzzleModeSequenceNumber) > 0 ? (
                    <b>#{Number(puzzleModeSequenceNumber)}</b>
                  ) : null}
                </div>
                <div className="g7-puzzle-row">
                  <span>Recompensa</span>
                  <strong>{puzzleRewardValue}X</strong>
                </div>
                {puzzleSolveTimerVisible ? (
                  <div className={`g7-puzzle-row${puzzleSolveTimeNearEnd ? " is-danger" : ""}`}>
                    <span>{i18nT("tournaments.tiempoRestanteDelPuzzle")}</span>
                    <strong>{puzzleSolveTimeRemainingLabel}</strong>
                  </div>
                ) : null}
                {puzzleAttemptLimit > 0 ? (
                  <div className="g7-puzzle-row">
                    <span>{t("PUZZLE_ATTEMPTS_TO_SOLVE")}</span>
                    <strong>
                      {puzzleAttemptDisplayCount} de {puzzleAttemptLimit}
                    </strong>
                    <em>{puzzleAttemptRemainingCount} restantes</em>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="g7-card g7-cimax-card">
                <div className="g7-card-title">
                  <IoPersonCircleOutline aria-hidden="true" />
                  <span>{i18nT("tournaments.cimaxJugadores")}</span>
                </div>
                <div className="g7-cimax-list">
                  {rightPanelCimaxRows.map((entry, index) => {
                    const avatarSrc = g7Data.avatarBySlot(index);
                    return (
                      <div key={`g7-cimax-${entry.id}`} className={`g7-cimax-row g7-slot-${index}`}>
                        <span className="g7-avatar">
                          {avatarSrc ? (
                            <img src={avatarSrc} alt="" />
                          ) : (
                            String(entry.displayName || "P").charAt(0).toUpperCase()
                          )}
                        </span>
                        <span className="g7-cimax-name" title={entry.displayName}>
                          {entry.displayName}
                        </span>
                        {entry.isBotRow ? (
                          <span className="g7-cimax-formula is-muted">Sin CIMAX</span>
                        ) : (
                          <span className="g7-cimax-formula">
                            {entry.initialLabel}{" "}
                            <b
                              className={
                                String(entry.gpLabel).startsWith("-") ? "is-negative" : "is-positive"
                              }
                            >
                              {String(entry.gpLabel).startsWith("+") ||
                              String(entry.gpLabel).startsWith("-")
                                ? entry.gpLabel
                                : `+${entry.gpLabel}`}
                            </b>{" "}
                            = <i>{entry.finalLabel}</i>
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {rightPanelCimaxRows.length < 2 ? (
                    <div className="g7-cimax-row is-waiting">
                      <span className="g7-avatar">?</span>
                      <span className="g7-cimax-name">Esperando rival…</span>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {isMatchInProgress && isOnlineMode && disconnectedPlayerAlerts.length > 0 ? (
              <div className="g7-card g7-disconnect-card">
                <div className="g7-card-title">
                  <IoRadioOutline aria-hidden="true" />
                  <span>{i18nT("tournaments.jugadorDesconectado")}</span>
                </div>
                {disconnectedPlayerAlerts.map((entry) => (
                  <div key={`g7-disconnect-${entry.id}`} className="g7-disconnect-alert">
                    <strong>{entry.displayName} se ha desconectado.</strong>
                    <span>
                      {i18nT("tournaments.leQuedan")} <b>{entry.remainingSeconds}</b>{" "}
                      {i18nT("tournaments.segundosParaReconectarse")}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="g7-status-row">
              <div className="g7-status-card g7-status-spectators">
                <span>Espectadores</span>
                <strong>
                  <IoEyeOutline aria-hidden="true" /> {spectatorCount}
                </strong>
              </div>
              <div
                className={`g7-status-card g7-status-connection${
                  connectionState === "connected" ? "" : " is-warning"
                }`}
              >
                <span>Conexión</span>
                <strong>
                  <IoRadioOutline aria-hidden="true" />{" "}
                  {connectionState === "connected" ? "Estable" : "Reconectando…"}
                </strong>
              </div>
            </div>
          </aside>
        ) : null}
        <div className={`xima-tournament-board-panel relative w-full md:w-[74%] h-[78svh] min-h-[360px] md:h-full flex flex-col border-2 border-white bg-gradient-to-r from-blue-900 to-indigo-800 bg-center bg-cover shadow-2xl rounded-lg overflow-hidden${showLiveEvalBar ? " has-eval" : ""}`}>
          {showLiveEvalBar ? (
            <div
              className={`g7-eval${liveEval ? "" : " is-pending"}`}
              role="img"
              aria-label={
                liveEval
                  ? `Evaluación: ${liveEvalLeader === 1 ? "va mejor el jugador 2" : liveEvalLeader === 0 ? "va mejor el jugador 1" : "igualado"}`
                  : "Evaluación: calculando"
              }
              title="Evaluación del bot (quién va mejor)"
            >
              <div className="g7-eval-p2" style={{ height: `${(1 - liveEvalP0) * 100}%` }} />
              <div className="g7-eval-p1" style={{ height: `${liveEvalP0 * 100}%` }} />
              <div className="g7-eval-mid" />
              {liveEvalLabel ? (
                <span className={`g7-eval-num ${liveEvalLeader === 1 ? "is-top" : "is-bottom"}`}>
                  {liveEvalLabel}
                </span>
              ) : null}
            </div>
          ) : null}
          <div ref={unityViewportRef} className="g7-board-viewport relative min-h-0 flex-1">
            {!isSpectatorRequested ? <ChampionshipWaitBanner tournamentId={id} /> : null}
            {turnIndicatorState ? (
              <div className="pointer-events-none absolute inset-x-0 top-1 z-30 flex justify-center px-1.5 sm:top-3 sm:px-3">
                <div
                  className={
                    isGame7Layout
                      ? `g7-turn-pill${g7Data.localIsActive ? " is-mine" : ""}`
                      : `w-fit max-w-[76%] overflow-hidden text-ellipsis whitespace-nowrap rounded-full border px-2 py-0.5 text-center text-[9px] font-bold leading-tight tracking-[0.01em] sm:max-w-[95%] sm:px-4 sm:py-2 sm:text-sm sm:leading-tight sm:tracking-wide ${turnIndicatorState.colorClass}`
                  }
                  style={{
                    WebkitTextSizeAdjust: "100%",
                    textSizeAdjust: "100%",
                  }}
                  title={turnIndicatorState.label}
                >
                  {turnIndicatorState.label}
                </div>
              </div>
            ) : null}
            <iframe
              ref={unityIframeRef}
              src={unityIframeSrc}
              title={i18nT("tournaments.ximaUnityGame")}
              tabIndex={0}
              allow="autoplay; fullscreen"
              onLoad={() => {
                setUnityIframeLoaded(true);
              }}
              className="absolute left-0 top-0 w-full h-full border-none"
              style={{
                touchAction: "none",
              }}
            />
            {isGame7Layout && open ? (
              <div className="g7-search-overlay" role="dialog" aria-modal="true">
                <div className="g7-search-card">
                  <div className="g7-search-spinner" aria-hidden="true" />
                  <div className="g7-search-title">
                    {isSpectatorRequested
                      ? "Entrando como espectador…"
                      : isPuzzleMode
                      ? "Cargando puzzle…"
                      : isBotLocalMode
                      ? "Preparando partida contra el bot…"
                      : ["localdice", "localcienzar", "localsciencevsazar", "localfree"].includes(
                          String(tourInfo.mode || "").toLowerCase()
                        )
                      ? "Preparando partida con amigos…"
                      : tourInfo.mode === "freebox"
                      ? "Preparando Free Box…"
                      : "Buscando rival…"}
                  </div>
                  <div className="g7-search-sub">
                    {localizeTournamentName(language, tourInfo.name) || tourInfo.name}
                    {!isSpectatorRequested && !isPuzzleMode ? (
                      <>
                        {" · "}
                        <b>
                          CIMAX {g7Data.cimaxModeLabel} {g7Data.myCimaxLabel}
                        </b>
                      </>
                    ) : null}
                  </div>
                  {tourInfo?.hasMatchPassword ? (
                    <div className="g7-search-lock">{i18nT("lobby.partidoConContrasena")}</div>
                  ) : null}
                  <button type="button" className="g7-search-cancel" onClick={cancelAndBackToLobby}>
                    {i18nT("tournaments.cancelar")}
                  </button>
                </div>
              </div>
            ) : null}
            {isMatchIntroOverlayActive ? (
              <div
                className="pointer-events-auto absolute inset-0 z-[200] flex items-center justify-center bg-black/72 backdrop-blur-sm"
                onClick={blockIntroOverlayPointerEvent}
                onPointerDown={blockIntroOverlayPointerEvent}
                onPointerUp={blockIntroOverlayPointerEvent}
                onTouchStart={blockIntroOverlayPointerEvent}
                onTouchEnd={blockIntroOverlayPointerEvent}
                onMouseDown={blockIntroOverlayPointerEvent}
                onMouseUp={blockIntroOverlayPointerEvent}
                onWheel={blockIntroOverlayPointerEvent}
              >
                {playerPanels.length === 2 ? (
                  <div className="match-intro-vs-grid -mt-8 sm:-mt-6 md:-mt-5 grid w-fit max-w-[96%] grid-cols-[auto_auto_auto] items-center gap-2 sm:gap-3">
                    <div
                      className="match-intro-vs-card w-[180px] sm:w-[210px] rounded-xl border border-cyan-300/35 bg-slate-950/68 p-2.5"
                    >
                      <div className="match-intro-vs-avatar mx-auto mb-2 h-28 w-28 overflow-hidden rounded-full border-2 border-cyan-200/80 bg-slate-900">
                        {resolveIntroAvatarUrl(playerPanels[0], 0) ? (
                          <img
                            src={resolveIntroAvatarUrl(playerPanels[0], 0)}
                            alt={playerPanels[0].displayName}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-3xl font-black text-cyan-100">
                            {String(playerPanels[0]?.displayName || "P").charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="match-intro-vs-name truncate text-center text-lg font-black text-cyan-100">
                        {playerPanels[0]?.displayName}
                      </div>
                      <div className="match-intro-vs-stat mt-1 text-center text-sm font-semibold text-cyan-200">
                        CIMAX: {playerPanels[0]?.cimaxLabel}
                      </div>
                      <div className="match-intro-vs-stat text-center text-sm font-semibold text-cyan-300/95">
                        Nivel: {playerPanels[0]?.scoreLabel}
                      </div>
                    </div>
                    <div className="match-intro-vs-divider px-1 text-center text-4xl font-black text-amber-300 drop-shadow-[0_0_16px_rgba(251,191,36,0.9)] animate-pulse">
                      V/S
                    </div>
                    <div
                      className="match-intro-vs-card w-[180px] sm:w-[210px] rounded-xl border border-cyan-300/35 bg-slate-950/68 p-2.5"
                    >
                      <div className="match-intro-vs-avatar mx-auto mb-2 h-28 w-28 overflow-hidden rounded-full border-2 border-cyan-200/80 bg-slate-900">
                        {resolveIntroAvatarUrl(playerPanels[1], 1) ? (
                          <img
                            src={resolveIntroAvatarUrl(playerPanels[1], 1)}
                            alt={playerPanels[1].displayName}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-3xl font-black text-cyan-100">
                            {String(playerPanels[1]?.displayName || "P").charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="match-intro-vs-name truncate text-center text-lg font-black text-cyan-100">
                        {playerPanels[1]?.displayName}
                      </div>
                      <div className="match-intro-vs-stat mt-1 text-center text-sm font-semibold text-cyan-200">
                        CIMAX: {playerPanels[1]?.cimaxLabel}
                      </div>
                      <div className="match-intro-vs-stat text-center text-sm font-semibold text-cyan-300/95">
                        Nivel: {playerPanels[1]?.scoreLabel}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="-mt-8 sm:-mt-6 md:-mt-5 w-fit max-w-[96%]">
                    <div className="mb-3 text-center text-2xl font-black tracking-wide text-cyan-100">{i18nT("tournaments.matchIntro")}</div>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {playerPanels.slice(0, 4).map((panel) => (
                        <div
                          key={`intro-card-${panel.id}`}
                          className="w-[180px] sm:w-[210px] rounded-xl border border-cyan-300/35 bg-slate-950/68 p-2.5"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 border-cyan-200/80 bg-slate-900">
                              {resolveIntroAvatarUrl(panel, panel.slotIndex || 0) ? (
                                <img
                                  src={resolveIntroAvatarUrl(panel, panel.slotIndex || 0)}
                                  alt={panel.displayName}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-xl font-black text-cyan-100">
                                  {String(panel.displayName || "P").charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate text-base font-black text-cyan-100">
                                {panel.displayName}
                              </div>
                              <div className="text-sm font-semibold text-cyan-200">
                                CIMAX: {panel.cimaxLabel}
                              </div>
                              <div className="text-sm font-semibold text-cyan-300/95">
                                Nivel: {panel.scoreLabel}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
            {SHOW_FRONTEND_PLAYER_PANELS && playerPanels.length > 0 ? (
              <div className="pointer-events-none absolute inset-x-0 top-0 z-20 px-3 pt-3">
                <div className="rounded-xl border border-white/10 bg-black/55 p-2 backdrop-blur-[2px]">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                    {playerPanels.map((panel) => (
                      <div
                        key={panel.id}
                        className={`relative overflow-hidden rounded-lg border bg-gradient-to-br p-3 transition-all ${
                          panel.colors.border
                        } ${panel.colors.background} ${panel.colors.glow} ${
                          panel.isActiveTurn ? "scale-[1.01] ring-1 ring-white/35" : ""
                        } ${panel.isOut ? "opacity-60 saturate-0" : ""}`}
                      >
                        <div
                          className={`absolute left-0 top-0 h-full w-1.5 ${panel.colors.accent}`}
                        />
                        <div className="pl-3">
                          <div
                            className={`truncate text-sm font-black uppercase tracking-wide ${panel.colors.text}`}
                          >
                            {panel.displayName}
                          </div>
                          <div className={`mt-1 text-xs font-semibold ${panel.colors.mutedText}`}>
                            CIMAX: {panel.cimaxLabel}
                          </div>
                          <div className={`text-xs font-semibold ${panel.colors.mutedText}`}>
                            Nivel: {panel.scoreLabel}
                          </div>
                          <div className={`text-xs font-semibold ${panel.colors.mutedText}`}>
                            Tiempo: {panel.clockLabel}
                          </div>
                          {panel.isOut ? (
                            <div className={`text-[11px] font-black uppercase ${panel.colors.mutedText}`}>{i18nT("tournaments.fueraDelJuego")}</div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            {canShowPuzzleEndActionButton ? (
              <div
                ref={puzzleEndActionButtonPanelRef}
                className="absolute z-40 w-fit touch-action-none"
                style={{
                  left: `${Number(
                    shouldUseCompactPuzzleEndActionLayout
                      ? resolvedPuzzleEndActionButtonPosition?.x ??
                        getDefaultPuzzleEndActionButtonPosition().x
                      : puzzleEndActionButtonPosition?.x ??
                        getDefaultPuzzleEndActionButtonPosition().x
                  )}px`,
                  top: `${Number(
                    shouldUseCompactPuzzleEndActionLayout
                      ? resolvedPuzzleEndActionButtonPosition?.y ??
                        getDefaultPuzzleEndActionButtonPosition().y
                      : puzzleEndActionButtonPosition?.y ??
                        getDefaultPuzzleEndActionButtonPosition().y
                  )}px`,
                }}
                onMouseDown={handlePuzzleEndActionButtonDragStart}
                onContextMenu={(event) => event.preventDefault()}
              >
                {canShowNextPuzzleButton ? (
                  <div className="pointer-events-auto rounded-xl border border-emerald-300/70 bg-emerald-900/75 px-4 py-3 text-center shadow-xl">
                    <div className="mb-2 text-sm font-black uppercase tracking-wide text-emerald-100">
                      {isTutorialLevel1
                        ? tutorialUiText("level1Complete")
                        : isTutorialLevel2
                        ? tutorialUiText("level2Complete")
                        : i18nT("tournaments.felicidadesLoHasLogrado")}
                    </div>
                    <Button
                      size="large"
                      type="primary"
                      className="border-emerald-300/90 bg-emerald-600/95 font-black uppercase tracking-wide text-white hover:bg-emerald-500"
                      onClick={
                        isTutorialLevel1
                          ? goToNextTutorialLevel
                          : isTutorialLevel2
                          ? goToGameHome
                          : goToNextPuzzle
                      }
                      loading={!isGuidedTutorial && openingNextPuzzle}
                    >
                      {isTutorialLevel1
                        ? tutorialUiText("nextLevel")
                        : isTutorialLevel2
                        ? tutorialUiText("backToLobby")
                        : i18nT("lobby.siguientePuzzle")}
                    </Button>
                  </div>
                ) : null}
                {canShowRetryPuzzleButton ? (
                  <div className="pointer-events-auto rounded-xl border border-red-300/70 bg-red-900/75 px-4 py-3 text-center shadow-xl">
                    <div className="mb-2 text-sm font-black uppercase tracking-wide text-red-100">
                      {isPuzzleAzarMode
                        ? "Revisa y vuelve a intentar"
                        : "Lo siento has fallado"}
                    </div>
                    <Button
                      size="large"
                      danger
                      type="primary"
                      className="border-red-300/90 bg-red-600/95 font-black uppercase tracking-wide text-white hover:bg-red-500"
                      onClick={retryCurrentPuzzle}
                      loading={retryingPuzzle}
                    >{i18nT("lobby.intentarDeNuevo")}</Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="xima-notation-bar shrink-0 border-t border-cyan-500/30 bg-[#061425f0] px-3 py-2">
            <style>
              {`
                .xima-hidden-scroll {
                  scrollbar-width: none;
                  -ms-overflow-style: none;
                }
                .xima-hidden-scroll::-webkit-scrollbar {
                  display: none !important;
                  width: 0 !important;
                  height: 0 !important;
                  background: transparent !important;
                }
                .xima-notation-scroll {
                  scrollbar-width: none;
                  -ms-overflow-style: none;
                }
                .xima-notation-scroll::-webkit-scrollbar {
                  display: none !important;
                  width: 0 !important;
                  height: 0 !important;
                  background: transparent !important;
                }
                @media (max-width: 640px) and (orientation: portrait) {
                  .match-intro-vs-grid {
                    width: min(100%, 346px);
                    max-width: 100%;
                    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
                    gap: 0.375rem;
                    margin-left: auto;
                    margin-right: auto;
                  }
                  .match-intro-vs-card {
                    width: 100%;
                    min-width: 0;
                    padding: 0.5rem;
                  }
                  .match-intro-vs-avatar {
                    width: 4.5rem;
                    height: 4.5rem;
                    margin-bottom: 0.35rem;
                  }
                  .match-intro-vs-name {
                    font-size: 1rem;
                    line-height: 1.2;
                  }
                  .match-intro-vs-stat {
                    font-size: 0.78rem;
                    line-height: 1.15;
                  }
                  .match-intro-vs-divider {
                    font-size: 1.9rem;
                    line-height: 1;
                    padding-left: 0.1rem;
                    padding-right: 0.1rem;
                  }
                }
              `}
            </style>
            <div
              ref={notationScrollRef}
              className="xima-hidden-scroll xima-notation-scroll overflow-x-auto overflow-y-hidden whitespace-nowrap text-xs text-cyan-100"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {isGame7Layout ? (
                <div className="g7-moves">
                  <span className="g7-moves-title">Jugadas</span>
                  {g7Data.notationLabels.length === 0 ? (
                    <span className="g7-moves-empty">Sin jugadas aún. Aquí aparecerá cada jugada.</span>
                  ) : (
                    g7Data.notationLabels.map((entry, index) => (
                      <span
                        key={entry.key}
                        className={`g7-move g7-slot-${entry.slot}${
                          index === g7Data.notationLabels.length - 1 ? " is-last" : ""
                        }`}
                      >
                        {entry.label}
                      </span>
                    ))
                  )}
                </div>
              ) : notationRows.length === 0 ? (
                <span className="text-cyan-200/55">{i18nT("tournaments.sinJugadasAun")}</span>
              ) : (
                notationRows.map((row, index) => {
                  const turnLabel = `${index + 1}.`;
                  const moveText = row
                    .map((item) => String(item || "").trim())
                    .filter(Boolean)
                    .join(" ");

                  if (!moveText) return null;
                  return (
                    <span
                      key={`notation-turn-${index}`}
                      className="mr-3 inline-block rounded border border-cyan-800/70 bg-black/25 px-2 py-1"
                    >
                      <span className="font-semibold text-cyan-100">{`${turnLabel} ${moveText}`}</span>
                    </span>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <section
          className="xima-tournament-mobile-direct-messages"
          aria-label="Mensajes privados"
        >
          <div className="xima-tournament-mobile-direct-row">
            <div
              id="xima-direct-notification-mobile-anchor"
              className="xima-direct-notification-mobile-anchor"
            />
            <div
              id="xima-direct-message-mobile-anchor"
              className="xima-direct-message-mobile-anchor"
            />
          </div>
          <div
            id="xima-direct-panel-mobile-anchor"
            className="xima-direct-panel-mobile-anchor"
          />
        </section>

        <div className="xima-tournament-right-panel w-full md:w-[24%] mt-2 md:mt-0 text-white">
          {isGame7Layout ? (
            <div className="g7-col g7-col-right">
              <div className={`g7-clock g7-clock-rival${g7Data.rivalIsActive ? " is-active" : ""}`}>
                <div className="g7-clock-value">{g7Data.rivalClock}</div>
                <div className={`g7-clock-pill${g7Data.rivalIsActive ? " is-turn-rival" : ""}`}>
                  {!g7Data.rivalPanel
                    ? "Sin rival aún"
                    : g7Data.rivalIsActive
                    ? "● Le toca mover"
                    : "Esperando"}
                </div>
              </div>

              {showAnalyzeFinishedMatch ? (
                <button
                  type="button"
                  className="g7-analyze"
                  onClick={openFinishedMatchAnalysis}
                  disabled={!finishedMatchHistory}
                  title={
                    finishedMatchHistory
                      ? "El bot revisa cada jugada de esta partida"
                      : finishedMatchLookupDone
                      ? "La repetición de esta partida no está disponible todavía; búscala en el Historial"
                      : "Guardando la partida…"
                  }
                >
                  {finishedMatchHistory
                    ? "🔍 Analizar partida"
                    : finishedMatchLookupDone
                    ? "Análisis no disponible aún"
                    : "Preparando análisis…"}
                </button>
              ) : null}

              {postGamePlayAgain ? (
                <div className="g7-postgame">
                  {rematchIncoming ? (
                    <div className="g7-rematch-card is-incoming" role="alertdialog" aria-live="assertive">
                      <strong>
                        {postGameRivalName || rematchIncoming.fromName || "Tu rival"} te propone la revancha
                      </strong>
                      <span>Responde en {rematchSecondsLeft(rematchIncoming)} s</span>
                      <div className="g7-rematch-actions">
                        <button
                          type="button"
                          className="g7-postgame-btn is-play"
                          onClick={() => answerRematchRequest(true)}
                          disabled={postGameBusy}
                        >
                          Aceptar
                        </button>
                        <button
                          type="button"
                          className="g7-postgame-btn is-decline"
                          onClick={() => answerRematchRequest(false)}
                          disabled={postGameBusy}
                        >
                          Rechazar
                        </button>
                      </div>
                    </div>
                  ) : rematchOutgoing ? (
                    <div className="g7-rematch-card" role="status" aria-live="polite">
                      <strong>Esperando respuesta…</strong>
                      <span>
                        {postGameRivalName || "Tu rival"} tiene {rematchSecondsLeft(rematchOutgoing)} s para
                        aceptar
                      </span>
                      <button type="button" className="g7-postgame-btn is-decline" onClick={cancelRematchRequest}>
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="g7-postgame-row">
                      <button
                        type="button"
                        className="g7-postgame-btn is-play"
                        onClick={handlePostGamePlayAgain}
                        disabled={postGamePlayAgain.disabled || postGameBusy}
                        title={postGamePlayAgain.reason}
                      >
                        <strong>▶ Volver a jugar</strong>
                        <span>{postGamePlayAgain.reason}</span>
                      </button>
                      {postGameRematch ? (
                        <button
                          type="button"
                          className="g7-postgame-btn is-rematch"
                          onClick={handlePostGameRematch}
                          disabled={postGameRematch.disabled || postGameBusy}
                          title={postGameRematch.reason}
                        >
                          <strong>⟲ Revancha</strong>
                          <span>{postGameRematch.reason}</span>
                        </button>
                      ) : null}
                    </div>
                  )}
                  {rematchNotice ? <div className="g7-postgame-note">{rematchNotice}</div> : null}
                </div>
              ) : null}

              {!gameEnded || shouldShowVoiceControlPanel ? (
                <div className="g7-tiles">
                  {!gameEnded ? (
                    <button
                      type="button"
                      className={`g7-tile g7-tile-blue${cameraFollowEnabled ? " is-on" : ""}`}
                      onClick={toggleCameraFollowDuringMatch}
                      aria-pressed={cameraFollowEnabled}
                    >
                      <IoCameraOutline aria-hidden="true" />
                      <strong>Seguir cámara</strong>
                      <span>
                        {cameraFollowEnabled
                          ? i18nT("tournaments.activated")
                          : i18nT("tournaments.deactivated")}
                      </span>
                    </button>
                  ) : null}
                  {!gameEnded ? (
                    <button type="button" className="g7-tile g7-tile-magenta" onClick={clearBoardArrows}>
                      <IoTrashOutline aria-hidden="true" />
                      <strong>{i18nT("tournaments.clearArrows")}</strong>
                      <span>Quita todas</span>
                    </button>
                  ) : null}
                  {!gameEnded ? (
                    <button
                      type="button"
                      className={`g7-tile g7-tile-violet${boardArrowsActive ? " is-on" : ""}`}
                      onClick={toggleBoardArrows}
                      aria-pressed={boardArrowsActive}
                    >
                      <IoBrushOutline aria-hidden="true" />
                      <strong>Dibujar flechas</strong>
                      <span>
                        {boardArrowsActive
                          ? i18nT("tournaments.activated")
                          : i18nT("tournaments.deactivated")}
                      </span>
                    </button>
                  ) : null}
                  {shouldShowVoiceControlPanel ? (
                    <button
                      type="button"
                      className={`g7-tile g7-tile-orange g7-voice-tone-${voiceControlTone}${
                        canDeactivateVoiceControlFromPanel ? " is-on" : ""
                      }`}
                      onClick={toggleVoiceControlFromMatch}
                      disabled={isVoiceControlButtonDisabled}
                      aria-pressed={canDeactivateVoiceControlFromPanel}
                      title={voiceControlButtonLabel}
                    >
                      <IoMicOutline aria-hidden="true" />
                      <strong>{isVoiceControlBusy ? "Activando…" : "Control de voz"}</strong>
                      <span role="status" aria-live="polite">
                        {voiceControlStatusText}
                      </span>
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="g7-card g7-speed-card">
                <div className="g7-card-title">
                  <IoSpeedometerOutline aria-hidden="true" />
                  <span>Velocidad de animación</span>
                  <b>
                    {blockAnimationSpeedPreference <= 0.75
                      ? "Lenta"
                      : blockAnimationSpeedPreference >= 1.25
                      ? "Rápida"
                      : "Normal"}{" "}
                    · {Number(blockAnimationSpeedPreference).toFixed(2).replace(/\.?0+$/, "")}x
                  </b>
                </div>
                <input
                  type="range"
                  className="g7-speed-range"
                  min={MIN_BLOCK_ANIMATION_SPEED}
                  max={MAX_BLOCK_ANIMATION_SPEED}
                  step="0.05"
                  value={blockAnimationSpeedPreference}
                  onChange={(event) => changeBlockAnimationSpeedFromMatch(event.target.value)}
                  aria-label="Velocidad de animación"
                  style={{
                    "--g7-range-progress": `${Math.round(
                      ((blockAnimationSpeedPreference - MIN_BLOCK_ANIMATION_SPEED) /
                        Math.max(0.01, MAX_BLOCK_ANIMATION_SPEED - MIN_BLOCK_ANIMATION_SPEED)) *
                        100
                    )}%`,
                  }}
                />
                <div className="g7-speed-labels">
                  <span>Lento</span>
                  <span>Normal</span>
                  <span>Rápido</span>
                </div>
              </div>

              {!gameEnded && boardArrowsActive ? (
                <div className="g7-card g7-arrows-card">
                  <div
                    className="g7-arrow-colors"
                    role="radiogroup"
                    aria-label={i18nT("lobby.replayArrows")}
                  >
                    {[
                      { id: "blue", hex: "#1f85ff" },
                      { id: "red", hex: "#f22929" },
                      { id: "yellow", hex: "#ffdb1a" },
                      { id: "green", hex: "#26d14d" },
                    ].map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={boardArrowColor === option.id}
                        title={option.id}
                        onClick={() => {
                          setBoardArrowColor(option.id);
                          focusUnitySurface();
                        }}
                        className={boardArrowColor === option.id ? "is-selected" : ""}
                        style={{ backgroundColor: option.hex }}
                      />
                    ))}
                  </div>
                  <div className="g7-arrows-hint">{i18nT("tournaments.boardArrowsHint")}</div>
                </div>
              ) : null}

              {voiceControlDebugText ? (
                <div className="g7-voice-debug" aria-live="polite">
                  {voiceControlDebugText}
                </div>
              ) : null}

              {shouldShowPlayerVoiceChat ? (
                <div className="g7-card g7-voice-card">
                  <TournamentVoiceChat
                    enabled={shouldShowPlayerVoiceChat}
                    tournamentId={id}
                    roomId={roomId}
                    localUserId={currentUserId || userEmail || user?.email || ""}
                    players={voiceChatPlayers}
                    connectionState={connectionState}
                  />
                </div>
              ) : null}

              {isPuzzleMode && !isSpectatorRequested && (isMatchInProgress || gameEnded) ? (
                <button type="button" className="g7-btn g7-btn-cyan" onClick={goToPuzzleSection}>
                  <IoCubeOutline aria-hidden="true" /> {i18nT("tournaments.irAPuzzles")}
                </button>
              ) : null}

              {shouldShowPostGameActions ? (
                <div className="g7-card g7-finished-card">
                  <div className="g7-card-title">
                    <IoTrophyOutline aria-hidden="true" />
                    <span>{i18nT("tournaments.partidaFinalizada")}</span>
                  </div>
                  {postGameMessage ? <div className="g7-winner">{postGameMessage}</div> : null}
                  <div className="g7-btn-stack">
                    {!shouldHidePostGameQueueButtons && !postGamePlayAgain ? (
                      <button
                        type="button"
                        className="g7-btn g7-btn-green"
                        onClick={() => goToTournamentWaitingRoom({ autoPlay: true })}
                      >
                        <IoRefreshOutline aria-hidden="true" /> {i18nT("tournaments.jugarDeNuevo")}
                      </button>
                    ) : null}
                    {!shouldHidePostGameQueueButtons ? (
                      <button type="button" className="g7-btn g7-btn-cyan" onClick={goToTournamentWaitingRoom}>
                        <IoTrophyOutline aria-hidden="true" /> {i18nT("tournaments.irATorneo")}
                      </button>
                    ) : null}
                    {shouldShowCreateGamePostGameButton && (isChampionshipRoom || !postGamePlayAgain) ? (
                      <button
                        type="button"
                        className="g7-btn g7-btn-rose"
                        onClick={isChampionshipRoom ? goToChampionship : playAgainSameMode}
                      >
                        <IoCubeOutline aria-hidden="true" />{" "}
                        {isChampionshipRoom ? "Volver al Campeonato" : "Volver a jugar"}
                      </button>
                    ) : null}
                    {shouldShowFreeBoxGoToPuzzleButton ? (
                      <button type="button" className="g7-btn g7-btn-cyan" onClick={goToPuzzleSection}>
                        <IoCubeOutline aria-hidden="true" /> {i18nT("tournaments.irAPuzzle")}
                      </button>
                    ) : null}
                    <button type="button" className="g7-btn g7-btn-cyan" onClick={goToGameHome}>
                      <IoHomeOutline aria-hidden="true" /> {returnToLabel || i18nT("tournaments.irAInicio")}
                    </button>
                  </div>
                </div>
              ) : null}

              {shouldShowManualRefreshButton ? (
                <button
                  type="button"
                  className="g7-btn g7-btn-cyan"
                  onClick={handleManualPositionRefresh}
                  disabled={manualPositionRefreshLoading}
                >
                  <IoRefreshOutline aria-hidden="true" />{" "}
                  {manualPositionRefreshLoading ? "Actualizando…" : "Actualizar"}
                </button>
              ) : null}

              {!shouldShowPostGameActions && !matchmaking && !isMatchInProgress ? (
                <button
                  type="button"
                  className="g7-btn g7-btn-green"
                  disabled={isPlayButtonDisabled}
                  onClick={startMatchmaking}
                >
                  {expired
                    ? "Expired"
                    : isSpectatorRequested
                    ? "Watch"
                    : gameEnded
                    ? "Volver a jugar"
                    : "Play"}
                </button>
              ) : null}

              {quickChatEnabled ? (
                <div className="g7-card g7-chat-card">
                  <div className="g7-chat-head">
                    <div className="g7-card-title">
                      <IoChatbubbleEllipsesOutline aria-hidden="true" />
                      <span>{i18nT("tournaments.chatRapido")}</span>
                    </div>
                    {!isSpectatorRequested ? (
                      <button
                        type="button"
                        className="g7-chat-window"
                        onClick={() => setChatModalOpen(true)}
                        disabled={!roomId}
                      >
                        ⧉ Ventana
                      </button>
                    ) : null}
                  </div>
                  <div className="g7-chat-list xima-hidden-scroll">
                    {chatMessages.length === 0 ? (
                      <div className="g7-chat-empty">
                        {roomId ? "Aun no hay mensajes en la partida." : "Entra a la sala para usar chat."}
                      </div>
                    ) : (
                      chatMessages.slice(-30).map((chatItem, index) => (
                        <div key={`${chatItem.sentAt}-${index}`} className="g7-chat-message">
                          <span>{formatChatUser(chatItem.userId)}:</span> <strong>{chatItem.message}</strong>
                        </div>
                      ))
                    )}
                  </div>
                  {isSpectatorRequested || !roomId ? (
                    <div className="g7-chat-note">
                      {isSpectatorRequested ? "Modo espectador: chat solo lectura." : "Esperando sala..."}
                    </div>
                  ) : canSendQuickChat ? (
                    <div className="g7-chat-quick xima-hidden-scroll">
                      {QUICK_CHAT_MESSAGES.slice(0, 6).map((message) => (
                        <button key={`g7-quick-${message}`} type="button" onClick={() => sendQuickChat(message)}>
                          {message}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="g7-spacer" />
              )}

              {shouldShowBottomHomeAction ? (
                <div className="g7-btn-stack">
                  {!hasGameFinishedForHomeAction &&
                  normalizedMode === "freebox" &&
                  canCreatePuzzleSnapshots &&
                  !isPuzzleRequested &&
                  !isPuzzleTournament ? (
                    <button
                      type="button"
                      className="g7-btn g7-btn-green"
                      onClick={openPuzzleNameModalForSave}
                      disabled={savingPuzzleSnapshot || loadingPuzzleSnapshot || isSpectatorRequested}
                    >
                      <IoCubeOutline aria-hidden="true" /> {i18nT("tournaments.guardarPosicion")}
                    </button>
                  ) : null}
                  {!hasGameFinishedForHomeAction && shouldShowFreeBoxGoToPuzzleButton ? (
                    <button type="button" className="g7-btn g7-btn-cyan" onClick={goToPuzzleSection}>
                      <IoCubeOutline aria-hidden="true" /> {i18nT("tournaments.irAPuzzle")}
                    </button>
                  ) : null}
                  <button type="button" className="g7-btn g7-btn-cyan" onClick={goToGameHome}>
                    <IoHomeOutline aria-hidden="true" />{" "}
                    {isChampionshipRoom
                      ? "Volver al Campeonato"
                      : hasGameFinishedForHomeAction || isBotLocalOrFreeBoxMode
                      ? i18nT("tournaments.irAInicio")
                      : "Inicio"}
                  </button>
                </div>
              ) : null}

              {!isSpectatorRequested &&
              isMatchInProgress &&
              !hasGameFinishedForHomeAction &&
              !isPuzzleMode ? (
                <button type="button" className="g7-resign" onClick={requestUnityResign}>
                  <IoFlagOutline aria-hidden="true" /> Rendirse
                </button>
              ) : null}
            </div>
          ) : (
          <div className="xima-tournament-right-shell">
            <div className="xima-tournament-right-main">
              <Card className="game-card xima-tournament-card xima-tournament-summary-card">
                <div className="xima-tournament-mode-hero">
                  <div className="xima-tournament-mode-icon" aria-hidden="true">
                    {rightPanelModeIconSrc ? (
                      <img src={rightPanelModeIconSrc} alt="" />
                    ) : (
                      <IoCubeOutline />
                    )}
                  </div>
                  <div className="xima-tournament-mode-copy">
                    <span>Modo de juego</span>
                    <strong>{rightPanelModeLabel}</strong>
                  </div>
                </div>

                <div className="xima-tournament-meta-grid">
                  <div className="xima-tournament-meta-card">
                    <div className="xima-tournament-meta-icon" aria-hidden="true">
                      {rightPanelPlayersIconSrc ? (
                        <img src={rightPanelPlayersIconSrc} alt="" />
                      ) : (
                        <IoPeopleOutline />
                      )}
                    </div>
                    <div className="xima-tournament-meta-copy">
                      <span>Jugadores</span>
                      <strong>{rightPanelPlayersLabel}</strong>
                    </div>
                  </div>
                  <div className="xima-tournament-meta-card">
                    <div className="xima-tournament-meta-icon" aria-hidden="true">
                      {rightPanelTimeIconSrc ? (
                        <img src={rightPanelTimeIconSrc} alt="" />
                      ) : (
                        <IoTimeOutline />
                      )}
                    </div>
                    <div className="xima-tournament-meta-copy">
                      <span>Tiempo</span>
                      <strong>{rightPanelTimeLabel}</strong>
                    </div>
                  </div>
                </div>

                <div className="xima-tournament-info-stack">
                  {tournamentSequenceNumber > 0 ? (
                    <div className="xima-tournament-info-tag xima-tournament-info-tag-violet">
                      <span>Numero del torneo</span>
                      <strong>{formatTournamentSequenceLabel(language, tournamentSequenceNumber)}</strong>
                    </div>
                  ) : null}
                  {isPuzzleMode && !isSpectatorRequested && (isMatchInProgress || gameEnded) ? (
                    <Button
                      className="xima-tournament-action-button xima-tournament-cyan-button"
                      size="large"
                      icon={<IoCubeOutline />}
                      onClick={goToPuzzleSection}
                    >
                      {i18nT("tournaments.irAPuzzles")}
                    </Button>
                  ) : null}
                  {!gameEnded ? (
                    <Button
                      className={`xima-tournament-action-button ${
                        cameraFollowEnabled
                          ? "xima-tournament-green-button"
                          : "xima-tournament-cyan-button"
                      }`}
                      size="large"
                      icon={<IoCameraOutline />}
                      onClick={toggleCameraFollowDuringMatch}
                      aria-pressed={cameraFollowEnabled}
                    >
                      {i18nT("tournaments.cameraFollow")}:{" "}
                      {cameraFollowEnabled
                        ? i18nT("tournaments.activated")
                        : i18nT("tournaments.deactivated")}
                    </Button>
                  ) : null}
                  {!gameEnded ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Button
                          className={`xima-tournament-action-button flex-1 ${
                            boardArrowsActive
                              ? "xima-tournament-green-button"
                              : "xima-tournament-cyan-button"
                          }`}
                          size="large"
                          onClick={toggleBoardArrows}
                          aria-pressed={boardArrowsActive}
                        >
                          {i18nT("lobby.replayArrows")}:{" "}
                          {boardArrowsActive
                            ? i18nT("tournaments.activated")
                            : i18nT("tournaments.deactivated")}
                        </Button>
                        <Button
                          className="xima-tournament-action-button xima-tournament-cyan-button"
                          size="large"
                          onClick={clearBoardArrows}
                        >
                          {i18nT("tournaments.clearArrows")}
                        </Button>
                      </div>
                      {boardArrowsActive ? (
                        <>
                          <div
                            className="flex items-center justify-center gap-3"
                            role="radiogroup"
                            aria-label={i18nT("lobby.replayArrows")}
                          >
                            {[
                              { id: "blue", hex: "#1f85ff" },
                              { id: "red", hex: "#f22929" },
                              { id: "yellow", hex: "#ffdb1a" },
                              { id: "green", hex: "#26d14d" },
                            ].map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                role="radio"
                                aria-checked={boardArrowColor === option.id}
                                title={option.id}
                                onClick={() => {
                                  setBoardArrowColor(option.id);
                                  focusUnitySurface();
                                }}
                                className={`h-8 w-8 rounded-full border-2 transition ${
                                  boardArrowColor === option.id
                                    ? "scale-110 border-white shadow-[0_0_10px_rgba(255,255,255,0.7)]"
                                    : "border-[#1b2f55] opacity-80 hover:opacity-100"
                                }`}
                                style={{ backgroundColor: option.hex }}
                              />
                            ))}
                          </div>
                          <div className="text-[11px] leading-snug text-[#9ecbff]">
                            {i18nT("tournaments.boardArrowsHint")}
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                  {shouldShowVoiceControlPanel ? (
                    <div className={`xima-tournament-voice-control xima-tournament-voice-control-${voiceControlTone}`}>
                      <Button
                        className={`xima-tournament-action-button ${voiceControlButtonClass}`}
                        size="large"
                        icon={<IoMicOutline />}
                        loading={isVoiceControlBusy}
                        disabled={isVoiceControlButtonDisabled}
                        onClick={toggleVoiceControlFromMatch}
                        aria-pressed={canDeactivateVoiceControlFromPanel}
                      >
                        {voiceControlButtonLabel}
                      </Button>
                      <div
                        className={`xima-tournament-voice-status xima-tournament-voice-status-${voiceControlTone}`}
                        role="status"
                        aria-live="polite"
                      >
                        <IoMicOutline aria-hidden="true" />
                        <span>{voiceControlStatusText}</span>
                      </div>
                      {voiceControlDebugText ? (
                        <div className="xima-tournament-voice-debug" aria-live="polite">
                          {voiceControlDebugText}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <TournamentVoiceChat
                    enabled={shouldShowPlayerVoiceChat}
                    tournamentId={id}
                    roomId={roomId}
                    localUserId={currentUserId || userEmail || user?.email || ""}
                    players={voiceChatPlayers}
                    connectionState={connectionState}
                  />
                  {isPuzzleMode ? (
                    <div className="xima-tournament-info-tag xima-tournament-info-tag-cyan">
                      <span>{t(puzzleModeTranslationKey)}</span>
                      {Number.isFinite(Number(puzzleModeSequenceNumber)) &&
                      Number(puzzleModeSequenceNumber) > 0 ? (
                        <strong>#{Number(puzzleModeSequenceNumber)}</strong>
                      ) : null}
                    </div>
                  ) : null}
                  {isPuzzleMode ? (
                    <div className="xima-tournament-info-tag xima-tournament-info-tag-green">
                      <span>Recompensa</span>
                      <strong>{puzzleRewardValue}X</strong>
                    </div>
                  ) : null}
                  {puzzleSolveTimerVisible ? (
                    <div
                      className={`xima-tournament-info-tag ${
                        puzzleSolveTimeNearEnd
                          ? "xima-tournament-info-tag-red"
                          : "xima-tournament-info-tag-cyan"
                      }`}
                    >
                      <span>{i18nT("tournaments.tiempoRestanteDelPuzzle")}</span>
                      <strong>{puzzleSolveTimeRemainingLabel}</strong>
                    </div>
                  ) : null}
                  {isPuzzleMode && puzzleAttemptLimit > 0 ? (
                    <div className="xima-tournament-info-tag xima-tournament-info-tag-amber">
                      <span>{t("PUZZLE_ATTEMPTS_TO_SOLVE")}</span>
                      <strong>
                        {puzzleAttemptDisplayCount} de {puzzleAttemptLimit}
                      </strong>
                      <em>{puzzleAttemptRemainingCount} restantes</em>
                    </div>
                  ) : null}
                  {shouldShowTournamentRemaining ? (
                    <div className="xima-tournament-info-tag xima-tournament-info-tag-violet">
                      <span>{i18nT("tournaments.torneoTerminaEn")}</span>
                      <strong className={tournamentRemainingTextClass}>
                        {tournamentRemainingLabel}
                      </strong>
                    </div>
                  ) : null}
                </div>

                {shouldShowPostGameActions ? (
                  <Button
                    className="xima-tournament-action-button xima-tournament-cyan-button"
                    size="large"
                    icon={<IoHomeOutline />}
                    onClick={goToGameHome}
                  >
                    {returnToLabel || i18nT("tournaments.irAInicio")}
                  </Button>
                ) : null}

                {!isSpectatorRequested &&
                isMatchInProgress &&
                !hasGameFinishedForHomeAction &&
                !isPuzzleMode ? (
                  <Button
                    className="xima-tournament-action-button xima-tournament-danger-button"
                    size="large"
                    danger
                    icon={<IoFlagOutline />}
                    onClick={requestUnityResign}
                  >
                    Rendirse
                  </Button>
                ) : null}

                {!isPuzzleMode ? (
                  <div className="xima-tournament-section xima-tournament-cimax-section">
                    <div className="xima-tournament-section-title">
                      <IoPersonCircleOutline />
                      <span>{i18nT("tournaments.cimaxJugadores")}</span>
                    </div>
                    <div className="xima-tournament-cimax-table">
                      <div className="xima-tournament-cimax-head">
                        <div>{i18nT("tournaments.nombre")}</div>
                        <div>{i18nT("tournaments.cimaxI")}</div>
                        <div>G/P</div>
                        <div>{i18nT("tournaments.cimaxF")}</div>
                      </div>
                      <div className="xima-tournament-cimax-body">
                        {rightPanelCimaxRows.map((entry) => (
                          <div
                            key={`rp-cimax-${entry.id}`}
                            className="xima-tournament-cimax-row"
                          >
                            <span className="xima-tournament-cimax-name">
                              {entry.displayName}
                            </span>
                            <span className="xima-tournament-cimax-value">
                              {entry.initialLabel}
                            </span>
                            <span
                              className={`xima-tournament-cimax-value xima-tournament-cimax-delta ${
                                entry.isBotRow
                                  ? "xima-tournament-cimax-muted"
                                  : String(entry.gpLabel).startsWith("+")
                                  ? "xima-tournament-cimax-positive"
                                  : String(entry.gpLabel).startsWith("-")
                                  ? "xima-tournament-cimax-negative"
                                  : "xima-tournament-cimax-neutral"
                              }`}
                            >
                              {entry.gpLabel}
                            </span>
                            <span className="xima-tournament-cimax-value xima-tournament-cimax-final">
                              {entry.finalLabel}
                            </span>
                            {!entry.isBotRow && entry.formulaLabel ? (
                              <span className="xima-tournament-cimax-formula">
                                {entry.formulaLabel}
                              </span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="xima-tournament-status-grid">
                  <div className="xima-tournament-status-card xima-tournament-status-card-violet">
                    <IoEyeOutline aria-hidden="true" />
                    <span>Espectadores:</span>
                    <strong>{spectatorCount}</strong>
                  </div>
                  <div
                    className={`xima-tournament-status-card ${
                      connectionState === "connected"
                        ? "xima-tournament-status-card-green"
                        : "xima-tournament-status-card-amber"
                    }`}
                  >
                    <IoRadioOutline aria-hidden="true" />
                    <span>Conexion:</span>
                    <strong>
                      {connectionState === "connected" ? "estable" : "reconectando..."}
                    </strong>
                  </div>
                </div>

                {isMatchInProgress && isOnlineMode && disconnectedPlayerAlerts.length > 0 ? (
                  <div className="xima-tournament-section xima-tournament-disconnect-section">
                    <div className="xima-tournament-section-title">
                      <IoRadioOutline />
                      <span>{i18nT("tournaments.jugadorDesconectado")}</span>
                    </div>
                    {disconnectedPlayerAlerts.map((entry) => (
                      <div
                        key={`disconnect-alert-${entry.id}`}
                        className="xima-tournament-disconnect-alert"
                      >
                        <strong>{entry.displayName} se ha desconectado.</strong>
                        <span>
                          {i18nT("tournaments.leQuedan")}{" "}
                          <b>{entry.remainingSeconds}</b>{" "}
                          {i18nT("tournaments.segundosParaReconectarse")}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="xima-tournament-action-stack">
                  {shouldShowManualRefreshButton ? (
                    <Button
                      className="xima-tournament-action-button xima-tournament-cyan-button"
                      size="large"
                      icon={<IoRefreshOutline />}
                      onClick={handleManualPositionRefresh}
                      loading={manualPositionRefreshLoading}
                      disabled={manualPositionRefreshLoading}
                    >
                      Actualizar
                    </Button>
                  ) : null}
                  {!shouldShowPostGameActions ? (
                    <Button
                      className="xima-tournament-action-button play-button"
                      size="large"
                      type="primary"
                      disabled={isPlayButtonDisabled}
                      onClick={startMatchmaking}
                    >
                      {expired
                        ? "Expired"
                        : matchmaking
                        ? isSpectatorRequested
                          ? "Connecting..."
                          : "Searching..."
                        : isMatchInProgress
                        ? "En partida"
                        : isSpectatorRequested
                        ? "Watch"
                        : gameEnded
                        ? "Volver a jugar"
                        : "Play"}
                    </Button>
                  ) : null}
                </div>

                {shouldShowPostGameActions ? (
                  <div className="xima-tournament-section xima-tournament-finished-section">
                    <div className="xima-tournament-section-title">
                      <IoTrophyOutline />
                      <span>{i18nT("tournaments.partidaFinalizada")}</span>
                    </div>
                    {postGameMessage ? (
                      <div className="xima-tournament-winner-tag">
                        <IoTrophyOutline aria-hidden="true" />
                        <span>{postGameMessage}</span>
                      </div>
                    ) : null}
                    <div className="xima-tournament-action-stack">
                      {!shouldHidePostGameQueueButtons ? (
                        <Button
                          className="xima-tournament-action-button xima-tournament-green-button"
                          size="large"
                          type="primary"
                          icon={<IoRefreshOutline />}
                          onClick={() => goToTournamentWaitingRoom({ autoPlay: true })}
                        >
                          {i18nT("tournaments.jugarDeNuevo")}
                        </Button>
                      ) : null}
                      {!shouldHidePostGameQueueButtons ? (
                        <Button
                          className="xima-tournament-action-button xima-tournament-cyan-button"
                          size="large"
                          icon={<IoTrophyOutline />}
                          onClick={goToTournamentWaitingRoom}
                        >
                          {i18nT("tournaments.irATorneo")}
                        </Button>
                      ) : null}
                      {shouldShowCreateGamePostGameButton ? (
                        <Button
                          className="xima-tournament-action-button xima-tournament-rose-button"
                          size="large"
                          icon={<IoCubeOutline />}
                          onClick={isChampionshipRoom ? goToChampionship : playAgainSameMode}
                        >
                          {isChampionshipRoom ? "Volver al Campeonato" : "Volver a jugar"}
                        </Button>
                      ) : null}
                      {shouldShowFreeBoxGoToPuzzleButton ? (
                        <Button
                          className="xima-tournament-action-button xima-tournament-cyan-button"
                          size="large"
                          icon={<IoCubeOutline />}
                          onClick={goToPuzzleSection}
                        >
                          {i18nT("tournaments.irAPuzzle")}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </Card>

              <div className="xima-tournament-chat-wrap hidden lg:block">
                {quickChatEnabled ? (
                  <Card className="game-card xima-tournament-card xima-tournament-chat-card">
                    <div className="xima-tournament-chat-heading">
                      <div className="xima-tournament-section-title">
                        <IoChatbubbleEllipsesOutline />
                        <span>{i18nT("tournaments.chatRapido")}</span>
                      </div>
                      {!isSpectatorRequested ? (
                        <Button
                          size="middle"
                          type="primary"
                          className="xima-tournament-window-button xima-tournament-cyan-button"
                          icon={<IoOpenOutline />}
                          onClick={() => setChatModalOpen(true)}
                          disabled={!roomId}
                        >
                          Ventana
                        </Button>
                      ) : null}
                    </div>
                    <div className="xima-hidden-scroll xima-tournament-chat-window">
                      {chatMessages.length === 0 ? (
                        <div className="xima-tournament-chat-empty">
                          {roomId
                            ? "Aun no hay mensajes en la partida."
                            : "Entra a la sala para usar chat."}
                        </div>
                      ) : (
                        chatMessages.slice(-3).map((chatItem, index) => (
                          <div
                            key={`${chatItem.sentAt}-${index}`}
                            className="xima-tournament-chat-message"
                          >
                            <span>{formatChatUser(chatItem.userId)}:</span>
                            <strong>{chatItem.message}</strong>
                          </div>
                        ))
                      )}
                    </div>
                    {isSpectatorRequested || !roomId ? (
                      <div className="xima-tournament-chat-note">
                        {isSpectatorRequested
                          ? "Modo espectador: chat solo lectura."
                          : "Esperando sala..."}
                      </div>
                    ) : null}
                  </Card>
                ) : null}
              </div>
            </div>

            <div className="xima-tournament-bottom-actions">
              {shouldShowBottomHomeAction ? (
                <div className="xima-tournament-action-stack">
                  {!hasGameFinishedForHomeAction &&
                  normalizedMode === "freebox" &&
                  canCreatePuzzleSnapshots &&
                  !isPuzzleRequested &&
                  !isPuzzleTournament ? (
                    <Button
                      size="large"
                      className="xima-tournament-action-button xima-tournament-green-button"
                      icon={<IoCubeOutline />}
                      onClick={openPuzzleNameModalForSave}
                      loading={savingPuzzleSnapshot}
                      disabled={loadingPuzzleSnapshot || isSpectatorRequested}
                    >
                      {i18nT("tournaments.guardarPosicion")}
                    </Button>
                  ) : null}
                  {!hasGameFinishedForHomeAction && shouldShowFreeBoxGoToPuzzleButton ? (
                    <Button
                      size="large"
                      className="xima-tournament-action-button xima-tournament-cyan-button"
                      icon={<IoCubeOutline />}
                      onClick={goToPuzzleSection}
                    >
                      {i18nT("tournaments.irAPuzzle")}
                    </Button>
                  ) : null}
                  <Button
                    size="large"
                    icon={<IoHomeOutline />}
                    className="xima-tournament-action-button xima-tournament-cyan-button"
                    onClick={goToGameHome}
                  >
                    {isChampionshipRoom
                      ? "Volver al Campeonato"
                      : hasGameFinishedForHomeAction || isBotLocalOrFreeBoxMode
                      ? i18nT("tournaments.irAInicio")
                      : "Inicio"}
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
          )}
        </div>
      </div>

      <Modal
        centered
        open={Boolean(leaveGameRequest)}
        closable={false}
        maskClosable={false}
        footer={null}
        width={460}
        rootClassName="xima-leave-game-modal"
        onCancel={() => setLeaveGameRequest(null)}
      >
        <div className="xima-leave-game">
          <h3>¿Seguro que quieres abandonar el juego?</h3>
          <p>
            Si sales de esta pantalla, <strong>perderás la partida</strong>: para salir tienes que
            rendirte.
          </p>
          <p className="xima-leave-game-hint">
            Si se te cayó la conexión, toca «Reconectar» para volver a la partida.
          </p>
          <div className="xima-leave-game-actions">
            <button type="button" className="is-danger" onClick={abandonGameAndLeave}>
              Sí, abandonar
            </button>
            <button type="button" className="is-primary" onClick={() => setLeaveGameRequest(null)}>
              Continuar jugando
            </button>
            <button
              type="button"
              className="is-reconnect"
              disabled={manualPositionRefreshLoading}
              onClick={() => {
                setLeaveGameRequest(null);
                handleManualPositionRefresh();
              }}
            >
              Reconectar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        centered
        open={puzzleNameModalOpen}
        title={<span className="text-emerald-200">{i18nT("tournaments.guardarPosicionComoPuzzle")}</span>}
        okText="Confirmar y guardar"
        cancelText="Cancelar"
        onOk={confirmPuzzleNameAndSave}
        onCancel={closePuzzleNameModal}
        maskClosable={!savingPuzzleSnapshot}
        keyboard={!savingPuzzleSnapshot}
        okButtonProps={{
          loading: savingPuzzleSnapshot,
          disabled:
            normalizePuzzleTurnLimit(puzzleTurnLimitDraft) <= 0 ||
            (puzzleSolveTimeLimitEnabledDraft &&
              normalizePuzzleSolveTimeLimitSeconds(
                Number(puzzleSolveTimeLimitMinutesDraft || 0) * 60,
                0
              ) <= 0),
        }}
        cancelButtonProps={{ disabled: savingPuzzleSnapshot }}
      >
        <div className="mb-3 text-xs text-gray-300">{i18nT("tournaments.elNombreEsOpcionalSiLoDejasVacio")}</div>
        <div className="flex items-center gap-2">
          <Input
            autoFocus
            allowClear
            value={puzzleNameDraft}
            maxLength={120}
            placeholder={i18nT("tournaments.nombreDelPuzzleOpcional")}
            onChange={(event) => {
              const nextValue = String(event?.target?.value || "").slice(0, 120);
              const resolvedTurnLimit = Math.max(
                1,
                normalizePuzzleTurnLimit(puzzleTurnLimitDraft, DEFAULT_PUZZLE_TURN_LIMIT)
              );
              const resolvedSolveTimeLimitSeconds = puzzleSolveTimeLimitEnabledDraft
                ? normalizePuzzleSolveTimeLimitSeconds(
                    Number(puzzleSolveTimeLimitMinutesDraft || 0) * 60,
                    0
                  )
                : 0;
              const currentAutoName = buildDefaultPuzzleNameFromRules(
                resolvedTurnLimit,
                resolvedSolveTimeLimitSeconds
              ).slice(0, 120);

              setPuzzleNameDraft(nextValue);
              if (!String(nextValue || "").trim()) {
                setPuzzleNameTouchedDraft(false);
                return;
              }
              setPuzzleNameTouchedDraft(String(nextValue || "").trim() !== currentAutoName);
            }}
            onFocus={(event) => event?.target?.select?.()}
            onPressEnter={confirmPuzzleNameAndSave}
            disabled={savingPuzzleSnapshot}
          />
          <InputNumber
            min={1}
            max={MAX_PUZZLE_TURN_LIMIT}
            value={puzzleTurnLimitDraft}
            className="w-[150px]"
            placeholder="Turnos"
            onChange={(value) =>
              setPuzzleTurnLimitDraft(normalizePuzzleTurnLimit(value, DEFAULT_PUZZLE_TURN_LIMIT))
            }
            onPressEnter={confirmPuzzleNameAndSave}
            disabled={savingPuzzleSnapshot}
          />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <InputNumber
            min={0}
            max={MAX_PUZZLE_REWARD_VALUE}
            value={puzzleRewardValueDraft}
            className="w-[180px]"
            placeholder={i18nT("lobby.recompensaX")}
            onFocus={(event) => event?.target?.select?.()}
            onChange={(value) => setPuzzleRewardValueDraft(normalizePuzzleRewardValue(value, 0))}
            onPressEnter={confirmPuzzleNameAndSave}
            disabled={savingPuzzleSnapshot}
          />
          <span className="text-xs text-gray-300">{i18nT("tournaments.recompensaAlResolver")}</span>
        </div>
        <div className="mt-3 rounded-lg border border-cyan-700/60 bg-cyan-950/25 p-2">
          <div className="mb-1 text-[11px] uppercase tracking-[0.12em] text-cyan-200/90">{i18nT("tournaments.tiempoDeSolucionOpcional")}</div>
          {puzzleSolveTimeLimitEnabledDraft ? (
            <div className="flex items-center gap-2">
              <InputNumber
                min={1}
                max={MAX_PUZZLE_SOLVE_TIME_MINUTES}
                value={puzzleSolveTimeLimitMinutesDraft}
                className="w-[160px]"
                placeholder="Minutos"
                onChange={(value) =>
                  setPuzzleSolveTimeLimitMinutesDraft(
                    Math.max(
                      1,
                      Math.min(
                        MAX_PUZZLE_SOLVE_TIME_MINUTES,
                        Math.floor(Number(value) || DEFAULT_PUZZLE_SOLVE_TIME_MINUTES)
                      )
                    )
                  )
                }
                onPressEnter={confirmPuzzleNameAndSave}
                disabled={savingPuzzleSnapshot}
              />
              <span className="text-xs text-cyan-100">min</span>
              <Button
                size="small"
                className="border-cyan-300/70 bg-cyan-900/30 text-cyan-100 hover:bg-cyan-800/40"
                onClick={() => setPuzzleSolveTimeLimitEnabledDraft(false)}
                disabled={savingPuzzleSnapshot}
              >{i18nT("tournaments.sinLimite")}</Button>
            </div>
          ) : (
            <Button
              size="small"
              className="border-cyan-300/70 bg-cyan-900/30 text-cyan-100 hover:bg-cyan-800/40"
              onClick={() => {
                setPuzzleSolveTimeLimitEnabledDraft(true);
                setPuzzleSolveTimeLimitMinutesDraft((current) =>
                  Math.max(
                    1,
                    Math.min(
                      MAX_PUZZLE_SOLVE_TIME_MINUTES,
                      Math.floor(Number(current) || DEFAULT_PUZZLE_SOLVE_TIME_MINUTES)
                    )
                  )
                );
              }}
              disabled={savingPuzzleSnapshot}
            >{i18nT("tournaments.definirTiempo")}</Button>
          )}
          <div className="mt-1 text-[11px] text-cyan-100/75">
            {puzzleSolveTimeLimitEnabledDraft
              ? `Cuenta regresiva: ${formatPuzzleSolveCountdownLabel(
                  Number(puzzleSolveTimeLimitMinutesDraft || 0) * 60
                )}.`
              : "Sin tiempo limite por defecto."}
          </div>
        </div>
      </Modal>

      <Modal
        centered
        open={open && !isGame7Layout}
        footer={
          <Button onClick={cancelAndBackToLobby} danger>{i18nT("tournaments.cancelar")}</Button>
        }
        width={300}
        onCancel={cancelAndBackToLobby}
        maskClosable={false}
        className="text-white shadow-lg bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl"
      >
        <div className="w-full text-center card-header">
          <div className="mb-4 text-2xl">
            {localizeTournamentName(language, tourInfo.name) || tourInfo.name}
          </div>
            {tourInfo?.hasMatchPassword ? (
              <div className="mb-3 rounded border border-[#ffd35a] bg-[#3a2c0a] px-2 py-1 text-xs font-semibold uppercase text-[#ffe9a6]">{i18nT("lobby.partidoConContrasena")}</div>
            ) : null}
            <Spin size="large" />
            <div className="mt-2 mb-4 text-lg font-semibold text-blue-400 animate-pulse">
              {isSpectatorRequested
                ? "Joining as spectator..."
                : isPuzzleMode
                ? "Cargando puzzle..."
                : isBotLocalMode
                ? "Starting local bot match..."
                : ["localdice", "localcienzar", "localsciencevsazar", "localfree"].includes(
                    String(tourInfo.mode || "").toLowerCase()
                  )
                ? "Starting local friends match..."
                : tourInfo.mode === "freebox"
                ? "Starting Free Box local mode..."
                : "Finding user..."}
            </div>
          </div>
      </Modal>

      {chatModalOpen && !isSpectatorRequested ? (
        <div
          className="fixed z-[95] flex flex-col rounded-xl border border-cyan-400/80 bg-slate-900/95 p-3 shadow-[0_0_28px_rgba(34,211,238,0.35)]"
          style={{
            left: `${chatFloatingPosition.x}px`,
            top: `${chatFloatingPosition.y}px`,
            width: `${chatFloatingSize.width}px`,
            height: `${chatFloatingSize.height}px`,
            minWidth: `${CHAT_FLOATING_MIN_WIDTH}px`,
            minHeight: `${CHAT_FLOATING_MIN_HEIGHT}px`,
            maxWidth: "98vw",
            maxHeight: "96vh",
            overflow: "hidden",
          }}
        >
          <div
            className="mb-2 flex cursor-move select-none items-center justify-between gap-2"
            onMouseDown={handleQuickChatFloatingDragStart}
          >
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.16em] text-cyan-200">{i18nT("tournaments.chatRapidoDeSala")}</div>
              <div className="text-xs text-cyan-100/70">{i18nT("tournaments.arrastraEstaBarraParaMoverYUsaLas")}</div>
            </div>
            <Button
              size="small"
              className="border-cyan-300/70 bg-cyan-900/40 text-cyan-100 hover:bg-cyan-800/55"
              onClick={() => setChatModalOpen(false)}
            >{i18nT("tournaments.cerrar")}</Button>
          </div>

          <div className="mb-2 text-sm text-cyan-100/80">
            {canSendQuickChat
              ? "Selecciona un mensaje para enviarlo a la sala."
              : "El chat se habilita cuando entras a una partida online."}
          </div>

          <div
            ref={chatFloatingScrollRef}
            className="xima-hidden-scroll mb-2 min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg border border-cyan-500/40 bg-black/30 p-3"
          >
            {chatMessages.length === 0 ? (
              <div className="text-base text-gray-300">{i18nT("tournaments.aunNoHayMensajes")}</div>
            ) : (
              chatMessages.map((chatItem, index) => (
                <div key={`${chatItem.sentAt}-${index}`} className="text-lg leading-tight">
                  <span className="mr-2 font-semibold text-cyan-300">
                    {formatChatUser(chatItem.userId)}:
                  </span>
                  <span className="text-gray-100">{chatItem.message}</span>
                </div>
              ))
            )}
          </div>

          <div className="xima-hidden-scroll grid max-h-56 grid-cols-2 gap-2 overflow-y-auto pr-1 md:grid-cols-3">
            {QUICK_CHAT_MESSAGES.map((message) => (
              <Button
                key={message}
                size="middle"
                className="h-auto whitespace-normal border-cyan-300/80 bg-cyan-950/45 py-2 text-base text-cyan-100 hover:bg-cyan-800/45"
                disabled={!canSendQuickChat}
                onClick={() => sendQuickChat(message)}
              >
                {message}
              </Button>
            ))}
          </div>

          <div
            className="absolute -left-1 -top-1 h-3 w-3 cursor-nwse-resize rounded-full border border-cyan-200 bg-cyan-800"
            onMouseDown={(event) => handleQuickChatFloatingResizeStart("nw", event)}
          />
          <div
            className="absolute -right-1 -top-1 h-3 w-3 cursor-nesw-resize rounded-full border border-cyan-200 bg-cyan-800"
            onMouseDown={(event) => handleQuickChatFloatingResizeStart("ne", event)}
          />
          <div
            className="absolute -left-1 -bottom-1 h-3 w-3 cursor-nesw-resize rounded-full border border-cyan-200 bg-cyan-800"
            onMouseDown={(event) => handleQuickChatFloatingResizeStart("sw", event)}
          />
          <div
            className="absolute -bottom-1 -right-1 h-3 w-3 cursor-nwse-resize rounded-full border border-cyan-200 bg-cyan-800"
            onMouseDown={(event) => handleQuickChatFloatingResizeStart("se", event)}
          />
        </div>
      ) : null}
    </ConfigProvider>
  );
};

export default Tournament;



